import { getAssignablePagePermissions, getAssignableTabPermissions, getAssignableOperationsTabPermissions, getPageLabelMap, MASTER_DATA_TABS, OPERATIONS_TABS } from '../config/pages';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { getLinkedPageOptions } from './appPages';
import { useAuth } from '../AuthContext';

// ── CONSTANTS ──────────────────────────────────────────────
const PERM_LABELS = {
  intern:      { label:'Intern',      cls:'badge-gray',   icon:'🎓' },
  executor:    { label:'Executor',    cls:'badge-blue',   icon:'🛠️' },
  contributor: { label:'Contributor', cls:'badge-blue',   icon:'✏️' },
  coordinator: { label:'Coordinator', cls:'badge-purple', icon:'📋' },
  reviewer:    { label:'Reviewer',    cls:'badge-yellow', icon:'🔍' },
  approver:    { label:'Approver',    cls:'badge-green',  icon:'✅' },
  manager:     { label:'Manager',     cls:'badge-orange', icon:'👔' },
  admin:       { label:'Admin',       cls:'badge-red',    icon:'🔑' },
};
const COLORS = ['#3b7ef0','#6c58f0','#1db87a','#e8a020','#e84545','#e87830','#9050e8','#18b8b8','#e83898'];
const AVAIL_OPTS = [
  {value:'available', label:'✅ Available', cls:'badge-green'},
  {value:'busy',      label:'🔴 Busy',      cls:'badge-red'},
  {value:'absent',    label:'⚫ Absent',     cls:'badge-gray'},
  {value:'partial',   label:'🟡 Partial',   cls:'badge-yellow'},
];

// ── STANDARD PRESETS ───────────────────────────────────────
const STANDARD_DEPT_SETS = {
  onehundred: {
    label: '🏢 One Hundred Style',
    desc: 'The department structure used at One Hundred Technologies',
    depts: [
      { name:'Account Management', color:'#3b7ef0', description:'Client relationships, briefings, approvals and reporting delivery' },
      { name:'Creative',           color:'#6c58f0', description:'Design, video, copy and all content production' },
      { name:'Strategy',           color:'#1db87a', description:'Campaign planning, market research and content calendar' },
      { name:'Paid Media',         color:'#e87830', description:'Google, Meta and LinkedIn paid advertising' },
      { name:'Social Media',       color:'#9050e8', description:'Organic social scheduling, community management and engagement' },
      { name:'Analytics',          color:'#18b8b8', description:'KPI tracking, dashboard reporting and performance insights' },
      { name:'PR & Outreach',      color:'#e83898', description:'Media relations, influencer partnerships and press releases' },
      { name:'Technical',          color:'#e84545', description:'SEO, website management and marketing technology' },
      { name:'Content',            color:'#1db87a', description:'Writes captions, scripts, post copy, ad copy, SEO articles and all content text' },
      { name:'Quality',            color:'#18b8b8', description:'Internal approval of contents, creatives etc.' },
      { name:'Management',         color:'#e8a020', description:'Leadership, strategy and overall agency direction' },
    ],
  },
  general: {
    label: '🌐 General Agency Standard',
    desc: 'A broader standard structure suitable for any digital marketing agency',
    depts: [
      { name:'Creative',              color:'#6c58f0', description:'Design, video, copy and all content production' },
      { name:'Copy & Content',        color:'#1db87a', description:'Copywriting, scripts, captions, blogs and all content text' },
      { name:'Social Media',          color:'#9050e8', description:'Organic social management, scheduling and engagement' },
      { name:'Performance Marketing', color:'#e87830', description:'Paid ads — Meta, Google, YouTube and other platforms' },
      { name:'Strategy & Planning',   color:'#18b8b8', description:'Campaign planning, client strategy and market research' },
      { name:'Client Servicing',      color:'#3b7ef0', description:'Account management and client communication' },
      { name:'Operations',            color:'#e8a020', description:'Workflow, project management and delivery' },
      { name:'Technology',            color:'#e84545', description:'Website, tools, automation and reporting' },
      { name:'Business Development',  color:'#e83898', description:'Sales, proposals and client onboarding' },
      { name:'HR & Admin',            color:'#888888', description:'Hiring, attendance, finance and administration' },
    ],
  },
};

const STANDARD_ROLES = [
  { name:'Coordinator',                        department:'Account Management', permissions:'coordinator', color:'#3b7ef0', responsibilities:'Client communication, briefing, approvals and briefing delivery',                                                                   skills:'CRM, Communication, MS Office' },
  { name:'Creative Director',                  department:'Creative',           permissions:'approver',    color:'#6c58f0', responsibilities:'Creative strategy, brand consistency, final design sign-off and team mentoring',                                                   skills:'Photoshop, Illustrator, Figma, Brand Design' },
  { name:'Graphic Designer',                   department:'Creative',           permissions:'contributor', color:'#6c58f0', responsibilities:'Design social media creatives, presentations and brand assets',                                                                     skills:'Photoshop, Illustrator, Canva, Figma' },
  { name:'Content Writer',                     department:'Content',            permissions:'contributor', color:'#1db87a', responsibilities:'Write captions, blogs, ad copy, email content and scripts',                                                                        skills:'SEO Writing, Grammarly, Google Docs' },
  { name:'Video Editor',                       department:'Creative',           permissions:'contributor', color:'#6c58f0', responsibilities:'Edit reels, YouTube videos, motion graphics and subtitles',                                                                        skills:'Premiere Pro, After Effects, DaVinci Resolve' },
  { name:'Scheduler',                          department:'Social Media',       permissions:'contributor', color:'#9050e8', responsibilities:'Post scheduling, community management and engagement monitoring',                                                                   skills:'Buffer, Hootsuite, Meta Suite, Canva' },
  { name:'Paid Media Specialist (Digital Marketer)', department:'Paid Media',   permissions:'contributor', color:'#e87830', responsibilities:'Run and optimise paid campaigns on Google, Meta and LinkedIn',                                                                     skills:'Google Ads, Meta Ads Manager, Google Analytics' },
  { name:'Analytics Specialist',               department:'Analytics',          permissions:'contributor', color:'#18b8b8', responsibilities:'Track KPIs, build dashboards and prepare monthly reports',                                                                         skills:'Google Analytics, Data Studio, Excel, SQL' },
  { name:'SEO Specialist',                     department:'Technical',          permissions:'contributor', color:'#e84545', responsibilities:'Keyword research, on-page optimisation and link building',                                                                         skills:'SEMrush, Ahrefs, Google Search Console' },
  { name:'Strategy Team',                      department:'Strategy',           permissions:'contributor', color:'#1db87a', responsibilities:'Campaign planning, market research, competitor analysis and content calendar',                                                     skills:'Research, PPT, Google Trends, SEMrush' },
  { name:'PR Manager',                         department:'PR & Outreach',      permissions:'contributor', color:'#e83898', responsibilities:'Media relations, influencer outreach and press releases',                                                                           skills:'PR tools, Influencer CRM, Gmail Outreach' },
  { name:'Research Analyst',                   department:'Analytics',          permissions:'contributor', color:'#18b8b8', responsibilities:'Market research, audience analysis and competitor benchmarking',                                                                    skills:'Surveys, Data Analysis, Google Trends' },
  { name:'Content Approver - Internal',        department:'Quality',            permissions:'approver',    color:'#18b8b8', responsibilities:'Internal approval of content before delivery',                                                                                     skills:'' },
  { name:'Videographer',                       department:'Creative',           permissions:'contributor', color:'#6c58f0', responsibilities:'Captures high-quality videos for social media content and brand storytelling',                                                     skills:'Camera Operation, Lighting Setup, Audio Recording, Gimbal Handling, DaVinci Resolve' },
  { name:'Graphics Approver - Internal',       department:'Quality',            permissions:'approver',    color:'#18b8b8', responsibilities:'Approve static creatives before publishing',                                                                                       skills:'' },
  { name:'Video Approver - Internal',          department:'Quality',            permissions:'approver',    color:'#18b8b8', responsibilities:'Video creatives approval before delivery',                                                                                         skills:'' },
  { name:'Managing Director',                  department:'Management',         permissions:'admin',       color:'#e8a020', responsibilities:'Owner / Head of agency — overall direction, decisions and final approvals',                                                        skills:'' },
  { name:'ADMIN-IT',                           department:'Management',         permissions:'admin',       color:'#e8a020', responsibilities:'System administration, tool access management and IT support',                                                                     skills:'' },
];

// TABS now comes from src/config/pages.js (MASTER_DATA_TABS)
const TABS = MASTER_DATA_TABS;

// ── HELPERS ────────────────────────────────────────────────
function initials(name='') {
  return name.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);
}
function fmtRupees(n) {
  if(!n) return '₹0';
  if(n>=100000) return '₹'+Math.round(n/1000)/100+'L';
  if(n>=1000) return '₹'+Math.round(n/1000)+'K';
  return '₹'+n;
}

// ── COLOR PICKER ───────────────────────────────────────────
function ColorPicker({ value, onChange }) {
  return (
    <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'4px'}}>
      {COLORS.map(c => (
        <div key={c}
          onClick={() => onChange(c)}
          style={{
            width:'24px', height:'24px', borderRadius:'50%', background:c, cursor:'pointer',
            border: value===c ? '3px solid var(--text)' : '2px solid transparent',
            boxShadow: value===c ? '0 0 0 1px '+c : 'none',
            transition:'border 0.15s',
          }}
        />
      ))}
    </div>
  );
}

// ── MODAL WRAPPER ──────────────────────────────────────────
function Modal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div className="modal-overlay open" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <div className="icon-btn" onClick={onClose}>✕</div>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ── TOGGLE ─────────────────────────────────────────────────
function Toggle({ checked, onChange }) {
  return (
    <div className="toggle-wrap">
      <label className="toggle">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
        <span className="toggle-slider"></span>
      </label>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: DEPARTMENTS
// ══════════════════════════════════════════════════════════
function DepartmentsTab() {
  const [depts, setDepts] = useState([]);
  const [team, setTeam]   = useState([]);
  const [roles, setRoles] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name:'', head:'', desc:'', color:COLORS[0] });
  const [saving, setSaving] = useState(false);
  const [stdModal, setStdModal] = useState(false);
  const [stdSet, setStdSet]     = useState('onehundred');
  const [stdSel, setStdSel]     = useState({});
  const [stdSaving, setStdSaving] = useState(false);

  const load = useCallback(async () => {
    const [d,t,r] = await Promise.all([
      api.get('/departments'), api.get('/team-members'), api.get('/roles')
    ]);
    setDepts(Array.isArray(d.data?.data) ? d.data.data : Array.isArray(d.data) ? d.data : []);
    setTeam(Array.isArray(t.data?.data) ? t.data.data : Array.isArray(t.data) ? t.data : []);
    setRoles(Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name:'', head:'', desc:'', color:COLORS[0] });
    setModal(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ name:d.name, head:d.head||'', desc:d.description||d.desc||'', color:d.color||COLORS[0] });
    setModal(true);
  };
  const save = async () => {
    if(!form.name.trim()) return;
    setSaving(true);
    try {
      if(editing) await api.put('/departments/'+editing.id, { name:form.name, head:form.head, description:form.desc, color:form.color });
      else         await api.post('/departments', { name:form.name, head:form.head, description:form.desc, color:form.color, enabled:true });
      setModal(false); load();
    } finally { setSaving(false); }
  };
  const del = async (d) => {
    const inUse = roles.filter(r => (r.department===d.name||r.dept===d.name)).length;
    if(inUse > 0) { alert(`Cannot delete — ${inUse} role(s) belong to this department`); return; }
    if(!window.confirm('Delete department "'+d.name+'"?')) return;
    await api.delete('/departments/'+d.id); load();
  };
  const toggle = async (d) => {
    await api.put('/departments/'+d.id, { ...d, enabled: !d.enabled });
    load();
  };

  const openStd = () => {
    const existing = new Set(depts.map(d => d.name.toLowerCase()));
    const initSel = {};
    STANDARD_DEPT_SETS[stdSet].depts.forEach(d => {
      initSel[d.name] = !existing.has(d.name.toLowerCase());
    });
    setStdSel(initSel);
    setStdModal(true);
  };
  const onStdSetChange = (key) => {
    setStdSet(key);
    const existing = new Set(depts.map(d => d.name.toLowerCase()));
    const initSel = {};
    STANDARD_DEPT_SETS[key].depts.forEach(d => {
      initSel[d.name] = !existing.has(d.name.toLowerCase());
    });
    setStdSel(initSel);
  };
  const addStandard = async () => {
    const toAdd = STANDARD_DEPT_SETS[stdSet].depts.filter(d => stdSel[d.name]);
    if (!toAdd.length) { setStdModal(false); return; }
    setStdSaving(true);
    try {
      for (const d of toAdd) {
        await api.post('/departments', { name:d.name, description:d.description, color:d.color, enabled:true });
      }
      setStdModal(false); load();
    } finally { setStdSaving(false); }
  };

  const getMemberCount = (d) => team.filter(m => {
    const mroles = Array.isArray(m.roles) ? m.roles : (m.role ? [m.role] : []);
    return roles.some(r => mroles.includes(r.name) && (r.department===d.name||r.dept===d.name));
  }).length;
  const getRoleCount  = (d) => roles.filter(r => r.department===d.name||r.dept===d.name).length;

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🏢 Department Master</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Define departments within your agency. Departments group roles and help organise the team structure.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={openStd} style={{fontSize:'0.82rem'}}>☆ Add Standard Departments</button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Department</button>
        </div>
      </div>

      {/* Standard Departments Modal */}
      <Modal open={stdModal} onClose={() => setStdModal(false)}
        title="☆ Add Standard Departments"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setStdModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={addStandard} disabled={stdSaving||!Object.values(stdSel).some(Boolean)}>
            {stdSaving ? 'Adding...' : `✅ Add ${Object.values(stdSel).filter(Boolean).length} Department(s)`}
          </button>
        </>}>
        <div style={{marginBottom:'12px'}}>
          <div className="text-xs text-muted" style={{marginBottom:'10px'}}>Choose a preset and select which departments to add. Existing departments are grayed out.</div>
          <div className="flex" style={{gap:'8px',marginBottom:'16px'}}>
            {Object.entries(STANDARD_DEPT_SETS).map(([key, set]) => (
              <button key={key}
                onClick={() => onStdSetChange(key)}
                className={stdSet===key ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{fontSize:'0.8rem'}}>
                {set.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-muted" style={{marginBottom:'10px'}}>{STANDARD_DEPT_SETS[stdSet].desc}</div>
          <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
            {STANDARD_DEPT_SETS[stdSet].depts.map(d => {
              const exists = depts.some(x => x.name.toLowerCase()===d.name.toLowerCase());
              return (
                <label key={d.name} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 10px',borderRadius:'8px',background:'var(--bg3)',border:'1px solid var(--border)',cursor:exists?'default':'pointer',opacity:exists?0.5:1}}>
                  <input type="checkbox" checked={!!stdSel[d.name]} disabled={exists}
                    onChange={e => setStdSel(s => ({...s,[d.name]:e.target.checked}))} />
                  <div style={{width:'10px',height:'10px',borderRadius:'50%',background:d.color,flexShrink:0}}></div>
                  <div style={{flex:1}}>
                    <div className="text-sm fw-700">{d.name} {exists && <span className="badge badge-gray" style={{fontSize:'0.6rem'}}>Already exists</span>}</div>
                    <div className="text-xs text-muted">{d.description}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </Modal>

      {/* Stat cards */}
      <div className="mb-16" style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'12px'}}>
        {depts.map(d => {
          const mc = getMemberCount(d), rc = getRoleCount(d);
          return (
            <div key={d.id} className="kpi-card" style={{borderTop:`3px solid ${d.color||'#3b7ef0'}`,cursor:'pointer',opacity:d.enabled===false?0.45:1}}>
              <div className="kpi-label" style={{display:'flex',alignItems:'center',gap:'6px'}}>
                <span style={{width:'8px',height:'8px',borderRadius:'50%',background:d.color||'#3b7ef0',display:'inline-block',flexShrink:0}}></span>
                {d.name}
                {d.enabled===false && <span className="badge badge-gray" style={{fontSize:'0.6rem',padding:'1px 5px'}}>Off</span>}
              </div>
              <div className="kpi-value" style={{fontSize:'1.4rem'}}>{mc}</div>
              <div className="kpi-sub">{rc} role{rc!==1?'s':''} · {mc} member{mc!==1?'s':''}</div>
              {d.head && <div className="text-xs text-muted" style={{marginTop:'4px'}}>Lead: {d.head}</div>}
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Department</th><th>Head / Lead</th><th>Description</th><th>Team Members</th><th>Status / Actions</th></tr>
          </thead>
          <tbody>
            {depts.map(d => {
              const mc = getMemberCount(d);
              const headMember = team.find(m => m.name===d.head);
              return (
                <tr key={d.id} style={{opacity:d.enabled===false?0.55:1}}>
                  <td>
                    <div className="flex" style={{gap:'8px'}}>
                      <div style={{width:'12px',height:'12px',borderRadius:'3px',background:d.color||'#3b7ef0',flexShrink:0,marginTop:'2px'}}></div>
                      <span className="fw-700">{d.name}</span>
                      {d.enabled===false && <span className="badge badge-gray" style={{fontSize:'0.6rem',padding:'1px 6px'}}>Disabled</span>}
                    </div>
                  </td>
                  <td>
                    {d.head
                      ? <div className="flex" style={{gap:'6px',alignItems:'center'}}>
                          <div className="avatar" style={{background:headMember?.color||'#888',width:'24px',height:'24px',fontSize:'0.62rem'}}>{initials(d.head)}</div>
                          <span className="text-sm">{d.head}</span>
                        </div>
                      : <span className="text-xs text-muted">— Not assigned —</span>
                    }
                  </td>
                  <td><div className="text-xs" style={{color:'var(--text2)',maxWidth:'220px',lineHeight:'1.5'}}>{d.description||d.desc||'—'}</div></td>
                  <td><span className={`badge ${mc>0?'badge-blue':'badge-gray'}`}>{mc} member{mc!==1?'s':''}</span></td>
                  <td>
                    <div className="flex" style={{gap:'6px',alignItems:'center'}}>
                      <Toggle checked={d.enabled!==false} onChange={() => toggle(d)} />
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => openEdit(d)}>✏️ Edit</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => del(d)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {depts.length===0 && <tr><td colSpan={5} className="text-muted text-sm" style={{padding:'20px',textAlign:'center'}}>No departments yet. Add your first department.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={modal} onClose={() => setModal(false)}
        title={editing ? '✏️ Edit Department' : '+ Add Department'}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 Save Department'}</button>
        </>}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Department Name</label>
            <input className="form-input" placeholder="e.g. Creative" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Department Head / Lead</label>
            <select className="form-select" value={form.head} onChange={e=>setForm({...form,head:e.target.value})}>
              <option value="">— Select team member —</option>
              {team.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-textarea" placeholder="What this department does..." value={form.desc} onChange={e=>setForm({...form,desc:e.target.value})} />
        </div>
        <div className="form-group">
          <label className="form-label">Color Tag</label>
          <ColorPicker value={form.color} onChange={c=>setForm({...form,color:c})} />
        </div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: ROLES
// ══════════════════════════════════════════════════════════
function RolesTab() {
  const [roles, setRoles]   = useState([]);
  const [depts, setDepts]   = useState([]);
  const [team, setTeam]     = useState([]);
  const [modal, setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]     = useState({ name:'', dept:'', responsibilities:'', skills:'', permissions:'contributor', color:COLORS[0] });
  const [saving, setSaving] = useState(false);
  const [stdModal, setStdModal]   = useState(false);
  const [stdSel, setStdSel]       = useState({});
  const [stdSaving, setStdSaving] = useState(false);

  const load = useCallback(async () => {
    const [r,d,t] = await Promise.all([api.get('/roles'), api.get('/departments'), api.get('/team-members')]);
    setRoles(Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : []);
    setDepts(Array.isArray(d.data?.data) ? d.data.data : Array.isArray(d.data) ? d.data : []);
    setTeam(Array.isArray(t.data?.data) ? t.data.data : Array.isArray(t.data) ? t.data : []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm({ name:'', dept:depts[0]?.name||'', responsibilities:'', skills:'', permissions:'contributor', color:COLORS[0] }); setModal(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name:r.name, dept:r.department||r.dept||'', responsibilities:r.responsibilities||'', skills:r.skills||'', permissions:r.permissions||'contributor', color:r.color||COLORS[0] }); setModal(true); };
  const save = async () => {
    if(!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name:form.name, department:form.dept, dept:form.dept, responsibilities:form.responsibilities, skills:form.skills, permissions:form.permissions, color:form.color };
      if(editing) await api.put('/roles/'+editing.id, payload);
      else         await api.post('/roles', { ...payload, enabled:true });
      setModal(false); load();
    } finally { setSaving(false); }
  };
  const del = async (r) => {
    const inUse = team.filter(m => { const ra = Array.isArray(m.roles)?m.roles:(m.role?[m.role]:[]); return ra.includes(r.name); }).length;
    if(inUse > 0) { alert(`Cannot delete — ${inUse} team member(s) have this role`); return; }
    if(!window.confirm('Delete role "'+r.name+'"?')) return;
    await api.delete('/roles/'+r.id); load();
  };
  const toggle = async (r) => { await api.put('/roles/'+r.id, { ...r, enabled: !r.enabled }); load(); };

  const openStdRoles = () => {
    const existing = new Set(roles.map(r => r.name.toLowerCase()));
    const initSel = {};
    STANDARD_ROLES.forEach(r => { initSel[r.name] = !existing.has(r.name.toLowerCase()); });
    setStdSel(initSel);
    setStdModal(true);
  };
  const addStandardRoles = async () => {
    const toAdd = STANDARD_ROLES.filter(r => stdSel[r.name]);
    if (!toAdd.length) { setStdModal(false); return; }
    setStdSaving(true);
    try {
      for (const r of toAdd) {
        await api.post('/roles', { name:r.name, department:r.department, dept:r.department, responsibilities:r.responsibilities, skills:r.skills, permissions:r.permissions, color:r.color, enabled:true });
      }
      setStdModal(false); load();
    } finally { setStdSaving(false); }
  };

  const getMemberCount = (r) => team.filter(m => { const ra = Array.isArray(m.roles)?m.roles:(m.role?[m.role]:[]); return ra.includes(r.name); }).length;

  // Summary pills grouped by dept
  const deptNames = [...new Set(roles.map(r => r.department||r.dept))].filter(Boolean);

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🎭 Role Master</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Define roles, responsibilities and permissions. These populate the Role dropdown when adding team members.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={openStdRoles} style={{fontSize:'0.82rem'}}>☆ Add Standard Roles</button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Role</button>
        </div>
      </div>

      {/* Standard Roles Modal */}
      <Modal open={stdModal} onClose={() => setStdModal(false)}
        title="☆ Add Standard Roles"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setStdModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={addStandardRoles} disabled={stdSaving||!Object.values(stdSel).some(Boolean)}>
            {stdSaving ? 'Adding...' : `✅ Add ${Object.values(stdSel).filter(Boolean).length} Role(s)`}
          </button>
        </>}>
        <div>
          <div className="text-xs text-muted" style={{marginBottom:'12px'}}>Select which standard roles to add. Roles that already exist are grayed out.</div>
          <div style={{display:'flex',flexDirection:'column',gap:'5px',maxHeight:'400px',overflowY:'auto'}}>
            {STANDARD_ROLES.map(r => {
              const exists = roles.some(x => x.name.toLowerCase()===r.name.toLowerCase());
              const perm = PERM_LABELS[r.permissions] || PERM_LABELS.contributor;
              return (
                <label key={r.name} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 10px',borderRadius:'8px',background:'var(--bg3)',border:'1px solid var(--border)',cursor:exists?'default':'pointer',opacity:exists?0.5:1}}>
                  <input type="checkbox" checked={!!stdSel[r.name]} disabled={exists}
                    onChange={e => setStdSel(s => ({...s,[r.name]:e.target.checked}))} />
                  <div style={{width:'10px',height:'10px',borderRadius:'50%',background:r.color,flexShrink:0}}></div>
                  <div style={{flex:1,minWidth:0}}>
                    <div className="text-sm fw-700" style={{display:'flex',alignItems:'center',gap:'6px',flexWrap:'wrap'}}>
                      {r.name}
                      {exists && <span className="badge badge-gray" style={{fontSize:'0.6rem'}}>Already exists</span>}
                      <span className={`badge ${perm.cls}`} style={{fontSize:'0.6rem'}}>{perm.icon} {perm.label}</span>
                    </div>
                    <div className="text-xs text-muted">{r.department} · {r.skills || 'No tools listed'}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <div style={{marginTop:'10px',display:'flex',gap:'8px'}}>
            <button className="btn btn-secondary" style={{fontSize:'0.75rem'}} onClick={() => {
              const existing = new Set(roles.map(x => x.name.toLowerCase()));
              const all = {};
              STANDARD_ROLES.forEach(r => { all[r.name] = !existing.has(r.name.toLowerCase()); });
              setStdSel(all);
            }}>Select All New</button>
            <button className="btn btn-secondary" style={{fontSize:'0.75rem'}} onClick={() => setStdSel({})}>Deselect All</button>
          </div>
        </div>
      </Modal>

      {/* Summary pills */}
      {deptNames.length > 0 && (
        <div className="mb-16" style={{display:'flex',flexWrap:'wrap',gap:'8px'}}>
          {deptNames.map(d => {
            const count = roles.filter(r => (r.department||r.dept)===d).length;
            const dept = depts.find(x => x.name===d);
            const color = dept?.color || '#888';
            return (
              <div key={d} style={{background:color,color:'#fff',padding:'4px 12px',borderRadius:'20px',fontSize:'0.78rem',fontWeight:600,display:'flex',alignItems:'center',gap:'5px'}}>
                <span style={{width:'6px',height:'6px',borderRadius:'50%',background:'rgba(255,255,255,0.6)',display:'inline-block'}}></span>
                {d} <span style={{opacity:0.75,fontWeight:400}}>({count})</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Role Name</th><th>Department</th><th>Responsibilities</th><th>Skills / Tools</th><th>Permissions</th><th>Team Members</th><th>Status / Actions</th></tr>
          </thead>
          <tbody>
            {roles.map(r => {
              const perm = PERM_LABELS[r.permissions] || PERM_LABELS.contributor;
              const mc = getMemberCount(r);
              return (
                <tr key={r.id} style={{opacity:r.enabled===false?0.55:1}}>
                  <td>
                    <div className="flex" style={{gap:'8px'}}>
                      <div style={{width:'10px',height:'10px',borderRadius:'50%',background:r.color||'#888',flexShrink:0,marginTop:'3px'}}></div>
                      <span className="fw-700">{r.name}</span>
                      {r.enabled===false && <span className="badge badge-gray" style={{fontSize:'0.6rem',padding:'1px 6px'}}>Disabled</span>}
                    </div>
                  </td>
                  <td><span className="badge badge-gray">{r.department||r.dept||'—'}</span></td>
                  <td><div className="text-xs" style={{color:'var(--text2)',maxWidth:'200px',lineHeight:'1.5'}}>{r.responsibilities||'—'}</div></td>
                  <td><div className="text-xs text-muted" style={{maxWidth:'160px'}}>{r.skills||'—'}</div></td>
                  <td><span className={`badge ${perm.cls}`}>{perm.icon} {perm.label}</span></td>
                  <td><span className={`badge ${mc>0?'badge-blue':'badge-gray'}`}>{mc} member{mc!==1?'s':''}</span></td>
                  <td>
                    <div className="flex" style={{gap:'6px',alignItems:'center'}}>
                      <Toggle checked={r.enabled!==false} onChange={() => toggle(r)} />
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => openEdit(r)}>✏️ Edit</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => del(r)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {roles.length===0 && <tr><td colSpan={7} className="text-muted text-sm" style={{padding:'20px',textAlign:'center'}}>No roles yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={modal} onClose={() => setModal(false)}
        title={editing ? '✏️ Edit Role' : '+ Add Role'}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 Save Role'}</button>
        </>}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Role Name</label>
            <input className="form-input" placeholder="e.g. Graphic Designer" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Department</label>
            <select className="form-select" value={form.dept} onChange={e=>setForm({...form,dept:e.target.value})}>
              {depts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Key Responsibilities</label>
          <textarea className="form-textarea" placeholder="e.g. Design social media creatives..." value={form.responsibilities} onChange={e=>setForm({...form,responsibilities:e.target.value})} />
        </div>
        <div className="form-group">
          <label className="form-label">Required Skills / Tools</label>
          <input className="form-input" placeholder="e.g. Photoshop, Illustrator, Canva, Figma" value={form.skills} onChange={e=>setForm({...form,skills:e.target.value})} />
        </div>
        <div className="form-group">
          <label className="form-label">Permissions Level</label>
          <select className="form-select" value={form.permissions} onChange={e=>setForm({...form,permissions:e.target.value})}>
            <option value="intern">🎓 Intern — View tasks assigned to them only</option>
            <option value="executor">🛠️ Executor — Create & submit work, no approvals</option>
            <option value="contributor">✏️ Contributor — Create, edit & update own tasks</option>
            <option value="coordinator">📋 Coordinator — Manage tasks, assign to others</option>
            <option value="reviewer">🔍 Reviewer — Review work & give internal feedback</option>
            <option value="approver">✅ Approver — Approve/reject tasks & client deliverables</option>
            <option value="manager">👔 Manager — Full team & project management</option>
            <option value="admin">🔑 Admin — Full access including settings & billing</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Color Tag</label>
          <ColorPicker value={form.color} onChange={c=>setForm({...form,color:c})} />
        </div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PERMISSIONS PANEL COMPONENT
// Reads from src/config/pages.js — no hardcoding here.
// Add a new page to pages.js and it automatically appears.
//
// Phase 1: 3-state access per page (None / View / Full)
// Stored as:  "page:calendar"       → Full access
//             "page:calendar:read"  → View only (sidebar visible, no edits)
//             absent                → Hidden from sidebar
// ══════════════════════════════════════════════════════════

const ROLE_DEFAULTS = {
  manager: [
    'page:dashboard','page:calendar','page:contentcalendar',
    'page:creatives','page:postcopy','page:scheduling','page:monitoring',
    'page:pipeline','page:assignments','page:kpi','page:performance',
    'page:costs','page:touchpoints','page:team','page:clients',
    'data:all_clients','data:all_workorders','data:costs','data:audit_log',
    'tab:contentcalendar:scripts','tab:contentcalendar:internal','tab:contentcalendar:client','tab:contentcalendar:approved',
    'tab:creatives:pending','tab:creatives:internal','tab:creatives:client','tab:creatives:approved',
    'tab:postcopy:draft','tab:postcopy:internal','tab:postcopy:client','tab:postcopy:approved',
    'tab:scheduling:ready','tab:scheduling:scheduled','tab:scheduling:posted',
    'tab:monitoring:active','tab:monitoring:closed',
  ],
  member: [
    'page:dashboard','page:calendar','page:contentcalendar',
    'page:creatives','page:postcopy','page:scheduling','page:assignments',
    'page:touchpoints','page:team',
    'tab:contentcalendar:scripts','tab:contentcalendar:internal','tab:contentcalendar:client','tab:contentcalendar:approved',
    'tab:creatives:pending','tab:creatives:internal','tab:creatives:client','tab:creatives:approved',
    'tab:postcopy:draft','tab:postcopy:internal','tab:postcopy:client','tab:postcopy:approved',
    'tab:scheduling:ready','tab:scheduling:scheduled','tab:scheduling:posted',
    'tab:monitoring:active','tab:monitoring:closed',
  ],
  client: [
    'page:creatives','page:contentcalendar',
    'tab:contentcalendar:client','tab:contentcalendar:approved',
    'tab:creatives:client','tab:creatives:approved',
    'tab:postcopy:client','tab:postcopy:approved',
  ],
};

const DATA_SCOPES = [
  { key:'workorders', label:'Work Orders' },
  { key:'clients',    label:'Clients'     },
  { key:'pipeline',   label:'Pipeline'    },
];

function PermissionsPanel({ systemRole, customPermissions, dataScope, onChange }) {
  const [open, setOpen] = React.useState(false);
  const defaults = ROLE_DEFAULTS[systemRole] || ROLE_DEFAULTS.member;
  const active = (Array.isArray(customPermissions)) ? customPermissions : defaults;
  const isCustomised = Array.isArray(customPermissions);

  // Read from central config — no hardcoding
  const pagePerms = getAssignablePagePermissions();
  const tabPerms  = getAssignableTabPermissions();
  const opsTabPerms = getAssignableOperationsTabPermissions();
  const pageLabelMap = getPageLabelMap();

  // Group pages by section
  const sections = {};
  pagePerms.forEach(p => {
    if (!sections[p.section]) sections[p.section] = [];
    sections[p.section].push(p);
  });

  // ── 3-state level helpers ──────────────────────────────
  // Returns 'full' | 'read' | 'none'
  const getLevel = (key) => {
    if (active.includes(key)) return 'full';
    if (active.includes(`${key}:read`)) return 'read';
    return 'none';
  };

  // Update permission array: strip both variants, then add the chosen one
  const setLevel = (key, level) => {
    let next = active.filter(k => k !== key && k !== `${key}:read`);
    if (level === 'full') next = [...next, key];
    if (level === 'read') next = [...next, `${key}:read`];
    onChange(next, dataScope);
  };

  const toggleScope = (module, value) => {
    const next = { ...(dataScope || {}), [module]: value };
    onChange(customPermissions, next);
  };

  const resetToDefault = () => { onChange(null, null); };

  // ── Reusable 3-state button group ─────────────────────
  const EyeIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{display:'block'}}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
  const LevelToggle = ({ permKey }) => {
  const level = getLevel(permKey);
  const opts = [
    { val: 'none', label: 'None', icon: '✕',         activeColor: '#6b7280' },
    { val: 'read', label: 'View', icon: <EyeIcon />,  activeColor: '#3b82f6' },
    { val: 'full', label: 'Full', icon: '✓',          activeColor: '#6c58f0' },
  ];
  return (
    <div style={{
      display: 'inline-flex',
      border: '1px solid var(--border)',
      borderRadius: 6,
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      {opts.map(({ val, label, icon, activeColor }, i) => {
        const sel = level === val;
        return (
          <button key={val} type="button" onClick={() => setLevel(permKey, val)}
            title={label}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px',
              padding: '4px 9px',
              minWidth: 36,
              cursor: 'pointer',
              border: 'none',
              borderLeft: i > 0 ? '1px solid var(--border)' : 'none',
              background: sel ? activeColor : 'var(--bg3)',
              color: sel ? '#fff' : 'var(--text-muted)',
              transition: 'background 0.12s, color 0.12s',
              lineHeight: 1,
            }}>
            <span style={{fontSize:'0.75rem', fontWeight:700, display:'flex', alignItems:'center'}}>
              {icon}
            </span>
            <span style={{fontSize:'0.55rem', fontWeight:600, letterSpacing:'0.03em', textTransform:'uppercase'}}>
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

  return (
    <div style={{marginTop:'20px',border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',background:'var(--bg3)',cursor:'pointer',userSelect:'none'}}
      >
        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
          <span>🔐</span>
          <div>
            <div style={{fontWeight:600,fontSize:'0.85rem'}}>Access Permissions</div>
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>
              {isCustomised ? '✏️ Custom permissions set' : `Using ${systemRole} role defaults`}
            </div>
          </div>
        </div>
        <span style={{color:'var(--text-muted)',fontSize:'0.8rem'}}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{padding:'16px',background:'var(--bg2)'}}>

          {/* Legend */}
          <div style={{display:'flex',gap:'10px',marginBottom:'14px',flexWrap:'wrap',alignItems:'center'}}>
          {[
          { color:'#6b7280', label:'None — hidden from sidebar' },
          { color:'#3b82f6', label:'View — can see, cannot edit' },
          { color:'#6c58f0', label:'Full — full access' },
          ].map(({ color, label }) => (
          <div key={label} style={{display:'flex',alignItems:'center',gap:'5px',fontSize:'0.72rem',color:'var(--text-muted)'}}>
          <div style={{width:10,height:10,borderRadius:2,background:color,flexShrink:0}}/>
          {label}
          </div>
          ))}
          </div>

          {isCustomised && (
            <div style={{marginBottom:'12px',display:'flex',justifyContent:'flex-end'}}>
              <button type="button" onClick={resetToDefault}
                style={{fontSize:'0.72rem',color:'var(--primary)',background:'none',border:'1px solid var(--primary)',borderRadius:5,padding:'3px 10px',cursor:'pointer'}}>
                ↺ Reset to {systemRole} defaults
              </button>
            </div>
          )}

          {/* Page access — grouped by section */}
          {Object.entries(sections).map(([sectionName, items]) => (
            <div key={sectionName} style={{marginBottom:'14px'}}>
              <div style={{fontSize:'0.72rem',fontWeight:600,color:'var(--text-muted)',marginBottom:'6px',textTransform:'uppercase',letterSpacing:'0.04em'}}>
                {sectionName}
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:'5px'}}>
                {items.map(p => {
                  const level = getLevel(p.key);
                  return (
                    <div key={p.key} style={{
                      display:'flex',alignItems:'center',justifyContent:'space-between',
                      padding:'6px 10px',border:'1px solid var(--border)',borderRadius:6,
                      background: level === 'full' ? 'rgba(108,88,240,0.07)'
                                : level === 'read' ? 'rgba(59,130,246,0.06)'
                                : 'var(--bg)',
                      transition:'background 0.15s',
                    }}>
                      <span style={{fontSize:'0.8rem'}}>{p.label}</span>
                      <LevelToggle permKey={p.key} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Master Data sensitive tabs — only shown when Master Data has any access */}
          {getLevel('page:masterdata') !== 'none' && tabPerms.length > 0 && (
            <div style={{marginBottom:'14px'}}>
              <div style={{fontSize:'0.72rem',fontWeight:600,color:'var(--text-muted)',marginBottom:'6px',textTransform:'uppercase',letterSpacing:'0.04em'}}>
                Master Data — Sub Tabs
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:'5px'}}>
                {tabPerms.map(p => {
                  const level = getLevel(p.key);
                  return (
                    <div key={p.key} style={{
                      display:'flex',alignItems:'center',justifyContent:'space-between',
                      padding:'6px 10px',border:'1px solid var(--border)',borderRadius:6,
                      background: level === 'full' ? 'rgba(239,68,68,0.07)'
                                : level === 'read' ? 'rgba(59,130,246,0.06)'
                                : 'var(--bg)',
                      transition:'background 0.15s',
                    }}>
                      <span style={{fontSize:'0.8rem'}}>
                        {p.icon} {p.label}
                        {p.sensitive && <span style={{fontSize:'0.65rem',color:'#ef4444',marginLeft:4}}>sensitive</span>}
                      </span>
                      <LevelToggle permKey={p.key} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}


          {/* Operations sub-tabs — shown per page when that page has access */}
          {Object.entries(opsTabPerms).map(([pageKey, tabs]) => {
            // Find the permission key for this page
            const pagePermKey = pagePerms.find(p => p.key === `page:${pageKey}`)?.key;
            if (!pagePermKey || getLevel(pagePermKey) === 'none') return null;
            return (
              <div key={pageKey} style={{marginBottom:'14px'}}>
                <div style={{fontSize:'0.72rem',fontWeight:600,color:'var(--text-muted)',marginBottom:'6px',textTransform:'uppercase',letterSpacing:'0.04em'}}>
                  {pageLabelMap[pageKey] || pageKey} — Sub Tabs
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:'5px'}}>
                  {tabs.map(p => {
                    const level = getLevel(p.key);
                    return (
                      <div key={p.key} style={{
                        display:'flex',alignItems:'center',justifyContent:'space-between',
                        padding:'6px 10px',border:'1px solid var(--border)',borderRadius:6,
                        background: level === 'full' ? 'rgba(108,88,240,0.07)'
                                  : level === 'read' ? 'rgba(59,130,246,0.06)'
                                  : 'var(--bg)',
                        transition:'background 0.15s',
                      }}>
                        <span style={{fontSize:'0.8rem'}}>{p.icon} {p.label}</span>
                        <LevelToggle permKey={p.key} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Data scope */}
          <div>
            <div style={{fontSize:'0.72rem',fontWeight:600,color:'var(--text-muted)',marginBottom:'6px',textTransform:'uppercase',letterSpacing:'0.04em'}}>Data Scope — Own or All?</div>
            <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
              {DATA_SCOPES.map(s => {
                const current = dataScope?.[s.key] || (systemRole==='manager'?'all':'own');
                return (
                  <div key={s.key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 12px',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg)'}}>
                    <span style={{fontSize:'0.82rem',fontWeight:500}}>{s.label}</span>
                    <div style={{display:'flex',gap:'6px'}}>
                      {['own','all'].map(v => (
                        <button key={v} type="button" onClick={() => toggleScope(s.key, v)}
                          style={{padding:'3px 12px',fontSize:'0.75rem',borderRadius:5,cursor:'pointer',fontWeight:600,
                            border:current===v?'none':'1px solid var(--border)',
                            background:current===v?(v==='all'?'#6c58f0':'#1db87a'):'var(--bg3)',
                            color:current===v?'#fff':'var(--text-muted)',transition:'all 0.15s'}}>
                          {v==='own'?'Own Only':'All'}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: TEAM
// ══════════════════════════════════════════════════════════
function TeamTab() {
  const [team, setTeam]   = useState([]);
  const [roles, setRoles] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name:'', roles:[], email:'', phone:'', rate:'',
    employment:'active', availability:'available',
    skills:'', join_date:'', color:COLORS[0],
    daily_rate:'', billing_rate:'', language:'English',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [t,r] = await Promise.all([api.get('/team-members'), api.get('/roles')]);
    const teamData = t.data?.data || t.data || [];
    setTeam(Array.isArray(teamData) ? teamData : []);
    const rolesData = r.data?.data || r.data || [];
    setRoles(Array.isArray(rolesData) ? rolesData : []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name:'', roles:[], email:'', phone:'', rate:'', employment:'active', availability:'available', skills:'', join_date:'', color:COLORS[0], custom_permissions:[], data_scope:{workorders:'own',clients:'own',pipeline:'own'} });
    setModal(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    // roles_list is stored as JSON string or array
    let memberRoles = [];
    if (Array.isArray(m.roles)) memberRoles = m.roles;
    else if (Array.isArray(m.roles_list)) memberRoles = m.roles_list;
    else if (typeof m.roles_list === 'string') { try { memberRoles = JSON.parse(m.roles_list); } catch(e) { memberRoles = m.role ? [m.role] : []; } }
    else if (m.role) memberRoles = [m.role];
    setForm({
      name: m.name||'',
      roles: memberRoles,
      email: m.email||'',
      phone: m.phone||'',
      rate: m.rate_per_day||m.rate||'',
      employment: m.employment||'active',
      availability: m.availability||'available',
      skills: m.skills||'',
      join_date: m.join_date||'',
      color: m.color||COLORS[0],
      daily_rate: m.daily_rate||'',
      billing_rate: m.billing_rate||'',
      language: m.language||'English',
      custom_permissions: m.custom_permissions||null,
      data_scope: m.data_scope||null,
    });
    setModal(true);
  };

  const toggleRole = (roleName) => {
    setForm(f => ({
      ...f,
      roles: f.roles.includes(roleName)
        ? f.roles.filter(r => r !== roleName)
        : [...f.roles, roleName]
    }));
  };

  const save = async () => {
    if(!form.name.trim()) return;
    setSaving(true);
    try {
      // Map role permissions to system role enum (owner/manager/member/client)
      const permToSystemRole = (perm) => {
        if (perm === 'admin')   return 'owner';
        if (perm === 'manager') return 'manager';
        return 'member';
      };
      // Find the highest permission among selected roles
      const selectedRoleObjs = roles.filter(r => form.roles.includes(r.name));
      const permOrder = ['admin','manager','approver','coordinator','reviewer','contributor','executor','intern'];
      const topPerm = permOrder.find(p => selectedRoleObjs.some(r => r.permissions === p)) || 'contributor';
      const systemRole = permToSystemRole(topPerm);

      const payload = {
        name: form.name,
        role: systemRole,
        roles_list: form.roles,
        email: form.email,
        phone: form.phone,
        rate_per_day: parseFloat(form.rate)||0,
        employment: form.employment,
        availability: form.availability,
        color: form.color,
        skills: form.skills,
        join_date: form.join_date||null,
        daily_rate: parseFloat(form.daily_rate)||null,
        billing_rate: parseFloat(form.billing_rate)||null,
        language: form.language||'English',
        custom_permissions: form.custom_permissions,
        data_scope: form.data_scope,
      };
      if(editing) await api.put('/users/'+editing.id, payload);
      else         await api.post('/users', payload);
      setModal(false); load();
    } catch(e) {
      alert('Save failed: '+(e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  };

  const del = async (m) => {
    if(!window.confirm('Remove '+m.name+' from team?')) return;
    try {
      await api.delete('/users/'+m.id);
    } catch(e) {
      alert('Delete failed: '+(e.response?.data?.message || e.message));
    }
    load();
  };

  const empBadge = (e) => {
    const map = {
      active:   { label:'✅ Active Employee', cls:'badge-green' },
      leave:    { label:'🟡 On Leave',        cls:'badge-yellow' },
      parttime: { label:'🔵 Part-Time',       cls:'badge-blue' },
      inactive: { label:'🔴 Inactive',        cls:'badge-red' },
      'Full-time': { label:'✅ Active Employee', cls:'badge-green' },
    };
    const o = map[e] || { label: e||'—', cls:'badge-gray' };
    return <span className={`badge ${o.cls}`}>{o.label}</span>;
  };

  const availDot = (v) => {
    const map = { available:'#1db87a', busy:'#e84545', absent:'#888', partial:'#e8a020' };
    const labels = { available:'Available', busy:'Busy', absent:'Absent', partial:'Partial' };
    return <span style={{display:'flex',alignItems:'center',gap:'5px',fontSize:'0.78rem'}}>
      <span style={{width:'8px',height:'8px',borderRadius:'50%',background:map[v]||'#888',display:'inline-block'}}></span>
      {labels[v]||v||'—'}
    </span>;
  };

  const getMemberRoles = (m) => {
    if (Array.isArray(m.roles_list)) return m.roles_list;
    if (typeof m.roles_list === 'string' && m.roles_list) {
      try {
        const parsed = JSON.parse(m.roles_list);
        if (Array.isArray(parsed)) return parsed;
        if (typeof parsed === 'string' && parsed) return [parsed];
        return [];
      } catch(e) { return [m.roles_list]; }
    }
    return m.role ? [m.role] : [];
  };

  const EMPLOYMENT_OPTS = [
    {value:'active',   label:'✅ Active Employee'},
    {value:'leave',    label:'🟡 On Long Leave'},
    {value:'parttime', label:'🔵 Part-Time / Freelance'},
    {value:'inactive', label:'🔴 Inactive / Left'},
  ];

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">👥 Team Master</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>All team members, their roles, skills and availability status.</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Member</button>
      </div>
      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Roles</th><th>Skills</th><th>Email</th><th>Phone</th><th>Rate/Day</th><th>Employment</th><th>Availability</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {team.map(m => {
              const memberRoles = getMemberRoles(m);
              return (
                <tr key={m.id}>
                  <td>
                    <div className="flex" style={{gap:'8px',alignItems:'center'}}>
                      <div className="avatar" style={{background:m.color||'#888',width:'28px',height:'28px',fontSize:'0.65rem',flexShrink:0}}>{initials(m.name)}</div>
                      <span className="fw-700">{m.name}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
                      {memberRoles.length > 0
                        ? memberRoles.map((r,i) => {
                            const roleObj = roles.find(x => x.name===r);
                            return <span key={i} className="badge badge-gray" style={{borderLeft:`3px solid ${roleObj?.color||'#888'}`}}>{r}</span>;
                          })
                        : <span className="text-xs text-muted">—</span>
                      }
                    </div>
                  </td>
                  <td><div className="text-xs text-muted" style={{maxWidth:'160px'}}>{m.skills||'—'}</div></td>
                  <td className="text-xs text-muted">{m.email||'—'}</td>
                  <td className="text-xs text-muted">{m.phone||'—'}</td>
                  <td className="mono text-sm">{(m.rate_per_day||m.rate) ? '₹'+Number(m.rate_per_day||m.rate).toLocaleString('en-IN') : '—'}</td>
                  <td>{empBadge(m.employment)}</td>
                  <td>{availDot(m.availability)}</td>
                  <td>
                    <div className="flex" style={{gap:'4px'}}>
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => openEdit(m)}>✏️ Edit</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => del(m)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {team.length===0 && <tr><td colSpan={9} style={{padding:'20px',textAlign:'center'}} className="text-muted text-sm">No team members yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={modal} onClose={() => setModal(false)}
        title={editing ? '✏️ Edit Team Member' : '+ Add Team Member'}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update Member':'Save Member')}</button>
        </>}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input className="form-input" placeholder="e.g. Arjun Menon" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Roles <span className="text-xs text-muted">(select one or more)</span></label>
            <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginTop:'6px',maxHeight:'120px',overflowY:'auto',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
              {roles.filter(r => r.enabled !== false).map(r => (
                <label key={r.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.78rem',cursor:'pointer',padding:'4px 8px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'6px',whiteSpace:'nowrap'}}>
                  <input type="checkbox"
                    checked={form.roles.includes(r.name)}
                    onChange={() => toggleRole(r.name)}
                    style={{accentColor:r.color||'var(--accent)',width:'13px',height:'13px'}}
                  />
                  <span style={{width:'8px',height:'8px',borderRadius:'50%',background:r.color||'#888',display:'inline-block',flexShrink:0}}></span>
                  {r.name}
                </label>
              ))}
              {roles.length===0 && <span className="text-xs text-muted">No roles defined yet — add roles first</span>}
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" placeholder="name@agency.in" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" placeholder="+91 98765 43210" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Day Rate (₹)</label>
            <input className="form-input" type="number" placeholder="0" value={form.rate} onChange={e=>setForm({...form,rate:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Availability</label>
            <select className="form-select" value={form.availability} onChange={e=>setForm({...form,availability:e.target.value})}>
              <option value="available">Available</option>
              <option value="busy">Busy</option>
              <option value="absent">Absent</option>
              <option value="partial">Partial</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Employment Status</label>
            <select className="form-select" value={form.employment} onChange={e=>setForm({...form,employment:e.target.value})}>
              {EMPLOYMENT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Join Date</label>
            <input className="form-input" type="date" value={form.join_date} onChange={e=>setForm({...form,join_date:e.target.value})} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Skills / Tools</label>
          <input className="form-input" placeholder="e.g. Photoshop, Canva, Google Ads, Video Editing, SEO" value={form.skills} onChange={e=>setForm({...form,skills:e.target.value})} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Daily Cost Rate (₹) <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(internal cost)</span></label>
            <input className="form-input" type="number" placeholder="e.g. 2500" value={form.daily_rate} onChange={e=>setForm({...form,daily_rate:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Billing Rate (₹/hr) <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(client billing)</span></label>
            <input className="form-input" type="number" placeholder="e.g. 1500" value={form.billing_rate} onChange={e=>setForm({...form,billing_rate:e.target.value})} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Language(s)</label>
          <input className="form-input" placeholder="e.g. English, Malayalam, Hindi" value={form.language} onChange={e=>setForm({...form,language:e.target.value})} />
        </div>
        <div className="form-group">
          <label className="form-label">Avatar Color</label>
          <ColorPicker value={form.color} onChange={c=>setForm({...form,color:c})} />
        </div>
        {/* ── Access Permissions ──────────────────────────────── */}
        <PermissionsPanel
          systemRole={(() => {
            const permToSystemRole = (perm) => {
              if (perm === 'admin')   return 'manager';
              if (perm === 'manager') return 'manager';
              return 'member';
            };
            const permOrder = ['admin','manager','approver','coordinator','reviewer','contributor','executor','intern'];
            const selectedRoleObjs = roles.filter(r => form.roles.includes(r.name));
            const topPerm = permOrder.find(p => selectedRoleObjs.some(r => r.permissions === p)) || 'contributor';
            return permToSystemRole(topPerm);
          })()}
          customPermissions={form.custom_permissions}
          dataScope={form.data_scope}
          onChange={(cp, ds) => setForm(f => ({...f, custom_permissions:cp, data_scope:ds}))}
        />
      </Modal>
    </div>
  );
}

// TAB: CLIENTS
// TAB: CLIENTS
// ══════════════════════════════════════════════════════════
const INDUSTRY_LIST = [
  'E-Commerce','Retail','Healthcare & Pharma','Education & EdTech',
  'Real Estate','Hospitality & Tourism','Food & Beverage','FMCG',
  'Automotive','Finance & Banking','Insurance','Technology & SaaS',
  'Manufacturing','Fashion & Apparel','Beauty & Wellness','Media & Entertainment',
  'NGO / Non-Profit','Government & PSU','Logistics','Professional Services','Other'
];

const CLIENT_STATUS_OPTS = [
  { value:'active',     label:'✅ Active',           color:'#1db87a' },
  { value:'onboarding', label:'🟡 Onboarding',        color:'#f7c34f' },
  { value:'paused',     label:'⏸️ Paused',            color:'#e87830' },
  { value:'inactive',   label:'🔴 Inactive / Churned',color:'#e84545' },
];

function ClientsTab() {
  const [clients,   setClients]   = useState([]);
  const [roles,     setRoles]     = useState([]);
  const [team,      setTeam]      = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [services,  setServices]  = useState([]);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [search, setSearch] = useState('');

  const emptyForm = {
    name:'', contact:'', email:'', phone:'', whatsapp:'',
    industry:'', budget:'', status:'active', since:'',
    website:'', gst:'', address:'',
    contract_start:'', contract_end:'', contract_value:'', renewal_date:'', nps_score:'',
    platforms:[], services:[], teamAssignments:{},
    notes:'', enabled:true, custom_permissions:null,
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [c, r, t, p, s] = await Promise.all([
      api.get('/clients?active_only=false'), api.get('/roles'), api.get('/team-members'),
      api.get('/platforms'), api.get('/services')
    ]);
    setClients(c.data?.data || c.data || []);
    setRoles(r.data?.data || r.data || []);
    setTeam((t.data?.data || t.data || []).filter(m => m.deleted_at == null && m.employment !== 'inactive'));
    setPlatforms(p.data?.data || p.data || []);
    setServices(s.data?.data || s.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    let plats = c.platforms;
    if (typeof plats === 'string') { try { plats = JSON.parse(plats); } catch(e) { plats = plats.split(',').map(x=>x.trim()).filter(Boolean); } }
    let svcs = c.services;
    if (typeof svcs === 'string') { try { svcs = JSON.parse(svcs); } catch(e) { svcs = []; } }
    let ta = c.team_assignments || c.teamAssignments;
    if (typeof ta === 'string') { try { ta = JSON.parse(ta); } catch(e) { ta = {}; } }
    setForm({
      name: c.name||'', contact: c.contact||'', email: c.email||'',
      phone: c.phone||'', whatsapp: c.whatsapp||'',
      industry: c.industry||'', budget: c.budget||c.monthly_budget||'',
      status: c.status||'active', since: c.since||c.client_since||'',
      website: c.website||'', gst: c.gst||'', address: c.address||'',
      contract_start: c.contract_start||'', contract_end: c.contract_end||'',
      contract_value: c.contract_value||'', renewal_date: c.renewal_date||'',
      nps_score: c.nps_score||'',
      platforms: Array.isArray(plats) ? plats : [],
      services: Array.isArray(svcs) ? svcs : [],
      teamAssignments: ta||{},
      notes: c.notes||'', enabled: c.enabled!==false,
      custom_permissions: c.custom_permissions||null,
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name, contact_name: form.contact, email: form.email,
        phone: form.phone, whatsapp: form.whatsapp,
        industry: form.industry, budget: parseFloat(form.budget)||0,
        account_manager: form.status, since: form.since||null,
        website: form.website, gstin: form.gst, address: form.address,
        contract_start: form.contract_start||null, contract_end: form.contract_end||null,
        contract_value: parseFloat(form.contract_value)||null,
        renewal_date: form.renewal_date||null,
        nps_score: form.nps_score!==''?parseInt(form.nps_score):null,
        platforms: JSON.stringify(form.platforms),
        services: JSON.stringify(form.services),
        team_assignments: JSON.stringify(form.teamAssignments),
        status: form.status, client_since: form.since||null,
        notes: form.notes, is_active: form.enabled,
        custom_permissions: form.custom_permissions,
      };
      if (editing) {
        await api.put('/master/clients/'+editing.id, payload);
      } else {
        const res = await api.post('/master/clients', payload);
        const d = res.data?.data || res.data;
        if (d?.plain_password) {
          setCreatedCreds({ name: d.name || d.contact_name, email: d.login_email || d.email, password: d.plain_password });
        }
      }
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };

  const del    = async (c) => { if(!window.confirm('Delete client "'+c.name+'"?')) return; await api.delete('/master/clients/'+c.id); load(); };
  const toggle = async (c) => { await api.put('/master/clients/'+c.id, { name:c.name, budget:c.budget||0, is_active:!c.is_active }); load(); };

  const togglePlatform = (name) => setForm(f => ({...f, platforms: f.platforms.includes(name) ? f.platforms.filter(p=>p!==name) : [...f.platforms, name]}));
  const toggleService  = (name) => setForm(f => ({...f, services: f.services.includes(name) ? f.services.filter(s=>s!==name) : [...f.services, name]}));
  const setAssignment  = (roleId, memberId) => setForm(f => ({...f, teamAssignments: {...f.teamAssignments, [roleId]: memberId}}));

  const getPlatforms = (c) => {
    let p = c.platforms;
    if (!p) return [];
    if (typeof p === 'string') { try { p = JSON.parse(p); } catch(e) { return p.split(',').map(x=>x.trim()).filter(Boolean); } }
    return Array.isArray(p) ? p : [];
  };

  const getTeamAssignments = (c) => {
    let ta = c.team_assignments || c.teamAssignments;
    if (!ta) return {};
    if (typeof ta === 'string') { try { ta = JSON.parse(ta); } catch(e) { return {}; } }
    return ta || {};
  };

  const statusInfo = (s) => CLIENT_STATUS_OPTS.find(x=>x.value===s) || CLIENT_STATUS_OPTS[0];

  const filtered = clients.filter(c => {
    const matchStatus = filterStatus==='All' || (c.status||'active')===filterStatus;
    const matchSearch = !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.contact?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  // KPI stats
  const active = clients.filter(c=>c.is_active!==false&&c.status==='active'||(c.is_active!==false&&!c.status));
  const totalBudget = active.reduce((s,c) => s+(parseFloat(c.budget||c.monthly_budget)||0), 0);

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">👔 Client Master</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>All client accounts — contacts, budgets, platforms, services and team assignments.</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Client</button>
      </div>

      {/* KPI Strip */}
      {clients.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:'10px',marginBottom:'16px'}}>
          <div className="card" style={{padding:'12px 16px',borderTop:'3px solid var(--accent)'}}>
            <div className="text-xs text-muted fw-700" style={{textTransform:'uppercase',letterSpacing:'0.5px'}}>Active Clients</div>
            <div style={{fontSize:'1.8rem',fontWeight:800,fontFamily:"'Fraunces',serif",marginTop:'2px'}}>{active.length}</div>
          </div>
          <div className="card" style={{padding:'12px 16px',borderTop:'3px solid #1db87a'}}>
            <div className="text-xs text-muted fw-700" style={{textTransform:'uppercase',letterSpacing:'0.5px'}}>Total MRR</div>
            <div style={{fontSize:'1.1rem',fontWeight:800,color:'#1db87a',marginTop:'2px'}}>{fmtRupees(totalBudget)}</div>
            <div className="text-xs text-muted">/ month</div>
          </div>
          {CLIENT_STATUS_OPTS.map(s => {
            const count = clients.filter(c=>(c.status||'active')===s.value).length;
            return count > 0 ? (
              <div key={s.value} className="card" style={{padding:'12px 16px',borderTop:`3px solid ${s.color}`}}>
                <div className="text-xs text-muted fw-700" style={{textTransform:'uppercase',letterSpacing:'0.5px'}}>{s.label.replace(/^.+? /,'')}</div>
                <div style={{fontSize:'1.8rem',fontWeight:800,fontFamily:"'Fraunces',serif",marginTop:'2px',color:s.color}}>{count}</div>
              </div>
            ) : null;
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex-between mb-12">
        <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
          {['All',...CLIENT_STATUS_OPTS.map(s=>s.value)].map(s => {
            const info = s==='All' ? {label:'All',color:'var(--accent)'} : statusInfo(s);
            const count = s==='All' ? clients.length : clients.filter(c=>(c.status||'active')===s).length;
            const active = filterStatus===s;
            return (
              <button key={s} onClick={()=>setFilterStatus(s)} style={{
                padding:'4px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',
                border:`2px solid ${active?(info.color||'var(--accent)'):'var(--border)'}`,
                background:active?(info.color||'var(--accent)'):'var(--bg2)',
                color:active?'#fff':'var(--text2)'
              }}>{s==='All'?'All':info.label} ({count})</button>
            );
          })}
        </div>
        <input className="form-input" style={{maxWidth:'220px'}} placeholder="Search clients..." value={search} onChange={e=>setSearch(e.target.value)} />
      </div>

      {/* Table */}
      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Contact</th>
              <th>Budget/mo</th>
              <th>Platforms</th>
              <th>Assigned Team</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">
                {clients.length===0 ? 'No clients yet. Click + Add Client to get started.' : 'No clients match your filter.'}
              </td></tr>
            )}
            {filtered.map(c => {
              const plats = getPlatforms(c);
              const ta    = getTeamAssignments(c);
              const si    = statusInfo(c.status||'active');
              return (
                <tr key={c.id} style={{opacity:c.is_active===false||c.enabled===false?0.45:1}}>
                  <td>
                    <div className="fw-700">{c.name}</div>
                    {c.industry && <div className="text-xs text-muted">{c.industry}</div>}
                    {c.since && <div className="text-xs text-muted">Since {c.since}</div>}
                    {c.contract_end && <div className="text-xs" style={{color:'#e87830'}}>📄 Until {c.contract_end}</div>}
                    {c.nps_score!=null && c.nps_score!=='' && <div className="text-xs" style={{color:parseInt(c.nps_score)>=50?'#1db87a':parseInt(c.nps_score)>=0?'#e87830':'#e84545'}}>NPS: {c.nps_score}</div>}
                  </td>
                  <td>
                    <div className="text-sm">{c.contact||'—'}</div>
                    {c.email && <div className="text-xs text-muted">{c.email}</div>}
                    {c.phone && <div className="text-xs text-muted">{c.phone}</div>}
                  </td>
                  <td><span className="mono fw-700" style={{color:'#1db87a'}}>{fmtRupees(c.budget||c.monthly_budget)}</span></td>
                  <td>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'3px',maxWidth:'200px'}}>
                      {plats.slice(0,4).map(p => {
                        const svg = platformSvgFor(p);
                        return (
                          <span key={p} title={p} style={{display:'inline-flex',alignItems:'center',gap:'3px',padding:'2px 6px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'10px',fontSize:'0.65rem',fontWeight:600}}>
                            {svg ? <span dangerouslySetInnerHTML={{__html:svg}} style={{width:'12px',height:'12px',display:'inline-block'}} /> : null}
                            {p}
                          </span>
                        );
                      })}
                      {plats.length > 4 && <span className="badge badge-gray" style={{fontSize:'0.65rem'}}>+{plats.length-4}</span>}
                      {plats.length === 0 && <span className="text-xs text-muted">—</span>}
                    </div>
                  </td>
                  <td>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
                      {Object.entries(ta).map(([roleId, memberId]) => {
                        const m = team.find(x=>String(x.id)===String(memberId));
                        const r = roles.find(x=>String(x.id)===String(roleId));
                        if (!m) return null;
                        return (
                          <div key={roleId} title={`${m.name} — ${r?.name||''}`} style={{display:'inline-flex',alignItems:'center',gap:'3px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'20px',padding:'2px 7px 2px 3px'}}>
                            <div className="avatar" style={{width:'16px',height:'16px',fontSize:'0.5rem',background:m.color||'var(--accent)',flexShrink:0}}>{initials(m.name)}</div>
                            <span style={{fontSize:'0.65rem'}}>{m.name.split(' ')[0]}</span>
                          </div>
                        );
                      }).filter(Boolean)}
                      {Object.keys(ta).length===0 && <span className="text-xs text-muted">—</span>}
                    </div>
                  </td>
                  <td><span className="badge" style={{background:`${si.color}18`,color:si.color,border:`1px solid ${si.color}40`,fontSize:'0.7rem'}}>{si.label}</span></td>
                  <td>
                    <div className="flex" style={{gap:'4px',alignItems:'center'}}>
                      <Toggle checked={c.is_active!==false&&c.enabled!==false} onChange={()=>toggle(c)} />
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(c)}>✏️ Edit</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(c)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'680px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Client':'+ Add Client'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">

              {/* Basic Info */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Company / Brand Name</label>
                  <input className="form-input" placeholder="e.g. ABC Corp" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Industry</label>
                  <select className="form-select" value={form.industry} onChange={e=>setForm({...form,industry:e.target.value})}>
                    <option value="">— Select Industry —</option>
                    {INDUSTRY_LIST.map(i=><option key={i}>{i}</option>)}
                  </select>
                </div>
              </div>

              {/* Contact */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Primary Contact Person</label>
                  <input className="form-input" placeholder="Full name" value={form.contact} onChange={e=>setForm({...form,contact:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Designation / Role</label>
                  <input className="form-input" placeholder="e.g. Marketing Manager, CEO" value={form.gst} onChange={e=>setForm({...form,gst:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" placeholder="contact@company.com" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" placeholder="+91 98765 43210" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">WhatsApp</label>
                  <input className="form-input" placeholder="+91 98765 43210" value={form.whatsapp} onChange={e=>setForm({...form,whatsapp:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Website</label>
                  <input className="form-input" placeholder="https://www.company.com" value={form.website} onChange={e=>setForm({...form,website:e.target.value})} />
                </div>
              </div>

              <div style={{height:'1px',background:'var(--border)',margin:'8px 0'}} />

              {/* Business Details */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Monthly Budget (₹)</label>
                  <input className="form-input" type="number" placeholder="0" value={form.budget} onChange={e=>setForm({...form,budget:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Client Status</label>
                  <select className="form-select" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                    {CLIENT_STATUS_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Client Since</label>
                  <input className="form-input" type="date" value={form.since} onChange={e=>setForm({...form,since:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Address / City</label>
                  <input className="form-input" placeholder="e.g. Mumbai, Maharashtra" value={form.address} onChange={e=>setForm({...form,address:e.target.value})} />
                </div>
              </div>

              <div style={{height:'1px',background:'var(--border)',margin:'8px 0'}} />

              {/* Team Assignments */}
              <div className="form-group">
                <label className="form-label">👥 Assigned Team <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>— tasks auto-assign to these people for this client</span></label>
                <div style={{display:'flex',flexDirection:'column',gap:'6px',marginTop:'6px'}}>
                  {roles.filter(r=>r.enabled!==false).map(r => {
                    const roleMembers = team.filter(m => {
                      let rl = m.roles_list;
                      if (typeof rl === 'string') { try { rl = JSON.parse(rl); } catch(e) { rl = []; } }
                      return Array.isArray(rl) ? rl.includes(r.name) : m.role===r.name;
                    });
                    const memberList = roleMembers.length ? roleMembers : team;
                    return (
                      <div key={r.id} style={{display:'grid',gridTemplateColumns:'200px 1fr',gap:'10px',alignItems:'center',padding:'6px 10px',background:'var(--bg3)',borderRadius:'var(--radius-sm)'}}>
                        <div className="flex" style={{gap:'6px',alignItems:'center'}}>
                          <div style={{width:'9px',height:'9px',borderRadius:'50%',background:r.color,flexShrink:0}} />
                          <span className="text-sm fw-700" style={{color:r.color}}>{r.name}</span>
                        </div>
                        <select className="form-select" style={{fontSize:'0.8rem'}} value={form.teamAssignments[r.id]||''} onChange={e=>setAssignment(r.id, e.target.value)}>
                          <option value="">— Not assigned —</option>
                          {memberList.map(m => {
                            const av = m.availability==='available'?'🟢':m.availability==='busy'?'🟡':'🔴';
                            return <option key={m.id} value={m.id}>{av} {m.name}</option>;
                          })}
                        </select>
                      </div>
                    );
                  })}
                  {roles.filter(r=>r.enabled!==false).length===0 && <div className="text-xs text-muted">Add roles in the Roles tab first.</div>}
                </div>
              </div>

              <div style={{height:'1px',background:'var(--border)',margin:'8px 0'}} />

              {/* Active Platforms */}
              <div className="form-group">
                <label className="form-label">Active Platforms</label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
                  {platforms.filter(p=>p.is_active!==false).map(p => {
                    const svg = platformSvgFor(p.name);
                    return (
                      <label key={p.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.78rem',cursor:'pointer',padding:'4px 8px',background: form.platforms.includes(p.name)?`${p.color||'var(--accent)'}18`:'var(--bg2)',border:`1px solid ${form.platforms.includes(p.name)?(p.color||'var(--accent)'):'var(--border)'}`,borderRadius:'6px',whiteSpace:'nowrap'}}>
                        <input type="checkbox" checked={form.platforms.includes(p.name)} onChange={()=>togglePlatform(p.name)} style={{accentColor:p.color||'var(--accent)'}} />
                        {svg && <span dangerouslySetInnerHTML={{__html:svg}} style={{width:'14px',height:'14px',display:'inline-block'}} />}
                        {p.name}
                      </label>
                    );
                  })}
                  {platforms.length===0 && <span className="text-xs text-muted">Add platforms in the Platforms tab first.</span>}
                </div>
              </div>

              {/* Services */}
              {services.filter(s=>s.enabled!==false).length > 0 && (
                <div className="form-group">
                  <label className="form-label">Active Services / Packages</label>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
                    {services.filter(s=>s.enabled!==false).map(s => (
                      <label key={s.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.78rem',cursor:'pointer',padding:'4px 8px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'6px',whiteSpace:'nowrap'}}>
                        <input type="checkbox" checked={form.services.includes(s.name)} onChange={()=>toggleService(s.name)} style={{accentColor:'var(--accent)'}} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Contract Details */}
              <div style={{height:'1px',background:'var(--border)',margin:'8px 0'}} />
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contract Start</label>
                  <input className="form-input" type="date" value={form.contract_start} onChange={e=>setForm({...form,contract_start:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Contract End</label>
                  <input className="form-input" type="date" value={form.contract_end} onChange={e=>setForm({...form,contract_end:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contract Value (₹) <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(total, not monthly)</span></label>
                  <input className="form-input" type="number" placeholder="0" value={form.contract_value} onChange={e=>setForm({...form,contract_value:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Renewal Date</label>
                  <input className="form-input" type="date" value={form.renewal_date} onChange={e=>setForm({...form,renewal_date:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">NPS Score <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(−100 to 100)</span></label>
                <div className="flex" style={{gap:'8px',alignItems:'center'}}>
                  <input className="form-input" type="number" min="-100" max="100" placeholder="e.g. 72" style={{maxWidth:'120px'}} value={form.nps_score} onChange={e=>setForm({...form,nps_score:e.target.value})} />
                  {form.nps_score!=='' && <span className="badge" style={{fontSize:'0.75rem',background:parseInt(form.nps_score)>=50?'rgba(29,184,122,0.1)':parseInt(form.nps_score)>=0?'rgba(247,195,79,0.1)':'rgba(232,69,69,0.1)',color:parseInt(form.nps_score)>=50?'#1db87a':parseInt(form.nps_score)>=0?'#e87830':'#e84545'}}>{parseInt(form.nps_score)>=50?'😊 Promoter':parseInt(form.nps_score)>=0?'😐 Passive':'😟 Detractor'}</span>}
                </div>
              </div>

              {/* Client Page Access */}
              <PermissionsPanel
                systemRole="client"
                customPermissions={form.custom_permissions}
                dataScope={null}
                onChange={(cp) => setForm(f => ({...f, custom_permissions:cp}))}
              />

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Notes <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(contract notes, special instructions, key contacts)</span></label>
                <textarea className="form-textarea" style={{minHeight:'70px'}} placeholder="e.g. Monthly call on 1st Tuesday. Invoice by 25th. Approval contact is Priya, not Rajesh..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update Client':'Save Client')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials popup after creating a client */}
      {createdCreds && (
        <div className="modal-overlay open">
          <div className="modal-box" style={{ maxWidth:420 }}>
            <div className="modal-header">
              <div className="modal-title">🔑 Login Credentials — {createdCreds.name}</div>
              <button className="modal-close" onClick={() => setCreatedCreds(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background:'var(--bg-alt,#f8fafc)', borderRadius:8, padding:16, marginBottom:12 }}>
                <div style={{ marginBottom:12 }}>
                  <div className="text-xs text-muted" style={{ marginBottom:4 }}>Login ID</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span className="text-sm fw-600" style={{ flex:1 }}>{createdCreds.email}</span>
                    <button className="btn btn-secondary" style={{ fontSize:'0.7rem', padding:'2px 8px' }}
                      onClick={() => navigator.clipboard.writeText(createdCreds.email)}>Copy</button>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted" style={{ marginBottom:4 }}>Password</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span className="text-sm fw-600" style={{ flex:1 }}>{createdCreds.password}</span>
                    <button className="btn btn-secondary" style={{ fontSize:'0.7rem', padding:'2px 8px' }}
                      onClick={() => navigator.clipboard.writeText(createdCreds.password)}>Copy</button>
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted">To manage passwords later, go to <strong>Settings → Security</strong>.</div>
              <div className="modal-footer" style={{ padding:0, border:'none', marginTop:12 }}>
                <button className="btn btn-primary" onClick={() => setCreatedCreds(null)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VendorsTab() {
  const [vendors, setVendors] = useState([]);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({ name:'', type:'Photographer', spec:'', email:'', phone:'', rate:'', ratetype:'per_day', availability:'available', notes:'' });
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => { const r = await api.get('/vendors'); setVendors(r.data?.data || r.data || []); }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditing(null); setForm({ name:'', type:'Photographer', spec:'', email:'', phone:'', rate:'', ratetype:'per_day', availability:'available', notes:'' }); setModal(true); };
  const openEdit = (v) => { setEditing(v); setForm({ name:v.name, type:v.type||'Photographer', spec:v.spec||v.specialization||'', email:v.email||'', phone:v.phone||'', rate:v.rate||'', ratetype:v.rate_type||v.ratetype||'per_day', availability:v.availability||'available', notes:v.notes||'' }); setModal(true); };
  const save = async () => {
    if(!form.name.trim()) return;
    setSaving(true);
    try {
      const p = { name:form.name, type:form.type, specialization:form.spec, email:form.email, phone:form.phone, rate:parseFloat(form.rate)||0, rate_type:form.ratetype, availability:form.availability, notes:form.notes, enabled:true };
      if(editing) await api.put('/vendors/'+editing.id, p);
      else         await api.post('/vendors', p);
      setModal(false); load();
    } finally { setSaving(false); }
  };
  const del = async (v) => { if(!window.confirm('Delete vendor "'+v.name+'"?')) return; await api.delete('/vendors/'+v.id); load(); };

  const TYPES = ['Photographer','Videographer','Illustrator','Animator','Copywriter','Web Developer','SEO Specialist','Voice Artist','Other'];
  const RATE_TYPES = [{value:'per_day',label:'Per Day'},{value:'per_project',label:'Per Project'},{value:'per_hour',label:'Per Hour'}];

  return (
    <div>
      <div className="flex-between mb-16">
        <div><div className="card-title">🤝 Vendors & Freelancers</div><div className="text-xs text-muted" style={{marginTop:'4px'}}>External resources your agency works with. Selectable when creating tasks.</div></div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Vendor</button>
      </div>
      <div className="card">
        <table className="data-table">
          <thead><tr><th>Name</th><th>Type</th><th>Specialization</th><th>Contact</th><th>Rate</th><th>Availability</th><th>Actions</th></tr></thead>
          <tbody>
            {vendors.map(v => {
              const avail = AVAIL_OPTS.find(o=>o.value===v.availability)||AVAIL_OPTS[0];
              const rt = RATE_TYPES.find(r=>r.value===(v.rate_type||v.ratetype));
              return (
                <tr key={v.id}>
                  <td><span className="fw-700">{v.name}</span></td>
                  <td><span className="badge badge-gray">{v.type||'—'}</span></td>
                  <td className="text-xs text-muted">{v.specialization||v.spec||'—'}</td>
                  <td><div className="text-xs"><div>{v.email||'—'}</div><div className="text-muted">{v.phone||''}</div></div></td>
                  <td className="mono text-sm">{v.rate ? '₹'+Number(v.rate).toLocaleString('en-IN')+(rt?' / '+rt.label:'') : '—'}</td>
                  <td><span className={`badge ${avail.cls}`}>{avail.label}</span></td>
                  <td>
                    <div className="flex" style={{gap:'4px'}}>
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => openEdit(v)}>✏️ Edit</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => del(v)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {vendors.length===0 && <tr><td colSpan={7} style={{padding:'20px',textAlign:'center'}} className="text-muted text-sm">No vendors yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? '✏️ Edit Vendor' : '+ Add Vendor / Freelancer'}
        footer={<><button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 Save Vendor'}</button></>}>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Name</label><input className="form-input" placeholder="e.g. Rahul Studio" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Type</label><select className="form-select" value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>{TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Specialization</label><input className="form-input" placeholder="e.g. Product photography, food shoots" value={form.spec} onChange={e=>setForm({...form,spec:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Availability</label><select className="form-select" value={form.availability} onChange={e=>setForm({...form,availability:e.target.value})}>{AVAIL_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Rate (₹)</label><input className="form-input" type="number" value={form.rate} onChange={e=>setForm({...form,rate:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Rate Type</label><select className="form-select" value={form.ratetype} onChange={e=>setForm({...form,ratetype:e.target.value})}>{RATE_TYPES.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
        </div>
        <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" placeholder="Any additional notes..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} /></div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: HOLIDAYS
// ══════════════════════════════════════════════════════════
const HOL_TYPES = [
  { value:'national', label:'🇮🇳 National Holiday',  badge:'badge-red',    color:'#e84545' },
  { value:'regional', label:'📍 Regional Holiday',   badge:'badge-orange', color:'#e87830' },
  { value:'agency',   label:'🏢 Agency Holiday',     badge:'badge-blue',   color:'#3b7ef0' },
  { value:'optional', label:'🗓️ Optional Holiday',  badge:'badge-yellow', color:'#f7c34f' },
  { value:'leave',    label:'🙋 Team Leave',         badge:'badge-purple', color:'#9050e8' },
];
const HOL_TYPE_MAP = Object.fromEntries(HOL_TYPES.map(t => [t.value, t]));
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const INDIA_HOLIDAYS_2026 = [
  { name:'New Year Day',        date:'2026-01-01', type:'agency',   applies_to:'all', recurring:'yes', notes:'Agency closed for New Year' },
  { name:'Republic Day',        date:'2026-01-26', type:'national', applies_to:'all', recurring:'yes', notes:'' },
  { name:'Maha Shivaratri',     date:'2026-02-26', type:'optional', applies_to:'all', recurring:'yes', notes:'Optional holiday' },
  { name:'Holi',                date:'2026-03-14', type:'national', applies_to:'all', recurring:'yes', notes:'Second day of Holi' },
  { name:'Good Friday',         date:'2026-04-03', type:'national', applies_to:'all', recurring:'yes', notes:'' },
  { name:'Vishu / Baisakhi',    date:'2026-04-14', type:'regional', applies_to:'all', recurring:'yes', notes:'Kerala/Punjab regional holiday' },
  { name:'May Day / Labour Day',date:'2026-05-01', type:'national', applies_to:'all', recurring:'yes', notes:'International Workers Day' },
  { name:'Eid al-Adha',         date:'2026-06-07', type:'national', applies_to:'all', recurring:'yes', notes:'Date may vary by moon sighting' },
  { name:'Independence Day',    date:'2026-08-15', type:'national', applies_to:'all', recurring:'yes', notes:'' },
  { name:'Janmashtami',         date:'2026-08-27', type:'optional', applies_to:'all', recurring:'yes', notes:'Optional holiday' },
  { name:'Onam',                date:'2026-09-01', type:'regional', applies_to:'all', recurring:'yes', notes:'Kerala regional holiday' },
  { name:'Gandhi Jayanti',      date:'2026-10-02', type:'national', applies_to:'all', recurring:'yes', notes:'' },
  { name:'Dussehra',            date:'2026-10-11', type:'national', applies_to:'all', recurring:'yes', notes:'Vijaya Dashami' },
  { name:'Diwali',              date:'2026-10-20', type:'national', applies_to:'all', recurring:'yes', notes:'Main Diwali day' },
  { name:'Diwali Holiday',      date:'2026-10-21', type:'agency',   applies_to:'all', recurring:'yes', notes:'Agency extended Diwali break' },
  { name:'Guru Nanak Jayanti',  date:'2026-11-14', type:'national', applies_to:'all', recurring:'yes', notes:'' },
  { name:'Christmas',           date:'2026-12-25', type:'national', applies_to:'all', recurring:'yes', notes:'' },
  { name:'Agency Annual Day',   date:'2026-12-31', type:'agency',   applies_to:'all', recurring:'yes', notes:'Last day of year — agency closed' },
];

function HolidaysTab() {
  const [holidays,   setHolidays]   = useState([]);
  const [team,       setTeam]       = useState([]);
  const [weeklyOff,  setWeeklyOff]  = useState([0, 6]);
  const [leadDays,   setLeadDays]   = useState(3);
  const [savingSettings, setSavingSettings] = useState(false);
  const [modal,      setModal]      = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [seeding,    setSeeding]    = useState(false);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterType, setFilterType] = useState('All');
  const [bulkResult, setBulkResult] = useState(null);

  const emptyForm = { name:'', date:new Date().toISOString().split('T')[0], type:'national', applies_to:'all', recurring:'yes', notes:'' };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [h, t] = await Promise.all([api.get('/holidays'), api.get('/team-members')]);
    setHolidays(h.data?.data || h.data || []);
    setTeam((t.data?.data || t.data || []).filter(m => !m.deleted_at));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Load weekly_off + scheduling_lead_days from server (tenant settings)
    api.get('/auth/me').then(r => {
      const t = r.data?.tenant || r.data?.data?.tenant;
      if (t) {
        if (Array.isArray(t.weekly_off) && t.weekly_off.length) {
          setWeeklyOff(t.weekly_off);
          localStorage.setItem('leadex_weekly_off', JSON.stringify(t.weekly_off));
        } else {
          try { const w = JSON.parse(localStorage.getItem('leadex_weekly_off') || '[0,6]'); setWeeklyOff(w); } catch(e) {}
        }
        if (t.scheduling_lead_days != null) setLeadDays(Number(t.scheduling_lead_days));
      }
    }).catch(() => {
      try { const w = JSON.parse(localStorage.getItem('leadex_weekly_off') || '[0,6]'); setWeeklyOff(w); } catch(e) {}
    });
  }, []);

  const toggleWeeklyOff = (day) => {
    const newDays = weeklyOff.includes(day) ? weeklyOff.filter(d=>d!==day) : [...weeklyOff, day].sort();
    setWeeklyOff(newDays);
    localStorage.setItem('leadex_weekly_off', JSON.stringify(newDays));
    // Save to server immediately
    api.put('/tenant/settings', { weekly_off: newDays }).catch(() => {});
  };

  const saveLeadDays = async (val) => {
    const n = Math.max(1, Math.min(30, parseInt(val) || 3));
    setLeadDays(n);
    setSavingSettings(true);
    try {
      await api.put('/tenant/settings', { scheduling_lead_days: n });
    } catch(e) {
      alert('Failed to save: ' + (e.response?.data?.message || e.message));
    } finally { setSavingSettings(false); }
  };

  const seedDefaults = async () => {
    if (!window.confirm(`Add ${INDIA_HOLIDAYS_2026.length} standard Indian holidays for 2026? Continue?`)) return;
    setSeeding(true);
    try {
      let added=0, failed=0;
      for (const h of INDIA_HOLIDAYS_2026) {
        try { await api.post('/holidays', h); added++; } catch(e) { failed++; }
      }
      if (failed) alert(`${added} added, ${failed} failed.`);
      load();
    } finally { setSeeding(false); }
  };

  // ── Download Template ──────────────────────────────────
  const downloadTemplate = () => {
    const rows = [
      ['Date (YYYY-MM-DD)', 'Name', 'Type (national/regional/agency/optional/leave)', 'Applies To (all or team member name)', 'Recurring (yes/no)', 'Notes'],
      ['2026-01-26', 'Republic Day',    'national', 'all',         'yes', ''],
      ['2026-03-14', 'Holi',           'national', 'all',         'yes', ''],
      ['2026-09-01', 'Onam',           'regional', 'all',         'yes', 'Kerala regional holiday'],
      ['2026-04-10', 'Office Annual Day','agency',  'all',         'no',  'Special agency event'],
      ['2026-06-20', 'Arjun Leave',    'leave',    'Arjun Menon', 'no',  'Planned leave'],
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'Holiday_Upload_Template.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Bulk Upload CSV ────────────────────────────────────
  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    // Parse CSV
    const rows = text.trim().split('\n').map(line =>
      line.split(',').map(c => c.trim().replace(/^"|"$/g,'').replace(/""/g,'"'))
    );
    const dataRows = rows.slice(1).filter(r => r[0] && r[0].trim());
    const validTypes = ['national','regional','agency','optional','leave'];
    let added=0, skipped=0, errors=[];

    for (let i=0; i<dataRows.length; i++) {
      const r = dataRows[i];
      const date      = String(r[0]||'').trim();
      const name      = String(r[1]||'').trim();
      const type      = String(r[2]||'national').trim().toLowerCase();
      const applies_to= String(r[3]||'all').trim();
      const recurring = String(r[4]||'yes').trim().toLowerCase()==='yes'?'yes':'no';
      const notes     = String(r[5]||'').trim();

      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { errors.push(`Row ${i+2}: Invalid date "${date}"`); skipped++; continue; }
      if (!name) { errors.push(`Row ${i+2}: Name required`); skipped++; continue; }
      const finalType = validTypes.includes(type) ? type : 'national';

      try {
        await api.post('/holidays', { date, name, type:finalType, applies_to, recurring, notes });
        added++;
      } catch(err) { errors.push(`Row ${i+2}: ${err.response?.data?.message||'Failed'}`); skipped++; }
    }
    setBulkResult({ added, skipped, errors: errors.slice(0,5) });
    load();
    e.target.value = '';
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (h) => {
    setEditing(h);
    setForm({ name:h.name||'', date:h.date||'', type:h.type||'national', applies_to:h.applies_to||h.applies||'all', recurring:h.recurring||'yes', notes:h.notes||'' });
    setModal(true);
  };
  const save = async () => {
    if (!form.name.trim() || !form.date) return;
    setSaving(true);
    try {
      const payload = { name:form.name, date:form.date, type:form.type, applies_to:form.applies_to, recurring:form.recurring, notes:form.notes };
      if (editing) await api.put('/holidays/'+editing.id, payload);
      else         await api.post('/holidays', payload);
      setModal(false); load();
    } finally { setSaving(false); }
  };
  const del = async (h) => { if(!window.confirm('Delete "'+h.name+'"?')) return; await api.delete('/holidays/'+h.id); load(); };

  const filtered = holidays.filter(h => {
    const matchYear = !h.date || h.date.startsWith(String(filterYear));
    const matchType = filterType==='All' || h.type===filterType;
    return matchYear && matchType;
  }).sort((a,b) => (a.date||'').localeCompare(b.date||''));

  const thisYear = String(filterYear);
  const byType   = {};
  HOL_TYPES.forEach(t => { byType[t.value] = holidays.filter(h => h.type===t.value && (h.date||'').startsWith(thisYear)).length; });
  const totalDaysOff = Object.values(byType).reduce((s,n)=>s+n,0);

  const getDayName   = (d) => { if(!d) return '—'; return DAYS[new Date(d+'T12:00:00').getDay()]; };
  const getDayOfWeek = (d) => { if(!d) return -1; return new Date(d+'T12:00:00').getDay(); };
  const formatDate   = (d) => { if(!d) return '—'; const dt=new Date(d+'T12:00:00'); return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`; };

  return (
    <div>
      {/* Header */}
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">📅 Holiday & Leave Calendar</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Non-working days used for working-day calculations and backward phase scheduling.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={downloadTemplate}>⬇️ Download Template</button>
          <label className="btn btn-secondary" style={{cursor:'pointer'}}>
            📤 Bulk Upload
            <input type="file" accept=".csv" style={{display:'none'}} onChange={handleBulkUpload} />
          </label>
          {holidays.length === 0 && <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>{seeding?'Adding...':'⚡ Add India 2026'}</button>}
          <button className="btn btn-primary" onClick={openAdd}>+ Add Holiday</button>
        </div>
      </div>

      {/* Bulk Upload Result */}
      {bulkResult && (
        <div className="card mb-12" style={{padding:'10px 16px',borderLeft:`4px solid ${bulkResult.errors.length?'#e87830':'#1db87a'}`}}>
          <div className="flex-between">
            <span className="text-sm">✅ {bulkResult.added} added, ⚠️ {bulkResult.skipped} skipped</span>
            <button className="icon-btn" onClick={()=>setBulkResult(null)}>✕</button>
          </div>
          {bulkResult.errors.length > 0 && (
            <div className="text-xs text-muted" style={{marginTop:'4px'}}>{bulkResult.errors.join(' · ')}</div>
          )}
        </div>
      )}

      {/* Weekly Off Picker */}
      <div className="card mb-16" style={{padding:'14px 18px'}}>
        <div className="flex-between mb-10">
          <div>
            <div className="fw-700 text-sm">🗓️ Weekly Off Days</div>
            <div className="text-xs text-muted">These days are always skipped in working-day calculations.</div>
          </div>
          <div className="text-xs text-muted">Off every: {weeklyOff.map(d=>DAYS[d]).join(' + ')}</div>
        </div>
        <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
          {DAYS.map((day, idx) => (
            <div key={idx} onClick={() => toggleWeeklyOff(idx)} style={{
              display:'inline-flex', alignItems:'center', justifyContent:'center',
              width:'52px', height:'36px', borderRadius:'20px', cursor:'pointer',
              fontWeight:700, fontSize:'0.8rem', userSelect:'none', transition:'all 0.15s',
              border: `2px solid ${weeklyOff.includes(idx) ? '#e84545' : 'var(--border)'}`,
              background: weeklyOff.includes(idx) ? 'rgba(232,69,69,0.1)' : 'var(--bg3)',
              color: weeklyOff.includes(idx) ? '#e84545' : 'var(--text3)',
            }}>{day}</div>
          ))}
        </div>
      </div>

      {/* Scheduling Lead Days */}
      <div className="card mb-16" style={{padding:'14px 18px'}}>
        <div className="flex-between mb-10">
          <div>
            <div className="fw-700 text-sm">⏱️ Scheduling Lead Days</div>
            <div className="text-xs text-muted">How many working days before the event should content be scheduled? Used to auto-calculate Scheduling Date in Work Orders.</div>
          </div>
          {savingSettings && <span className="text-xs text-muted">Saving...</span>}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <button onClick={()=>saveLeadDays(leadDays-1)} disabled={leadDays<=1}
            style={{width:'32px',height:'32px',borderRadius:'50%',border:'1px solid var(--border)',background:'var(--bg3)',fontSize:'1.1rem',cursor:leadDays<=1?'not-allowed':'pointer',color:'var(--text2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>−</button>
          <div style={{textAlign:'center',minWidth:'80px'}}>
            <div style={{fontSize:'2rem',fontWeight:800,fontFamily:"'Fraunces',serif",color:'var(--accent)',lineHeight:1}}>{leadDays}</div>
            <div className="text-xs text-muted">working days</div>
          </div>
          <button onClick={()=>saveLeadDays(leadDays+1)} disabled={leadDays>=30}
            style={{width:'32px',height:'32px',borderRadius:'50%',border:'1px solid var(--border)',background:'var(--bg3)',fontSize:'1.1rem',cursor:leadDays>=30?'not-allowed':'pointer',color:'var(--text2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>+</button>
          <div style={{flex:1,marginLeft:'8px'}}>
            <input type="range" min="1" max="30" value={leadDays}
              onChange={e=>setLeadDays(Number(e.target.value))}
              onMouseUp={e=>saveLeadDays(e.target.value)}
              onTouchEnd={e=>saveLeadDays(e.target.value)}
              style={{width:'100%',accentColor:'var(--accent)'}} />
            <div style={{display:'flex',justifyContent:'space-between'}} className="text-xs text-muted">
              <span>1 day</span><span>15 days</span><span>30 days</span>
            </div>
          </div>
          <div style={{padding:'8px 14px',background:'rgba(59,126,240,0.08)',border:'1px solid rgba(59,126,240,0.2)',borderRadius:'8px',fontSize:'0.78rem',fontWeight:600,color:'var(--accent)',whiteSpace:'nowrap',flexShrink:0}}>
            Content due <strong>{leadDays} working day{leadDays!==1?'s':''}</strong> before event
          </div>
        </div>
      </div>

      {/* KPI Strip */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))',gap:'10px',marginBottom:'16px'}}>
        <div className="card" style={{padding:'10px 14px',borderTop:'3px solid var(--accent)',textAlign:'center'}}>
          <div className="text-xs text-muted fw-700" style={{textTransform:'uppercase',fontSize:'0.62rem',letterSpacing:'0.5px'}}>IN Total</div>
          <div style={{fontSize:'1.8rem',fontWeight:800,fontFamily:"'Fraunces',serif"}}>{totalDaysOff}</div>
          <div className="text-xs text-muted">this year</div>
        </div>
        {HOL_TYPES.map(t => (
          <div key={t.value} className="card" style={{padding:'10px 14px',borderTop:`3px solid ${t.color}`,textAlign:'center'}}>
            <div className="text-xs fw-700" style={{color:t.color,fontSize:'0.62rem',textTransform:'uppercase',letterSpacing:'0.5px'}}>{t.label.split(' ').slice(1).join(' ')}</div>
            <div style={{fontSize:'1.8rem',fontWeight:800,fontFamily:"'Fraunces',serif",color:t.color}}>{byType[t.value]||0}</div>
            <div className="text-xs text-muted">this year</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex" style={{gap:'6px',flexWrap:'wrap',marginBottom:'12px'}}>
        {[2025,2026,2027].map(y => (
          <button key={y} onClick={()=>setFilterYear(y)} style={{
            padding:'4px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',
            border:`2px solid ${filterYear===y?'var(--accent)':'var(--border)'}`,
            background:filterYear===y?'var(--accent)':'var(--bg2)',color:filterYear===y?'#fff':'var(--text2)'
          }}>{y}</button>
        ))}
        <div style={{width:'1px',background:'var(--border)',margin:'0 4px'}} />
        {['All',...HOL_TYPES.map(t=>t.value)].map(type => {
          const info  = type==='All' ? {label:'All',color:'var(--accent)'} : HOL_TYPE_MAP[type];
          const count = type==='All' ? filtered.length : filtered.filter(h=>h.type===type).length;
          const act   = filterType===type;
          return (
            <button key={type} onClick={()=>setFilterType(type)} style={{
              padding:'4px 10px',borderRadius:'20px',fontSize:'0.72rem',fontWeight:600,cursor:'pointer',
              border:`2px solid ${act?(info?.color||'var(--accent)'):'var(--border)'}`,
              background:act?(info?.color||'var(--accent)'):'var(--bg2)',color:act?'#fff':'var(--text2)'
            }}>{type==='All'?'All':info?.label?.split(' ').slice(1).join(' ')} ({count})</button>
          );
        })}
      </div>

      {/* Table */}
      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Date</th><th>Day</th><th>Holiday / Event</th><th>Type</th><th>Applies To</th><th>Recurring</th><th>Notes</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length===0 && (
              <tr><td colSpan={8} style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">
                {holidays.length===0 ? <>No holidays yet. Click <b>⚡ Add India 2026</b> or <b>⬇️ Download Template</b> to bulk upload.</> : 'No holidays for this filter.'}
              </td></tr>
            )}
            {filtered.map(h => {
              const dow      = getDayOfWeek(h.date);
              const isWOff   = weeklyOff.includes(dow);
              const typeInfo = HOL_TYPE_MAP[h.type] || HOL_TYPES[0];
              return (
                <tr key={h.id} style={{opacity:isWOff?0.5:1}} title={isWOff?'This day is already a weekly off':''}> 
                  <td>
                    <div className="mono fw-700">{h.date}</div>
                    <div className="text-xs text-muted">{formatDate(h.date)}</div>
                  </td>
                  <td><span className={`badge ${isWOff?'badge-gray':'badge-blue'}`} style={{fontSize:'0.68rem'}}>{getDayName(h.date)}{isWOff?' ⚠️':''}</span></td>
                  <td className="fw-700">{h.name}</td>
                  <td><span className={`badge ${typeInfo.badge}`}>{typeInfo.label}</span></td>
                  <td className="text-xs text-muted">{(h.applies_to||h.applies)==='all'?'All Team':(h.applies_to||h.applies)}</td>
                  <td>{h.recurring==='yes'?<span className="badge badge-green" style={{fontSize:'0.68rem'}}>🔄 Recurring</span>:<span className="badge badge-gray" style={{fontSize:'0.68rem'}}>Once</span>}</td>
                  <td className="text-xs text-muted" style={{maxWidth:'160px'}}>{h.notes||'—'}</td>
                  <td>
                    <div className="flex" style={{gap:'4px'}}>
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(h)}>✏️</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(h)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'500px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Holiday':'+ Add Holiday / Leave'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Holiday / Event Name</label>
                <input className="form-input" placeholder="e.g. Diwali, Team Outing, Agency Day Off" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input className="form-input" type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} />
                  {form.date && <div className="text-xs text-muted" style={{marginTop:'4px'}}>{getDayName(form.date)}, {formatDate(form.date)}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-select" value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                    {HOL_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Applies To</label>
                  <select className="form-select" value={form.applies_to} onChange={e=>setForm({...form,applies_to:e.target.value})}>
                    <option value="all">👥 All Team</option>
                    {team.map(m=><option key={m.id} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Recurring</label>
                  <select className="form-select" value={form.recurring} onChange={e=>setForm({...form,recurring:e.target.value})}>
                    <option value="yes">🔄 Yes — every year</option>
                    <option value="no">Once — this date only</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" style={{minHeight:'60px'}} placeholder="Optional notes..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// TAB: SLA / TURNAROUND
// ══════════════════════════════════════════════════════════
const SLA_PRIORITY_OPTS = [
  { value:'urgent', icon:'🔴', label:'Urgent',  badge:'badge-red',    color:'#e84545' },
  { value:'high',   icon:'🟠', label:'High',    badge:'badge-yellow', color:'#e87830' },
  { value:'normal', icon:'🟡', label:'Normal',  badge:'badge-gray',   color:'#6b7280' },
  { value:'low',    icon:'🟢', label:'Low',     badge:'badge-green',  color:'#1db87a' },
];
const SLA_PRIORITY_MAP = Object.fromEntries(SLA_PRIORITY_OPTS.map(p=>[p.value,p]));

const DEFAULT_SLA_RULES = [
  // By priority — applies to all task types
  { task_type:'All Task Types', priority:'urgent', turnaround_days:1, internal_review_days:0, client_response_days:1, escalate_after_days:1, notes:'Drop everything. Same-day delivery target. Notify all stakeholders immediately.' },
  { task_type:'All Task Types', priority:'high',   turnaround_days:2, internal_review_days:1, client_response_days:2, escalate_after_days:2, notes:'Next-day delivery target. Assign dedicated resource.' },
  { task_type:'All Task Types', priority:'normal', turnaround_days:3, internal_review_days:1, client_response_days:2, escalate_after_days:3, notes:'Standard delivery window. Follow regular workflow.' },
  { task_type:'All Task Types', priority:'low',    turnaround_days:5, internal_review_days:2, client_response_days:3, escalate_after_days:5, notes:'Low priority — batch with other deliverables.' },
  // Specific task types
  { task_type:'Static Post Design', priority:'normal', turnaround_days:2, internal_review_days:1, client_response_days:2, escalate_after_days:2, notes:'Design within 2 days, QA on day 3.' },
  { task_type:'Instagram Reel',     priority:'normal', turnaround_days:4, internal_review_days:1, client_response_days:2, escalate_after_days:3, notes:'Includes shoot/edit time. Plan at least 5 days ahead.' },
  { task_type:'Blog Post / SEO',    priority:'normal', turnaround_days:3, internal_review_days:1, client_response_days:3, escalate_after_days:4, notes:'Research + writing + SEO review.' },
  { task_type:'Monthly Report',     priority:'normal', turnaround_days:2, internal_review_days:1, client_response_days:3, escalate_after_days:2, notes:'Report must be sent by 5th of each month.' },
  { task_type:'Ad Creative',        priority:'high',   turnaround_days:2, internal_review_days:1, client_response_days:1, escalate_after_days:1, notes:'Paid ad creative — fast turnaround for campaign go-live.' },
  { task_type:'Campaign Setup',     priority:'high',   turnaround_days:1, internal_review_days:0, client_response_days:1, escalate_after_days:1, notes:'Campaign setup must complete before launch date.' },
];

function SLATab() {
  const [rules,     setRules]     = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [seeding,   setSeeding]   = useState(false);

  const emptyForm = { task_type:'', priority:'normal', turnaround_days:3, internal_review_days:1, client_response_days:2, escalate_after_days:3, notes:'' };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [r, t] = await Promise.all([api.get('/sla-rules'), api.get('/task-types')]);
    setRules(r.data?.data || r.data || []);
    setTaskTypes(t.data?.data || t.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    if (!window.confirm(`Add ${DEFAULT_SLA_RULES.length} standard SLA rules? Continue?`)) return;
    setSeeding(true);
    try {
      let added=0, failed=0;
      for (const r of DEFAULT_SLA_RULES) {
        try {
          await api.post('/sla-rules', {
            ...r,
            name:             r.task_type ? `${r.task_type} — ${r.priority}` : `All Tasks — ${r.priority}`,
            phase:            r.priority,
            trigger:          'task_created',
            working_days:     String(r.turnaround_days),
            action_on_breach: 'escalate',
          });
          added++;
        } catch(e) { failed++; }
      }
      if (failed) alert(`${added} added, ${failed} failed.`);
      load();
    } finally { setSeeding(false); }
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      task_type:           r.task_type||'',
      priority:            r.priority||'normal',
      turnaround_days:     r.turnaround_days||3,
      internal_review_days:r.internal_review_days||1,
      client_response_days:r.client_response_days||2,
      escalate_after_days: r.escalate_after_days||3,
      notes:               r.notes||''
    });
    setModal(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        name:             form.task_type ? `${form.task_type} — ${form.priority}` : `All Tasks — ${form.priority}`,
        phase:            form.priority,
        trigger:          'task_created',
        working_days:     String(parseInt(form.turnaround_days)||1),
        action_on_breach: 'escalate',
        turnaround_days:      parseInt(form.turnaround_days)||1,
        internal_review_days: parseInt(form.internal_review_days)||0,
        client_response_days: parseInt(form.client_response_days)||1,
        escalate_after_days:  parseInt(form.escalate_after_days)||1,
      };
      if (editing) await api.put('/sla-rules/'+editing.id, payload);
      else         await api.post('/sla-rules', payload);
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };
  const del = async (r) => { if(!window.confirm('Delete this SLA rule?')) return; await api.delete('/sla-rules/'+r.id); load(); };

  // Group by task type
  const grouped = rules.reduce((acc, r) => {
    const k = r.task_type || 'All Task Types';
    if (!acc[k]) acc[k] = [];
    acc[k].push(r);
    return acc;
  }, {});

  const totalDays = (r) => (parseInt(r.turnaround_days)||0) + (parseInt(r.internal_review_days)||0) + (parseInt(r.client_response_days)||0);

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">⏱️ Turnaround Standards</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Define delivery timeframes per task type and priority. Used to calculate deadlines and flag overdue tasks automatically.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>{seeding?'Adding...':'⚡ Add Standard Rules'}</button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Rule</button>
        </div>
      </div>

      {/* How it works */}
      {rules.length === 0 && (
        <div className="card mb-16" style={{padding:'14px 18px',borderLeft:'4px solid var(--accent)',background:'rgba(59,126,240,0.03)'}}>
          <div className="fw-700 text-sm mb-6">💡 How Turnaround Standards work</div>
          <div className="text-xs text-muted" style={{lineHeight:1.8}}>
            Each rule defines the maximum working days for: <b>Production</b> (creation) + <b>Internal Review</b> (QA) + <b>Client Response Window</b> (approval). The system uses these to auto-calculate task due dates and flag overdue items. You can set global rules by priority, and override for specific task types.
          </div>
        </div>
      )}

      {/* SLA Summary Cards */}
      {rules.filter(r=>!r.task_type||r.task_type==='All Task Types').length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:'10px',marginBottom:'20px'}}>
          {SLA_PRIORITY_OPTS.map(p => {
            const rule = rules.find(r=>(r.task_type===''||r.task_type==='All Task Types')&&r.priority===p.value);
            if (!rule) return null;
            return (
              <div key={p.value} className="card" style={{padding:'12px 16px',borderTop:`3px solid ${p.color}`,textAlign:'center'}}>
                <div className="fw-700 text-xs" style={{color:p.color,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'6px'}}>{p.icon} {p.label}</div>
                <div style={{fontSize:'1.8rem',fontWeight:800,fontFamily:"'Fraunces',serif",color:p.color}}>{rule.turnaround_days}d</div>
                <div className="text-xs text-muted">production</div>
                <div style={{marginTop:'6px',fontSize:'0.72rem',color:'var(--text3)'}}>+{rule.internal_review_days}d review · +{rule.client_response_days}d client</div>
                <div style={{marginTop:'4px'}}><span className="badge badge-red" style={{fontSize:'0.62rem'}}>🚨 Escalate after {rule.escalate_after_days}d</span></div>
              </div>
            );
          })}
        </div>
      )}

      {/* Grouped table */}
      {Object.entries(grouped).map(([taskTypeName, rows]) => (
        <div key={taskTypeName} className="card mb-14">
          <div style={{padding:'8px 16px',borderBottom:'1px solid var(--border)',background:'var(--bg3)',borderRadius:'var(--radius) var(--radius) 0 0',display:'flex',alignItems:'center',gap:'8px'}}>
            <span className="fw-700 text-sm">
              {taskTypeName === 'All Task Types' ? '⚖️ All Task Types (Default Rules)' : `🗂️ ${taskTypeName}`}
            </span>
            <span className="badge badge-gray" style={{fontSize:'0.65rem'}}>{rows.length} rule{rows.length!==1?'s':''}</span>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Production</th>
                <th>Internal Review</th>
                <th>Client Window</th>
                <th>Total Days</th>
                <th>Escalate After</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.sort((a,b)=>['urgent','high','normal','low'].indexOf(a.priority)-['urgent','high','normal','low'].indexOf(b.priority)).map(r => {
                const pInfo = SLA_PRIORITY_MAP[r.priority] || SLA_PRIORITY_OPTS[2];
                const total = totalDays(r);
                return (
                  <tr key={r.id}>
                    <td><span className={`badge ${pInfo.badge}`}>{pInfo.icon} {pInfo.label}</span></td>
                    <td>
                      <span className="mono fw-700" style={{color:'var(--accent)',fontSize:'1rem'}}>{r.turnaround_days}</span>
                      <span className="text-xs text-muted"> day{r.turnaround_days!==1?'s':''}</span>
                    </td>
                    <td className="mono text-sm">{r.internal_review_days} day{r.internal_review_days!==1?'s':''}</td>
                    <td className="mono text-sm">{r.client_response_days} day{r.client_response_days!==1?'s':''}</td>
                    <td>
                      <span className="badge badge-blue" style={{fontSize:'0.72rem'}}>⏱️ {total} days total</span>
                    </td>
                    <td><span className="badge badge-red" style={{fontSize:'0.68rem'}}>🚨 {r.escalate_after_days}d overdue</span></td>
                    <td className="text-xs text-muted" style={{maxWidth:'180px'}}>{r.notes||'—'}</td>
                    <td>
                      <div className="flex" style={{gap:'4px'}}>
                        <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(r)}>✏️</button>
                        <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(r)}>✕</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {rules.length===0 && (
        <div className="card" style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">
          No SLA rules yet. Click <b>⚡ Add Standard Rules</b> to populate with {DEFAULT_SLA_RULES.length} industry-standard turnaround rules.
        </div>
      )}

      {/* ── Modal ── */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'560px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit SLA Rule':'+ Add SLA Rule'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Task Type <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(leave empty = applies to all)</span></label>
                  <select className="form-select" value={form.task_type} onChange={e=>setForm({...form,task_type:e.target.value})}>
                    <option value="">— All Task Types —</option>
                    {taskTypes.filter(t=>t.enabled!==false).map(t=><option key={t.id} value={t.name}>{t.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Priority Level</label>
                  <select className="form-select" value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})}>
                    {SLA_PRIORITY_OPTS.map(p=><option key={p.value} value={p.value}>{p.icon} {p.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Visual timeline */}
              <div className="card mb-12" style={{padding:'12px 16px',background:'var(--bg3)',border:'1px solid var(--border)'}}>
                <div className="text-xs fw-700 mb-8" style={{textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--text3)'}}>Timeline Preview</div>
                <div style={{display:'flex',gap:'0',alignItems:'center'}}>
                  {[
                    {label:'Production',  days:form.turnaround_days,     color:'#3b7ef0'},
                    {label:'QA Review',   days:form.internal_review_days, color:'#f7c34f'},
                    {label:'Client',      days:form.client_response_days, color:'#1db87a'},
                  ].map((s,i,arr) => (
                    <React.Fragment key={s.label}>
                      <div style={{flex:parseInt(s.days)||1,background:`${s.color}20`,border:`2px solid ${s.color}`,borderRadius:'6px',padding:'4px 6px',textAlign:'center',minWidth:'50px'}}>
                        <div className="text-xs fw-700" style={{color:s.color}}>{s.days}d</div>
                        <div style={{fontSize:'0.6rem',color:s.color}}>{s.label}</div>
                      </div>
                      {i<arr.length-1 && <div style={{width:'8px',height:'2px',background:'var(--border)',flexShrink:0}} />}
                    </React.Fragment>
                  ))}
                  <div style={{width:'8px',height:'2px',background:'var(--border)',flexShrink:0}} />
                  <div style={{padding:'4px 8px',background:'rgba(232,69,69,0.1)',border:'2px solid #e84545',borderRadius:'6px',textAlign:'center',minWidth:'50px'}}>
                    <div className="text-xs fw-700" style={{color:'#e84545'}}>Total</div>
                    <div style={{fontSize:'0.6rem',color:'#e84545'}}>{(parseInt(form.turnaround_days)||0)+(parseInt(form.internal_review_days)||0)+(parseInt(form.client_response_days)||0)}d</div>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Production Days <span className="text-xs" style={{color:'#3b7ef0',fontWeight:500,textTransform:'none'}}>(creation & design)</span></label>
                  <input className="form-input" type="number" min="1" value={form.turnaround_days} onChange={e=>setForm({...form,turnaround_days:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Internal Review Days <span className="text-xs" style={{color:'#f7c34f',fontWeight:500,textTransform:'none'}}>(QA check)</span></label>
                  <input className="form-input" type="number" min="0" value={form.internal_review_days} onChange={e=>setForm({...form,internal_review_days:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Client Response Window <span className="text-xs" style={{color:'#1db87a',fontWeight:500,textTransform:'none'}}>(approval)</span></label>
                  <input className="form-input" type="number" min="1" value={form.client_response_days} onChange={e=>setForm({...form,client_response_days:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Escalate After <span className="text-xs" style={{color:'#e84545',fontWeight:500,textTransform:'none'}}>(days overdue)</span></label>
                  <input className="form-input" type="number" min="1" value={form.escalate_after_days} onChange={e=>setForm({...form,escalate_after_days:e.target.value})} />
                  <div className="text-xs text-muted" style={{marginTop:'4px'}}>Flag as overdue and notify account manager after this many days past deadline.</div>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes / Special Conditions</label>
                <input className="form-input" placeholder="e.g. Report must be sent by 5th of month. Assign dedicated resource." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' SLA Rule'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// TAB: PLATFORMS (cards grid — with Standard Platforms picker)
// ══════════════════════════════════════════════════════════

const PLATFORM_SVG = {
  'Instagram': `<svg viewBox="0 0 32 32" width="100%" height="100%"><defs><linearGradient id="igG" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#feda77"/><stop offset=".5" stop-color="#f58529"/><stop offset="1" stop-color="#dd2a7b"/></linearGradient></defs><rect x="3" y="3" width="26" height="26" rx="7" fill="url(#igG)"/><circle cx="16" cy="16" r="6.2" fill="none" stroke="#fff" stroke-width="2.2"/><circle cx="22.5" cy="9.5" r="1.6" fill="#fff"/></svg>`,
  'Facebook': `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#1877f2"/><path d="M19.4 16h-2.5v9h-3.5v-9H11.7v-3h1.7v-2c0-2.4 1.5-3.7 3.6-3.7l2.3 0v3h-1.7c-.6 0-.9.3-.9 1v1.7h2.7l-.4 3z" fill="#fff"/></svg>`,
  'LinkedIn': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#0a66c2"/><rect x="7" y="12" width="3.5" height="13" fill="#fff"/><circle cx="8.75" cy="8.75" r="2" fill="#fff"/><path d="M13 12h3.4v1.8c.5-.9 1.7-2 3.6-2 3.4 0 4 2.2 4 5.1V25h-3.6v-6.8c0-1.6-.3-2.8-2-2.8s-2.3 1.3-2.3 2.6V25H13V12z" fill="#fff"/></svg>`,
  'YouTube': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect x="2" y="6.5" width="28" height="19" rx="4" fill="#ff0000"/><polygon points="13,11.5 23,16 13,20.5" fill="#fff"/></svg>`,
  'Google Ads': `<svg viewBox="0 0 32 32" width="100%" height="100%"><path d="M14.5 4l-9 15.5a4 4 0 0 0 1.5 5.5 4 4 0 0 0 5.5-1.5l9-15.5a4 4 0 0 0-1.5-5.5A4 4 0 0 0 14.5 4z" fill="#fbbc04"/><path d="M17.5 4l9 15.5a4 4 0 0 1-1.5 5.5 4 4 0 0 1-5.5-1.5l-9-15.5a4 4 0 0 1 1.5-5.5A4 4 0 0 1 17.5 4z" fill="#4285f4"/><circle cx="9" cy="24" r="4" fill="#34a853"/></svg>`,
  'X / Twitter': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#000"/><path d="M21.5 7h2.8l-6.1 7 7.2 9.7H20l-4.5-6-5.1 6H7.5l6.5-7.5L6.7 7h5.6l4 5.4L21.5 7zm-1 14.9h1.5L11.5 8.5H9.9l10.6 13.4z" fill="#fff"/></svg>`,
  'Pinterest': `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#e60023"/><path d="M16.4 7c-5 0-7.5 3.3-7.5 6.2 0 1.7.7 3.3 2.3 3.9.3.1.5 0 .6-.3l.2-.9c.1-.3 0-.4-.2-.6-.5-.6-.8-1.3-.8-2.4 0-3.1 2.4-5.9 6.2-5.9 3.4 0 5.3 2 5.3 4.7 0 3.6-1.6 6.6-4 6.6-1.3 0-2.3-1.1-2-2.4.4-1.6 1.2-3.4 1.2-4.5 0-1-.6-1.9-1.7-1.9-1.4 0-2.5 1.4-2.5 3.3 0 1.2.4 2 .4 2l-1.7 7.1c-.5 2.1-.1 4.7 0 5 0 .1.2.2.3.1.1-.1 1.7-2.1 2.2-4.2.2-.6.9-3.6.9-3.6.5.8 1.7 1.6 3.1 1.6 4.1 0 6.9-3.7 6.9-8.7 0-3.8-3.2-7.3-8-7.3z" fill="#fff"/></svg>`,
  'WhatsApp': `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#25d366"/><path d="M22.4 18.7c-.4-.2-2.1-1-2.4-1.1-.3-.1-.6-.2-.8.2-.2.3-.9 1.1-1.1 1.3-.2.2-.4.3-.7.1-2.1-1-3.4-1.9-4.8-4.2-.4-.6.4-.6 1-2 .1-.2 0-.4-.1-.6l-1.1-2.6c-.3-.7-.6-.6-.8-.6-.2 0-.4 0-.6 0-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9 0 1.7 1.2 3.4 1.4 3.6.2.2 2.4 3.6 5.8 5.1 2.1.9 3 1 4 .8.6-.1 1.9-.8 2.2-1.6.3-.8.3-1.4.2-1.6-.1-.2-.3-.3-.7-.5z" fill="#fff"/></svg>`,
  'Telegram': `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#229ed9"/><path d="M9 16.2l13.2-5.1c.6-.2 1.2.1 1 1L20.9 22c-.2.7-.6.9-1.2.6l-3.4-2.5-1.6 1.6c-.2.2-.4.3-.7.3l.3-3.6 6.7-6c.3-.3-.1-.4-.4-.2l-8.3 5.2-3.6-1.1c-.8-.2-.8-.7.3-1z" fill="#fff"/></svg>`,
  'Snapchat': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#fffc00"/><path d="M16 6c3.5 0 6 2.4 6 5.9v3.5c.2.1.6.3 1.2.3.4 0 .9-.2 1.2-.4l.4 1.1c-.3.2-1 .6-2 .6-.4 0-.8-.1-1.1-.2 0 .4-.1.8-.2 1.2 1.4.3 2.7.9 3 1.1l-.5 1.1c-.3-.2-1.4-.7-2.6-1-.7 1.4-2 2.6-4.4 3-.3 0-.5.7-1.5.7s-1.2-.6-1.5-.7c-2.4-.4-3.7-1.6-4.4-3-1.2.3-2.3.8-2.6 1l-.5-1.1c.3-.2 1.6-.8 3-1.1-.1-.4-.2-.8-.2-1.2-.3.1-.7.2-1.1.2-1 0-1.7-.4-2-.6l.4-1.1c.3.2.8.4 1.2.4.6 0 1-.2 1.2-.3v-3.5C10 8.4 12.5 6 16 6z" fill="#000"/></svg>`,
  'TikTok': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#010101"/><path d="M22.2 11.7c-1.6-.3-2.9-1.3-3.6-2.7h-3v11.5c0 1.5-1.2 2.7-2.7 2.7s-2.7-1.2-2.7-2.7 1.2-2.7 2.7-2.7c.3 0 .5 0 .8.1V15c-.3 0-.5-.1-.8-.1-3 0-5.5 2.5-5.5 5.5s2.5 5.5 5.5 5.5 5.5-2.5 5.5-5.5v-6c1.2.7 2.6 1.1 4 1.1V12.7c-.1 0-.1 0-.2-1z" fill="#fff"/></svg>`,
  'Email / CRM': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect x="3" y="7" width="26" height="18" rx="3" fill="#e87830"/><path d="M3 9.5L16 17 29 9.5" stroke="#fff" stroke-width="2" fill="none"/></svg>`,
  'Amazon Ads': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#232f3e"/><path d="M8 24c2.5 1.8 5.5 2.5 8 2.5s5.5-.7 8-2.5c.3-.2 0-.5-.3-.3-2.3 1.3-4.7 1.9-7.4 1.9-2.7 0-5.1-.6-7.4-1.9-.3-.2-.6.1-.3.3z" fill="#ff9900"/><text x="16" y="18" text-anchor="middle" font-size="10" font-weight="bold" fill="#ff9900">a</text></svg>`,
  'Flipkart Ads': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#2874f0"/><path d="M9 9h14v3h-7v3h6v3h-6v6h-3V12H9V9z" fill="#fff"/></svg>`,
  'Spotify': `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#1db954"/><path d="M22.6 21.5c-.3.4-.8.6-1.2.3-3.4-2.1-7.6-2.5-12.6-1.4-.5.1-1-.2-1.1-.7-.1-.5.2-1 .7-1.1 5.4-1.2 10.1-.7 13.8 1.6.5.3.6.8.4 1.3zm1.7-3.4c-.4.5-1 .7-1.5.4-3.9-2.4-9.8-3.1-14.4-1.7-.6.2-1.3-.2-1.5-.8-.2-.6.2-1.3.8-1.5 5.3-1.6 11.8-.8 16.3 2 .5.3.7 1 .3 1.6zm.1-3.6c-4.6-2.7-12.3-3-16.7-1.7-.8.2-1.6-.2-1.8-1-.2-.8.2-1.6 1-1.8 5-1.5 13.5-1.2 18.8 1.9.7.4 1 1.3.5 2-.4.7-1.3.9-1.8.6z" fill="#fff"/></svg>`,
  'Meta Ads': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#1877f2"/><path d="M6 16c0-4.7 2.7-9 7.5-9 2.8 0 4.7 1.5 6.4 3.7 1.6-2.1 3.5-3.7 6.1-3.7 4 0 6 3.4 6 7.4 0 5.6-3.4 11.6-7.7 11.6-2.4 0-3.9-1.4-5.4-3.7-1.8-2.4-2.8-4.2-3.9-4.2-1.3 0-2.2 1.5-2.8 3.2C11.8 26.3 11 27 10 27c-3.5 0-4-4-4-11z" fill="#fff"/></svg>`,
  'Quora Ads': `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#a82400"/><path d="M16 7C11 7 7 11 7 16s4 9 9 9c1.7 0 3.2-.5 4.6-1.3l2 2.3 2.2-2-2-2.2c1.4-1.6 2.2-3.6 2.2-5.8 0-5-4-9-9-9zm0 15c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6c0 1.2-.4 2.4-1 3.3l-2.4-2.7-2.2 2 2.4 2.7c-.8.5-1.7.7-2.8.7z" fill="#fff"/></svg>`,
  'Reddit': `<svg viewBox="0 0 32 32" width="100%" height="100%"><circle cx="16" cy="16" r="14" fill="#ff4500"/><circle cx="16" cy="18" r="8" fill="#fff"/><circle cx="12.5" cy="17.5" r="1.4" fill="#ff4500"/><circle cx="19.5" cy="17.5" r="1.4" fill="#ff4500"/><path d="M11.5 21c1 1 2.7 1.7 4.5 1.7s3.5-.7 4.5-1.7" stroke="#ff4500" stroke-width="1.4" fill="none" stroke-linecap="round"/><circle cx="23.5" cy="13.5" r="2.2" fill="#ff4500"/><circle cx="8.5" cy="13.5" r="2.2" fill="#ff4500"/></svg>`,
  'Discord': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#5865f2"/><path d="M22.5 10.5c-1.4-.6-2.9-1.1-4.4-1.4l-.3.5c-1.3-.2-2.7-.2-4 0l-.3-.5c-1.5.3-3 .7-4.4 1.4-2.7 4-3.5 7.9-3.1 11.8 1.7 1.3 3.4 2 5.1 2.5l.4-.6c-.6-.2-1.3-.5-1.9-.9l.5-.3c2.4 1.1 5 1.6 7.6 1.6 2.6 0 5.2-.5 7.6-1.6l.5.3c-.6.4-1.2.7-1.9.9l.4.6c1.7-.5 3.4-1.2 5.1-2.5.4-4.5-.7-8.4-3.1-11.8zm-9.3 9.6c-1 0-1.9-.9-1.9-2.1s.9-2.1 1.9-2.1 1.9.9 1.9 2.1-.9 2.1-1.9 2.1zm5.6 0c-1 0-1.9-.9-1.9-2.1s.9-2.1 1.9-2.1 1.9.9 1.9 2.1-.9 2.1-1.9 2.1z" fill="#fff"/></svg>`,
  'Threads': `<svg viewBox="0 0 32 32" width="100%" height="100%"><rect width="32" height="32" rx="6" fill="#000"/><path d="M21.5 14.5c-.1-2.7-1.7-4.5-4.4-4.7-2.6-.2-4.4 1-5.2 2.7l1.6.8c.5-1.1 1.7-1.7 3.5-1.6 1.6.1 2.5 1.1 2.6 2.5-.7-.2-1.4-.3-2.2-.3-2.6 0-4.6 1.5-4.4 3.7.1 1.8 1.7 3 3.7 3 1.7 0 3-1 3.6-2.3.5.8 1 1.5 1.5 2.1l1.4-1.2c-.6-.6-1.4-2-1.7-4.7zm-4.6 4c-.9 0-1.9-.5-1.9-1.5 0-1 1-1.7 2.6-1.7.7 0 1.4.1 2 .3-.1 1.6-1.1 2.9-2.7 2.9z" fill="#fff"/></svg>`,
};

let _svgNonce = 0;
function platformSvgFor(name) {
  let svg = PLATFORM_SVG[name];
  if (!svg) return null;
  const nonce = 'p' + (++_svgNonce);
  return svg
    .replace(/id="([a-zA-Z][\w-]*)"/g, (_, id) => `id="${id}_${nonce}"`)
    .replace(/url\(#([a-zA-Z][\w-]*)\)/g, (_, id) => `url(#${id}_${nonce})`);
}

const STD_PLATFORMS = [
  { name:'Instagram',    category:'Social Media',     icon:'📸', color:'#e8323c', audience:'B2C, Lifestyle, Gen Z' },
  { name:'Facebook',     category:'Social Media',     icon:'📘', color:'#1877f2', audience:'B2C, B2B, All Ages' },
  { name:'LinkedIn',     category:'Social Media',     icon:'💼', color:'#0a66c2', audience:'B2B, Professionals' },
  { name:'YouTube',      category:'Video',            icon:'▶️', color:'#e84545', audience:'All Ages, Video' },
  { name:'Google Ads',   category:'Search & Display', icon:'🔍', color:'#4285f4', audience:'Intent-based' },
  { name:'X / Twitter',  category:'Social Media',     icon:'🐦', color:'#1da1f2', audience:'News, Tech, B2B' },
  { name:'Pinterest',    category:'Social Media',     icon:'📌', color:'#e60023', audience:'DIY, Fashion, Home Decor' },
  { name:'WhatsApp',     category:'Messaging',        icon:'💬', color:'#25d366', audience:'Direct, All Ages' },
  { name:'Telegram',     category:'Messaging',        icon:'📡', color:'#2ca5e0', audience:'Tech, Communities' },
  { name:'Snapchat',     category:'Social Media',     icon:'👻', color:'#ffcc00', audience:'Gen Z, 13–24' },
  { name:'TikTok',       category:'Video',            icon:'🎵', color:'#010101', audience:'Gen Z, Millennials' },
  { name:'Email / CRM',  category:'Email & CRM',      icon:'📧', color:'#e87830', audience:'All Segments' },
  { name:'Amazon Ads',   category:'E-Commerce',       icon:'🛒', color:'#ff9900', audience:'E-Commerce, Buyers' },
  { name:'Flipkart Ads', category:'E-Commerce',       icon:'🏷️', color:'#2874f0', audience:'E-Commerce India' },
  { name:'Spotify',      category:'Audio / Podcast',  icon:'🎵', color:'#1db954', audience:'Music & Podcast Listeners' },
  { name:'Meta Ads',     category:'Search & Display', icon:'🎯', color:'#1877f2', audience:'Broad Targeting' },
  { name:'Quora Ads',    category:'Search & Display', icon:'❓', color:'#a82400', audience:'Curious, Research-Intent' },
  { name:'Reddit',       category:'Social Media',     icon:'🔥', color:'#ff4500', audience:'Niche Communities' },
  { name:'Discord',      category:'Messaging',        icon:'🎮', color:'#5865f2', audience:'Gaming, Communities' },
  { name:'Threads',      category:'Social Media',     icon:'🧵', color:'#000000', audience:'Instagram Adjacent' },
];

const RESERVE_ICONS = [
  '📸','📘','💼','▶️','🔍','🐦','📌','💬','📧','🎵','🛒','📰',
  '📺','🎙️','📡','🌐','💡','🎯','📊','📣','🤝','🏷️','🔔','✍️',
  '🎬','🖼️','📝','📱','💻','🖥️','🎨','🎤','📷','🎥','🔗','⭐',
  '🌟','🔥','💥','🚀','👁️','❤️','📲','🗺️','🏪','🧩','🎁','❓',
];

const PLATFORM_COLORS = ['#e8323c','#1877f2','#0a66c2','#e84545','#4285f4','#25d366','#e60023','#2ca5e0','#ffcc00','#010101','#e87830','#ff9900','#2874f0','#1db954','#ff4500','#5865f2','#a82400','#9050e8','#18b8b8','#3b7ef0'];

const CATEGORY_OPTS = ['Social Media','Video','Search & Display','Messaging','Email & CRM','E-Commerce','Audio / Podcast','Other'];

const PLATFORM_DEFAULT_METRICS = {
  'Instagram':   ['Reach','Likes','Comments','Shares','Saves','Profile Visits'],
  'Facebook':    ['Reach','Reactions','Comments','Shares','Link Clicks'],
  'LinkedIn':    ['Impressions','Reactions','Comments','Reposts','Clicks'],
  'YouTube':     ['Views','Watch Time','Click-Through Rate (CTR)','Average View Duration','Likes','Comments'],
  'X / Twitter': ['Impressions','Likes','Reposts','Replies','Profile Visits'],
  'Google Ads':  ['Impressions','Clicks','Click-Through Rate (CTR)','Conversions','Cost'],
  'WhatsApp':    ['Views','Replies'],
  'TikTok':      ['Views','Likes','Comments','Shares','Saves','Profile Visits'],
  'Pinterest':   ['Impressions','Saves','Clicks','Outbound Clicks'],
  'Threads':     ['Views','Likes','Replies','Reposts'],
  'Snapchat':    ['Views','Screenshots','Shares'],
  'Telegram':    ['Views','Forwards','Reactions'],
  'Meta Ads':    ['Reach','Impressions','Clicks','Conversions','Cost'],
  'Google Ads':  ['Impressions','Clicks','CTR','Conversions','Cost Per Click'],
};

function PlatformsTab() {
  const [platforms, setPlatforms] = useState([]);
  const [stdModal, setStdModal]   = useState(false);
  const [stdSelected, setStdSelected] = useState([]);
  const [customModal, setCustomModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState({ name:'', category:'Social Media', icon:'📱', color:PLATFORM_COLORS[0], audience:'', notes:'' });
  const [saving, setSaving]       = useState(false);
  // Report Template modal
  const [rptModal, setRptModal]   = useState(false);
  const [rptPlatform, setRptPlatform] = useState(null);
  const [rptMetrics, setRptMetrics]   = useState([]);
  const [rptNewMetric, setRptNewMetric] = useState('');
  const [rptRefImage, setRptRefImage]   = useState('');
  const [rptNotes, setRptNotes]         = useState('');
  const [rptSaving, setRptSaving]       = useState(false);


  const QUICK_METRICS = ['Reach','Impressions','Likes','Comments','Shares','Saves','Profile Visits','Engagement','Reactions','Link Clicks','Reposts','Video Views','Clicks','Conversions','Cost'];

  const load = useCallback(async () => {
    const r = await api.get('/platforms');
    setPlatforms(r.data?.data || r.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Standard platforms picker
  const openStdPicker = () => { setStdSelected([]); setStdModal(true); };
  const toggleStd = (name) => {
    const already = platforms.some(p => p.name.toLowerCase()===name.toLowerCase());
    if (already) return;
    setStdSelected(sel => sel.includes(name) ? sel.filter(x=>x!==name) : [...sel, name]);
  };
  const addStdPlatforms = async () => {
    if (!stdSelected.length) return;
    setSaving(true);
    try {
      for (const name of stdSelected) {
        const p = STD_PLATFORMS.find(x=>x.name===name);
        if (p) {
          const defaults = PLATFORM_DEFAULT_METRICS[name] || ['Reach','Likes','Comments','Shares'];
          const tpl = { metrics: defaults.map((label,i) => ({id:'m'+i,label})), referenceImage:'', notes:'' };
          await api.post('/platforms', { name:p.name, category:p.category, icon:p.icon, color:p.color, audience:p.audience, notes:'', report_template:JSON.stringify(tpl), is_active:true });
        }
      }
      setStdModal(false); load();
    } finally { setSaving(false); }
  };

  // Custom platform modal
  const openCustom = () => { setEditing(null); setForm({ name:'', category:'Social Media', icon:'📱', color:PLATFORM_COLORS[0], audience:'', notes:'' }); setCustomModal(true); };
  const openEdit   = (p) => { setEditing(p); setForm({ name:p.name, category:p.category||'Social Media', icon:p.icon||'📱', color:p.color||PLATFORM_COLORS[0], audience:p.audience||'', notes:p.notes||'' }); setCustomModal(true); };
  const save = async () => {
    if(!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name:form.name, category:form.category, icon:form.icon, color:form.color, audience:form.audience, notes:form.notes, is_active:true };
      if(editing) await api.put('/platforms/'+editing.id, payload);
      else         await api.post('/platforms', payload);
      setCustomModal(false); load();
    } finally { setSaving(false); }
  };
  const del    = async (p) => { if(!window.confirm('Delete platform "'+p.name+'"?')) return; await api.delete('/platforms/'+p.id); load(); };
  const toggle = async (p) => { await api.put('/platforms/'+p.id, { ...p, is_active: !p.is_active }); load(); };

  // Report Template modal
  const openRptModal = (p) => {
    setRptPlatform(p);
    let tpl = {};
    try { tpl = p.report_template ? JSON.parse(p.report_template) : {}; } catch(e) {}
    const defaults = PLATFORM_DEFAULT_METRICS[p.name] || ['Reach','Likes','Comments','Shares'];
    const metrics = tpl.metrics?.length ? tpl.metrics : defaults.map((label,i) => ({id:'m'+i,label}));
    setRptMetrics(metrics);
    setRptRefImage(tpl.referenceImage||'');
    setRptNotes(tpl.notes||'');
    setRptNewMetric('');
    setRptModal(true);
  };

  const rptAddMetric = () => {
    const label = rptNewMetric.trim();
    if (!label) return;
    if (rptMetrics.some(m => m.label.toLowerCase()===label.toLowerCase())) return;
    setRptMetrics(m => [...m, {id:'m'+Date.now(), label}]);
    setRptNewMetric('');
  };
  const rptQuickAdd = (label) => {
    if (rptMetrics.some(m => m.label.toLowerCase()===label.toLowerCase())) return;
    setRptMetrics(m => [...m, {id:'m'+Date.now(), label}]);
  };
  const rptRemoveMetric = (id) => setRptMetrics(m => m.filter(x=>x.id!==id));

  const rptUploadImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setRptRefImage(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const rptSave = async () => {
    if (!rptPlatform) return;
    setRptSaving(true);
    try {
      const tpl = { metrics: rptMetrics, referenceImage: rptRefImage, notes: rptNotes };
      await api.put('/platforms/'+rptPlatform.id, { report_template: JSON.stringify(tpl) });
      setRptModal(false); load();
    } finally { setRptSaving(false); }
  };

  const getMetricCount = (p) => {
    try {
      const tpl = p.report_template ? JSON.parse(p.report_template) : {};
      return tpl.metrics?.length || 0;
    } catch(e) { return 0; }
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">📱 Platforms</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Manage the platforms you work on. Used in task creation, KPI tracking and client profiles.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={openStdPicker}>⚡ Add Standard Platforms</button>
          <button className="btn btn-primary"   onClick={openCustom}>+ Custom Platform</button>
        </div>
      </div>

      {/* Platform cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))',gap:'12px'}}>
        {platforms.map(p => {
          const metricCount = getMetricCount(p);
          return (
            <div key={p.id} className="platform-card" style={{
              background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'var(--radius)',
              borderTop:`3px solid ${p.color||'#3b7ef0'}`,
              padding:'16px 14px 14px', opacity:!p.is_active?0.45:1,
              boxShadow:'var(--card-shadow)', textAlign:'center'
            }}>
              {/* Actions — hidden, show on hover via CSS */}
              <div className="platform-card-actions">
                <button className="btn btn-secondary" style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={() => openEdit(p)}>✏️</button>
                <button className="btn btn-red"       style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={() => del(p)}>✕</button>
              </div>
              {/* Logo */}
              <div style={{
                width:'52px', height:'52px', borderRadius:'12px', margin:'4px auto 8px',
                background:`${p.color||'#3b7ef0'}18`, border:`2px solid ${p.color||'#3b7ef0'}25`,
                display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden'
              }}>
                {(() => { const svg = platformSvgFor(p.name); return svg
                  ? <div dangerouslySetInnerHTML={{__html: svg}} style={{width:'40px',height:'40px'}} />
                  : <span style={{fontSize:'1.6rem'}}>{p.icon||'📱'}</span>;
                })()}
              </div>
              <div style={{fontWeight:700, fontSize:'0.85rem', marginBottom:'2px'}}>{p.name}</div>
              <div className="text-xs text-muted" style={{marginBottom:'6px'}}>{p.category||''}</div>
              {/* Content types count badge */}
              <div style={{marginBottom:'8px'}}>
                <span style={{
                  display:'inline-block', padding:'2px 10px', borderRadius:'20px', fontSize:'0.68rem',
                  background:'var(--bg3)', border:'1px solid var(--border)', color:'var(--text3)'
                }}>
                  {metricCount ? `${metricCount} metric fields` : '0 metric fields'}
                </span>
              </div>
              {/* Report Template button */}
              <button onClick={() => openRptModal(p)} style={{
                width:'100%', padding:'5px 8px', fontSize:'0.68rem', fontWeight:600, cursor:'pointer',
                background: metricCount ? 'rgba(29,184,122,0.08)' : 'var(--bg3)',
                color: metricCount ? 'var(--green)' : 'var(--text2)',
                border: `1px solid ${metricCount ? 'rgba(29,184,122,0.3)' : 'var(--border)'}`,
                borderRadius:'7px', marginBottom:'8px', display:'flex', alignItems:'center', justifyContent:'center', gap:'5px'
              }}>
                <span>📊</span> Report Template {metricCount ? `(${metricCount})` : ''}
              </button>
              {/* Toggle */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                <Toggle checked={!!p.is_active} onChange={() => toggle(p)} />
                <span className="text-xs text-muted">{!p.is_active?'Disabled':'Active'}</span>
              </div>
            </div>
          );
        })}
        {platforms.length===0 && (
          <div className="text-sm text-muted" style={{padding:'20px',gridColumn:'1/-1',textAlign:'center'}}>
            No platforms yet. Click ⚡ Add Standard Platforms to get started quickly.
          </div>
        )}
      </div>

      {/* ── Standard Platforms Picker Modal ── */}
      {stdModal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setStdModal(false)}>
          <div className="modal" style={{maxWidth:'600px'}}>
            <div className="modal-header">
              <div className="modal-title">⚡ Add Standard Platforms</div>
              <div className="icon-btn" onClick={() => setStdModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="text-xs text-muted" style={{marginBottom:'12px'}}>Select the platforms your agency works on. Already-added platforms are shown as active.</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))',gap:'10px',maxHeight:'420px',overflowY:'auto'}}>
                {STD_PLATFORMS.map(p => {
                  const already = platforms.some(x => x.name.toLowerCase()===p.name.toLowerCase());
                  const selected = stdSelected.includes(p.name);
                  const svg = platformSvgFor(p.name);
                  return (
                    <div key={p.name} onClick={() => toggleStd(p.name)} style={{
                      padding:'12px 8px', borderRadius:'10px', textAlign:'center', cursor: already?'default':'pointer',
                      border: selected ? `2px solid ${p.color}` : '2px solid var(--border)',
                      background: already ? 'var(--bg3)' : selected ? `${p.color}12` : 'var(--bg2)',
                      opacity: already ? 0.6 : 1, transition:'all 0.15s'
                    }}>
                      <div style={{width:'48px',height:'48px',margin:'0 auto 6px',borderRadius:'10px',overflow:'hidden',background:`${p.color}15`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                        {svg
                          ? <div dangerouslySetInnerHTML={{__html: svg}} style={{width:'36px',height:'36px'}} />
                          : <span style={{fontSize:'1.8rem'}}>{p.icon}</span>
                        }
                      </div>
                      <div style={{fontSize:'0.72rem',fontWeight:600}}>{p.name}</div>
                      <div style={{fontSize:'0.6rem',color:'var(--text3)',marginTop:'1px'}}>{p.category}</div>
                      {already && <div style={{fontSize:'0.62rem',color:'var(--green)',fontWeight:700,marginTop:'2px'}}>✓ Added</div>}
                      {selected && !already && <div style={{fontSize:'0.62rem',color:p.color,fontWeight:700,marginTop:'2px'}}>✓ Selected</div>}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setStdModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addStdPlatforms} disabled={saving||!stdSelected.length}>
                {saving ? 'Adding...' : `✅ Add Selected (${stdSelected.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Custom Platform Modal ── */}
      {customModal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setCustomModal(false)}>
          <div className="modal" style={{maxWidth:'520px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing ? '✏️ Edit Platform' : '+ Custom Platform'}</div>
              <div className="icon-btn" onClick={() => setCustomModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Platform Name</label>
                  <input className="form-input" placeholder="e.g. Threads, Telegram..." value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                    {CATEGORY_OPTS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Logo / Icon <span className="text-xs text-muted">(pick from reserve icons)</span></label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',maxHeight:'120px',overflowY:'auto'}}>
                  {RESERVE_ICONS.map(ic => (
                    <div key={ic} onClick={() => setForm({...form,icon:ic})}
                      style={{width:'32px',height:'32px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.2rem',cursor:'pointer',borderRadius:'6px',background:form.icon===ic?'var(--accent)22':'transparent',border:form.icon===ic?'2px solid var(--accent)':'2px solid transparent'}}>
                      {ic}
                    </div>
                  ))}
                </div>
                <div style={{marginTop:'6px',display:'flex',alignItems:'center',gap:'8px'}}>
                  <span className="text-xs text-muted">Selected:</span>
                  <span style={{fontSize:'1.5rem'}}>{form.icon}</span>
                  <input className="form-input" style={{width:'80px'}} placeholder="or type emoji" value={form.icon} onChange={e=>setForm({...form,icon:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Primary Audience</label>
                <input className="form-input" placeholder="e.g. B2C, Gen Z, Professionals" value={form.audience} onChange={e=>setForm({...form,audience:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'8px',marginTop:'4px'}}>
                  {PLATFORM_COLORS.map(c => (
                    <div key={c} onClick={() => setForm({...form,color:c})} style={{width:'26px',height:'26px',borderRadius:'50%',background:c,cursor:'pointer',border:form.color===c?'3px solid var(--text)':'2px solid transparent',boxShadow:form.color===c?`0 0 0 1px ${c}`:'none'}} />
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" placeholder="Optional notes about this platform..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} style={{minHeight:'60px'}} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCustomModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 Save Platform'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Report Template Modal ── */}
      {rptModal && rptPlatform && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setRptModal(false)}>
          <div className="modal" style={{maxWidth:'680px'}}>
            <div className="modal-header">
              <div className="modal-title" style={{display:'flex',alignItems:'center',gap:'8px'}}>
                📊 Report Template —
                <div style={{width:'24px',height:'24px',borderRadius:'6px',overflow:'hidden',display:'inline-flex',alignItems:'center',justifyContent:'center',background:`${rptPlatform.color||'#888'}18`}}>
                  {(() => { const svg = platformSvgFor(rptPlatform.name); return svg
                    ? <div dangerouslySetInnerHTML={{__html:svg}} style={{width:'20px',height:'20px'}} />
                    : <span style={{fontSize:'0.9rem'}}>{rptPlatform.icon||'📱'}</span>;
                  })()}
                </div>
                {rptPlatform.name}
              </div>
              <div className="icon-btn" onClick={() => setRptModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="text-xs text-muted mb-10">
                Define which metrics matter for this platform's reports, and optionally upload a labeled reference screenshot to help the team find the right numbers on the platform's own analytics page.
              </div>

              {/* Metrics list */}
              <div className="form-group">
                <label className="form-label">📈 Metric Fields</label>
                <div style={{display:'flex',flexDirection:'column',gap:'6px',marginBottom:'8px'}}>
                  {rptMetrics.length === 0 && (
                    <div className="text-xs text-muted" style={{padding:'8px',fontStyle:'italic'}}>No metrics added yet — add some below or use quick-add tags.</div>
                  )}
                  {rptMetrics.map(m => (
                    <div key={m.id} style={{display:'flex',alignItems:'center',gap:'8px',padding:'6px 10px',background:'var(--bg3)',borderRadius:'6px'}}>
                      <span style={{flex:1,fontSize:'0.82rem',fontWeight:600}}>{m.label}</span>
                      <button onClick={() => rptRemoveMetric(m.id)} style={{padding:'2px 8px',fontSize:'0.7rem',background:'none',border:'1px solid rgba(232,69,69,0.4)',borderRadius:'6px',cursor:'pointer',color:'var(--red)'}}>✕</button>
                    </div>
                  ))}
                </div>
                <div className="flex" style={{gap:'6px'}}>
                  <input className="form-input" style={{flex:1}} placeholder="e.g. Reach, Likes, Comments, Saves..." value={rptNewMetric}
                    onChange={e=>setRptNewMetric(e.target.value)}
                    onKeyDown={e=>{ if(e.key==='Enter'){e.preventDefault();rptAddMetric();} }} />
                  <button className="btn btn-secondary" onClick={rptAddMetric} style={{whiteSpace:'nowrap'}}>+ Add</button>
                </div>
                <div className="text-xs text-muted" style={{marginTop:'6px'}}>
                  Quick add:{' '}
                  {QUICK_METRICS.map(label => (
                    <span key={label} onClick={() => rptQuickAdd(label)} style={{
                      display:'inline-block',padding:'2px 9px',margin:'2px 3px 2px 0',background:'var(--bg3)',
                      border:'1px solid var(--border)',borderRadius:'20px',cursor:'pointer',fontSize:'0.72rem',
                      color: rptMetrics.some(m=>m.label===label) ? 'var(--green)' : 'var(--text2)'
                    }}>{label}</span>
                  ))}
                </div>
              </div>

              {/* Reference screenshot */}
              <div className="form-group" style={{marginTop:'18px'}}>
                <label className="form-label">🖼️ Reference Screenshot <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(optional — helps the team find these metrics on the platform's dashboard)</span></label>
                {rptRefImage && (
                  <div style={{marginBottom:'8px'}}>
                    <img src={rptRefImage} alt="reference" style={{maxWidth:'100%',maxHeight:'260px',borderRadius:'8px',border:'1px solid var(--border)'}} />
                  </div>
                )}
                <div className="flex" style={{gap:'8px',flexWrap:'wrap'}}>
                  <label className="btn btn-secondary" style={{cursor:'pointer'}}>
                    ⬆️ Upload Screenshot
                    <input type="file" accept="image/*" style={{display:'none'}} onChange={rptUploadImage} />
                  </label>
                  <button className="btn btn-secondary" onClick={() => {
                    const q = encodeURIComponent(rptPlatform.name + ' analytics insights dashboard screenshot');
                    window.open('https://www.google.com/search?tbm=isch&q='+q, '_blank', 'noopener');
                  }}>🔍 Search the Web for an Example</button>
                  {rptRefImage && <button className="btn btn-danger" onClick={() => setRptRefImage('')}>🗑 Remove</button>}
                </div>
                <div className="text-xs text-muted" style={{marginTop:'6px'}}>
                  Opens a Google Images search for "{rptPlatform.name} analytics dashboard screenshot" in a new tab.
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">📝 Notes for the Team</label>
                <textarea className="form-textarea" style={{height:'60px'}}
                  placeholder="e.g. Find Reach under Insights → Overview. Ignore 'Accounts Engaged' — that's not needed."
                  value={rptNotes} onChange={e=>setRptNotes(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setRptModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={rptSave} disabled={rptSaving}>{rptSaving?'Saving...':'💾 Save Template'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
function PlaceholderTab({ icon, title, subtitle, apiPath, columns, renderRow, modalFields, modalTitle }) {
  const [items, setItems]   = useState([]);
  const [modal, setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]     = useState({});
  const [saving, setSaving] = useState(false);

  const initF = (item) => {
    const f = {};
    modalFields.forEach(mf => { f[mf.key] = item ? (item[mf.key]||'') : (mf.default||''); });
    return f;
  };

  const load = useCallback(async () => {
    if(!apiPath) return;
    try { const r = await api.get(apiPath); setItems(r.data?.data || r.data || []); } catch(e) { setItems([]); }
  }, [apiPath]);
  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditing(null); setForm(initF(null)); setModal(true); };
  const openEdit = (item) => { setEditing(item); setForm(initF(item)); setModal(true); };
  const save = async () => {
    setSaving(true);
    try {
      if(editing) await api.put(apiPath+'/'+editing.id, form);
      else         await api.post(apiPath, form);
      setModal(false); load();
    } catch(e) { } finally { setSaving(false); }
  };
  const del = async (item) => {
    if(!window.confirm('Delete this record?')) return;
    try { await api.delete(apiPath+'/'+item.id); load(); } catch(e) {}
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <div><div className="card-title">{icon} {title}</div>{subtitle && <div className="text-xs text-muted" style={{marginTop:'4px'}}>{subtitle}</div>}</div>
        {apiPath && <button className="btn btn-primary" onClick={openAdd}>+ Add {title.split(' ')[0]}</button>}
      </div>
      {apiPath && (
        <div className="card">
          <table className="data-table">
            <thead><tr>{columns.map((c,i)=><th key={i}>{c}</th>)}<th>Actions</th></tr></thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  {renderRow(item)}
                  <td>
                    <div className="flex" style={{gap:'4px'}}>
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => openEdit(item)}>✏️ Edit</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => del(item)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length===0 && <tr><td colSpan={columns.length+1} style={{padding:'20px',textAlign:'center'}} className="text-muted text-sm">No records yet. Click "+ Add" to get started.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {apiPath && (
        <Modal open={modal} onClose={() => setModal(false)} title={editing ? `✏️ Edit ${title}` : modalTitle||`+ Add ${title}`}
          footer={<><button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 Save'}</button></>}>
          {modalFields.map(mf => (
            <div key={mf.key} className="form-group">
              <label className="form-label">{mf.label}</label>
              {mf.type==='textarea'
                ? <textarea className="form-textarea" placeholder={mf.placeholder||''} value={form[mf.key]||''} onChange={e=>setForm({...form,[mf.key]:e.target.value})} />
                : mf.type==='select'
                  ? <select className="form-select" value={form[mf.key]||''} onChange={e=>setForm({...form,[mf.key]:e.target.value})}>{(mf.options||[]).map(o=><option key={o} value={o}>{o}</option>)}</select>
                  : <input className="form-input" type={mf.type||'text'} placeholder={mf.placeholder||''} value={form[mf.key]||''} onChange={e=>setForm({...form,[mf.key]:e.target.value})} />
              }
            </div>
          ))}
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MAIN MASTERDATA COMPONENT
// ══════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════
// PHASES TAB COMPONENT
// ══════════════════════════════════════════════════════════
function PhasesTab() {
  const [campaignTypes, setCampaignTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activeCTId, setActiveCTId] = useState(null);
  const [modal, setModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copyModal, setCopyModal] = useState(false);

  const DEFAULT_COLORS = ['#3b7ef0','#6c58f0','#1db87a','#e8a020','#e84545','#e87830','#9050e8','#18b8b8','#e83898','#f7c34f'];

  // Dynamic — driven by appPages.js. Add new pages/tabs there; they appear here automatically.
  const LINKED_PAGES = getLinkedPageOptions();

  const emptyForm = {
    name:'', desc:'', color:'#3b7ef0', executionMode:'sequential', parallelGroup:'',
    owners:[], internalApproval:false, clientApproval:false, doneWhenApproved:false,
    requiresMetrics:false, showInAssignments:true, autoAdvance:true, canSkip:false,
    predecessorIds:[], dayWeight:10, bufferDays:1, fixedDeadlineDay:'', linkedPageId:''
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [ct, r] = await Promise.all([api.get('/campaign-types'), api.get('/roles')]);
    const cts = ct.data?.data || ct.data || [];
    setCampaignTypes(cts);
    setRoles(r.data?.data || r.data || []);
    if (cts.length && !activeCTId) setActiveCTId(cts[0].id);
  }, [activeCTId]);
  useEffect(() => { load(); }, [load]);

  const activeCT = campaignTypes.find(c => c.id === activeCTId);
  const getPhases = (ct) => {
    if (!ct) return [];
    try {
      const p = ct.phases;
      if (Array.isArray(p)) return [...p].sort((a,b) => a.order - b.order);
      if (typeof p === 'string') return [...JSON.parse(p)].sort((a,b) => a.order - b.order);
    } catch(e) {}
    return [];
  };
  const phases = getPhases(activeCT);

  const savePhases = async (ct, newPhases) => {
    await api.put('/campaign-types/'+ct.id, { ...ct, phases: newPhases });
    load();
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModal(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name||'', desc: p.desc||'', color: p.color||'#3b7ef0',
      executionMode: p.executionMode||'sequential', parallelGroup: p.parallelGroup||'',
      owners: Array.isArray(p.owners) ? p.owners : (p.owner ? [p.owner] : []),
      internalApproval: !!p.internalApproval, clientApproval: !!p.clientApproval,
      doneWhenApproved: !!p.doneWhenApproved, requiresMetrics: !!p.requiresMetrics,
      showInAssignments: p.showInAssignments !== false, autoAdvance: p.autoAdvance !== false,
      canSkip: !!p.canSkip, predecessorIds: p.predecessorIds||[],
      dayWeight: p.dayWeight||10, bufferDays: p.bufferDays??1,
      fixedDeadlineDay: p.fixedDeadlineDay||'', linkedPageId: p.linkedPageId||''
    });
    setModal(true);
  };

  const savePhase = async () => {
    if (!form.name.trim() || !activeCT) return;
    setSaving(true);
    try {
      let newPhases = [...phases];
      if (editingId !== null) {
        newPhases = newPhases.map(p => p.id === editingId ? {
          ...p, ...form, owner: form.owners.join(', '),
          fixedDeadlineDay: form.fixedDeadlineDay ? parseInt(form.fixedDeadlineDay) : null,
        } : p);
      } else {
        const maxId = phases.length ? Math.max(...phases.map(p=>p.id)) : 0;
        const maxOrder = phases.length ? Math.max(...phases.map(p=>p.order)) : 0;
        newPhases.push({
          id: maxId+1, order: maxOrder+1,
          ...form, owner: form.owners.join(', '), enabled: true,
          fixedDeadlineDay: form.fixedDeadlineDay ? parseInt(form.fixedDeadlineDay) : null,
        });
      }
      await savePhases(activeCT, newPhases);
      setModal(false);
    } finally { setSaving(false); }
  };

  const deletePhase = async (id) => {
    if (!window.confirm('Delete this phase?')) return;
    const newPhases = phases.filter(p => p.id !== id).map((p,i) => ({...p, order:i+1}));
    await savePhases(activeCT, newPhases);
  };

  const toggleEnabled = async (id) => {
    const newPhases = phases.map(p => p.id===id ? {...p, enabled:!p.enabled} : p);
    await savePhases(activeCT, newPhases);
  };

  const movePhase = async (id, dir) => {
    const idx = phases.findIndex(p=>p.id===id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= phases.length) return;
    const newPhases = [...phases];
    [newPhases[idx], newPhases[newIdx]] = [newPhases[newIdx], newPhases[idx]];
    newPhases.forEach((p,i) => p.order = i+1);
    await savePhases(activeCT, newPhases);
  };

  const toggleOwner = (name) => {
    setForm(f => ({...f, owners: f.owners.includes(name) ? f.owners.filter(o=>o!==name) : [...f.owners, name]}));
  };

  const togglePredecessor = (id) => {
    setForm(f => ({...f, predecessorIds: f.predecessorIds.includes(id) ? f.predecessorIds.filter(x=>x!==id) : [...f.predecessorIds, id]}));
  };

  const copyWorkflow = async (sourceCtId) => {
    const source = campaignTypes.find(c=>c.id===sourceCtId);
    if (!source || !activeCT) return;
    const copied = getPhases(source).map((p,i) => ({...p, id:i+1}));
    await savePhases(activeCT, copied);
    setCopyModal(false);
  };

  const execModes = [
    { value:'sequential', icon:'🔢', label:'Sequential', desc:'Runs after previous phase completes' },
    { value:'parallel',   icon:'⚡', label:'Parallel',   desc:'Runs simultaneously with other phases' },
    { value:'milestone',  icon:'◆',  label:'Milestone',  desc:'A checkpoint or sign-off gate' },
  ];

  const execBadge = { sequential:'badge-gray', parallel:'badge-blue', milestone:'badge-purple' };
  const execIcon  = { sequential:'🔢', parallel:'⚡', milestone:'◆' };

  return (
    <div>
      {/* Header */}
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🔄 Workflow Phases</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Each campaign type has its own workflow phases. Use <b>Manage Workflow</b> to edit phases, or <b>Copy Workflow</b> to clone from another campaign type.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={() => setCopyModal(true)} disabled={!activeCT}>📋 Copy Workflow</button>
          <button className="btn btn-primary"   onClick={openAdd} disabled={!activeCT}>+ Add Phase</button>
        </div>
      </div>

      {/* Campaign Type pills */}
      <div style={{display:'flex',flexWrap:'wrap',gap:'8px',marginBottom:'20px'}}>
        {campaignTypes.map(ct => {
          const ctPhases = getPhases(ct);
          const count = ctPhases.filter(p=>p.enabled!==false).length;
          const isActive = ct.id === activeCTId;
          return (
            <button key={ct.id} onClick={() => setActiveCTId(ct.id)} style={{
              padding:'7px 14px', borderRadius:'20px', fontSize:'0.78rem', fontWeight:600, cursor:'pointer',
              border: `2px solid ${isActive ? ct.color||'var(--accent)' : 'var(--border)'}`,
              background: isActive ? ct.color||'var(--accent)' : 'var(--bg2)',
              color: isActive ? '#fff' : 'var(--text2)',
              boxShadow: isActive ? `0 2px 8px ${ct.color||'var(--accent)'}44` : 'none',
              display:'flex', alignItems:'center', gap:'6px'
            }}>
              <span style={{width:'8px',height:'8px',borderRadius:'50%',background:isActive?'rgba(255,255,255,0.6)':ct.color,display:'inline-block'}}></span>
              {ct.name}
              <span style={{opacity:0.7,fontWeight:400,fontSize:'0.7rem'}}>{count} phases</span>
            </button>
          );
        })}
        {campaignTypes.length === 0 && <div className="text-xs text-muted">No campaign types yet. Add them in the Campaign Types tab first.</div>}
      </div>

      {activeCT && (
        <>
          {/* Flow visual */}
          <div className="card mb-16" style={{padding:'14px 18px'}}>
            <div className="text-xs text-muted fw-700 mb-8" style={{letterSpacing:'0.5px',textTransform:'uppercase'}}>Workflow Flow — {activeCT.name}</div>
            <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:'6px'}}>
              {phases.filter(p=>p.enabled!==false).map((p,i,arr) => (
                <React.Fragment key={p.id}>
                  <div style={{
                    background:`${p.color}18`, border:`2px solid ${p.color}40`,
                    borderRadius: p.executionMode==='milestone' ? '6px' : '20px',
                    padding:'5px 14px', fontSize:'0.75rem', fontWeight:700, color:p.color,
                    display:'inline-flex', alignItems:'center', gap:'5px', whiteSpace:'nowrap'
                  }}>
                    {p.executionMode==='milestone'?'◆ ':''}{p.order}. {p.name}
                    {p.internalApproval && <span style={{fontSize:'0.65rem'}}>🔒</span>}
                    {p.clientApproval && <span style={{fontSize:'0.65rem'}}>👔</span>}
                    {p.autoAdvance && <span title="Auto-advance" style={{fontSize:'0.65rem'}}>🚀</span>}
                  </div>
                  {i < arr.length-1 && <span style={{color:'var(--text3)',fontSize:'0.9rem'}}>→</span>}
                </React.Fragment>
              ))}
              {phases.filter(p=>p.enabled!==false).length === 0 && (
                <div className="text-xs text-muted">No active phases yet. Click + Add Phase.</div>
              )}
            </div>
          </div>

          {/* Phases table */}
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{width:'60px'}}>Order</th>
                  <th>Phase Name</th>
                  <th>Mode</th>
                  <th>Owners</th>
                  <th>Approvals & Flags</th>
                  <th>Timing</th>
                  <th>Auto</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {phases.map(p => {
                  const disabled = p.enabled === false;
                  return (
                    <tr key={p.id} style={{opacity:disabled?0.45:1}}>
                      <td>
                        <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'2px'}}>
                          <button className="icon-btn" style={{width:'24px',height:'20px',fontSize:'0.65rem'}} onClick={() => movePhase(p.id,-1)} disabled={p.order===1}>▲</button>
                          <span className="mono fw-700" style={{fontSize:'0.78rem'}}>{p.order}</span>
                          <button className="icon-btn" style={{width:'24px',height:'20px',fontSize:'0.65rem'}} onClick={() => movePhase(p.id,1)} disabled={p.order===phases.length}>▼</button>
                        </div>
                      </td>
                      <td>
                        <div className="flex" style={{gap:'8px',alignItems:'flex-start'}}>
                          <div style={{width:'10px',height:'10px',borderRadius:p.executionMode==='milestone'?'2px':'50%',background:p.color,flexShrink:0,marginTop:'4px'}}></div>
                          <div>
                            <div className="fw-700">{p.name} {disabled && <span className="badge badge-gray" style={{fontSize:'0.6rem'}}>Off</span>}</div>
                            {p.desc && <div className="text-xs text-muted" style={{marginTop:'2px',maxWidth:'180px'}}>{p.desc}</div>}
                            {p.predecessorIds?.length > 0 && (
                              <div style={{marginTop:'4px',display:'flex',flexWrap:'wrap',gap:'3px',alignItems:'center'}}>
                                <span className="text-xs text-muted">After:</span>
                                {p.predecessorIds.map(pid => {
                                  const pred = phases.find(x=>x.id===pid);
                                  return pred ? <span key={pid} className="badge badge-gray" style={{fontSize:'0.6rem'}}>{pred.order}. {pred.name}</span> : null;
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td><span className={`badge ${execBadge[p.executionMode]||'badge-gray'}`}>{execIcon[p.executionMode]||''} {p.executionMode||'Sequential'}</span>
                        {p.parallelGroup && <div className="text-xs text-muted" style={{marginTop:'2px'}}>{p.parallelGroup}</div>}
                      </td>
                      <td>
                        <div style={{display:'flex',flexWrap:'wrap',gap:'3px'}}>
                          {(Array.isArray(p.owners)&&p.owners.length ? p.owners : (p.owner ? p.owner.split(',').map(x=>x.trim()).filter(Boolean) : [])).map(o => {
                            const r = roles.find(x=>x.name===o);
                            return <span key={o} className="badge badge-gray" style={{fontSize:'0.65rem',borderLeft:`3px solid ${r?.color||'#888'}`}}>{o}</span>;
                          })}
                          {!(Array.isArray(p.owners)&&p.owners.length) && !p.owner && <span className="text-xs text-muted">—</span>}
                        </div>
                      </td>
                      <td>
                        <div style={{display:'flex',flexWrap:'wrap',gap:'3px'}}>
                          {p.internalApproval && <span className="badge badge-yellow" style={{fontSize:'0.65rem'}}>🔒 Internal</span>}
                          {p.clientApproval   && <span className="badge badge-blue"   style={{fontSize:'0.65rem'}}>👔 Client</span>}
                          {p.doneWhenApproved && <span className="badge badge-green"  style={{fontSize:'0.65rem'}}>✅ Auto-Done</span>}
                          {p.requiresMetrics  && <span className="badge badge-orange" style={{fontSize:'0.65rem'}}>📊 Needs Metrics</span>}
                          {p.showInAssignments!==false ? <span className="badge badge-blue" style={{fontSize:'0.65rem',background:'rgba(59,126,240,0.08)'}}>📋 Assignments</span> : <span className="badge badge-gray" style={{fontSize:'0.65rem'}}>📋 Hidden</span>}
                          {p.linkedPageId && <span className="badge" style={{fontSize:'0.65rem',background:'rgba(108,88,240,0.1)',color:'#6c58f0',border:'1px solid rgba(108,88,240,0.3)'}}>🔗 {p.linkedPageId}</span>}
                        </div>
                      </td>
                      <td>
                        <div style={{display:'flex',flexDirection:'column',gap:'2px'}}>
                          <span className="text-xs fw-700" style={{color:'var(--accent)'}}>⏱️ {p.dayWeight||10}%</span>
                          <span className="text-xs text-muted">+{p.bufferDays??0}d buffer</span>
                          {p.fixedDeadlineDay && <span className="text-xs fw-700" style={{color:'var(--red)'}}>📅 {p.fixedDeadlineDay}th</span>}
                        </div>
                      </td>
                      <td>
                        <div style={{display:'flex',flexWrap:'wrap',gap:'3px'}}>
                          {p.autoAdvance && <span className="badge badge-green"  style={{fontSize:'0.65rem'}}>🚀 Auto</span>}
                          {p.canSkip     && <span className="badge badge-yellow" style={{fontSize:'0.65rem'}}>⏭️ Skip</span>}
                          {!p.autoAdvance && !p.canSkip && <span className="text-xs text-muted">—</span>}
                        </div>
                      </td>
                      <td>
                        <div className="flex" style={{gap:'4px',alignItems:'center'}}>
                          <Toggle checked={p.enabled!==false} onChange={() => toggleEnabled(p.id)} />
                          <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => openEdit(p)}>✏️</button>
                          <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => deletePhase(p.id)}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {phases.length === 0 && (
                  <tr><td colSpan={8} style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">
                    No phases yet for <b>{activeCT.name}</b>. Click + Add Phase to define the workflow.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Add/Edit Phase Modal ── */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'680px'}}>
            <div className="modal-header">
              <div className="modal-title">{editingId ? '✏️ Edit Phase' : '+ Add Workflow Phase'}</div>
              <div className="icon-btn" onClick={() => setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phase Name</label>
                  <input className="form-input" placeholder="e.g. Content Creation" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Color</label>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginTop:'4px'}}>
                    {DEFAULT_COLORS.map(c => (
                      <div key={c} onClick={() => setForm({...form,color:c})} style={{
                        width:'24px',height:'24px',borderRadius:'50%',background:c,cursor:'pointer',
                        border:form.color===c?'3px solid var(--text)':'2px solid transparent',
                        boxShadow:form.color===c?`0 0 0 1px ${c}`:'none'
                      }} />
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" style={{minHeight:'56px'}} placeholder="Brief description of what happens in this phase..." value={form.desc} onChange={e=>setForm({...form,desc:e.target.value})} />
              </div>

              {/* Execution Mode */}
              <div className="form-group">
                <label className="form-label">Execution Mode</label>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'8px',marginTop:'4px'}}>
                  {execModes.map(m => (
                    <div key={m.value} onClick={() => setForm({...form,executionMode:m.value})} style={{
                      padding:'10px 12px', borderRadius:'8px', cursor:'pointer', textAlign:'center',
                      border:`2px solid ${form.executionMode===m.value?'var(--accent)':'var(--border)'}`,
                      background: form.executionMode===m.value ? 'rgba(59,126,240,0.06)' : 'var(--bg3)',
                      transition:'all 0.15s'
                    }}>
                      <div style={{fontSize:'1.2rem',marginBottom:'4px'}}>{m.icon}</div>
                      <div style={{fontSize:'0.75rem',fontWeight:700,color:form.executionMode===m.value?'var(--accent)':'var(--text)'}}>{m.label}</div>
                      <div style={{fontSize:'0.65rem',color:'var(--text3)',marginTop:'2px'}}>{m.desc}</div>
                    </div>
                  ))}
                </div>
                {form.executionMode==='parallel' && (
                  <div style={{marginTop:'8px'}}>
                    <label className="form-label">Parallel Group Name</label>
                    <input className="form-input" placeholder="e.g. Creative Production" value={form.parallelGroup} onChange={e=>setForm({...form,parallelGroup:e.target.value})} />
                  </div>
                )}
              </div>

              {/* Owners */}
              <div className="form-group">
                <label className="form-label">Phase Owner(s) — Responsible Roles</label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',maxHeight:'120px',overflowY:'auto'}}>
                  {roles.filter(r=>r.enabled!==false).map(r => (
                    <label key={r.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.78rem',cursor:'pointer',padding:'4px 8px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'6px',whiteSpace:'nowrap'}}>
                      <input type="checkbox" checked={form.owners.includes(r.name)} onChange={() => toggleOwner(r.name)} style={{accentColor:r.color||'var(--accent)'}} />
                      <span style={{width:'8px',height:'8px',borderRadius:'50%',background:r.color||'#888',display:'inline-block'}}></span>
                      {r.name}
                    </label>
                  ))}
                  {roles.length===0 && <span className="text-xs text-muted">No roles defined yet.</span>}
                </div>
              </div>

              {/* Approvals */}
              <div className="form-group">
                <label className="form-label">Approvals & Behaviour</label>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                  {[
                    {key:'internalApproval', icon:'🔒', label:'Internal Approval Required', desc:'Team lead must approve before advancing'},
                    {key:'clientApproval',   icon:'👔', label:'Client Approval Required',   desc:'Client must sign off before advancing'},
                    {key:'doneWhenApproved', icon:'✅', label:'Auto-Done When Approved',     desc:'Phase auto-completes on approval'},
                    {key:'requiresMetrics',  icon:'📊', label:'Requires Metrics Entry',      desc:'Team must enter monitoring metrics'},
                    {key:'showInAssignments',icon:'📋', label:'Show in Work Assignments',    desc:'Tasks visible in assignments view'},
                    {key:'autoAdvance',      icon:'🚀', label:'Auto-Advance to Next',        desc:'Automatically moves to next phase'},
                    {key:'canSkip',          icon:'⏭️', label:'Skippable Phase',             desc:'Can be skipped if not needed'},
                  ].map(f => (
                    <label key={f.key} style={{display:'flex',alignItems:'flex-start',gap:'8px',padding:'8px 10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'8px',cursor:'pointer'}}>
                      <input type="checkbox" checked={!!form[f.key]} onChange={e=>setForm({...form,[f.key]:e.target.checked})} style={{accentColor:'var(--accent)',marginTop:'2px',flexShrink:0}} />
                      <div>
                        <div style={{fontSize:'0.78rem',fontWeight:600}}>{f.icon} {f.label}</div>
                        <div style={{fontSize:'0.65rem',color:'var(--text3)',marginTop:'1px'}}>{f.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Predecessors */}
              {phases.filter(p => p.id !== editingId).length > 0 && (
                <div className="form-group">
                  <label className="form-label">Predecessor Phases <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(this phase starts after these complete)</span></label>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'8px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
                    {phases.filter(p => p.id!==editingId && p.enabled!==false).sort((a,b)=>a.order-b.order).map(p => (
                      <label key={p.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.78rem',cursor:'pointer',padding:'4px 8px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'6px',whiteSpace:'nowrap'}}>
                        <input type="checkbox" checked={form.predecessorIds.includes(p.id)} onChange={() => togglePredecessor(p.id)} style={{accentColor:p.color}} />
                        <span style={{width:'8px',height:'8px',borderRadius:'50%',background:p.color,display:'inline-block'}}></span>
                        {p.order}. {p.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Timing */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Day Weight (%)</label>
                  <input className="form-input" type="number" min="1" max="100" placeholder="10" value={form.dayWeight} onChange={e=>setForm({...form,dayWeight:parseInt(e.target.value)||10})} />
                  <div className="text-xs text-muted" style={{marginTop:'4px'}}>Proportion of total campaign days for this phase</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Buffer Days</label>
                  <input className="form-input" type="number" min="0" placeholder="1" value={form.bufferDays} onChange={e=>setForm({...form,bufferDays:parseInt(e.target.value)||0})} />
                  <div className="text-xs text-muted" style={{marginTop:'4px'}}>Extra days added as buffer after this phase</div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Fixed Monthly Deadline Day <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(optional, 1–31)</span></label>
                  <input className="form-input" type="number" min="1" max="31" placeholder="e.g. 25 = always due on 25th" value={form.fixedDeadlineDay} onChange={e=>setForm({...form,fixedDeadlineDay:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Linked Work Page <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(optional)</span></label>
                  <select className="form-select" value={form.linkedPageId} onChange={e=>setForm({...form,linkedPageId:e.target.value})}>
                    <option value="">— Not linked —</option>
                    {LINKED_PAGES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePhase} disabled={saving}>{saving?'Saving...':'💾 Save Phase'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Copy Workflow Modal ── */}
      {copyModal && activeCT && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setCopyModal(false)}>
          <div className="modal" style={{maxWidth:'540px'}}>
            <div className="modal-header">
              <div className="modal-title">📋 Copy Workflow Into "{activeCT.name}"</div>
              <div className="icon-btn" onClick={() => setCopyModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="text-xs text-muted mb-16">Select a campaign type to copy its phase workflow. This will <b>replace</b> all current phases in "{activeCT.name}".</div>
              <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                {campaignTypes.filter(ct=>ct.id!==activeCTId).map(ct => {
                  const ctPhases = getPhases(ct).filter(p=>p.enabled!==false);
                  return (
                    <div key={ct.id} onClick={() => { if(window.confirm(`Copy workflow from "${ct.name}" into "${activeCT.name}"? This will replace all current phases.`)) copyWorkflow(ct.id); }} style={{
                      padding:'12px 14px', borderRadius:'10px', cursor:'pointer',
                      border:`2px solid var(--border)`, background:'var(--bg2)',
                      transition:'all 0.15s'
                    }} onMouseOver={e=>e.currentTarget.style.borderColor=ct.color} onMouseOut={e=>e.currentTarget.style.borderColor='var(--border)'}>
                      <div className="flex-between mb-8">
                        <div className="fw-700" style={{color:ct.color}}>{ct.name}</div>
                        <span className="badge badge-gray" style={{fontSize:'0.65rem'}}>{ctPhases.length} phases</span>
                      </div>
                      <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:'4px'}}>
                        {ctPhases.slice(0,5).map((p,i,arr) => (
                          <React.Fragment key={p.id}>
                            <span style={{padding:'2px 8px',borderRadius:'10px',background:`${p.color}18`,color:p.color,fontSize:'0.65rem',fontWeight:600}}>{p.name}</span>
                            {i < Math.min(arr.length,5)-1 && <span style={{color:'var(--text3)',fontSize:'0.7rem'}}>→</span>}
                          </React.Fragment>
                        ))}
                        {ctPhases.length > 5 && <span className="text-xs text-muted">+{ctPhases.length-5} more</span>}
                      </div>
                    </div>
                  );
                })}
                {campaignTypes.filter(ct=>ct.id!==activeCTId).length === 0 && (
                  <div className="text-sm text-muted" style={{textAlign:'center',padding:'20px'}}>No other campaign types available to copy from.</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCopyModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CampaignTypesTab({ onManagePhases }) {
  const CT_COLORS = ['#e87830','#3b7ef0','#1db87a','#6c58f0','#e84545','#9050e8','#18b8b8','#e83898'];

  const DEFAULT_CAMPAIGN_TYPES_SEED = [
    {
      name: 'Monthly Social Media Retainer',
      description: 'Regular monthly content production for social media. Covers planning, creation, approvals, scheduling and reporting.',
      color: '#3b7ef0', buffer: 3, total_days: 25,
      phases: [
        { id:1,  order:1,  name:'Strategy & Calendar Planning', color:'#3b7ef0', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:true,  enabled:true, canSkip:false, autoAdvance:true,  showInAssignments:true,  dayWeight:10, bufferDays:1, owners:[], predecessorIds:[], desc:'Define monthly themes, content pillars and post schedule. Share content calendar for client approval. Lock topics before production begins.' },
        { id:2,  order:2,  name:'Script & Copy Writing',        color:'#6c58f0', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:15, bufferDays:0, owners:[], predecessorIds:[1], desc:'Write captions, scripts for reels, blog outlines and ad copy. Include hooks, CTAs and hashtag lists. Pass to design after copy lock.' },
        { id:3,  order:3,  name:'Creative Production',          color:'#9050e8', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:25, bufferDays:1, owners:[], predecessorIds:[2], desc:'Design static posts, carousels, reel covers and story templates. Edit reels and short videos. Export all assets in platform-specific formats.' },
        { id:4,  order:4,  name:'Internal QA Review',           color:'#f7c34f', executionMode:'sequential', internalApproval:true,  clientApproval:false, autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:0, owners:[], predecessorIds:[3], desc:'Senior team reviews all content for brand consistency, grammar, quality and platform specs. Flag and fix issues before client preview.' },
        { id:5,  order:5,  name:'Client Preview & Approval',    color:'#e87830', executionMode:'sequential', internalApproval:false, clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:15, bufferDays:1, owners:[], predecessorIds:[4], desc:'Share content batch with client via portal. Client reviews, comments and approves. Max 2 revision rounds. Get written sign-off before scheduling.' },
        { id:6,  order:6,  name:'Revisions',                    color:'#e84545', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:true,  showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:[], predecessorIds:[5], desc:'Incorporate client feedback. Minor edits only — copy tweaks, colour changes, resize. Major scope changes require new WO.' },
        { id:7,  order:7,  name:'Scheduling & Publishing',      color:'#1db87a', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:0, owners:[], predecessorIds:[5,6], desc:'Schedule approved content using Buffer/Hootsuite/Meta Business. Set UTM parameters for all links. Confirm publish dates match content calendar.' },
        { id:8,  order:8,  name:'Community Management',         color:'#18b8b8', executionMode:'parallel',   internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:true,  showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:[], predecessorIds:[7], desc:'Monitor comments, DMs and mentions daily. Reply within 24h. Escalate negative feedback to account manager. Log interactions.' },
        { id:9,  order:9,  name:'Performance Monitoring',       color:'#e87830', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  requiresMetrics:true, dayWeight:5, bufferDays:1, owners:[], predecessorIds:[7], desc:'Pull analytics from native platform insights. Track reach, engagement, follower growth, website clicks. Note top and bottom performers.' },
        { id:10, order:10, name:'Monthly Report Delivery',      color:'#e83898', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:1, owners:[], predecessorIds:[9], desc:'Prepare branded performance report with KPIs, insights and next month recommendations. Present to client in review call. Get sign-off.' },
      ]
    },
    {
      name: 'Festival / Special Day Campaign',
      description: 'Time-sensitive campaign for festivals, national days or brand occasions. Fast-track production with hard publish deadline.',
      color: '#e87830', buffer: 2, total_days: 30,
      phases: [
        { id:1,  order:1,  name:'Special days Planning',                  color:'#f7c34f', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:2,  bufferDays:0, owners:[], predecessorIds:[],     fixedDeadlineDay:15, linkedPageId:'planning.step2', desc:'Plan and identify upcoming special days and festivals for the month. Prepare a comprehensive list for client review.' },
        { id:2,  order:2,  name:'Special days Client Approval',           color:'#e8a020', executionMode:'milestone',  internalApproval:false, clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:2,  bufferDays:0, owners:[], predecessorIds:[1],   fixedDeadlineDay:16, desc:'Client reviews and approves the list of special days and festivals selected for campaign production.' },
        { id:3,  order:3,  name:'Monthly Calandar Days Selection',        color:'#3b7ef0', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:2,  bufferDays:0, owners:[], predecessorIds:[8],   fixedDeadlineDay:17, desc:'Finalise and lock the monthly content calendar with selected special days and campaign dates.' },
        { id:4,  order:4,  name:'Script & Brief',                         color:'#e8a020', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:20, bufferDays:0, owners:[], predecessorIds:[9],   fixedDeadlineDay:20, doneWhenApproved:true, desc:'Write scripts and creative briefs for each festival/special day campaign. Include messaging, tone, key visuals and platform specs.' },
        { id:5,  order:5,  name:'Monthly Calandar-Internal Approval',     color:'#e84545', executionMode:'sequential', internalApproval:true,  clientApproval:false, autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:0, owners:[], predecessorIds:[10],  doneWhenApproved:true, desc:'Internal team reviews and approves the monthly content calendar before sharing with the client.' },
        { id:6,  order:6,  name:'Monthly Calandar-Client Approval',       color:'#3b7ef0', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:[], predecessorIds:[11],  fixedDeadlineDay:21, doneWhenApproved:true, desc:'Client reviews and gives final approval on the monthly content calendar for all special day campaigns.' },
        { id:7,  order:7,  name:'Creatives Making',                       color:'#1db87a', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:30, bufferDays:0, owners:[], predecessorIds:[12],  doneWhenApproved:true, desc:'Design and produce all creative assets for each special day — static posts, stories, reels, banners and video content.' },
        { id:8,  order:8,  name:'Creatives Internal Review',              color:'#3b7ef0', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:['Coordinator','Graphics Approver- Internal'], predecessorIds:[13], doneWhenApproved:true, desc:'Internal review of all creatives by coordinator and graphics approver. Check brand consistency, quality and platform specs.' },
        { id:9,  order:9,  name:'Creatives-Client Approval',              color:'#3b7ef0', executionMode:'sequential', internalApproval:false, clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:[], predecessorIds:[15],  doneWhenApproved:true, desc:'Send completed creatives to client for review and approval. Incorporate feedback within one revision round.' },
        { id:10, order:10, name:'Post Copy Preperation',                  color:'#f7c34f', executionMode:'parallel',   internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:0, owners:[], predecessorIds:[12],  doneWhenApproved:true, desc:'Write post captions, hashtags and copy for each special day creative. Align tone with brief and brand voice guidelines.' },
        { id:11, order:11, name:'Post copy- Internal Review',             color:'#e84545', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:[], predecessorIds:[14],  desc:'Internal review of all post copy for grammar, tone, accuracy and brand compliance before sending to client.' },
        { id:12, order:12, name:'Post copy- Client Approval',             color:'#3b7ef0', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:true,  showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:[], predecessorIds:[17],  desc:'Share post copy with client for review and approval. Client may request minor edits. This step can be skipped if client approves in creative review.' },
        { id:13, order:13, name:'Scheduling/Publishing',                  color:'#1db87a', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:[], predecessorIds:[18,16], desc:'Schedule and publish all approved festival/special day content across platforms on the exact dates and times specified in the content calendar.' },
      ]
    },
    {
      name: 'Paid Campaign Launch',
      description: 'End-to-end paid advertising campaign on Meta, Google or both. Includes strategy, creative, setup, launch and optimisation.',
      color: '#e84545', buffer: 3, total_days: 18,
      phases: [
        { id:1, order:1, name:'Campaign Strategy & Brief',    color:'#3b7ef0', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:1, owners:[], predecessorIds:[], desc:'Define objective (awareness/traffic/leads/sales), budget, platforms, audience targeting, bid strategy and KPIs. Share media plan for client approval.' },
        { id:2, order:2, name:'Audience Research & Setup',   color:'#6c58f0', executionMode:'parallel',   internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:0, owners:[], predecessorIds:[1], desc:'Build custom and lookalike audiences. Set up pixel/conversion tracking. Create UTM parameters. Verify ad account access and payment method.' },
        { id:3, order:3, name:'Ad Copy & Creative Brief',    color:'#9050e8', executionMode:'parallel',   internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:0, owners:[], predecessorIds:[1], desc:'Write headline variations (3–5), primary text variants and CTA options. Brief design on ad creative formats needed per placement.' },
        { id:4, order:4, name:'Ad Creative Production',      color:'#e87830', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:20, bufferDays:0, owners:[], predecessorIds:[3], desc:'Produce all ad creatives — static images, carousels, video ads, story ads. Ensure each format meets platform specs. A/B test variants.' },
        { id:5, order:5, name:'Internal Review',             color:'#f7c34f', executionMode:'sequential', internalApproval:true,  clientApproval:false, autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:[], predecessorIds:[4], desc:'Review ad copy for compliance, brand voice and quality. Check all links, landing pages and tracking. Verify pixel fires correctly.' },
        { id:6, order:6, name:'Client Approval',             color:'#e84545', executionMode:'sequential', internalApproval:false, clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:1, owners:[], predecessorIds:[5], desc:'Present ad creatives and media plan to client. Get written approval on copy, creative and budget allocation before campaign goes live.' },
        { id:7, order:7, name:'Campaign Setup & Launch',     color:'#1db87a', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:0, owners:[], predecessorIds:[6], desc:'Build campaign structure in ad manager — campaigns, ad sets, ads. Upload creatives, set budgets, schedule. Submit for review. Confirm live status.' },
        { id:8, order:8, name:'Active Optimisation',         color:'#18b8b8', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  requiresMetrics:true, dayWeight:15, bufferDays:1, owners:[], predecessorIds:[7], desc:'Monitor daily — CTR, CPC, CPM, ROAS, conversion rate. Pause underperforming ads, scale winners. Adjust bids and audience as needed. Weekly report.' },
        { id:9, order:9, name:'Campaign Report & Review',    color:'#e83898', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:1, owners:[], predecessorIds:[8], desc:'Compile end-of-campaign report: spend vs budget, impressions, clicks, leads/sales, ROAS. Insights and recommendations for next campaign.' },
      ]
    },
    {
      name: 'Product / Service Launch',
      description: 'Full go-to-market campaign for a new product or service. Multi-channel: social, paid ads, email, PR and influencer.',
      color: '#1db87a', buffer: 5, total_days: 30,
      phases: [
        { id:1,  order:1,  name:'Discovery & Brief',           color:'#3b7ef0', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:1, owners:[], predecessorIds:[], desc:'Understand product features, USP, pricing, target audience and competitor positioning. Define launch goals, channels, budget and timeline. Document brief.' },
        { id:2,  order:2,  name:'Market Research & Competitor Analysis', color:'#6c58f0', executionMode:'sequential', internalApproval:true, clientApproval:false, autoAdvance:true, enabled:true, canSkip:false, showInAssignments:true, dayWeight:8, bufferDays:0, owners:[], predecessorIds:[1], desc:'Analyse competitor launches, messaging and positioning. Identify market gaps and audience pain points. Summarise findings to inform creative strategy.' },
        { id:3,  order:3,  name:'Creative Strategy & Concept',  color:'#9050e8', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:1, owners:[], predecessorIds:[2], desc:'Develop launch campaign concept — visual identity, key message, tagline, content themes. Present mood board and creative direction. Get client sign-off.' },
        { id:4,  order:4,  name:'Content & Asset Production',   color:'#e87830', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:20, bufferDays:1, owners:[], predecessorIds:[3], desc:'Produce all campaign assets — launch video, teaser reels, product photography, carousel posts, email templates, ad creatives, landing page content.' },
        { id:5,  order:5,  name:'Teaser Campaign',              color:'#f7c34f', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:true,  showInAssignments:true,  dayWeight:8,  bufferDays:0, owners:[], predecessorIds:[4], desc:'Publish pre-launch teasers 7–10 days before launch — countdown posts, sneak peeks, behind-the-scenes. Build anticipation. Track early engagement.' },
        { id:6,  order:6,  name:'Internal & Client Review',     color:'#e84545', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:1, owners:[], predecessorIds:[4], desc:'Full review of all launch assets — creative, copy, landing page, email. Two-round revision. Final sign-off from client before go-live.' },
        { id:7,  order:7,  name:'Launch Day Execution',         color:'#1db87a', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:[], predecessorIds:[6], desc:'Execute launch day plan — publish posts, send email blast, activate paid ads, brief influencers, issue PR release. All-hands monitoring on launch day.' },
        { id:8,  order:8,  name:'Post-Launch Paid Amplification',color:'#18b8b8', executionMode:'parallel',  internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:true,  showInAssignments:true,  requiresMetrics:true, dayWeight:10, bufferDays:0, owners:[], predecessorIds:[7], desc:'Run paid ads to amplify launch content — retarget website visitors, boost top-performing organic posts, run lead gen ads.' },
        { id:9,  order:9,  name:'Community Engagement',         color:'#e83898', executionMode:'parallel',  internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:0, owners:[], predecessorIds:[7], desc:'Actively engage with launch-day comments, shares and DMs. Respond to questions about the product. Escalate negative feedback immediately.' },
        { id:10, order:10, name:'Launch Report & Retrospective', color:'#e87830', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:2, owners:[], predecessorIds:[8,9], desc:'Compile 30-day launch performance report — reach, engagement, leads, sales, ROAS. Insights, lessons learned and next phase recommendations.' },
      ]
    },
    {
      name: 'SEO & Content Marketing',
      description: 'Monthly SEO-driven content production — keyword research, blog writing, on-page optimisation and link building.',
      color: '#9050e8', buffer: 3, total_days: 28,
      phases: [
        { id:1, order:1, name:'Keyword Research & Planning',   color:'#3b7ef0', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:12, bufferDays:1, owners:[], predecessorIds:[], desc:'Identify target keywords using SEMrush/Ahrefs — search volume, difficulty, intent. Map keywords to content topics. Get client approval on topic list.' },
        { id:2, order:2, name:'Content Brief Creation',        color:'#6c58f0', executionMode:'sequential', internalApproval:true,  clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:0, owners:[], predecessorIds:[1], desc:'Write detailed content briefs per article — target keyword, secondary keywords, word count, heading structure, competitor references, linking strategy.' },
        { id:3, order:3, name:'Blog / Article Writing',        color:'#9050e8', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:20, bufferDays:1, owners:[], predecessorIds:[2], desc:'Write SEO-optimised articles per brief. Include target keyword in H1, first 100 words, subheadings and meta description. Internal links to 3+ pages.' },
        { id:4, order:4, name:'Editorial Review',              color:'#f7c34f', executionMode:'sequential', internalApproval:true,  clientApproval:false, autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:0, owners:[], predecessorIds:[3], desc:'Editor reviews grammar, tone, accuracy, SEO compliance and readability. Check Yoast/RankMath score is green. Return to writer if major changes needed.' },
        { id:5, order:5, name:'Client Review',                 color:'#e87830', executionMode:'sequential', internalApproval:false, clientApproval:true,  autoAdvance:false, enabled:true, canSkip:true,  showInAssignments:true,  dayWeight:8,  bufferDays:1, owners:[], predecessorIds:[4], desc:'Share articles with client for factual accuracy review. Client may add product-specific information. Limit to one revision round for blog content.' },
        { id:6, order:6, name:'Design & Visuals',              color:'#1db87a', executionMode:'parallel',   internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:true,  showInAssignments:true,  dayWeight:8,  bufferDays:0, owners:[], predecessorIds:[4], desc:'Design blog featured image, infographics and in-article visuals. Compress images for web performance. Alt text for all images.' },
        { id:7, order:7, name:'Publishing & On-Page SEO',      color:'#18b8b8', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:0, owners:[], predecessorIds:[5,6], desc:'Publish on CMS. Add meta title/description, canonical tag, schema markup, internal links and categories. Submit URL to Google Search Console.' },
        { id:8, order:8, name:'Link Building & Distribution',  color:'#e83898', executionMode:'parallel',   internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:true,  showInAssignments:true,  dayWeight:10, bufferDays:1, owners:[], predecessorIds:[7], desc:'Share articles on social platforms, email newsletter, LinkedIn. Outreach for guest post opportunities and backlinks. Add to content repurposing queue.' },
        { id:9, order:9, name:'SEO Performance Report',        color:'#e84545', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  requiresMetrics:true, dayWeight:10, bufferDays:1, owners:[], predecessorIds:[7], desc:'Monthly SEO report — organic traffic, keyword rankings, backlinks gained, domain authority change. Top-performing articles and next month recommendations.' },
      ]
    },
    {
      name: 'Influencer Marketing Campaign',
      description: 'End-to-end influencer campaign — from identification and outreach to content review, publishing and ROI reporting.',
      color: '#18b8b8', buffer: 5, total_days: 25,
      phases: [
        { id:1, order:1, name:'Campaign Brief & Budget',       color:'#3b7ef0', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:1, owners:[], predecessorIds:[], desc:'Define campaign objective, target audience, platforms, deliverables per influencer, content guidelines, hashtags, budget and timeline. Client sign-off.' },
        { id:2, order:2, name:'Influencer Research & Shortlist',color:'#6c58f0', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:12, bufferDays:1, owners:[], predecessorIds:[1], desc:'Research influencers by niche, follower count, engagement rate, audience demographics and past brand collaborations. Shortlist 10–15. Client approves final 5–8.' },
        { id:3, order:3, name:'Outreach & Contracting',        color:'#9050e8', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:10, bufferDays:2, owners:[], predecessorIds:[2], desc:'Contact approved influencers with campaign brief, deliverables and compensation. Negotiate terms. Issue contracts. Confirm posting dates and payment.' },
        { id:4, order:4, name:'Content Brief to Influencers',  color:'#e87830', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:5,  bufferDays:0, owners:[], predecessorIds:[3], desc:'Send detailed creative brief — key message, mandatory mentions, hashtags, dos and donts, brand guidelines and link/promo code to include.' },
        { id:5, order:5, name:'Content Review & Approval',     color:'#f7c34f', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:12, bufferDays:1, owners:[], predecessorIds:[4], desc:'Receive drafts from influencers. Review for brand safety, message accuracy and FTC compliance. Share with client for approval. Feedback to influencers.' },
        { id:6, order:6, name:'Publishing & Tracking',         color:'#1db87a', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:0, owners:[], predecessorIds:[5], desc:'Confirm influencers post on scheduled dates. Save live post links. Track initial metrics — views, likes, comments, saves, link clicks, promo code usage.' },
        { id:7, order:7, name:'Performance Monitoring',        color:'#18b8b8', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, canSkip:false, showInAssignments:true,  requiresMetrics:true, dayWeight:10, bufferDays:1, owners:[], predecessorIds:[6], desc:'Monitor all posts for 14 days post-publish. Collect screenshots of insights from each influencer. Track promo code redemptions and referral traffic.' },
        { id:8, order:8, name:'Campaign Report & ROI',         color:'#e83898', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, canSkip:false, showInAssignments:true,  dayWeight:8,  bufferDays:2, owners:[], predecessorIds:[7], desc:'Compile influencer performance report — total reach, engagement rate per influencer, EMV, conversions, ROI vs budget. Recommend top performers for next campaign.' },
      ]
    },
  ];

  const DEFAULT_PHASES_SEED = [
    { id:1, order:1, name:'Planning',          color:'#3b7ef0', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:true,  enabled:true, owners:[], predecessorIds:[], dayWeight:15, bufferDays:1, desc:'Briefing, goals, research and content calendar.' },
    { id:2, order:2, name:'Content Creation',  color:'#6c58f0', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, owners:[], predecessorIds:[], dayWeight:30, bufferDays:1, desc:'Design, copywriting, video editing and production.' },
    { id:3, order:3, name:'Review & Approval', color:'#f7c34f', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, owners:[], predecessorIds:[], dayWeight:15, bufferDays:1, desc:'Internal QA, client preview and final sign-off.' },
    { id:4, order:4, name:'Publishing',        color:'#1db87a', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, owners:[], predecessorIds:[], dayWeight:10, bufferDays:0, desc:'Scheduling and publishing across all platforms.' },
    { id:5, order:5, name:'Monitoring',        color:'#e87830', executionMode:'sequential', internalApproval:false, clientApproval:false, autoAdvance:true,  enabled:true, owners:[], predecessorIds:[], dayWeight:20, bufferDays:0, desc:'Community management, engagement and performance tracking.' },
    { id:6, order:6, name:'Reporting',         color:'#9050e8', executionMode:'sequential', internalApproval:true,  clientApproval:true,  autoAdvance:false, enabled:true, owners:[], predecessorIds:[], dayWeight:10, bufferDays:1, desc:'Report preparation, KPI review and client presentation.' },
  ];

  const [cts, setCts] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [services, setServices] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name:'', serviceId:'', totalDays:'', buffer:'1', desc:'', startPhaseId:'', schedulingPhaseId:'', endPhaseId:'' });

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([api.get('/campaign-types'), api.get('/services')]);
    setCts(c.data?.data || c.data || []);
    setServices(s.data?.data || s.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    if (!window.confirm(`Add ${DEFAULT_CAMPAIGN_TYPES_SEED.length} default campaign types with pre-built phases? Continue?`)) return;
    setSeeding(true);
    let added = 0, skipped = 0;
    try {
      for (const ct of DEFAULT_CAMPAIGN_TYPES_SEED) {
        try {
          await api.post('/campaign-types', {
            name: ct.name, description: ct.description,
            color: ct.color, buffer: ct.buffer, total_days: ct.total_days,
            phases: JSON.stringify(ct.phases), enabled: true,
          });
          added++;
        } catch(e) {
          // Skip if already exists (unique constraint)
          skipped++;
        }
      }
      load();
      if (skipped > 0) alert(`Done! Added ${added} new type(s). ${skipped} already existed and were skipped.`);
    } catch(e) { alert('Failed: ' + (e.response?.data?.message || e.message)); }
    finally { setSeeding(false); }
  };

  const getPhases = (ct) => {
    try {
      const p = ct.phases;
      if (Array.isArray(p)) return [...p].sort((a,b)=>a.order-b.order);
      if (typeof p === 'string' && p) return [...JSON.parse(p)].sort((a,b)=>a.order-b.order);
    } catch(e) {}
    return [];
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name:'', serviceId:'', totalDays:'', buffer:'1', desc:'', startPhaseId:'', schedulingPhaseId:'', endPhaseId:'' });
    setModal(true);
  };

  const openEdit = (ct) => {
    setEditing(ct);
    setForm({
      name: ct.name||'', serviceId: ct.service_id||ct.serviceId||'',
      totalDays: ct.total_days||ct.totalDays||'', buffer: ct.buffer||'1',
      desc: ct.description||ct.desc||'',
      startPhaseId: ct.start_phase_id||ct.startPhaseId||'',
      schedulingPhaseId: ct.scheduling_phase_id||ct.schedulingPhaseId||'',
      endPhaseId: ct.end_phase_id||ct.endPhaseId||'',
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.desc,
        service_id: form.serviceId||null, total_days: parseInt(form.totalDays)||0,
        buffer: parseInt(form.buffer)||1,
        start_phase_id: form.startPhaseId||null,
        scheduling_phase_id: form.schedulingPhaseId||null,
        end_phase_id: form.endPhaseId||null,
      };
      if (editing) {
        await api.put('/campaign-types/'+editing.id, payload);
      } else {
        const color = CT_COLORS[cts.length % CT_COLORS.length];
        const phases = JSON.stringify(DEFAULT_PHASES_SEED);
        await api.post('/campaign-types', { ...payload, color, phases, enabled: true });
      }
      setModal(false); load();
    } finally { setSaving(false); }
  };

  const del = async (ct) => {
    if (!window.confirm(`Delete campaign type "${ct.name}"? This will also remove all its phases.`)) return;
    await api.delete('/campaign-types/'+ct.id); load();
  };

  const duplicate = async (ct) => {
    const phases = getPhases(ct).map((p,i) => ({...p, id:i+1}));
    const color = CT_COLORS[cts.length % CT_COLORS.length];
    await api.post('/campaign-types', {
      name: ct.name+' (Copy)', description: ct.description||ct.desc||'',
      color, phases: JSON.stringify(phases), buffer: ct.buffer||1,
      total_days: ct.total_days||ct.totalDays||0, enabled: true
    });
    load();
  };

  const goToPhases = (ct) => {
    if (onManagePhases) onManagePhases(ct.id);
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🎯 Campaign Types</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Each campaign type has its own workflow phases. Use <b>Manage Workflow</b> to edit phases, or <b>Duplicate</b> to create a variant.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>{seeding ? 'Adding...' : '⚡ Add Default Types'}</button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Campaign Type</button>
        </div>
      </div>

      {/* Campaign Type Cards */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'16px'}}>
        {cts.map(ct => {
          const phases = getPhases(ct).filter(p=>p.enabled!==false);
          const color = ct.color || '#3b7ef0';
          const startPhase = form.startPhaseId ? phases.find(p=>String(p.id)===String(ct.start_phase_id||ct.startPhaseId)) : null;
          const endPhase   = phases.find(p=>String(p.id)===String(ct.end_phase_id||ct.endPhaseId));
          const schedPhase = phases.find(p=>String(p.id)===String(ct.scheduling_phase_id||ct.schedulingPhaseId));
          return (
            <div key={ct.id} className="card" style={{borderTop:`4px solid ${color}`, padding:'16px', position:'relative'}}>
              {/* Header */}
              <div className="flex-between mb-8">
                <div>
                  <div className="fw-700" style={{fontSize:'0.95rem',color}}>{ct.name}</div>
                  <div className="text-xs text-muted">{phases.length} phases · {ct.buffer||0}d buffer</div>
                </div>
                <div className="flex" style={{gap:'4px',flexWrap:'wrap',justifyContent:'flex-end'}}>
                  <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => openEdit(ct)}>✏️ Edit</button>
                  <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => duplicate(ct)}>📋 Duplicate</button>
                  <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={() => del(ct)}>✕</button>
                </div>
              </div>

              {/* Description */}
              {(ct.description||ct.desc) && <div className="text-xs text-muted mb-8">{ct.description||ct.desc}</div>}

              {/* Start / End / Scheduling phase indicators */}
              {(ct.start_phase_id||ct.startPhaseId) && (() => {
                const sp = phases.find(p=>String(p.id)===String(ct.start_phase_id||ct.startPhaseId));
                return sp ? <div className="text-xs mb-4" style={{color:'#6c58f0'}}><b>🚀 Starts from:</b> {sp.order}. {sp.name}</div> : null;
              })()}
              {(ct.end_phase_id||ct.endPhaseId) && (() => {
                const ep = phases.find(p=>String(p.id)===String(ct.end_phase_id||ct.endPhaseId));
                return ep ? <div className="text-xs mb-4" style={{color:'#1db87a'}}><b>🏁 End:</b> {ep.name}</div> : null;
              })()}
              {(ct.scheduling_phase_id||ct.schedulingPhaseId) && (() => {
                const sp = phases.find(p=>String(p.id)===String(ct.scheduling_phase_id||ct.schedulingPhaseId));
                return sp ? <div className="text-xs mb-8" style={{color:'#1db87a'}}><b>📅 Scheduling Phase:</b> {sp.order}. {sp.name}</div> : null;
              })()}

              {/* Phase flow */}
              <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:'3px',marginBottom:'12px',minHeight:'24px'}}>
                {phases.length > 0 ? phases.map((p,i) => (
                  <React.Fragment key={p.id}>
                    <span style={{fontSize:'0.65rem',fontWeight:700,padding:'2px 8px',borderRadius:'10px',background:`${p.color}18`,color:p.color,whiteSpace:'nowrap'}}>{p.name}</span>
                    {i < phases.length-1 && <span style={{color:'var(--text3)',fontSize:'0.7rem',margin:'0 1px'}}>→</span>}
                  </React.Fragment>
                )) : <span className="text-xs text-muted">No phases yet</span>}
              </div>

              {/* Manage Workflow button */}
              <button className="btn btn-primary" style={{width:'100%',justifyContent:'center'}} onClick={() => goToPhases(ct)}>
                🔄 Manage Workflow Phases
              </button>
            </div>
          );
        })}
        {cts.length === 0 && (
          <div className="text-sm text-muted" style={{padding:'20px'}}>No campaign types yet. Add your first one.</div>
        )}
      </div>

      {/* ── Add/Edit Modal ── */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'560px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing ? '✏️ Edit Campaign Type' : '+ Add Campaign Type'}</div>
              <div className="icon-btn" onClick={() => setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Campaign Type Name</label>
                  <input className="form-input" placeholder="e.g. Festival Campaign, Monthly Retainer" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Linked Service <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(optional)</span></label>
                  <select className="form-select" value={form.serviceId} onChange={e=>setForm({...form,serviceId:e.target.value})}>
                    <option value="">— Any Service —</option>
                    {services.filter(s=>s.enabled!==false).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Typical Total Duration <span className="text-xs" style={{color:'var(--green)',fontWeight:500,textTransform:'none'}}>(working days — ideal)</span></label>
                  <input className="form-input" type="number" placeholder="e.g. 21" value={form.totalDays} onChange={e=>setForm({...form,totalDays:e.target.value})} />
                  <div className="text-xs text-muted" style={{marginTop:'4px'}}>Planner compresses/expands proportionally based on actual dates.</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Buffer Days <span className="text-xs" style={{color:'var(--red)',fontWeight:500,textTransform:'none'}}>(safety margin before event)</span></label>
                  <input className="form-input" type="number" min="0" placeholder="1" value={form.buffer} onChange={e=>setForm({...form,buffer:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" placeholder="When is this used, what it covers..." value={form.desc} onChange={e=>setForm({...form,desc:e.target.value})} />
              </div>

              {/* Phase selectors — only shown when editing (phases exist) */}
              {editing && (() => {
                const phases = getPhases(editing).filter(p=>p.enabled!==false);
                if (!phases.length) return null;
                return (
                  <>
                    <div className="form-group">
                      <label className="form-label">🚀 Push to Workflow — Start from Phase <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>— which phase does work enter when pushed from Monthly Planner</span></label>
                      <select className="form-select" value={form.startPhaseId} onChange={e=>setForm({...form,startPhaseId:e.target.value})}>
                        <option value="">— Phase 1 (default) —</option>
                        {phases.map(p => <option key={p.id} value={p.id}>{p.order}. {p.name}</option>)}
                      </select>
                      <div className="text-xs text-muted" style={{marginTop:'4px'}}>Phases before the selected one will be skipped. Useful when planning is already done outside the system.</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">📅 Scheduling Phase <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>— the phase that marks the scheduling/publishing anchor date</span></label>
                      <select className="form-select" value={form.schedulingPhaseId} onChange={e=>setForm({...form,schedulingPhaseId:e.target.value})}>
                        <option value="">— Not set —</option>
                        {phases.map(p => <option key={p.id} value={p.id}>📅 {p.order}. {p.name}</option>)}
                      </select>
                      <div className="text-xs text-muted" style={{marginTop:'4px'}}>Available days from Push Date to this phase's date will be auto-divided across all tasks.</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">🏁 End of Workflow Phase <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>— when this phase is Done, the WO is automatically marked Complete</span></label>
                      <select className="form-select" value={form.endPhaseId} onChange={e=>setForm({...form,endPhaseId:e.target.value})}>
                        <option value="">— Not set —</option>
                        {phases.map(p => <option key={p.id} value={p.id}>🏁 {p.order}. {p.name}</option>)}
                      </select>
                      <div className="text-xs text-muted" style={{marginTop:'4px'}}>Typically the last phase (e.g. Reporting). Once Done → WO status changes to <b>Done</b> and moves to completed view.</div>
                    </div>
                  </>
                );
              })()}

              {!editing && (
                <div style={{padding:'10px 14px',background:'rgba(248,196,79,0.1)',border:'1px solid rgba(248,196,79,0.4)',borderRadius:'8px',fontSize:'0.78rem',color:'var(--yellow)'}}>
                  💡 After saving, click <b>Manage Workflow Phases</b> on the card to customise the phases for this campaign type.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Campaign Type'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ══════════════════════════════════════════════════════════
// TASK / CONTENT TYPES TAB
// ══════════════════════════════════════════════════════════
const DEFAULT_TASK_TYPES = [
  { name:'Instagram Reel',        platforms:['Instagram'],             format:'Video',          size:'15–90 sec, 9:16',      duration:3, priority:'normal', desc:'Shoot/edit a reel with hook, body and CTA. Hook in first 3 sec.' },
  { name:'Instagram Carousel',    platforms:['Instagram'],             format:'Carousel',       size:'Up to 10 slides, 1:1', duration:2, priority:'normal', desc:'Design a multi-slide carousel. Swipe CTA on slide 1.' },
  { name:'Instagram Story',       platforms:['Instagram','Facebook'],  format:'Story',          size:'15 sec, 9:16',         duration:1, priority:'normal', desc:'Design or edit a story. Include sticker/CTA.' },
  { name:'Facebook Post',         platforms:['Facebook'],              format:'Image',          size:'1200×628px',           duration:1, priority:'normal', desc:'Design a single-frame post for Facebook.' },
  { name:'LinkedIn Article',      platforms:['LinkedIn'],              format:'Text / Article', size:'800–1500 words',       duration:3, priority:'normal', desc:'Write a thought leadership article for LinkedIn.' },
  { name:'YouTube Video',         platforms:['YouTube'],               format:'Video',          size:'3–15 min, 16:9',       duration:4, priority:'normal', desc:'Edit raw footage into a polished YouTube video with graphics and subtitles.' },
  { name:'Google Display Ad',     platforms:['Google Ads'],            format:'Image',          size:'Multiple sizes',       duration:1, priority:'high',   desc:'Design ad creatives. Standard sizes: 300×250, 728×90, 160×600.' },
  { name:'Blog Post / SEO',       platforms:[],                        format:'Text / Article', size:'800–2000 words',       duration:3, priority:'normal', desc:'Research and write an SEO-optimised blog post. Target 1 primary keyword.' },
  { name:'Static Post Design',    platforms:['Instagram','Facebook'],  format:'Image',          size:'1080×1080px',          duration:1, priority:'normal', desc:'Design a single-frame static post.' },
  { name:'Ad Copy Writing',       platforms:['Google Ads','Meta Ads'], format:'Text / Article', size:'30–90 chars headline', duration:1, priority:'high',   desc:'Write ad copy for Google or Meta campaigns.' },
  { name:'Content Calendar',      platforms:[],                        format:'PDF / Doc',      size:'Monthly plan',         duration:2, priority:'normal', desc:'Prepare monthly content calendar with themes and post schedule.' },
  { name:'Campaign Setup',        platforms:['Google Ads','Meta Ads'], format:'Other',          size:'',                     duration:2, priority:'high',   desc:'Set up paid campaign — audience, creatives, budget, tracking.' },
  { name:'Monthly Report',        platforms:[],                        format:'PDF / Doc',      size:'',                     duration:2, priority:'normal', desc:'Prepare monthly performance report for client delivery.' },
  { name:'Client Review Call',    platforms:[],                        format:'Other',          size:'',                     duration:1, priority:'high',   desc:'Scheduled call with client for content preview or feedback.' },
  { name:'Internal QA Review',    platforms:[],                        format:'Other',          size:'',                     duration:1, priority:'normal', desc:'Internal quality check of all deliverables before client sharing.' },
  { name:'Keyword Research',      platforms:[],                        format:'PDF / Doc',      size:'',                     duration:2, priority:'normal', desc:'Identify target keywords, search volume and competition analysis.' },
];

const FORMAT_OPTS    = ['Image','Video','Carousel','Story','Text / Article','PDF / Doc','Animation','Print','Other'];
const PRIORITY_OPTS  = [{v:'normal',l:'🟢 Normal'},{v:'high',l:'🟠 High'},{v:'urgent',l:'🔴 Urgent'},{v:'low',l:'⚪ Low'}];
const PRIORITY_BADGE = {normal:'badge-green',high:'badge-yellow',urgent:'badge-red',low:'badge-gray'};
const PRIORITY_LABEL = {normal:'Normal',high:'High',urgent:'Urgent',low:'Low'};

function TaskTypesTab() {
  const [items, setItems]     = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [search, setSearch]   = useState('');
  const emptyForm = { name:'', platforms:[], format:'Image', size:'', duration:1, priority:'normal', desc:'', enabled:true };
  const [form, setForm]       = useState(emptyForm);

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([api.get('/task-types'), api.get('/platforms')]);
    setItems(r.data?.data || r.data || []);
    setPlatforms(p.data?.data || p.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    if (!window.confirm(`This will add ${DEFAULT_TASK_TYPES.length} standard task/content types. Continue?`)) return;
    setSeeding(true);
    try {
      const errors = [];
      for (const t of DEFAULT_TASK_TYPES) {
        try {
          await api.post('/task-types', { name:t.name, platforms:JSON.stringify(t.platforms), format:t.format, size:t.size||'', duration:t.duration, priority:t.priority, description:t.desc, enabled:true });
        } catch(e) { errors.push(t.name + ': ' + (e.response?.data?.message || e.message)); }
      }
      if (errors.length) alert('Some failed:\n' + errors.slice(0,3).join('\n'));
      load();
    } finally { setSeeding(false); }
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (item) => {
    setEditing(item);
    let plats = item.platforms;
    if (typeof plats === 'string') { try { plats = JSON.parse(plats); } catch(e) { plats = []; } }
    setForm({ name:item.name||'', platforms:plats||[], format:item.format||'Image', size:item.size||'', duration:item.duration||1, priority:item.priority||'normal', desc:item.description||item.desc||'', enabled:item.enabled!==false });
    setModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name:form.name, platforms:JSON.stringify(form.platforms), format:form.format, size:form.size, duration:parseInt(form.duration)||1, priority:form.priority, description:form.desc, enabled:form.enabled };
      if (editing) await api.put('/task-types/'+editing.id, payload);
      else         await api.post('/task-types', payload);
      setModal(false); load();
    } finally { setSaving(false); }
  };

  const del    = async (item) => { if(!window.confirm('Delete "'+item.name+'"?')) return; await api.delete('/task-types/'+item.id); load(); };
  const toggle = async (item) => { await api.put('/task-types/'+item.id, {...item, enabled:!item.enabled}); load(); };
  const togglePlatform = (name) => setForm(f => ({ ...f, platforms: f.platforms.includes(name) ? f.platforms.filter(p=>p!==name) : [...f.platforms, name] }));

  const filtered = items.filter(i => !search || i.name?.toLowerCase().includes(search.toLowerCase()));
  const grouped  = FORMAT_OPTS.reduce((acc, f) => { acc[f] = filtered.filter(i=>i.format===f); return acc; }, {});

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🗂️ Task / Content Types</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Define the types of tasks your agency handles. These populate task creation and work order workflows.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          {items.length === 0 && <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>{seeding?'Adding...':'⚡ Add Standard Types'}</button>}
          <button className="btn btn-primary" onClick={openAdd}>+ Add Type</button>
        </div>
      </div>

      {/* Search + Stats */}
      <div className="flex-between mb-16">
        <input className="form-input" style={{maxWidth:'260px'}} placeholder="Search task types..." value={search} onChange={e=>setSearch(e.target.value)} />
        <div className="flex" style={{gap:'8px'}}>
          {['Image','Video','Text / Article','Other'].map(f => {
            const count = items.filter(i=>i.format===f&&i.enabled!==false).length;
            return count > 0 ? <span key={f} className="badge badge-gray" style={{fontSize:'0.72rem'}}>{f}: {count}</span> : null;
          })}
          <span className="badge badge-blue" style={{fontSize:'0.72rem'}}>Total: {items.filter(i=>i.enabled!==false).length}</span>
        </div>
      </div>

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Task / Content Type</th>
              <th>Format</th>
              <th>Platforms</th>
              <th>Size / Spec</th>
              <th style={{width:'70px'}}>Days</th>
              <th style={{width:'90px'}}>Priority</th>
              <th>Status / Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">
                {items.length === 0 ? <>No task types yet. Click <b>⚡ Add Standard Types</b> to populate with 16 defaults.</> : 'No results found.'}
              </td></tr>
            )}
            {filtered.map(item => {
              let plats = item.platforms;
              if (typeof plats === 'string') { try { plats = JSON.parse(plats); } catch(e) { plats = []; } }
              return (
                <tr key={item.id} style={{opacity:item.enabled===false?0.45:1}}>
                  <td>
                    <div className="fw-700 text-sm">{item.name}</div>
                    {(item.description||item.desc) && <div className="text-xs text-muted" style={{marginTop:'2px',maxWidth:'260px'}}>{item.description||item.desc}</div>}
                  </td>
                  <td><span className="badge badge-gray">{item.format||'—'}</span></td>
                  <td>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'3px'}}>
                      {plats?.length ? plats.map(p=><span key={p} className="badge badge-blue" style={{fontSize:'0.65rem',background:'rgba(59,126,240,0.08)',color:'var(--accent)'}}>{p}</span>) : <span className="text-xs text-muted">All / General</span>}
                    </div>
                  </td>
                  <td className="text-xs text-muted">{item.size||'—'}</td>
                  <td className="text-xs fw-700 mono">{item.duration||1}d</td>
                  <td><span className={`badge ${PRIORITY_BADGE[item.priority]||'badge-gray'}`}>{PRIORITY_LABEL[item.priority]||item.priority||'Normal'}</span></td>
                  <td>
                    <div className="flex" style={{gap:'4px',alignItems:'center'}}>
                      <Toggle checked={item.enabled!==false} onChange={()=>toggle(item)} />
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(item)}>✏️</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(item)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'600px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Task / Content Type':'+ Add Task / Content Type'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Task / Content Type Name</label>
                  <input className="form-input" placeholder="e.g. Instagram Reel, Blog Post..." value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Format / Output Type</label>
                  <select className="form-select" value={form.format} onChange={e=>setForm({...form,format:e.target.value})}>
                    {FORMAT_OPTS.map(f=><option key={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Platforms <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(leave empty for general/all platforms)</span></label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
                  {platforms.filter(p=>p.is_active!==false).map(p=>(
                    <label key={p.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.78rem',cursor:'pointer',padding:'4px 8px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'6px',whiteSpace:'nowrap'}}>
                      <input type="checkbox" checked={form.platforms.includes(p.name)} onChange={()=>togglePlatform(p.name)} style={{accentColor:'var(--accent)'}} />
                      {p.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Size / Specs</label>
                  <input className="form-input" placeholder="e.g. 1080×1080px, 15–90 sec..." value={form.size} onChange={e=>setForm({...form,size:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Typical Duration (days)</label>
                  <input className="form-input" type="number" min="0.5" step="0.5" placeholder="1" value={form.duration} onChange={e=>setForm({...form,duration:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-select" value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})}>
                    {PRIORITY_OPTS.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.enabled?'enabled':'disabled'} onChange={e=>setForm({...form,enabled:e.target.value==='enabled'})}>
                    <option value="enabled">✅ Active</option>
                    <option value="disabled">⛔ Disabled</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description / Instructions</label>
                <textarea className="form-textarea" placeholder="What needs to happen for this task type? Include key requirements..." value={form.desc} onChange={e=>setForm({...form,desc:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// OUTPUT FORMATS TAB
// ══════════════════════════════════════════════════════════
const OUTPUT_FORMAT_SEED = [
  { platform:'Instagram', name:'Feed Post – Square',    type:'Image', width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'' },
  { platform:'Instagram', name:'Feed Post – Portrait',  type:'Image', width:'1080 px', height:'1350 px', ratio:'4:5',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'' },
  { platform:'Instagram', name:'Feed Post – Landscape', type:'Image', width:'1080 px', height:'566 px',  ratio:'1.91:1',   duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'' },
  { platform:'Instagram', name:'Carousel Post',         type:'Image', width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'Max 10 slides' },
  { platform:'Instagram', name:'Story',                 type:'Video', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 15s',    fileFormat:'MP4/JPG',     fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'Add captions; no hard text in safe zone' },
  { platform:'Instagram', name:'Reel',                  type:'Video', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'15–90s',     fileFormat:'MP4',         fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'Best: 15–30s; captions recommended' },
  { platform:'Instagram', name:'Profile Photo',         type:'Image', width:'320 px',  height:'320 px',  ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'—',      safeZone:'—',                  notes:'Displayed as circle' },
  { platform:'Instagram', name:'Highlight Cover',       type:'Image', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'—',          fileFormat:'JPG/PNG',     fileSize:'—',      safeZone:'Center 400×400 px',  notes:'Keep key art centered' },
  { platform:'Facebook',  name:'Feed Post – Square',    type:'Image', width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'' },
  { platform:'Facebook',  name:'Feed Post – Landscape', type:'Image', width:'1200 px', height:'630 px',  ratio:'1.91:1',   duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'' },
  { platform:'Facebook',  name:'Story',                 type:'Video', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 20s',    fileFormat:'MP4/JPG',     fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'' },
  { platform:'Facebook',  name:'Cover Photo',           type:'Image', width:'820 px',  height:'312 px',  ratio:'—',        duration:'—',          fileFormat:'JPG/PNG',     fileSize:'100 KB', safeZone:'—',                  notes:'Left side may be obscured by profile pic' },
  { platform:'Facebook',  name:'Reel',                  type:'Video', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'15–90s',     fileFormat:'MP4',         fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'' },
  { platform:'YouTube',   name:'Video – Standard',      type:'Video', width:'1920 px', height:'1080 px', ratio:'16:9',     duration:'Any',        fileFormat:'MP4',         fileSize:'256 GB', safeZone:'—',                  notes:'' },
  { platform:'YouTube',   name:'YouTube Short',         type:'Video', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 60s',    fileFormat:'MP4',         fileSize:'—',      safeZone:'—',                  notes:'Vertical only' },
  { platform:'YouTube',   name:'Thumbnail',             type:'Image', width:'1280 px', height:'720 px',  ratio:'16:9',     duration:'—',          fileFormat:'JPG/PNG',     fileSize:'2 MB',   safeZone:'—',                  notes:'Min 640×360 px; use bold text' },
  { platform:'YouTube',   name:'Channel Art / Banner',  type:'Image', width:'2560 px', height:'1440 px', ratio:'—',        duration:'—',          fileFormat:'JPG/PNG',     fileSize:'6 MB',   safeZone:'Center 1546×423 px', notes:'Safe zone shows on all devices' },
  { platform:'LinkedIn',  name:'Feed Post – Square',    type:'Image', width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'5 MB',   safeZone:'—',                  notes:'' },
  { platform:'LinkedIn',  name:'Feed Post – Landscape', type:'Image', width:'1200 px', height:'627 px',  ratio:'1.91:1',   duration:'—',          fileFormat:'JPG/PNG',     fileSize:'5 MB',   safeZone:'—',                  notes:'' },
  { platform:'LinkedIn',  name:'Document / Carousel',   type:'PDF',   width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'PDF',         fileSize:'100 MB', safeZone:'—',                  notes:'Max 300 pages' },
  { platform:'LinkedIn',  name:'Feed Video',            type:'Video', width:'1920 px', height:'1080 px', ratio:'16:9',     duration:'3s–10min',   fileFormat:'MP4',         fileSize:'5 GB',   safeZone:'—',                  notes:'' },
  { platform:'X / Twitter', name:'In-feed Image',       type:'Image', width:'1600 px', height:'900 px',  ratio:'16:9',     duration:'—',          fileFormat:'JPG/PNG',     fileSize:'5 MB',   safeZone:'—',                  notes:'' },
  { platform:'X / Twitter', name:'In-feed Video',       type:'Video', width:'1280 px', height:'720 px',  ratio:'16:9',     duration:'Max 140s',   fileFormat:'MP4',         fileSize:'512 MB', safeZone:'—',                  notes:'' },
  { platform:'Pinterest', name:'Standard Pin',          type:'Image', width:'1000 px', height:'1500 px', ratio:'2:3',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'20 MB',  safeZone:'—',                  notes:'Optimal size' },
  { platform:'Pinterest', name:'Video Pin',             type:'Video', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'4s–15min',   fileFormat:'MP4',         fileSize:'2 GB',   safeZone:'—',                  notes:'' },
  { platform:'WhatsApp',  name:'Status – Image',        type:'Image', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'—',          fileFormat:'JPG/PNG',     fileSize:'16 MB',  safeZone:'—',                  notes:'Visible for 24 hours' },
  { platform:'WhatsApp',  name:'Status – Video',        type:'Video', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 30s',    fileFormat:'MP4',         fileSize:'16 MB',  safeZone:'—',                  notes:'' },
  { platform:'WhatsApp',  name:'Broadcast Image',       type:'Image', width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'16 MB',  safeZone:'—',                  notes:'' },
  { platform:'Meta Ads',  name:'Single Image Ad',       type:'Image', width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'Less than 20% text recommended' },
  { platform:'Meta Ads',  name:'Carousel Ad',           type:'Image', width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'2–10 cards; each 1:1' },
  { platform:'Meta Ads',  name:'Story Ad',              type:'Video', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 15s',    fileFormat:'MP4/JPG',     fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'Keep CTA above safe zone' },
  { platform:'Meta Ads',  name:'Reel Ad',               type:'Video', width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 60s',    fileFormat:'MP4',         fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'' },
  { platform:'Google Ads', name:'Leaderboard Banner',   type:'Image', width:'728 px',  height:'90 px',   ratio:'—',        duration:'—',          fileFormat:'JPG/PNG/GIF', fileSize:'150 KB', safeZone:'—',                  notes:'' },
  { platform:'Google Ads', name:'Medium Rectangle',     type:'Image', width:'300 px',  height:'250 px',  ratio:'—',        duration:'—',          fileFormat:'JPG/PNG/GIF', fileSize:'150 KB', safeZone:'—',                  notes:'Most common placement' },
  { platform:'Google Ads', name:'Responsive Display',   type:'Image', width:'1200 px', height:'628 px',  ratio:'1.91:1',   duration:'—',          fileFormat:'JPG/PNG',     fileSize:'5 MB',   safeZone:'—',                  notes:'Google auto-resizes for placements' },
  { platform:'Google Ads', name:'YouTube Pre-roll',     type:'Video', width:'1920 px', height:'1080 px', ratio:'16:9',     duration:'15–30s',     fileFormat:'MP4',         fileSize:'—',      safeZone:'—',                  notes:'Skippable after 5s' },
  { platform:'Email',     name:'Email Header Banner',   type:'Image', width:'600 px',  height:'200 px',  ratio:'3:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'200 KB', safeZone:'—',                  notes:'Keep branding in top 150 px' },
  { platform:'Email',     name:'Email Body Image',      type:'Image', width:'600 px',  height:'400 px',  ratio:'3:2',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'200 KB', safeZone:'—',                  notes:'Alt text required' },
  { platform:'Print / Offline', name:'A4 Flyer',        type:'Print', width:'210 mm',  height:'297 mm',  ratio:'—',        duration:'—',          fileFormat:'PDF',         fileSize:'—',      safeZone:'3mm bleed all sides', notes:'CMYK, 300 DPI' },
  { platform:'Print / Offline', name:'Business Card',   type:'Print', width:'90 mm',   height:'55 mm',   ratio:'—',        duration:'—',          fileFormat:'PDF',         fileSize:'—',      safeZone:'3mm bleed all sides', notes:'CMYK, 300 DPI' },
  { platform:'Print / Offline', name:'Roll-up Banner',  type:'Print', width:'850 mm',  height:'2000 mm', ratio:'—',        duration:'—',          fileFormat:'PDF',         fileSize:'—',      safeZone:'50mm from bottom',   notes:'CMYK, 150 DPI at final size' },
];

const OUTPUT_TYPE_BADGE = { Image:'badge-blue', Video:'badge-green', PDF:'badge-red', Animation:'badge-purple', Print:'badge-orange', Other:'badge-gray' };

// ══════════════════════════════════════════════════════════
// OUTPUT FORMATS TAB
// ══════════════════════════════════════════════════════════
const OUTPUT_FORMAT_SEED_FULL = [
  // Image
  { outputType:'Image', platform:'Instagram',    name:'Feed Post – Square',    width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'Instagram',    name:'Feed Post – Portrait',  width:'1080 px', height:'1350 px', ratio:'4:5',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'Instagram',    name:'Feed Post – Landscape', width:'1080 px', height:'566 px',  ratio:'1.91:1',   duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'Instagram',    name:'Carousel Post',         width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'Max 10 slides' },
  { outputType:'Image', platform:'Instagram',    name:'Profile Photo',         width:'320 px',  height:'320 px',  ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'—',      safeZone:'—',                  notes:'Displayed as circle' },
  { outputType:'Image', platform:'Instagram',    name:'Highlight Cover',       width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'—',          fileFormat:'JPG/PNG',     fileSize:'—',      safeZone:'Center 400×400 px',  notes:'Keep key art centered' },
  { outputType:'Image', platform:'Facebook',     name:'Feed Post – Square',    width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'Facebook',     name:'Feed Post – Landscape', width:'1200 px', height:'630 px',  ratio:'1.91:1',   duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'Facebook',     name:'Cover Photo',           width:'820 px',  height:'312 px',  ratio:'—',        duration:'—',          fileFormat:'JPG/PNG',     fileSize:'100 KB', safeZone:'—',                  notes:'Left side may be obscured by profile pic' },
  { outputType:'Image', platform:'Facebook',     name:'Event Cover',           width:'1920 px', height:'1005 px', ratio:'—',        duration:'—',          fileFormat:'JPG/PNG',     fileSize:'—',      safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'Facebook',     name:'Carousel Ad',           width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'Max 10 cards' },
  { outputType:'Image', platform:'YouTube',      name:'Thumbnail',             width:'1280 px', height:'720 px',  ratio:'16:9',     duration:'—',          fileFormat:'JPG/PNG',     fileSize:'2 MB',   safeZone:'—',                  notes:'Min 640×360 px; use bold text' },
  { outputType:'Image', platform:'YouTube',      name:'Channel Art / Banner',  width:'2560 px', height:'1440 px', ratio:'—',        duration:'—',          fileFormat:'JPG/PNG',     fileSize:'6 MB',   safeZone:'Center 1546×423 px', notes:'Safe zone shows on all devices' },
  { outputType:'Image', platform:'YouTube',      name:'Channel Icon',          width:'800 px',  height:'800 px',  ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'4 MB',   safeZone:'—',                  notes:'Displayed as circle' },
  { outputType:'Image', platform:'LinkedIn',     name:'Feed Post – Square',    width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'5 MB',   safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'LinkedIn',     name:'Feed Post – Landscape', width:'1200 px', height:'627 px',  ratio:'1.91:1',   duration:'—',          fileFormat:'JPG/PNG',     fileSize:'5 MB',   safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'LinkedIn',     name:'Company Banner',        width:'1584 px', height:'396 px',  ratio:'4:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'4 MB',   safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'X / Twitter',  name:'In-feed Image',         width:'1600 px', height:'900 px',  ratio:'16:9',     duration:'—',          fileFormat:'JPG/PNG',     fileSize:'5 MB',   safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'X / Twitter',  name:'Profile Photo',         width:'400 px',  height:'400 px',  ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'2 MB',   safeZone:'—',                  notes:'Displayed as circle' },
  { outputType:'Image', platform:'X / Twitter',  name:'Header Photo',          width:'1500 px', height:'500 px',  ratio:'3:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'5 MB',   safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'Pinterest',    name:'Standard Pin',          width:'1000 px', height:'1500 px', ratio:'2:3',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'20 MB',  safeZone:'—',                  notes:'Optimal size' },
  { outputType:'Image', platform:'Pinterest',    name:'Square Pin',            width:'1000 px', height:'1000 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'20 MB',  safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'WhatsApp',     name:'Status – Image',        width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'—',          fileFormat:'JPG/PNG',     fileSize:'16 MB',  safeZone:'—',                  notes:'Visible for 24 hours' },
  { outputType:'Image', platform:'WhatsApp',     name:'Broadcast Image',       width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'16 MB',  safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'Meta Ads',     name:'Single Image Ad',       width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'Less than 20% text recommended' },
  { outputType:'Image', platform:'Meta Ads',     name:'Carousel Ad',           width:'1080 px', height:'1080 px', ratio:'1:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'2–10 cards; each 1:1' },
  { outputType:'Image', platform:'Meta Ads',     name:'Collection Ad',         width:'1200 px', height:'628 px',  ratio:'1.91:1',   duration:'—',          fileFormat:'JPG/PNG',     fileSize:'30 MB',  safeZone:'—',                  notes:'Hero image above product grid' },
  { outputType:'Image', platform:'Google Ads',   name:'Leaderboard Banner',    width:'728 px',  height:'90 px',   ratio:'—',        duration:'—',          fileFormat:'JPG/PNG/GIF', fileSize:'150 KB', safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'Google Ads',   name:'Medium Rectangle',      width:'300 px',  height:'250 px',  ratio:'—',        duration:'—',          fileFormat:'JPG/PNG/GIF', fileSize:'150 KB', safeZone:'—',                  notes:'Most common placement' },
  { outputType:'Image', platform:'Google Ads',   name:'Large Rectangle',       width:'336 px',  height:'280 px',  ratio:'—',        duration:'—',          fileFormat:'JPG/PNG/GIF', fileSize:'150 KB', safeZone:'—',                  notes:'' },
  { outputType:'Image', platform:'Google Ads',   name:'Half Page',             width:'300 px',  height:'600 px',  ratio:'—',        duration:'—',          fileFormat:'JPG/PNG/GIF', fileSize:'150 KB', safeZone:'High impact',        notes:'' },
  { outputType:'Image', platform:'Google Ads',   name:'Responsive Display',    width:'1200 px', height:'628 px',  ratio:'1.91:1',   duration:'—',          fileFormat:'JPG/PNG',     fileSize:'5 MB',   safeZone:'—',                  notes:'Google auto-resizes for placements' },
  { outputType:'Image', platform:'Email',        name:'Email Header Banner',   width:'600 px',  height:'200 px',  ratio:'3:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'200 KB', safeZone:'—',                  notes:'Keep branding in top 150 px' },
  { outputType:'Image', platform:'Email',        name:'Email Body Image',      width:'600 px',  height:'400 px',  ratio:'3:2',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'200 KB', safeZone:'—',                  notes:'Alt text required' },
  { outputType:'Image', platform:'Email',        name:'Email Footer',          width:'600 px',  height:'100 px',  ratio:'6:1',      duration:'—',          fileFormat:'JPG/PNG',     fileSize:'100 KB', safeZone:'—',                  notes:'Include unsubscribe info' },
  // Video
  { outputType:'Video', platform:'Instagram',    name:'Reel',                  width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'15–90s',     fileFormat:'MP4',         fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'Best: 15–30s; captions recommended' },
  { outputType:'Video', platform:'Instagram',    name:'Story',                 width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 15s',    fileFormat:'MP4/JPG',     fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'Add captions; no hard text in safe zone' },
  { outputType:'Video', platform:'Facebook',     name:'Story',                 width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 20s',    fileFormat:'MP4/JPG',     fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'' },
  { outputType:'Video', platform:'Facebook',     name:'Reel',                  width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'15–90s',     fileFormat:'MP4',         fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'' },
  { outputType:'Video', platform:'Facebook',     name:'Feed Video',            width:'1280 px', height:'720 px',  ratio:'16:9',     duration:'Max 240min', fileFormat:'MP4',         fileSize:'10 GB',  safeZone:'—',                  notes:'' },
  { outputType:'Video', platform:'YouTube',      name:'Video – Standard',      width:'1920 px', height:'1080 px', ratio:'16:9',     duration:'Any',        fileFormat:'MP4',         fileSize:'256 GB', safeZone:'—',                  notes:'' },
  { outputType:'Video', platform:'YouTube',      name:'YouTube Short',         width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 60s',    fileFormat:'MP4',         fileSize:'—',      safeZone:'—',                  notes:'Vertical only' },
  { outputType:'Video', platform:'YouTube',      name:'End Screen',            width:'1280 px', height:'720 px',  ratio:'16:9',     duration:'—',          fileFormat:'JPG/PNG',     fileSize:'—',      safeZone:'—',                  notes:'Added in last 20s of video' },
  { outputType:'Video', platform:'LinkedIn',     name:'Feed Video',            width:'1920 px', height:'1080 px', ratio:'16:9',     duration:'3s–10min',   fileFormat:'MP4',         fileSize:'5 GB',   safeZone:'—',                  notes:'' },
  { outputType:'Video', platform:'LinkedIn',     name:'Story',                 width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 20s',    fileFormat:'MP4/JPG',     fileSize:'—',      safeZone:'Top/Bottom 250 px',  notes:'' },
  { outputType:'Video', platform:'X / Twitter',  name:'In-feed Video',         width:'1280 px', height:'720 px',  ratio:'16:9',     duration:'Max 140s',   fileFormat:'MP4',         fileSize:'512 MB', safeZone:'—',                  notes:'' },
  { outputType:'Video', platform:'Pinterest',    name:'Video Pin',             width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'4s–15min',   fileFormat:'MP4',         fileSize:'2 GB',   safeZone:'—',                  notes:'' },
  { outputType:'Video', platform:'WhatsApp',     name:'Status – Video',        width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 30s',    fileFormat:'MP4',         fileSize:'16 MB',  safeZone:'—',                  notes:'' },
  { outputType:'Video', platform:'Meta Ads',     name:'Story Ad',              width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 15s',    fileFormat:'MP4/JPG',     fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'Keep CTA above safe zone' },
  { outputType:'Video', platform:'Meta Ads',     name:'Reel Ad',               width:'1080 px', height:'1920 px', ratio:'9:16',     duration:'Max 60s',    fileFormat:'MP4',         fileSize:'100 MB', safeZone:'Top/Bottom 250 px',  notes:'' },
  { outputType:'Video', platform:'Google Ads',   name:'YouTube Pre-roll',      width:'1920 px', height:'1080 px', ratio:'16:9',     duration:'15–30s',     fileFormat:'MP4',         fileSize:'—',      safeZone:'—',                  notes:'Skippable after 5s' },
  // Animation
  { outputType:'Animation', platform:'Email',    name:'GIF Animation',         width:'600 px',  height:'400 px',  ratio:'3:2',      duration:'—',          fileFormat:'GIF',         fileSize:'1 MB',   safeZone:'—',                  notes:'First frame should make sense without animation' },
  // PDF / Document
  { outputType:'PDF / Document', platform:'LinkedIn', name:'Document / Carousel', width:'1080 px', height:'1080 px', ratio:'1:1',  duration:'—',          fileFormat:'PDF',         fileSize:'100 MB', safeZone:'—',                  notes:'Max 300 pages' },
  // Print
  { outputType:'Print', platform:'Print / Offline', name:'A4 Flyer',          width:'210 mm',  height:'297 mm',  ratio:'—',        duration:'—',          fileFormat:'PDF',         fileSize:'—',      safeZone:'3mm bleed all sides', notes:'CMYK, 300 DPI' },
  { outputType:'Print', platform:'Print / Offline', name:'A5 Flyer',          width:'148 mm',  height:'210 mm',  ratio:'—',        duration:'—',          fileFormat:'PDF',         fileSize:'—',      safeZone:'3mm bleed all sides', notes:'CMYK, 300 DPI' },
  { outputType:'Print', platform:'Print / Offline', name:'Business Card',      width:'90 mm',   height:'55 mm',   ratio:'—',        duration:'—',          fileFormat:'PDF',         fileSize:'—',      safeZone:'3mm bleed all sides', notes:'CMYK, 300 DPI; keep text 5mm from edge' },
  { outputType:'Print', platform:'Print / Offline', name:'Hoarding / Billboard', width:'Custom', height:'Custom', ratio:'—',       duration:'—',          fileFormat:'PDF/AI',      fileSize:'—',      safeZone:'Custom',             notes:'72 DPI at final size; CMYK' },
  { outputType:'Print', platform:'Print / Offline', name:'Roll-up Banner',     width:'850 mm',  height:'2000 mm', ratio:'—',        duration:'—',          fileFormat:'PDF',         fileSize:'—',      safeZone:'50mm from bottom',   notes:'CMYK, 150 DPI at final size' },
];

function OutputFormatsTab() {
  const [items, setItems]          = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [modal, setModal]          = useState(false);
  const [editing, setEditing]      = useState(null);
  const [saving, setSaving]        = useState(false);
  const [seeding, setSeeding]      = useState(false);
  const [filterType, setFilterType] = useState('All');

  const emptyForm = { outputType:'Image', platform:'', name:'', width:'', height:'', ratio:'', duration:'—', fileFormat:'JPG/PNG', fileSize:'', safeZone:'—', notes:'', enabled:true };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [r, ct] = await Promise.all([api.get('/output-formats'), api.get('/content-types')]);
    setItems(r.data?.data || r.data || []);
    setContentTypes(ct.data?.data || ct.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const getOutputTypeList = () => {
    const fromDB = contentTypes.filter(ct=>ct.enabled!==false).map(ct=>ct.name);
    return fromDB.length > 0 ? fromDB : ['Image','Video','Animation','Carousel','Story','Audio','PDF / Document','Copy / Text','Print','Presentation','Email Template','Other'];
  };

  const seedDefaults = async () => {
    if (!window.confirm(`Add ${OUTPUT_FORMAT_SEED_FULL.length} standard output formats? Continue?`)) return;
    setSeeding(true);
    try {
      const errors = [];
      for (const f of OUTPUT_FORMAT_SEED_FULL) {
        try {
          await api.post('/output-formats', {
            name: f.name, platform: f.platform, type: f.outputType,
            width: f.width, height: f.height, ratio: f.ratio,
            dimensions: f.width && f.height ? `${f.width} × ${f.height}` : '',
            aspect_ratio: f.ratio,
            duration: f.duration, file_format: f.fileFormat,
            file_size: f.fileSize, max_size: f.fileSize,
            safe_zone: f.safeZone,
            notes: f.notes, enabled: true
          });
        } catch(e) { errors.push(f.name + ': ' + (e.response?.data?.message || e.message)); }
      }
      if (errors.length) alert('Some failed:\n' + errors.slice(0,3).join('\n'));
      load();
    } finally { setSeeding(false); }
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      outputType: item.type || 'Image',
      platform:   item.platform || '',
      name:       item.name || '',
      width:      item.width || '',
      height:     item.height || '',
      ratio:      item.ratio || '',
      duration:   item.duration || '—',
      fileFormat: item.file_format || item.fileFormat || '',
      fileSize:   item.file_size || item.fileSize || '',
      safeZone:   item.safe_zone || item.safeZone || '—',
      notes:      item.notes || '',
      enabled:    item.enabled !== false,
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name, platform: form.platform, type: form.outputType,
        width: form.width, height: form.height, ratio: form.ratio,
        dimensions: form.width && form.height ? `${form.width} × ${form.height}` : '',
        aspect_ratio: form.ratio,
        duration: form.duration, file_format: form.fileFormat,
        file_size: form.fileSize, max_size: form.fileSize,
        safe_zone: form.safeZone,
        notes: form.notes, enabled: form.enabled,
      };
      if (editing) await api.put('/output-formats/' + editing.id, payload);
      else         await api.post('/output-formats', payload);
      setModal(false); load();
    } finally { setSaving(false); }
  };

  const del    = async (item) => { if (!window.confirm('Delete "' + item.name + '"?')) return; await api.delete('/output-formats/' + item.id); load(); };
  const toggle = async (item) => {
    await api.put('/output-formats/' + item.id, {
      name: item.name, platform: item.platform, type: item.type,
      width: item.width, height: item.height, ratio: item.ratio,
      dimensions: item.dimensions, aspect_ratio: item.aspect_ratio,
      duration: item.duration, file_format: item.file_format || item.fileFormat,
      file_size: item.file_size || item.fileSize, max_size: item.max_size,
      safe_zone: item.safe_zone, notes: item.notes, enabled: !item.enabled,
    });
    load();
  };

  // Group by Output Type (not platform)
  const allTypes = ['All', ...new Set(items.map(i => i.type).filter(Boolean))];
  const filtered = filterType === 'All' ? items : items.filter(i => i.type === filterType);
  const groupedByType = filtered.reduce((acc, i) => {
    const k = i.type || 'Other';
    if (!acc[k]) acc[k] = {};
    const p = i.platform || 'General';
    if (!acc[k][p]) acc[k][p] = [];
    acc[k][p].push(i);
    return acc;
  }, {});

  // Find content type color/icon for badge
  const getTypeInfo = (typeName) => contentTypes.find(ct => ct.name === typeName) || { icon:'📦', color:'#888' };

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">📐 Output Formats</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Standard deliverable specifications grouped by Output Type. Used as reference when creating content.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          {items.length === 0 && (
            <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>
              {seeding ? 'Adding...' : `⚡ Add Standard Formats (${OUTPUT_FORMAT_SEED_FULL.length})`}
            </button>
          )}
          <button className="btn btn-primary" onClick={openAdd}>+ Add Format</button>
        </div>
      </div>

      {/* Output Type filter pills */}
      <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'16px'}}>
        {allTypes.map(t => {
          const info = getTypeInfo(t);
          const count = t === 'All' ? items.length : items.filter(i=>i.type===t).length;
          const active = filterType === t;
          return (
            <button key={t} onClick={() => setFilterType(t)} style={{
              padding:'5px 12px', borderRadius:'20px', fontSize:'0.75rem', fontWeight:600, cursor:'pointer',
              border: `2px solid ${active ? (info.color||'var(--accent)') : 'var(--border)'}`,
              background: active ? (info.color||'var(--accent)') : 'var(--bg2)',
              color: active ? '#fff' : 'var(--text2)',
              display:'flex', alignItems:'center', gap:'4px'
            }}>
              {t !== 'All' && <span>{info.icon||'📦'}</span>}
              {t} <span style={{opacity:0.7,fontWeight:400}}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Grouped by Output Type → Platform */}
      {Object.entries(groupedByType).map(([outputType, platformGroups]) => {
        const typeInfo = getTypeInfo(outputType);
        return (
          <div key={outputType} style={{marginBottom:'20px'}}>
            {/* Output Type header */}
            <div className="flex" style={{alignItems:'center',gap:'8px',marginBottom:'10px'}}>
              <span style={{fontSize:'1.2rem'}}>{typeInfo.icon||'📦'}</span>
              <span className="fw-700" style={{color:typeInfo.color||'var(--text)',fontSize:'1rem'}}>{outputType}</span>
              <span className="badge badge-gray" style={{fontSize:'0.65rem'}}>{Object.values(platformGroups).flat().length} formats</span>
            </div>

            {/* Platform sub-groups */}
            {Object.entries(platformGroups).map(([platform, rows]) => (
              <div key={platform} className="card mb-10">
                <div style={{padding:'8px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'8px',background:'var(--bg3)',borderRadius:'var(--radius) var(--radius) 0 0'}}>
                  {(() => { const svg = platformSvgFor(platform); return svg ? <div dangerouslySetInnerHTML={{__html:svg}} style={{width:'16px',height:'16px'}} /> : null; })()}
                  <span className="fw-700 text-sm">{platform}</span>
                  <span className="text-xs text-muted">({rows.length})</span>
                </div>
                <table className="data-table" style={{fontSize:'0.75rem'}}>
                  <thead>
                    <tr>
                      <th>Format Name</th>
                      <th>Dimensions</th>
                      <th>Ratio</th>
                      <th>Duration</th>
                      <th>File</th>
                      <th>Max Size</th>
                      <th>Safe Zone</th>
                      <th>Notes</th>
                      <th style={{width:'100px'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(item => (
                      <tr key={item.id} style={{opacity:item.enabled===false?0.4:1}}>
                        <td className="fw-700">{item.name}</td>
                        <td className="mono">{item.width&&item.height?`${item.width} × ${item.height}`:'—'}</td>
                        <td className="mono">{item.ratio||'—'}</td>
                        <td>{item.duration||'—'}</td>
                        <td className="mono">{item.file_format||item.fileFormat||'—'}</td>
                        <td>{item.file_size||item.fileSize||'—'}</td>
                        <td style={{maxWidth:'100px',color:'var(--text3)'}}>{item.safe_zone||item.safeZone||'—'}</td>
                        <td style={{maxWidth:'120px',color:'var(--text3)'}}>{item.notes||'—'}</td>
                        <td>
                          <div className="flex" style={{gap:'3px'}}>
                            <Toggle checked={item.enabled!==false} onChange={()=>toggle(item)} />
                            <button className="btn btn-secondary" style={{padding:'2px 6px',fontSize:'0.65rem'}} onClick={()=>openEdit(item)}>✏️</button>
                            <button className="btn btn-red"       style={{padding:'2px 6px',fontSize:'0.65rem'}} onClick={()=>del(item)}>✕</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        );
      })}

      {items.length === 0 && (
        <div className="card" style={{padding:'32px',textAlign:'center'}}>
          <div className="text-sm text-muted">No output formats yet. Click <b>⚡ Add Standard Formats</b> to populate with {OUTPUT_FORMAT_SEED_FULL.length} specs across all platforms.</div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'620px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing ? '✏️ Edit Output Format' : '+ Add Output Format'}</div>
              <div className="icon-btn" onClick={() => setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Output Type</label>
                  <select className="form-select" value={form.outputType} onChange={e=>setForm({...form,outputType:e.target.value})}>
                    {getOutputTypeList().map(t => {
                      const info = getTypeInfo(t);
                      return <option key={t} value={t}>{info.icon||''} {t}</option>;
                    })}
                  </select>
                  <div className="text-xs text-muted" style={{marginTop:'4px'}}>From your Output Type master data</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Platform</label>
                  <input className="form-input" placeholder="e.g. Instagram, YouTube, Print / Offline" value={form.platform} onChange={e=>setForm({...form,platform:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Format Name</label>
                <input className="form-input" placeholder="e.g. Feed Post – Square, Story, Reel..." value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Width</label>
                  <input className="form-input" placeholder="e.g. 1080 px, 210 mm" value={form.width} onChange={e=>setForm({...form,width:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Height</label>
                  <input className="form-input" placeholder="e.g. 1920 px, 297 mm" value={form.height} onChange={e=>setForm({...form,height:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Aspect Ratio</label>
                  <input className="form-input" placeholder="e.g. 9:16, 1:1, 16:9" value={form.ratio} onChange={e=>setForm({...form,ratio:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Duration</label>
                  <input className="form-input" placeholder="e.g. Max 60s, 15–90s, —" value={form.duration} onChange={e=>setForm({...form,duration:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">File Format</label>
                  <input className="form-input" placeholder="e.g. JPG/PNG, MP4, PDF, GIF" value={form.fileFormat} onChange={e=>setForm({...form,fileFormat:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Max File Size</label>
                  <input className="form-input" placeholder="e.g. 30 MB, 100 MB, 150 KB" value={form.fileSize} onChange={e=>setForm({...form,fileSize:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Safe Zone</label>
                <input className="form-input" placeholder="e.g. Top/Bottom 250 px, 3mm bleed all sides, —" value={form.safeZone} onChange={e=>setForm({...form,safeZone:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" style={{minHeight:'60px'}} placeholder="Additional notes, tips or requirements..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.enabled?'enabled':'disabled'} onChange={e=>setForm({...form,enabled:e.target.value==='enabled'})}>
                  <option value="enabled">✅ Active</option>
                  <option value="disabled">⛔ Disabled</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════
// OUTPUT CONTENT TYPES TAB
// ══════════════════════════════════════════════════════════
const DEFAULT_OUTPUT_CONTENT_TYPES = [
  { icon:'🖼️',  name:'Image',           color:'#3b7ef0', desc:'Static image posts, banners, thumbnails, photos' },
  { icon:'🎬',  name:'Video',           color:'#e84545', desc:'Reels, stories, YouTube, ads, films' },
  { icon:'✨',  name:'Animation',       color:'#9050e8', desc:'GIFs, motion graphics, cinemagraphs, looping visuals' },
  { icon:'🎠',  name:'Carousel',        color:'#e87830', desc:'Multi-slide image series for social platforms' },
  { icon:'📱',  name:'Story',           color:'#1db87a', desc:'Vertical ephemeral content — Instagram, Facebook, WhatsApp' },
  { icon:'🎙️', name:'Audio',           color:'#f7c34f', desc:'Podcasts, voiceovers, jingles, sound bites' },
  { icon:'📄',  name:'PDF / Document',  color:'#6c58f0', desc:'Reports, presentations, content calendars, brochures' },
  { icon:'✍️', name:'Copy / Text',     color:'#18b8b8', desc:'Captions, blogs, scripts, ad copy, emails, WhatsApp messages' },
  { icon:'📡',  name:'Live',            color:'#e83898', desc:'Live streams, live events, Q&A sessions' },
  { icon:'🖨️', name:'Print',           color:'#6b7280', desc:'Flyers, posters, banners, business cards, hoardings' },
  { icon:'📊',  name:'Presentation',    color:'#3b7ef0', desc:'Pitch decks, client review decks, strategy presentations' },
  { icon:'📧',  name:'Email Template',  color:'#1db87a', desc:'Designed HTML email layouts for campaigns and newsletters' },
  { icon:'📦',  name:'Other',           color:'#9ba3b5', desc:'Custom or non-standard content types' },
];

const CT_ICON_OPTIONS = ['🖼️','🎬','✨','🎠','📱','🎙️','📄','✍️','📡','🖨️','📊','📧','📦','🎯','🔥','💡','🎨','🎤','📷','🎥','📝','🗂️','📌','⭐'];
const CT_COLOR_OPTIONS = ['#3b7ef0','#e84545','#9050e8','#e87830','#1db87a','#f7c34f','#6c58f0','#18b8b8','#e83898','#6b7280','#ff9900','#a82400','#5865f2'];

function OutputContentTypesTab() {
  const [items, setItems]     = useState([]);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [seeding, setSeeding] = useState(false);
  const emptyForm = { icon:'📦', name:'', color:'#3b7ef0', desc:'', enabled:true };
  const [form, setForm]       = useState(emptyForm);

  const load = useCallback(async () => {
    const r = await api.get('/content-types');
    setItems(r.data?.data || r.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    if (!window.confirm(`Add ${DEFAULT_OUTPUT_CONTENT_TYPES.length} standard output content types? Continue?`)) return;
    setSeeding(true);
    try {
      for (const t of DEFAULT_OUTPUT_CONTENT_TYPES) {
        await api.post('/content-types', { name:t.name, description:t.desc, icon:t.icon, color:t.color, enabled:true });
      }
      load();
    } finally { setSeeding(false); }
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ icon:item.icon||'📦', name:item.name||'', color:item.color||'#3b7ef0', desc:item.description||item.desc||'', enabled:item.enabled!==false });
    setModal(true);
  };
  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name:form.name, description:form.desc, icon:form.icon, color:form.color, enabled:form.enabled };
      if (editing) await api.put('/content-types/'+editing.id, payload);
      else         await api.post('/content-types', payload);
      setModal(false); load();
    } finally { setSaving(false); }
  };
  const del    = async (item) => { if(!window.confirm('Delete "'+item.name+'"?')) return; await api.delete('/content-types/'+item.id); load(); };
  const toggle = async (item) => { await api.put('/content-types/'+item.id, { name:item.name, description:item.description||'', icon:item.icon||'', color:item.color||'', enabled:!item.enabled }); load(); };

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🗂 Output Content Types</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Media type classification — used to tag Output Formats, Task Types and filter the workflow.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          {items.length === 0 && <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>{seeding?'Adding...':'⚡ Add Standard Types'}</button>}
          <button className="btn btn-primary" onClick={openAdd}>+ Add Type</button>
        </div>
      </div>

      {/* Cards grid */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:'12px'}}>
        {items.map(item => (
          <div key={item.id} className="card" style={{borderLeft:`4px solid ${item.color||'#888'}`, opacity:item.enabled===false?0.45:1, padding:'14px 16px'}}>
            <div className="flex-between">
              <div className="flex" style={{gap:'10px',alignItems:'center'}}>
                <span style={{fontSize:'1.6rem',lineHeight:1}}>{item.icon||'📦'}</span>
                <div>
                  <div className="fw-700">
                    {item.name}
                    {item.enabled===false && <span className="badge badge-gray" style={{fontSize:'0.6rem',marginLeft:'6px'}}>Off</span>}
                  </div>
                  {(item.description||item.desc) && <div className="text-xs text-muted" style={{marginTop:'2px',maxWidth:'160px'}}>{item.description||item.desc}</div>}
                </div>
              </div>
              <div className="flex" style={{gap:'4px',alignItems:'center',flexShrink:0}}>
                <Toggle checked={item.enabled!==false} onChange={()=>toggle(item)} />
                <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(item)}>✏️</button>
                <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(item)}>✕</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-sm text-muted" style={{padding:'20px',gridColumn:'1/-1',textAlign:'center'}}>
            No output content types yet. Click <b>⚡ Add Standard Types</b> to add {DEFAULT_OUTPUT_CONTENT_TYPES.length} defaults.
          </div>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'480px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Output Content Type':'+ Add Output Content Type'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="form-input" placeholder="e.g. Video, Image, Animation..." value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Icon</label>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'5px',padding:'8px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',marginBottom:'6px'}}>
                    {CT_ICON_OPTIONS.map(ic => (
                      <span key={ic} onClick={()=>setForm({...form,icon:ic})} style={{fontSize:'1.3rem',cursor:'pointer',padding:'3px 5px',borderRadius:'5px',background:form.icon===ic?'var(--accent)22':'transparent',border:form.icon===ic?'2px solid var(--accent)':'2px solid transparent'}}>{ic}</span>
                    ))}
                  </div>
                  <input className="form-input" style={{width:'80px'}} placeholder="emoji" value={form.icon} onChange={e=>setForm({...form,icon:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginTop:'4px'}}>
                  {CT_COLOR_OPTIONS.map(c => (
                    <div key={c} onClick={()=>setForm({...form,color:c})} style={{width:'24px',height:'24px',borderRadius:'50%',background:c,cursor:'pointer',border:form.color===c?'3px solid var(--text)':'2px solid transparent'}} />
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" style={{minHeight:'60px'}} placeholder="What this content type covers..." value={form.desc} onChange={e=>setForm({...form,desc:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// SERVICES TAB
// ══════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════
// SERVICES TAB — with structured deliverables picker
// ══════════════════════════════════════════════════════════
const DEFAULT_SERVICES = [
  { name:'Social Media Management',        category:'Social Media',         desc:'End-to-end social media content creation, scheduling and community management.', deliverables:[{item:'Static Post',qty:8},{item:'Reel / Short Video',qty:4},{item:'Story',qty:8},{item:'Caption & Hashtags',qty:12},{item:'Community Management',qty:1},{item:'Monthly Report',qty:1}], monthly_price:35000, setup_fee:10000 },
  { name:'Instagram Growth Package',       category:'Social Media',         desc:'Instagram-focused content, reels and engagement strategy.', deliverables:[{item:'Static Post',qty:12},{item:'Reel / Short Video',qty:8},{item:'Story',qty:8},{item:'Hashtag Strategy',qty:1},{item:'Monthly Report',qty:1}], monthly_price:25000, setup_fee:8000 },
  { name:'LinkedIn B2B Growth',            category:'Social Media',         desc:'LinkedIn profile optimisation, thought leadership and lead generation.', deliverables:[{item:'LinkedIn Post',qty:12},{item:'Long-form Article',qty:2},{item:'Profile Audit',qty:1},{item:'Monthly Report',qty:1}], monthly_price:18000, setup_fee:5000 },
  { name:'YouTube Channel Management',     category:'Video Marketing',      desc:'YouTube video production, optimisation and channel growth.', deliverables:[{item:'Edited YouTube Video',qty:4},{item:'Custom Thumbnail',qty:4},{item:'SEO Optimisation',qty:4},{item:'Channel Analytics Report',qty:1}], monthly_price:40000, setup_fee:15000 },
  { name:'Short Form Video Package',       category:'Video Marketing',      desc:'Reels, Shorts and TikTok-style content for all platforms.', deliverables:[{item:'Reel / Short Video',qty:8},{item:'Caption & Hashtags',qty:8},{item:'Monthly Report',qty:1}], monthly_price:30000, setup_fee:8000 },
  { name:'Performance Marketing – Meta',   category:'Performance Marketing',desc:'Paid ad campaigns on Facebook and Instagram with creative and optimisation.', deliverables:[{item:'Campaign Setup',qty:1},{item:'Ad Creative',qty:10},{item:'Weekly Optimisation Report',qty:4},{item:'Monthly Report',qty:1}], monthly_price:25000, setup_fee:8000 },
  { name:'Performance Marketing – Google', category:'Performance Marketing',desc:'Google Search, Display and YouTube ad campaigns.', deliverables:[{item:'Campaign Setup',qty:1},{item:'Ad Copy Variants',qty:5},{item:'Keyword Management',qty:1},{item:'Monthly Report',qty:1}], monthly_price:22000, setup_fee:7000 },
  { name:'Full Funnel Paid Ads',           category:'Performance Marketing',desc:'Complete paid media strategy across Google and Meta.', deliverables:[{item:'Multichannel Campaign',qty:1},{item:'Ad Creative',qty:15},{item:'A/B Testing Report',qty:2},{item:'Weekly Report',qty:4}], monthly_price:45000, setup_fee:15000 },
  { name:'SEO Package',                    category:'SEO',                  desc:'On-page SEO, content marketing and link building.', deliverables:[{item:'Blog Post / Article',qty:10},{item:'Keyword Research Report',qty:1},{item:'On-page Optimisation',qty:1},{item:'Backlinks',qty:5},{item:'Monthly Ranking Report',qty:1}], monthly_price:20000, setup_fee:5000 },
  { name:'Content Marketing',              category:'Content',              desc:'Blog posts, articles and long-form content for brand authority.', deliverables:[{item:'Blog Post / Article',qty:8},{item:'SEO Optimisation',qty:8},{item:'Social Distribution',qty:8},{item:'Monthly Report',qty:1}], monthly_price:18000, setup_fee:5000 },
  { name:'Email Marketing',               category:'Email Marketing',      desc:'Email campaign design, copywriting and list management.', deliverables:[{item:'Email Campaign',qty:4},{item:'Email Template Design',qty:1},{item:'List Segmentation',qty:1},{item:'Performance Report',qty:1}], monthly_price:15000, setup_fee:5000 },
  { name:'WhatsApp Marketing',            category:'Messaging',            desc:'WhatsApp broadcast campaigns, automation and engagement.', deliverables:[{item:'Broadcast Message',qty:8},{item:'Template Design',qty:2},{item:'Automation Setup',qty:1},{item:'Engagement Report',qty:1}], monthly_price:12000, setup_fee:5000 },
  { name:'Graphic Design Retainer',       category:'Design',               desc:'Monthly creative design for all digital and print needs.', deliverables:[{item:'Design Asset',qty:20},{item:'Revision Round',qty:2},{item:'Source File',qty:20}], monthly_price:20000, setup_fee:5000 },
  { name:'Brand Identity Package',        category:'Design',               desc:'Complete brand identity — logo, colours, typography, brand guidelines.', deliverables:[{item:'Logo Design',qty:3},{item:'Brand Guidelines PDF',qty:1},{item:'Social Media Template',qty:5},{item:'Business Card Design',qty:1}], monthly_price:0, setup_fee:50000 },
  { name:'Digital Marketing Strategy',    category:'Strategy',             desc:'Monthly digital marketing strategy, planning and consultation.', deliverables:[{item:'Monthly Strategy Document',qty:1},{item:'Content Calendar',qty:1},{item:'Competitor Analysis',qty:1},{item:'Review Call',qty:1}], monthly_price:15000, setup_fee:10000 },
  { name:'Monthly Performance Report',    category:'Reporting',            desc:'Detailed monthly reporting across all channels.', deliverables:[{item:'Performance Report',qty:1},{item:'KPI Dashboard',qty:1},{item:'Presentation Call',qty:1}], monthly_price:8000, setup_fee:0 },
  { name:'E-Commerce Marketing',          category:'E-Commerce',           desc:'Full digital marketing for e-commerce brands.', deliverables:[{item:'Paid Campaigns',qty:2},{item:'Product Listing Optimisation',qty:1},{item:'Static Post',qty:12},{item:'Monthly Report',qty:1}], monthly_price:50000, setup_fee:15000 },
  { name:'Influencer Marketing',          category:'Influencer & PR',      desc:'Influencer identification, campaign management and ROI tracking.', deliverables:[{item:'Influencer Collaboration',qty:5},{item:'Content Brief',qty:5},{item:'Performance Report',qty:1}], monthly_price:30000, setup_fee:10000 },
];

const SERVICE_CATEGORIES = ['Social Media','Video Marketing','Performance Marketing','SEO','Content','Email Marketing','Messaging','Design','Strategy','Reporting','E-Commerce','Influencer & PR','Other'];
const CATEGORY_COLORS = { 'Social Media':'#3b7ef0','Video Marketing':'#e84545','Performance Marketing':'#e87830','SEO':'#1db87a','Content':'#6c58f0','Email Marketing':'#f7c34f','Messaging':'#25d366','Design':'#9050e8','Strategy':'#18b8b8','Reporting':'#6b7280','E-Commerce':'#ff9900','Influencer & PR':'#e83898','Other':'#888' };

// Standard deliverable line items for picker
const DELIVERABLE_ITEMS = [
  'Static Post','Carousel Post','Reel / Short Video','Story','YouTube Video','Short Film / TVC',
  'Custom Thumbnail','GIF / Animation','Ad Creative','Banner Design','Infographic',
  'Blog Post / Article','Long-form Article','Email Campaign','Newsletter',
  'LinkedIn Post','WhatsApp Broadcast Message','Caption & Hashtags','Script / Copywriting',
  'Hashtag Strategy','Content Calendar','Campaign Setup','Keyword Research Report',
  'On-page Optimisation','Backlinks','SEO Audit','Technical SEO Fix',
  'Community Management','Competitor Analysis','Monthly Report','Weekly Report',
  'Performance Report','Presentation Deck','KPI Dashboard','Review Call',
  'Brand Guidelines PDF','Logo Design','Social Media Template','Business Card Design',
  'Source File','Revision Round','Ad Copy Variants','A/B Testing Report',
  'Influencer Collaboration','Content Brief','Multichannel Campaign',
  'Email Template Design','List Segmentation','Automation Setup','Engagement Report',
  'Profile Audit','Channel Analytics Report','Product Listing Optimisation',
  'Weekly Optimisation Report','Campaign End Report','Strategy Document',
];

function ServicesTab() {
  const [items,     setItems]     = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [seeding,   setSeeding]   = useState(false);
  const [filterCat, setFilterCat] = useState('All');
  const [delivModal, setDelivModal] = useState(false);
  const [delivSearch, setDelivSearch] = useState('');

  const emptyForm = { name:'', category:'Social Media', desc:'', platforms:[], deliverables:[], monthly_price:'', setup_fee:'', contract_duration:'', tier:'Standard', enabled:true };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([api.get('/services'), api.get('/platforms')]);
    setItems(r.data?.data || r.data || []);
    setPlatforms(p.data?.data || p.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Parse deliverables from various storage formats
  const parseDeliverables = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch(e) {}
      // Legacy newline format → convert
      return raw.split('\n').map(s=>s.trim()).filter(Boolean).map(s => {
        const match = s.match(/^(\d+)\s*[x×]\s*(.+)$/i);
        if (match) return { item: match[2].trim(), qty: parseInt(match[1]) };
        const match2 = s.match(/^(.+)\s*[x×]\s*(\d+)$/i);
        if (match2) return { item: match2[1].trim(), qty: parseInt(match2[2]) };
        return { item: s, qty: 1 };
      });
    }
    return [];
  };

  const seedDefaults = async () => {
    if (!window.confirm(`Add ${DEFAULT_SERVICES.length} standard service packages? Continue?`)) return;
    setSeeding(true);
    try {
      let added=0, failed=0;
      for (const s of DEFAULT_SERVICES) {
        try {
          await api.post('/services', { name:s.name, category:s.category, description:s.desc, monthly_price:s.monthly_price, setup_fee:s.setup_fee, deliverables:JSON.stringify(s.deliverables), enabled:true });
          added++;
        } catch(e) { failed++; }
      }
      if (failed) alert(`${added} added, ${failed} failed.`);
      load();
    } finally { setSeeding(false); }
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (item) => {
    setEditing(item);
    let plats = item.platforms;
    if (typeof plats === 'string') { try { plats = JSON.parse(plats); } catch(e) { plats=[]; } }
    setForm({
      name: item.name||'', category: item.category||'Social Media',
      desc: item.description||item.desc||'',
      platforms: Array.isArray(plats)?plats:[],
      deliverables: parseDeliverables(item.deliverables),
      monthly_price: item.monthly_price||'', setup_fee: item.setup_fee||'',
      contract_duration: item.contract_duration||'',
      tier: item.tier||'Standard',
      enabled: item.enabled!==false,
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name:form.name, category:form.category, description:form.desc, deliverables:JSON.stringify(form.deliverables), monthly_price:parseFloat(form.monthly_price)||0, setup_fee:parseFloat(form.setup_fee)||0, contract_duration:form.contract_duration, tier:form.tier, enabled:form.enabled };
      if (editing) await api.put('/services/'+editing.id, payload);
      else         await api.post('/services', payload);
      setModal(false); load();
    } finally { setSaving(false); }
  };

  const del    = async (item) => { if(!window.confirm('Delete "'+item.name+'"?')) return; await api.delete('/services/'+item.id); load(); };
  const toggle = async (item) => { await api.put('/services/'+item.id, { name:item.name, category:item.category, monthly_price:item.monthly_price||0, setup_fee:item.setup_fee||0, enabled:!item.enabled }); load(); };
  const togglePlatform = (name) => setForm(f=>({...f, platforms:f.platforms.includes(name)?f.platforms.filter(p=>p!==name):[...f.platforms,name]}));

  const addDeliverable  = (itemName) => { setForm(f=>({...f, deliverables:[...f.deliverables,{item:itemName,qty:1}]})); };
  const removeDeliv     = (idx) => setForm(f=>({...f, deliverables:f.deliverables.filter((_,i)=>i!==idx)}));
  const updateDelivQty  = (idx,qty) => setForm(f=>({...f, deliverables:f.deliverables.map((d,i)=>i===idx?{...d,qty:parseInt(qty)||1}:d)}));
  const updateDelivItem = (idx,val) => setForm(f=>({...f, deliverables:f.deliverables.map((d,i)=>i===idx?{...d,item:val}:d)}));

  const allCats  = ['All', ...new Set(items.map(i=>i.category).filter(Boolean))];
  const filtered = filterCat==='All' ? items : items.filter(i=>i.category===filterCat);
  const activeItems  = items.filter(i=>i.enabled!==false);
  const totalMRR     = activeItems.reduce((s,i)=>s+(parseFloat(i.monthly_price)||0),0);

  const filtDelivItems = delivSearch ? DELIVERABLE_ITEMS.filter(d=>d.toLowerCase().includes(delivSearch.toLowerCase())) : DELIVERABLE_ITEMS;

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">📦 Services & Packages</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Define what your agency offers — service packages, pricing and deliverables.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>{seeding?'Adding...':'⚡ Add Standard Services'}</button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Service</button>
        </div>
      </div>

      {/* KPI */}
      {items.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:'10px',marginBottom:'16px'}}>
          <div className="card" style={{padding:'12px 16px',borderTop:'3px solid var(--accent)'}}>
            <div className="text-xs text-muted fw-700" style={{textTransform:'uppercase',letterSpacing:'0.5px'}}>Active Services</div>
            <div style={{fontSize:'1.6rem',fontWeight:800,fontFamily:"'Fraunces',serif",marginTop:'4px'}}>{activeItems.length}</div>
          </div>
          <div className="card" style={{padding:'12px 16px',borderTop:'3px solid #1db87a'}}>
            <div className="text-xs text-muted fw-700" style={{textTransform:'uppercase',letterSpacing:'0.5px'}}>Potential MRR</div>
            <div style={{fontSize:'1.2rem',fontWeight:800,color:'#1db87a',marginTop:'4px'}}>{fmtRupees(totalMRR)}</div>
            <div className="text-xs text-muted">if all sold</div>
          </div>
        </div>
      )}

      {/* Category filters */}
      <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'14px'}}>
        {allCats.map(cat => {
          const count  = cat==='All'?items.length:items.filter(i=>i.category===cat).length;
          const active = filterCat===cat;
          const color  = CATEGORY_COLORS[cat]||'var(--accent)';
          return (
            <button key={cat} onClick={()=>setFilterCat(cat)} style={{padding:'4px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',border:`2px solid ${active?color:'var(--border)'}`,background:active?color:'var(--bg2)',color:active?'#fff':'var(--text2)'}}>
              {cat} ({count})
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Service Name</th><th>Category</th><th>Deliverables</th><th>Monthly Price</th><th>Setup Fee</th><th>Status / Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length===0 && (
              <tr><td colSpan={6} style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">
                {items.length===0?<>No services yet. Click <b>⚡ Add Standard Services</b>.</>:'No services in this category.'}
              </td></tr>
            )}
            {filtered.map(item => {
              const delivs = parseDeliverables(item.deliverables);
              const color  = CATEGORY_COLORS[item.category]||'#888';
              return (
                <tr key={item.id} style={{opacity:item.enabled===false?0.45:1}}>
                  <td>
                    <div className="fw-700 text-sm">{item.name}</div>
                    {(item.description||item.desc)&&<div className="text-xs text-muted" style={{marginTop:'2px',maxWidth:'220px'}}>{item.description||item.desc}</div>}
                  </td>
                  <td><span className="badge" style={{background:`${color}18`,color,border:`1px solid ${color}40`,fontSize:'0.7rem'}}>{item.category||'—'}</span></td>
                  <td>
                    {delivs.length>0?(
                      <div style={{display:'flex',flexWrap:'wrap',gap:'3px',maxWidth:'200px'}}>
                        {delivs.slice(0,4).map((d,i)=>(
                          <span key={i} className="badge badge-gray" style={{fontSize:'0.65rem'}}>{d.qty}× {d.item}</span>
                        ))}
                        {delivs.length>4&&<span className="badge badge-gray" style={{fontSize:'0.65rem'}}>+{delivs.length-4} more</span>}
                      </div>
                    ):<span className="text-xs text-muted">—</span>}
                  </td>
                  <td><span className="mono fw-700" style={{color:'#1db87a'}}>{fmtRupees(item.monthly_price)}</span>{item.monthly_price>0&&<span className="text-xs text-muted">/mo</span>}</td>
                  <td className="mono text-muted text-sm">{fmtRupees(item.setup_fee)}</td>
                  <td>
                    <div className="flex" style={{gap:'4px',alignItems:'center'}}>
                      <Toggle checked={item.enabled!==false} onChange={()=>toggle(item)} />
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(item)}>✏️</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(item)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Add/Edit Modal ── */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'640px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Service':'+ Add Service Package'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Service Name</label>
                  <input className="form-input" placeholder="e.g. Social Media Management" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                    {SERVICE_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" style={{minHeight:'50px'}} value={form.desc} onChange={e=>setForm({...form,desc:e.target.value})} />
              </div>

              {/* Deliverables structured picker */}
              <div className="form-group">
                <label className="form-label">
                  📋 Deliverables
                  <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none',marginLeft:'6px'}}>({form.deliverables.length} items)</span>
                </label>

                {/* Selected deliverables */}
                {form.deliverables.length>0 && (
                  <div style={{border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',marginBottom:'8px',overflow:'hidden'}}>
                    {form.deliverables.map((d,idx)=>(
                      <div key={idx} style={{display:'grid',gridTemplateColumns:'1fr 80px 32px',gap:'8px',alignItems:'center',padding:'6px 10px',borderBottom:idx<form.deliverables.length-1?'1px solid var(--border)':'none',background:'var(--bg2)'}}>
                        <input className="form-input" style={{fontSize:'0.8rem',padding:'4px 8px'}} value={d.item} onChange={e=>updateDelivItem(idx,e.target.value)} />
                        <div className="flex" style={{gap:'4px',alignItems:'center'}}>
                          <button onClick={()=>updateDelivQty(idx,Math.max(1,(d.qty||1)-1))} style={{width:'20px',height:'20px',borderRadius:'4px',border:'1px solid var(--border)',background:'var(--bg3)',cursor:'pointer',fontSize:'0.75rem',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>−</button>
                          <input type="number" min="1" className="form-input" style={{width:'36px',textAlign:'center',padding:'4px 2px',fontSize:'0.8rem'}} value={d.qty} onChange={e=>updateDelivQty(idx,e.target.value)} />
                          <button onClick={()=>updateDelivQty(idx,(d.qty||1)+1)} style={{width:'20px',height:'20px',borderRadius:'4px',border:'1px solid var(--border)',background:'var(--bg3)',cursor:'pointer',fontSize:'0.75rem',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>+</button>
                        </div>
                        <button onClick={()=>removeDeliv(idx)} style={{width:'24px',height:'24px',borderRadius:'4px',border:'none',background:'rgba(232,69,69,0.1)',color:'#e84545',cursor:'pointer',fontSize:'0.85rem',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add deliverable button */}
                <button className="btn btn-secondary" style={{width:'100%',justifyContent:'center',fontSize:'0.78rem'}} onClick={()=>setDelivModal(true)}>
                  + Add Deliverable
                </button>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Monthly Price (₹)</label>
                  <input className="form-input" type="number" placeholder="0" value={form.monthly_price} onChange={e=>setForm({...form,monthly_price:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Setup Fee (₹)</label>
                  <input className="form-input" type="number" placeholder="0" value={form.setup_fee} onChange={e=>setForm({...form,setup_fee:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contract Duration</label>
                  <select className="form-select" value={form.contract_duration} onChange={e=>setForm({...form,contract_duration:e.target.value})}>
                    <option value="">— Ongoing —</option>
                    {['1 Month','3 Months','6 Months','12 Months','24 Months'].map(d=><option key={d}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Service Tier</label>
                  <select className="form-select" value={form.tier} onChange={e=>setForm({...form,tier:e.target.value})}>
                    {['Basic','Standard','Premium','Enterprise'].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.enabled?'enabled':'disabled'} onChange={e=>setForm({...form,enabled:e.target.value==='enabled'})}>
                  <option value="enabled">✅ Active</option>
                  <option value="disabled">⛔ Disabled</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Service'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Deliverable Picker Modal ── */}
      {delivModal && (
        <div className="modal-overlay open" style={{zIndex:1100}} onClick={e=>e.target===e.currentTarget&&setDelivModal(false)}>
          <div className="modal" style={{maxWidth:'480px',zIndex:1101}}>
            <div className="modal-header">
              <div className="modal-title">+ Add Deliverable</div>
              <div className="icon-btn" onClick={()=>setDelivModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <input className="form-input mb-10" placeholder="Search deliverables..." value={delivSearch} onChange={e=>setDelivSearch(e.target.value)} autoFocus />
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px',maxHeight:'300px',overflowY:'auto'}}>
                {filtDelivItems.map(d=>{
                  const already = form.deliverables.some(x=>x.item===d);
                  return (
                    <div key={d} onClick={()=>{ if(!already){addDeliverable(d);} setDelivModal(false); setDelivSearch(''); }}
                      style={{padding:'6px 10px',borderRadius:'6px',fontSize:'0.78rem',cursor:already?'default':'pointer',
                        background:already?'var(--bg3)':'var(--bg2)',border:'1px solid var(--border)',
                        color:already?'var(--text3)':'var(--text)',opacity:already?0.5:1,
                        display:'flex',alignItems:'center',gap:'6px'}}>
                      {already?'✓':'+'}  {d}
                    </div>
                  );
                })}
              </div>
              <div className="text-xs text-muted" style={{marginTop:'8px'}}>Or type a custom deliverable name in the search box and press Enter to add it.</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setDelivModal(false)}>Done</button>
              {delivSearch && !DELIVERABLE_ITEMS.includes(delivSearch) && (
                <button className="btn btn-primary" onClick={()=>{ addDeliverable(delivSearch); setDelivModal(false); setDelivSearch(''); }}>+ Add "{delivSearch}"</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// RATE CARD TAB
// ══════════════════════════════════════════════════════════
const RATE_UNITS = ['Per Post','Per Reel / Video','Per Story','Per Design','Per Article','Per Email','Per Hour','Per Day','Per Month','Per Campaign','Per Project','Per Platform','Per Report','Per Ad Creative','Per Influencer'];
const RATE_CATEGORIES = ['Content Creation','Design & Creative','Video Production','Paid Ads','SEO & Content','Social Media Management','Strategy & Consulting','Reporting','Technology & Tools','Other'];
const RATE_CATEGORY_COLORS = { 'Content Creation':'#6c58f0','Design & Creative':'#9050e8','Video Production':'#e84545','Paid Ads':'#e87830','SEO & Content':'#1db87a','Social Media Management':'#3b7ef0','Strategy & Consulting':'#18b8b8','Reporting':'#6b7280','Technology & Tools':'#f7c34f','Other':'#888' };

const DEFAULT_RATE_CARDS = [
  // Content Creation
  { name:'Social Media Caption (per post)',      category:'Content Creation',       unit:'Per Post',         rate:400,  min_rate:300,  max_rate:600  },
  { name:'Long-form Blog / Article',             category:'Content Creation',       unit:'Per Article',      rate:2000, min_rate:1500, max_rate:4000 },
  { name:'Video Script',                         category:'Content Creation',       unit:'Per Script',       rate:1500, min_rate:1000, max_rate:3000 },
  { name:'Ad Copywriting (per variant)',         category:'Content Creation',       unit:'Per Ad Creative',  rate:800,  min_rate:500,  max_rate:1500 },
  // Design & Creative
  { name:'Static Post Design',                   category:'Design & Creative',      unit:'Per Design',       rate:800,  min_rate:600,  max_rate:1500 },
  { name:'Carousel Design (per slide set)',       category:'Design & Creative',      unit:'Per Design',       rate:1500, min_rate:1000, max_rate:3000 },
  { name:'Story / Reel Cover Design',            category:'Design & Creative',      unit:'Per Design',       rate:500,  min_rate:300,  max_rate:800  },
  { name:'Banner / Display Ad Design',           category:'Design & Creative',      unit:'Per Design',       rate:1000, min_rate:700,  max_rate:2000 },
  { name:'Infographic Design',                   category:'Design & Creative',      unit:'Per Design',       rate:3000, min_rate:2000, max_rate:5000 },
  // Video Production
  { name:'Reel Editing (per reel)',              category:'Video Production',       unit:'Per Reel / Video', rate:2500, min_rate:1500, max_rate:5000 },
  { name:'YouTube Video Editing',                category:'Video Production',       unit:'Per Reel / Video', rate:5000, min_rate:3000, max_rate:10000},
  { name:'Motion Graphics / Animation',          category:'Video Production',       unit:'Per Reel / Video', rate:8000, min_rate:5000, max_rate:15000},
  // Paid Ads
  { name:'Meta Ads Campaign Setup',              category:'Paid Ads',               unit:'Per Campaign',     rate:8000, min_rate:5000, max_rate:15000},
  { name:'Google Ads Campaign Setup',            category:'Paid Ads',               unit:'Per Campaign',     rate:8000, min_rate:5000, max_rate:15000},
  { name:'Paid Ads Monthly Management',          category:'Paid Ads',               unit:'Per Month',        rate:20000,min_rate:15000,max_rate:40000},
  // SEO & Content
  { name:'On-page SEO Optimisation (per page)',  category:'SEO & Content',          unit:'Per Post',         rate:2000, min_rate:1500, max_rate:4000 },
  { name:'Technical SEO Audit',                  category:'SEO & Content',          unit:'Per Project',      rate:15000,min_rate:10000,max_rate:25000},
  { name:'Link Building (per backlink)',         category:'SEO & Content',          unit:'Per Post',         rate:1500, min_rate:800,  max_rate:3000 },
  // Social Media Management
  { name:'Platform Management (per platform)',   category:'Social Media Management', unit:'Per Month',       rate:8000, min_rate:5000, max_rate:15000},
  { name:'Community Management',                 category:'Social Media Management', unit:'Per Month',       rate:6000, min_rate:4000, max_rate:10000},
  // Strategy & Consulting
  { name:'Strategy Consultation',               category:'Strategy & Consulting',  unit:'Per Hour',         rate:3000, min_rate:2000, max_rate:5000 },
  { name:'Digital Marketing Strategy',          category:'Strategy & Consulting',  unit:'Per Month',        rate:15000,min_rate:10000,max_rate:25000},
  // Reporting
  { name:'Monthly Performance Report',          category:'Reporting',              unit:'Per Report',       rate:5000, min_rate:3000, max_rate:8000 },
  { name:'Weekly Update Report',                category:'Reporting',              unit:'Per Report',       rate:2000, min_rate:1000, max_rate:3000 },
];

function RateCardTab() {
  const [rates,     setRates]     = useState([]);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [seeding,   setSeeding]   = useState(false);
  const [filterCat, setFilterCat] = useState('All');

  const emptyForm = { name:'', category:'Content Creation', unit:'Per Post', rate:'', min_rate:'', max_rate:'', currency:'INR', client_override:'', effective_date:'', notes:'' };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const r = await api.get('/rate-cards');
    setRates(r.data?.data || r.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    if (!window.confirm(`Add ${DEFAULT_RATE_CARDS.length} standard rate card items? Continue?`)) return;
    setSeeding(true);
    try {
      let added=0, failed=0;
      for (const r of DEFAULT_RATE_CARDS) {
        try { await api.post('/rate-cards', { service:r.name, category:r.category, unit:r.unit, rate:r.rate, min_rate:r.min_rate, max_rate:r.max_rate, notes:r.notes||'' }); added++; } catch(e) { failed++; }
      }
      if (failed) alert(`${added} added, ${failed} failed.`);
      load();
    } finally { setSeeding(false); }
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name:r.name||r.service||'', category:r.category||'Content Creation', unit:r.unit||'Per Post', rate:r.rate||'', min_rate:r.min_rate||'', max_rate:r.max_rate||'', currency:r.currency||'INR', client_override:r.client_override||'', effective_date:r.effective_date||'', notes:r.notes||'' });
    setModal(true);
  };
  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { service:form.name, category:form.category, unit:form.unit, rate:parseFloat(form.rate)||0, min_rate:parseFloat(form.min_rate)||0, max_rate:parseFloat(form.max_rate)||0, currency:form.currency||'INR', client_override:form.client_override, effective_date:form.effective_date||null, notes:form.notes };
      if (editing) await api.put('/rate-cards/'+editing.id, payload);
      else         await api.post('/rate-cards', payload);
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };
  const del = async (r) => { if(!window.confirm('Delete "'+r.name+'"?')) return; await api.delete('/rate-cards/'+r.id); load(); };

  const allCats  = ['All',...new Set(rates.map(r=>r.category).filter(Boolean))];
  const filtered = filterCat==='All' ? rates : rates.filter(r=>r.category===filterCat);
  const grouped  = filtered.reduce((acc,r)=>{ const k=r.category||'Other'; if(!acc[k]) acc[k]=[]; acc[k].push(r); return acc; },{});
  const totalStd = rates.reduce((s,r)=>s+(parseFloat(r.rate)||0),0);

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">💸 Rate Card</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Standard unit rates for all services and tasks — used for cost estimation, proposals and billing.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>{seeding?'Adding...':'⚡ Add Standard Rates'}</button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Rate</button>
        </div>
      </div>

      {/* Category filters */}
      <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'14px'}}>
        {allCats.map(cat => {
          const count  = cat==='All'?rates.length:rates.filter(r=>r.category===cat).length;
          const active = filterCat===cat;
          const color  = RATE_CATEGORY_COLORS[cat]||'var(--accent)';
          return (
            <button key={cat} onClick={()=>setFilterCat(cat)} style={{padding:'4px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',border:`2px solid ${active?color:'var(--border)'}`,background:active?color:'var(--bg2)',color:active?'#fff':'var(--text2)'}}>
              {cat} ({count})
            </button>
          );
        })}
      </div>

      {/* Grouped tables */}
      {Object.entries(grouped).map(([catName, rows]) => {
        const color = RATE_CATEGORY_COLORS[catName]||'#888';
        return (
          <div key={catName} className="card mb-14">
            <div style={{padding:'8px 16px',borderBottom:'1px solid var(--border)',background:'var(--bg3)',borderRadius:'var(--radius) var(--radius) 0 0',display:'flex',alignItems:'center',gap:'8px'}}>
              <div style={{width:'10px',height:'10px',borderRadius:'50%',background:color,flexShrink:0}} />
              <span className="fw-700 text-sm">{catName}</span>
              <span className="badge badge-gray" style={{fontSize:'0.65rem'}}>{rows.length} item{rows.length!==1?'s':''}</span>
            </div>
            <table className="data-table">
              <thead>
                <tr><th>Service / Task</th><th>Unit</th><th>Standard Rate</th><th>Min Rate</th><th>Max Rate</th><th>Notes</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {rows.map(r=>(
                  <tr key={r.id}>
                    <td className="fw-700 text-sm">{r.name||r.service||'—'}</td>
                    <td><span className="badge badge-gray" style={{fontSize:'0.68rem'}}>{r.unit}</span></td>
                    <td><span className="mono fw-700" style={{color:'#1db87a',fontSize:'1rem'}}>{fmtRupees(r.rate)}</span></td>
                    <td className="mono text-sm text-muted">{r.min_rate?fmtRupees(r.min_rate):'—'}</td>
                    <td className="mono text-sm text-muted">{r.max_rate?fmtRupees(r.max_rate):'—'}</td>
                    <td className="text-xs text-muted" style={{maxWidth:'140px'}}>{r.notes||'—'}</td>
                    <td>
                      <div className="flex" style={{gap:'4px'}}>
                        <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(r)}>✏️</button>
                        <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(r)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {rates.length===0 && (
        <div className="card" style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">
          No rates yet. Click <b>⚡ Add Standard Rates</b> to populate with {DEFAULT_RATE_CARDS.length} industry-standard rates.
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'560px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Rate':'+ Add Rate'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Service / Task Name</label>
                <input className="form-input" placeholder="e.g. Reel Editing (per reel)" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                    {RATE_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Billing Unit</label>
                  <select className="form-select" value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})}>
                    {RATE_UNITS.map(u=><option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Standard Rate (₹)</label>
                  <input className="form-input" type="number" placeholder="0" value={form.rate} onChange={e=>setForm({...form,rate:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Min Rate (₹) <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(negotiable floor)</span></label>
                  <input className="form-input" type="number" placeholder="0" value={form.min_rate} onChange={e=>setForm({...form,min_rate:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Max Rate (₹) <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(premium ceiling)</span></label>
                  <input className="form-input" type="number" placeholder="0" value={form.max_rate} onChange={e=>setForm({...form,max_rate:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <select className="form-select" value={form.currency} onChange={e=>setForm({...form,currency:e.target.value})}>
                    {['INR','USD','AED','SGD','GBP','EUR'].map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Effective Date <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(rate valid from)</span></label>
                  <input className="form-input" type="date" value={form.effective_date} onChange={e=>setForm({...form,effective_date:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input className="form-input" placeholder="e.g. Includes 2 revision rounds" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Client-Specific Overrides <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(e.g. ABC Corp: ₹2000, XYZ Brand: ₹3500)</span></label>
                <textarea className="form-textarea" style={{minHeight:'56px'}} placeholder="e.g. ABC Corp: ₹2000 per post&#10;XYZ Brand: ₹3500 per reel" value={form.client_override} onChange={e=>setForm({...form,client_override:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Rate'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// INTERNAL APPROVERS TAB
// ══════════════════════════════════════════════════════════
function InternalApproversTab() {
  const [approvers,  setApprovers]  = useState([]);
  const [team,       setTeam]       = useState([]);
  const [taskTypes,  setTaskTypes]  = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [modal,      setModal]      = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);

  const PRIORITY_OPTS = [
    { value:'urgent', icon:'🔴', label:'Urgent' },
    { value:'high',   icon:'🟠', label:'High' },
    { value:'normal', icon:'🟡', label:'Normal' },
    { value:'low',    icon:'🟢', label:'Low' },
  ];

  const emptyForm = {
    member:'', backup:'', taskTypeIds:[], categories:[],
    priorities:['urgent','high','normal'], notes:'', enabled:true
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [a, t, tt, ct] = await Promise.all([
      api.get('/internal-approvers'),
      api.get('/team-members'),
      api.get('/task-types'),
      api.get('/content-types'),
    ]);
    setApprovers(a.data?.data || a.data || []);
    setTeam((t.data?.data || t.data || []).filter(m => !m.deleted_at && m.employment !== 'inactive'));
    setTaskTypes(tt.data?.data || tt.data || []);
    setContentTypes(ct.data?.data || ct.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (ia) => {
    setEditing(ia);
    let taskTypeIds = ia.task_type_ids || ia.taskTypeIds || [];
    if (typeof taskTypeIds === 'string') { try { taskTypeIds = JSON.parse(taskTypeIds); } catch(e) { taskTypeIds = []; } }
    let categories = ia.categories || [];
    if (typeof categories === 'string') { try { categories = JSON.parse(categories); } catch(e) { categories = []; } }
    let priorities = ia.priorities || [];
    if (typeof priorities === 'string') { try { priorities = JSON.parse(priorities); } catch(e) { priorities = ['urgent','high','normal']; } }
    setForm({
      member: ia.team_member || ia.member || '',
      backup: ia.backup_approver || ia.backup || '',
      taskTypeIds: taskTypeIds.map(String),
      categories,
      priorities,
      notes: ia.notes || '',
      enabled: ia.enabled !== false,
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.member) return;
    if (!form.priorities.length) { alert('Select at least one priority level'); return; }
    setSaving(true);
    try {
      const payload = {
        team_member:     form.member,
        backup_approver: form.backup,
        task_types:      JSON.stringify(form.taskTypeIds),
        priority_levels: JSON.stringify(form.priorities),
        enabled:         form.enabled,
        // Extended fields stored as JSON strings
        task_type_ids:   JSON.stringify(form.taskTypeIds),
        categories:      JSON.stringify(form.categories),
        priorities:      JSON.stringify(form.priorities),
        notes:           form.notes,
      };
      if (editing) await api.put('/internal-approvers/'+editing.id, payload);
      else         await api.post('/internal-approvers', payload);
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };

  const del    = async (ia) => { if(!window.confirm('Remove approver rule for "'+ia.team_member+'"?')) return; await api.delete('/internal-approvers/'+ia.id); load(); };
  const toggle = async (ia) => { await api.put('/internal-approvers/'+ia.id, { team_member:ia.team_member, enabled:!ia.enabled }); load(); };

  const toggleTaskType = (id) => setForm(f => ({ ...f, taskTypeIds: f.taskTypeIds.includes(String(id)) ? f.taskTypeIds.filter(x=>x!==String(id)) : [...f.taskTypeIds, String(id)] }));
  const toggleCategory = (name) => setForm(f => ({ ...f, categories: f.categories.includes(name) ? f.categories.filter(x=>x!==name) : [...f.categories, name] }));
  const togglePriority = (val)  => setForm(f => ({ ...f, priorities: f.priorities.includes(val) ? f.priorities.filter(x=>x!==val) : [...f.priorities, val] }));

  const getTaskTypeNames = (ia) => {
    let ids = ia.task_type_ids || ia.taskTypeIds || [];
    if (typeof ids === 'string') { try { ids = JSON.parse(ids); } catch(e) { ids=[]; } }
    if (!ids.length) return null;
    return ids.map(id => taskTypes.find(t=>String(t.id)===String(id))?.name).filter(Boolean);
  };

  const getCategories = (ia) => {
    let cats = ia.categories || [];
    if (typeof cats === 'string') { try { cats = JSON.parse(cats); } catch(e) { cats=[]; } }
    return cats;
  };

  const getPriorities = (ia) => {
    let p = ia.priorities || [];
    if (typeof p === 'string') { try { p = JSON.parse(p); } catch(e) { p=[]; } }
    if (!p.length) {
      const pl = ia.priority_levels || '[]';
      try { p = JSON.parse(pl); } catch(e) { p=[]; }
    }
    return p;
  };

  const prioIcon = { urgent:'🔴', high:'🟠', normal:'🟡', low:'🟢' };

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🔐 Internal Approvers</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>
            Define <b>who approves what</b> — maps team members to task types, content categories and priority levels.
            This auto-routes QA assignments in work orders.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Approver Rule</button>
      </div>

      {/* How it works info box */}
      {approvers.length === 0 && (
        <div className="card mb-16" style={{padding:'16px 18px',borderLeft:'4px solid var(--accent)',background:'rgba(59,126,240,0.04)'}}>
          <div className="fw-700 text-sm mb-8">💡 How Internal Approvers work</div>
          <div className="text-xs text-muted" style={{lineHeight:'1.7'}}>
            Your team members with the <b>Approver role</b> can approve work — but the system needs to know <b>which person approves which type of work</b>.<br/>
            <b>Example:</b> Creative Director approves all Video and Design tasks → Account Manager approves all client-facing Reports → MD approves all Urgent tasks.<br/>
            Set these rules here. The system uses them to auto-assign the right approver when a work order moves to an approval phase.
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {approvers.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'10px',marginBottom:'16px'}}>
          {approvers.map(ia => {
            const m = team.find(t => t.name === (ia.team_member||ia.member));
            const color = m?.color || 'var(--accent)';
            const ttNames = getTaskTypeNames(ia);
            const cats    = getCategories(ia);
            const prios   = getPriorities(ia);
            return (
              <div key={ia.id} className="card" style={{padding:'12px 14px',borderTop:`3px solid ${color}`,opacity:ia.enabled===false?0.45:1}}>
                <div className="flex" style={{gap:'8px',alignItems:'center',marginBottom:'6px'}}>
                  <div className="avatar" style={{width:'24px',height:'24px',fontSize:'0.6rem',background:color,flexShrink:0}}>{initials(ia.team_member||ia.member||'?')}</div>
                  <div className="fw-700 text-sm" style={{color}}>{ia.team_member||ia.member}</div>
                </div>
                <div className="text-xs text-muted">{ttNames?.length ? ttNames.length+' task type'+(ttNames.length!==1?'s':'') : '✅ All task types'}</div>
                <div className="text-xs text-muted">{cats?.length ? cats.length+' categor'+(cats.length!==1?'ies':'y') : '✅ All categories'}</div>
                <div className="text-xs text-muted" style={{marginTop:'4px'}}>
                  {prios.map(p=><span key={p}>{prioIcon[p]||''} </span>)}
                  {prios.join(', ')||'—'}
                </div>
                {(ia.backup_approver||ia.backup) && (
                  <div className="text-xs text-muted" style={{marginTop:'4px'}}>🔄 Backup: {ia.backup_approver||ia.backup}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Table */}
      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Team Member</th>
              <th>Role</th>
              <th>Approves Task Types</th>
              <th>Content Categories</th>
              <th>Priority Levels</th>
              <th>Backup Approver</th>
              <th>Status / Actions</th>
            </tr>
          </thead>
          <tbody>
            {approvers.length === 0 && (
              <tr><td colSpan={7} style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">
                No approver rules yet. Click <b>+ Add Approver Rule</b> to get started.
              </td></tr>
            )}
            {approvers.map(ia => {
              const m       = team.find(t => t.name===(ia.team_member||ia.member));
              const color   = m?.color || 'var(--accent)';
              const ttNames = getTaskTypeNames(ia);
              const cats    = getCategories(ia);
              const prios   = getPriorities(ia);
              return (
                <tr key={ia.id} style={{opacity:ia.enabled===false?0.4:1}}>
                  <td>
                    <div className="flex" style={{gap:'8px',alignItems:'center'}}>
                      <div className="avatar" style={{width:'28px',height:'28px',fontSize:'0.65rem',background:color,flexShrink:0}}>{initials(ia.team_member||ia.member||'?')}</div>
                      <div className="fw-700">{ia.team_member||ia.member}</div>
                    </div>
                  </td>
                  <td><span className="badge badge-gray" style={{fontSize:'0.68rem'}}>{m?.role||ia.role||'—'}</span></td>
                  <td>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'3px',maxWidth:'200px'}}>
                      {ttNames?.length
                        ? ttNames.map(n=><span key={n} className="badge badge-blue" style={{fontSize:'0.65rem'}}>{n}</span>)
                        : <span className="badge badge-green" style={{fontSize:'0.65rem'}}>✅ All Task Types</span>
                      }
                    </div>
                  </td>
                  <td>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'3px',maxWidth:'160px'}}>
                      {cats?.length
                        ? cats.map(c=><span key={c} className="badge badge-purple" style={{fontSize:'0.65rem'}}>{c}</span>)
                        : <span className="badge badge-green" style={{fontSize:'0.65rem'}}>✅ All Categories</span>
                      }
                    </div>
                  </td>
                  <td>
                    <div style={{display:'flex',gap:'3px',flexWrap:'wrap'}}>
                      {prios.map(p=><span key={p} className="badge badge-gray" style={{fontSize:'0.65rem'}}>{prioIcon[p]||''} {p}</span>)}
                      {!prios.length && <span className="text-xs text-muted">—</span>}
                    </div>
                  </td>
                  <td className="text-xs text-muted">{ia.backup_approver||ia.backup||'—'}</td>
                  <td>
                    <div className="flex" style={{gap:'4px',alignItems:'center'}}>
                      <Toggle checked={ia.enabled!==false} onChange={()=>toggle(ia)} />
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(ia)}>✏️</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(ia)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Modal ── */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'620px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Approver Rule':'+ Add Internal Approver Rule'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">

              {/* Member + Backup */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Team Member (Approver)</label>
                  <select className="form-select" value={form.member} onChange={e=>setForm({...form,member:e.target.value})}>
                    <option value="">— Select team member —</option>
                    {team.map(m => {
                      const av = m.availability==='available'?'🟢':m.availability==='busy'?'🟡':'🔴';
                      return <option key={m.id} value={m.name}>{av} {m.name} — {m.role||''}</option>;
                    })}
                  </select>
                  <div className="text-xs text-muted" style={{marginTop:'4px'}}>This person will be assigned as the approver for tasks matching the rules below.</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Backup Approver <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(if primary is unavailable)</span></label>
                  <select className="form-select" value={form.backup} onChange={e=>setForm({...form,backup:e.target.value})}>
                    <option value="">— None —</option>
                    {team.filter(m=>m.name!==form.member).map(m=><option key={m.id} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Task Types */}
              <div className="form-group">
                <label className="form-label">Approves Task Types <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(leave empty = approves ALL task types)</span></label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',maxHeight:'120px',overflowY:'auto'}}>
                  {taskTypes.filter(t=>t.enabled!==false).map(t=>(
                    <label key={t.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.75rem',cursor:'pointer',padding:'3px 8px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'6px',whiteSpace:'nowrap'}}>
                      <input type="checkbox" checked={form.taskTypeIds.includes(String(t.id))} onChange={()=>toggleTaskType(t.id)} style={{accentColor:'var(--accent)'}} />
                      {t.name}
                    </label>
                  ))}
                  {taskTypes.length===0 && <span className="text-xs text-muted">Add task types first in the Task Types tab.</span>}
                </div>
              </div>

              {/* Content Categories */}
              <div className="form-group">
                <label className="form-label">Content Categories <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(leave empty = approves ALL categories)</span></label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
                  {contentTypes.filter(c=>c.enabled!==false).map(c=>(
                    <label key={c.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.75rem',cursor:'pointer',padding:'3px 8px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'6px',whiteSpace:'nowrap'}}>
                      <input type="checkbox" checked={form.categories.includes(c.name)} onChange={()=>toggleCategory(c.name)} style={{accentColor:'var(--accent)'}} />
                      {c.icon||''} {c.name}
                    </label>
                  ))}
                  {contentTypes.length===0 && <span className="text-xs text-muted">Add output types first in the Output Type tab.</span>}
                </div>
              </div>

              {/* Priority Levels */}
              <div className="form-group">
                <label className="form-label">Priority Levels <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(select which priorities this approver handles)</span></label>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'4px'}}>
                  {PRIORITY_OPTS.map(p=>(
                    <label key={p.value} style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'6px 12px',borderRadius:'8px',cursor:'pointer',
                      border:`2px solid ${form.priorities.includes(p.value)?'var(--accent)':'var(--border)'}`,
                      background:form.priorities.includes(p.value)?'rgba(59,126,240,0.06)':'var(--bg3)',
                      fontSize:'0.8rem',fontWeight:600}}>
                      <input type="checkbox" checked={form.priorities.includes(p.value)} onChange={()=>togglePriority(p.value)} style={{display:'none'}} />
                      {p.icon} {p.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Client Page Access */}
              <PermissionsPanel
                systemRole="client"
                customPermissions={form.custom_permissions}
                dataScope={null}
                onChange={(cp) => setForm(f => ({...f, custom_permissions:cp}))}
              />

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" style={{minHeight:'56px'}} placeholder="e.g. Priya approves all creative output before client sharing. For urgent tasks, call directly." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Approver Rule'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// BRAND GUIDELINES TAB
// ══════════════════════════════════════════════════════════
const TONE_OPTS = [
  'Professional & Formal',
  'Friendly & Conversational',
  'Bold & Energetic',
  'Minimal & Clean',
  'Witty & Playful',
  'Inspirational & Motivational',
  'Educational & Authoritative',
  'Luxury & Premium',
  'Warm & Empathetic',
  'Youthful & Trendy',
];

function BrandGuidelinesTab() {
  const [brands,   setBrands]   = useState([]);
  const [clients,  setClients]  = useState([]);
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [viewCard, setViewCard] = useState(null); // full view modal

  const emptyForm = {
    client:'', personality:'', logo_data:'',
    color_primary:'#3b7ef0', color_secondary:'#6c58f0',
    color_accent:'#1db87a', color_bg:'#ffffff', color_text:'#1a1d2e',
    font_heading:'', font_body:'', font_accent:'',
    tone:'Professional & Formal', audience:'',
    dos:'', donts:'', hashtags:'', reference_accounts:'', notes:'',
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [b, c] = await Promise.all([api.get('/brand-guidelines'), api.get('/clients')]);
    setBrands(b.data?.data || b.data || []);
    setClients(c.data?.data || c.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 400*1024) { alert('Max 300KB logo size'); return; }
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({...f, logo_data: ev.target.result}));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      client:             b.client||'',
      personality:        b.personality||'',
      logo_data:          b.logo_data||b.logoDataUrl||'',
      color_primary:      b.color_primary||'#3b7ef0',
      color_secondary:    b.color_secondary||'#6c58f0',
      color_accent:       b.color_accent||'#1db87a',
      color_bg:           b.color_bg||'#ffffff',
      color_text:         b.color_text||'#1a1d2e',
      font_heading:       b.font_heading||'',
      font_body:          b.font_body||'',
      font_accent:        b.font_accent||'',
      tone:               b.tone||'Professional & Formal',
      audience:           b.audience||'',
      dos:                b.dos||'',
      donts:              b.donts||'',
      hashtags:           b.hashtags||'',
      reference_accounts: b.reference_accounts||'',
      notes:              b.notes||'',
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.client) { alert('Select a client'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) await api.put('/brand-guidelines/'+editing.id, payload);
      else         await api.post('/brand-guidelines', payload);
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };

  const del = async (b) => { if(!window.confirm('Delete brand kit for "'+b.client+'"?')) return; await api.delete('/brand-guidelines/'+b.id); load(); };

  // Helper — split textarea into lines
  const lines = (str) => (str||'').split('\n').map(s=>s.trim()).filter(Boolean);

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🎨 Brand Guidelines</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Brand kits per client — colors, fonts, tone, logo, dos & donts. Referenced during content creation.</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Brand Kit</button>
      </div>

      {/* Brand Kit Cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:'16px'}}>
        {brands.map(b => (
          <div key={b.id} className="card platform-card" style={{borderTop:`4px solid ${b.color_primary||'var(--accent)'}`,padding:'16px',position:'relative'}}>
            {/* Actions */}
            <div className="platform-card-actions">
              <button className="btn btn-secondary" style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={()=>openEdit(b)}>✏️</button>
              <button className="btn btn-red"       style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={()=>del(b)}>✕</button>
            </div>

            {/* Header — logo + client name */}
            <div className="flex" style={{gap:'12px',alignItems:'center',marginBottom:'12px'}}>
              {b.logo_data ? (
                <img src={b.logo_data} alt={b.client} style={{height:'40px',maxWidth:'90px',objectFit:'contain',borderRadius:'6px'}} />
              ) : (
                <div style={{width:'40px',height:'40px',borderRadius:'8px',background:`${b.color_primary||'#3b7ef0'}18`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.2rem',flexShrink:0}}>🎨</div>
              )}
              <div>
                <div className="fw-700">{b.client}</div>
                <div className="text-xs text-muted">{b.personality||''}</div>
              </div>
            </div>

            {/* Color swatches */}
            <div className="flex" style={{gap:'6px',marginBottom:'10px',alignItems:'center'}}>
              {[
                {k:'color_primary',   l:'Primary'},
                {k:'color_secondary', l:'Secondary'},
                {k:'color_accent',    l:'Accent'},
                {k:'color_bg',        l:'BG'},
                {k:'color_text',      l:'Text'},
              ].map(({k,l}) => b[k] && (
                <div key={k} title={`${l}: ${b[k]}`} style={{width:'24px',height:'24px',borderRadius:'50%',background:b[k],border:'1px solid var(--border)',cursor:'pointer'}} />
              ))}
              {b.color_primary && <span className="text-xs text-muted mono">{b.color_primary}</span>}
            </div>

            {/* Fonts */}
            {(b.font_heading||b.font_body) && (
              <div className="text-xs text-muted mb-8">
                {b.font_heading && <span>H: <b>{b.font_heading}</b></span>}
                {b.font_body    && <span style={{marginLeft:'8px'}}>B: <b>{b.font_body}</b></span>}
              </div>
            )}

            {/* Tone */}
            <div style={{display:'flex',flexWrap:'wrap',gap:'4px',marginBottom:'8px'}}>
              <span className="badge badge-gray" style={{fontSize:'0.65rem'}}>🗣️ {b.tone||'—'}</span>
              {b.audience && <span className="badge badge-blue" style={{fontSize:'0.65rem',background:'rgba(59,126,240,0.08)',color:'var(--accent)'}}>👥 {b.audience}</span>}
            </div>

            {/* Dos / Donts preview */}
            {(b.dos||b.donts) && (
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px',marginBottom:'8px'}}>
                {b.dos && (
                  <div style={{background:'rgba(29,184,122,0.06)',border:'1px solid rgba(29,184,122,0.2)',borderRadius:'6px',padding:'6px 8px'}}>
                    <div className="text-xs fw-700" style={{color:'#1db87a',marginBottom:'3px'}}>✅ Dos</div>
                    {lines(b.dos).slice(0,3).map((l,i)=><div key={i} className="text-xs text-muted">• {l}</div>)}
                    {lines(b.dos).length>3 && <div className="text-xs text-muted">+{lines(b.dos).length-3} more</div>}
                  </div>
                )}
                {b.donts && (
                  <div style={{background:'rgba(232,69,69,0.06)',border:'1px solid rgba(232,69,69,0.2)',borderRadius:'6px',padding:'6px 8px'}}>
                    <div className="text-xs fw-700" style={{color:'#e84545',marginBottom:'3px'}}>❌ Donts</div>
                    {lines(b.donts).slice(0,3).map((l,i)=><div key={i} className="text-xs text-muted">• {l}</div>)}
                    {lines(b.donts).length>3 && <div className="text-xs text-muted">+{lines(b.donts).length-3} more</div>}
                  </div>
                )}
              </div>
            )}

            {/* Hashtags */}
            {b.hashtags && (
              <div className="text-xs text-muted" style={{marginBottom:'4px'}}>
                <span className="fw-700">Hashtags: </span>
                {b.hashtags.split(/[\s,]+/).filter(Boolean).slice(0,5).map(h=>(
                  <span key={h} style={{marginRight:'4px',color:'var(--accent)'}}>#{h.replace(/^#/,'')}</span>
                ))}
              </div>
            )}

            {/* View Full button */}
            <button className="btn btn-secondary" style={{width:'100%',marginTop:'8px',fontSize:'0.75rem',justifyContent:'center'}} onClick={()=>setViewCard(b)}>
              👁️ View Full Brand Kit
            </button>
          </div>
        ))}

        {brands.length === 0 && (
          <div className="text-sm text-muted" style={{padding:'20px',gridColumn:'1/-1',textAlign:'center'}}>
            No brand kits yet. Click <b>+ Add Brand Kit</b> to create one per client.
          </div>
        )}
      </div>

      {/* ── View Full Brand Kit Modal ── */}
      {viewCard && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setViewCard(null)}>
          <div className="modal" style={{maxWidth:'680px'}}>
            <div className="modal-header">
              <div className="modal-title" style={{display:'flex',alignItems:'center',gap:'10px'}}>
                {viewCard.logo_data && <img src={viewCard.logo_data} style={{height:'28px',objectFit:'contain'}} alt="" />}
                🎨 {viewCard.client} — Brand Kit
              </div>
              <div style={{display:'flex',gap:'6px'}}>
                <button className="btn btn-secondary" style={{padding:'4px 10px',fontSize:'0.75rem'}} onClick={()=>{setViewCard(null);openEdit(viewCard);}}>✏️ Edit</button>
                <div className="icon-btn" onClick={()=>setViewCard(null)}>✕</div>
              </div>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
                {/* Colors */}
                <div>
                  <div className="fw-700 text-sm mb-8">🎨 Brand Colors</div>
                  {[
                    {k:'color_primary',   l:'Primary'},
                    {k:'color_secondary', l:'Secondary'},
                    {k:'color_accent',    l:'Accent'},
                    {k:'color_bg',        l:'Background'},
                    {k:'color_text',      l:'Text'},
                  ].map(({k,l}) => viewCard[k] && (
                    <div key={k} className="flex" style={{gap:'8px',alignItems:'center',marginBottom:'6px'}}>
                      <div style={{width:'28px',height:'28px',borderRadius:'6px',background:viewCard[k],border:'1px solid var(--border)',flexShrink:0}} />
                      <div>
                        <div className="text-xs fw-700">{l}</div>
                        <div className="text-xs mono text-muted">{viewCard[k]}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Typography + Tone */}
                <div>
                  <div className="fw-700 text-sm mb-8">✍️ Typography & Voice</div>
                  {viewCard.font_heading && <div className="text-xs mb-6"><span className="text-muted">Heading Font:</span> <b>{viewCard.font_heading}</b></div>}
                  {viewCard.font_body    && <div className="text-xs mb-6"><span className="text-muted">Body Font:</span> <b>{viewCard.font_body}</b></div>}
                  {viewCard.font_accent  && <div className="text-xs mb-6"><span className="text-muted">Accent Font:</span> <b>{viewCard.font_accent}</b></div>}
                  {viewCard.tone         && <div className="text-xs mb-6"><span className="text-muted">Tone:</span> <b>{viewCard.tone}</b></div>}
                  {viewCard.personality  && <div className="text-xs mb-6"><span className="text-muted">Personality:</span> <b>{viewCard.personality}</b></div>}
                  {viewCard.audience     && <div className="text-xs mb-6"><span className="text-muted">Audience:</span> <b>{viewCard.audience}</b></div>}
                </div>
              </div>

              {/* Dos & Donts */}
              {(viewCard.dos||viewCard.donts) && (
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginTop:'14px'}}>
                  {viewCard.dos && (
                    <div style={{background:'rgba(29,184,122,0.06)',border:'1px solid rgba(29,184,122,0.25)',borderRadius:'8px',padding:'10px 12px'}}>
                      <div className="fw-700 text-sm mb-6" style={{color:'#1db87a'}}>✅ Dos</div>
                      {lines(viewCard.dos).map((l,i)=><div key={i} className="text-xs mb-4">✓ {l}</div>)}
                    </div>
                  )}
                  {viewCard.donts && (
                    <div style={{background:'rgba(232,69,69,0.06)',border:'1px solid rgba(232,69,69,0.25)',borderRadius:'8px',padding:'10px 12px'}}>
                      <div className="fw-700 text-sm mb-6" style={{color:'#e84545'}}>❌ Donts</div>
                      {lines(viewCard.donts).map((l,i)=><div key={i} className="text-xs mb-4">✗ {l}</div>)}
                    </div>
                  )}
                </div>
              )}

              {/* Hashtags */}
              {viewCard.hashtags && (
                <div style={{marginTop:'12px'}}>
                  <div className="fw-700 text-sm mb-6"># Hashtags</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                    {viewCard.hashtags.split(/[\s,]+/).filter(Boolean).map(h=>(
                      <span key={h} className="badge badge-blue" style={{fontSize:'0.72rem',background:'rgba(59,126,240,0.08)',color:'var(--accent)'}}>#{h.replace(/^#/,'')}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Reference Accounts */}
              {viewCard.reference_accounts && (
                <div style={{marginTop:'12px'}}>
                  <div className="fw-700 text-sm mb-6">📌 Reference Accounts</div>
                  <div className="text-xs text-muted" style={{lineHeight:1.8}}>{viewCard.reference_accounts}</div>
                </div>
              )}

              {/* Notes */}
              {viewCard.notes && (
                <div style={{marginTop:'12px',padding:'10px 12px',background:'var(--bg3)',borderRadius:'8px'}}>
                  <div className="fw-700 text-sm mb-4">📝 Additional Guidelines</div>
                  <div className="text-xs text-muted" style={{lineHeight:1.7,whiteSpace:'pre-line'}}>{viewCard.notes}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'700px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Brand Kit':'+ Add Brand Kit'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Client</label>
                  <select className="form-select" value={form.client} onChange={e=>setForm({...form,client:e.target.value})}>
                    <option value="">— Select Client —</option>
                    {clients.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Brand Personality</label>
                  <input className="form-input" placeholder="e.g. Bold, Innovative, Trustworthy" value={form.personality} onChange={e=>setForm({...form,personality:e.target.value})} />
                </div>
              </div>

              {/* Logo Upload */}
              <div className="form-group">
                <label className="form-label">Brand Logo <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(PNG/SVG, max 300KB)</span></label>
                {form.logo_data ? (
                  <div className="flex" style={{gap:'10px',alignItems:'center',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',marginBottom:'6px'}}>
                    <img src={form.logo_data} alt="logo" style={{maxHeight:'48px',maxWidth:'160px',objectFit:'contain'}} />
                    <button className="btn btn-red" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>setForm({...form,logo_data:''})}>✕ Remove</button>
                  </div>
                ) : (
                  <label style={{display:'block',padding:'12px',background:'var(--bg3)',border:'2px dashed var(--border)',borderRadius:'var(--radius-sm)',cursor:'pointer',textAlign:'center',fontSize:'0.82rem',color:'var(--text3)',marginBottom:'4px'}}>
                    📁 Click to upload logo (PNG/SVG, under 300KB)
                    <input type="file" accept="image/*" style={{display:'none'}} onChange={handleLogoUpload} />
                  </label>
                )}
              </div>

              {/* Brand Colors */}
              <div className="form-group">
                <label className="form-label">Brand Colors</label>
                <div style={{display:'flex',gap:'16px',flexWrap:'wrap',marginTop:'6px'}}>
                  {[
                    {key:'color_primary',   label:'Primary'},
                    {key:'color_secondary', label:'Secondary'},
                    {key:'color_accent',    label:'Accent'},
                    {key:'color_bg',        label:'Background'},
                    {key:'color_text',      label:'Text'},
                  ].map(({key,label}) => (
                    <div key={key} style={{display:'flex',flexDirection:'column',gap:'4px',alignItems:'center'}}>
                      <label style={{fontSize:'0.65rem',color:'var(--text3)',fontWeight:600}}>{label}</label>
                      <input type="color" value={form[key]||'#000000'} onChange={e=>setForm({...form,[key]:e.target.value})}
                        style={{width:'44px',height:'36px',borderRadius:'8px',border:'1px solid var(--border)',cursor:'pointer',padding:'2px'}} />
                      <span className="mono" style={{fontSize:'0.6rem',color:'var(--text3)'}}>{form[key]}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Typography */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Heading Font</label>
                  <input className="form-input" placeholder="e.g. Montserrat Bold, Fraunces" value={form.font_heading} onChange={e=>setForm({...form,font_heading:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Body Font</label>
                  <input className="form-input" placeholder="e.g. Open Sans Regular, DM Sans" value={form.font_body} onChange={e=>setForm({...form,font_body:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Accent / Caption Font <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(optional)</span></label>
                  <input className="form-input" placeholder="e.g. Space Mono, Courier New" value={form.font_accent} onChange={e=>setForm({...form,font_accent:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Tone of Voice</label>
                  <select className="form-select" value={form.tone} onChange={e=>setForm({...form,tone:e.target.value})}>
                    {TONE_OPTS.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Target Audience</label>
                <input className="form-input" placeholder="e.g. Urban millennials aged 22–35, B2B decision makers in manufacturing" value={form.audience} onChange={e=>setForm({...form,audience:e.target.value})} />
              </div>

              {/* Dos & Donts */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" style={{color:'#1db87a'}}>✅ Dos <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(one per line)</span></label>
                  <textarea className="form-textarea" style={{minHeight:'90px',borderColor:'rgba(29,184,122,0.3)'}}
                    placeholder={'Use vibrant colors\nInclude CTA on every post\nMaintain consistent grid\nUse branded hashtags'}
                    value={form.dos} onChange={e=>setForm({...form,dos:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{color:'#e84545'}}>❌ Donts <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(one per line)</span></label>
                  <textarea className="form-textarea" style={{minHeight:'90px',borderColor:'rgba(232,69,69,0.3)'}}
                    placeholder={'No dark backgrounds\nAvoid serif fonts in digital\nNo competitor mentions\nDo not use stock photos'}
                    value={form.donts} onChange={e=>setForm({...form,donts:e.target.value})} />
                </div>
              </div>

              {/* Hashtags */}
              <div className="form-group">
                <label className="form-label"># Brand Hashtags <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(space or comma separated)</span></label>
                <input className="form-input" placeholder="#BrandName #ProductLine #CampaignTag" value={form.hashtags} onChange={e=>setForm({...form,hashtags:e.target.value})} />
              </div>

              {/* Reference Accounts */}
              <div className="form-group">
                <label className="form-label">📌 Reference / Competitor Accounts <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(inspiration accounts to follow)</span></label>
                <textarea className="form-textarea" style={{minHeight:'60px'}}
                  placeholder={'@brandaccount (Instagram)\n@competitorxyz (LinkedIn)\nhttps://www.referencebrand.com'}
                  value={form.reference_accounts} onChange={e=>setForm({...form,reference_accounts:e.target.value})} />
              </div>

              {/* Client Page Access */}
              <PermissionsPanel
                systemRole="client"
                customPermissions={form.custom_permissions}
                dataScope={null}
                onChange={(cp) => setForm(f => ({...f, custom_permissions:cp}))}
              />

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">📝 Additional Guidelines</label>
                <textarea className="form-textarea" style={{minHeight:'70px'}}
                  placeholder="Brand voice examples, content pillars, usage rules, approval notes, special instructions..."
                  value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Brand Kit'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// APPROVAL CONTACTS TAB
// ══════════════════════════════════════════════════════════
const AUTHORITY_OPTS = [
  { value:'full',     icon:'🔑', label:'Full — All Approvals',       color:'#3b7ef0' },
  { value:'creative', icon:'🎨', label:'Creative Only',               color:'#9050e8' },
  { value:'copy',     icon:'✍️', label:'Copy & Text Only',           color:'#6c58f0' },
  { value:'budget',   icon:'💰', label:'Budget & Spend Only',         color:'#1db87a' },
  { value:'final',    icon:'✅', label:'Final Sign-off Only',         color:'#e87830' },
  { value:'cc',       icon:'👁️', label:'CC / FYI — No Approval',    color:'#888'    },
];
const PREFERRED_OPTS  = ['WhatsApp','Email','Phone Call','In-person','Video Call'];
const RESPONSE_OPTS   = ['Same day','Within 24 hours','1–2 days','2–3 days','1 week','2+ weeks'];

function ApprovalContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [clients,  setClients]  = useState([]);
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [filterClient, setFilterClient] = useState('All');

  const emptyForm = {
    client:'', name:'', designation:'', authority:'full',
    email:'', phone:'', whatsapp:'',
    preferred:'WhatsApp', response:'Within 24 hours', notes:''
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [c, cl] = await Promise.all([api.get('/approval-contacts'), api.get('/clients')]);
    setContacts(c.data?.data || c.data || []);
    setClients(cl.data?.data || cl.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      client: c.client||'', name: c.name||'', designation: c.designation||'',
      authority: c.authority||'full', email: c.email||'',
      phone: c.phone||'', whatsapp: c.whatsapp||'',
      preferred: c.preferred_contact||c.preferred||'WhatsApp',
      response: c.response_time||c.response||'Within 24 hours',
      notes: c.notes||''
    });
    setModal(true);
  };
  const save = async () => {
    if (!form.client || !form.name.trim()) { alert('Client and name are required'); return; }
    setSaving(true);
    try {
      const payload = {
        client: form.client, name: form.name, designation: form.designation,
        authority: form.authority, email: form.email,
        phone: form.phone, whatsapp: form.whatsapp,
        preferred_contact: form.preferred,
        response_time: form.response, notes: form.notes
      };
      if (editing) await api.put('/approval-contacts/'+editing.id, payload);
      else         await api.post('/approval-contacts', payload);
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };
  const del    = async (c) => { if(!window.confirm('Delete contact "'+c.name+'"?')) return; await api.delete('/approval-contacts/'+c.id); load(); };
  const toggle = async (c) => { await api.put('/approval-contacts/'+c.id, { name:c.name, client:c.client, enabled:!c.enabled }); load(); };

  const authorityInfo = (val) => AUTHORITY_OPTS.find(a=>a.value===val) || AUTHORITY_OPTS[0];
  const allClientNames = ['All', ...new Set(contacts.map(c=>c.client).filter(Boolean))];
  const filtered = filterClient==='All' ? contacts : contacts.filter(c=>c.client===filterClient);

  // Group by client
  const grouped = filtered.reduce((acc, c) => {
    const k = c.client||'Unknown';
    if (!acc[k]) acc[k]=[];
    acc[k].push(c);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">✅ Approval Contacts</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Client-side contacts who approve content — their authority level, preferred contact method and response time.</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Contact</button>
      </div>

      {/* Client filter pills */}
      {allClientNames.length > 1 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'14px'}}>
          {allClientNames.map(name => (
            <button key={name} onClick={()=>setFilterClient(name)} style={{
              padding:'4px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',
              border:`2px solid ${filterClient===name?'var(--accent)':'var(--border)'}`,
              background:filterClient===name?'var(--accent)':'var(--bg2)',
              color:filterClient===name?'#fff':'var(--text2)'
            }}>{name} {name!=='All'&&`(${contacts.filter(c=>c.client===name).length})`}</button>
          ))}
        </div>
      )}

      {/* Grouped by client */}
      {Object.entries(grouped).map(([clientName, rows]) => (
        <div key={clientName} className="card mb-16">
          <div style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',background:'var(--bg3)',borderRadius:'var(--radius) var(--radius) 0 0',display:'flex',alignItems:'center',gap:'8px'}}>
            <span className="fw-700">👔 {clientName}</span>
            <span className="badge badge-gray" style={{fontSize:'0.65rem'}}>{rows.length} contact{rows.length!==1?'s':''}</span>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Contact Name</th>
                <th>Designation</th>
                <th>Authority</th>
                <th>Email</th>
                <th>Phone / WhatsApp</th>
                <th>Preferred</th>
                <th>Response Time</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => {
                const auth = authorityInfo(c.authority);
                return (
                  <tr key={c.id}>
                    <td className="fw-700">{c.name}</td>
                    <td className="text-xs text-muted">{c.designation||'—'}</td>
                    <td>
                      <span className="badge" style={{background:`${auth.color}18`,color:auth.color,border:`1px solid ${auth.color}40`,fontSize:'0.68rem'}}>
                        {auth.icon} {auth.label}
                      </span>
                    </td>
                    <td className="text-xs text-muted">{c.email||'—'}</td>
                    <td>
                      <div style={{display:'flex',flexDirection:'column',gap:'2px'}}>
                        {c.phone    && <span className="text-xs">📞 {c.phone}</span>}
                        {c.whatsapp && <span className="text-xs">💬 {c.whatsapp}</span>}
                        {!c.phone && !c.whatsapp && <span className="text-xs text-muted">—</span>}
                      </div>
                    </td>
                    <td><span className="badge badge-blue" style={{fontSize:'0.68rem'}}>{c.preferred_contact||c.preferred||'—'}</span></td>
                    <td><span className="badge badge-gray" style={{fontSize:'0.68rem'}}>⏱️ {c.response_time||c.response||'—'}</span></td>
                    <td className="text-xs text-muted" style={{maxWidth:'150px'}}>{c.notes||'—'}</td>
                    <td>
                      <div className="flex" style={{gap:'4px',alignItems:'center'}}>
                        <Toggle checked={c.enabled!==false} onChange={()=>toggle(c)} />
                        <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(c)}>✏️</button>
                        <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(c)}>✕</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {contacts.length === 0 && (
        <div className="card" style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">
          No approval contacts yet. Click <b>+ Add Contact</b> to add client-side approvers.
        </div>
      )}

      {/* ── Modal ── */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'580px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Approval Contact':'+ Add Approval Contact'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Client</label>
                  <select className="form-select" value={form.client} onChange={e=>setForm({...form,client:e.target.value})}>
                    <option value="">— Select Client —</option>
                    {clients.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Name</label>
                  <input className="form-input" placeholder="Full name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Designation</label>
                  <input className="form-input" placeholder="e.g. Marketing Manager, CEO" value={form.designation} onChange={e=>setForm({...form,designation:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Approval Authority</label>
                  <select className="form-select" value={form.authority} onChange={e=>setForm({...form,authority:e.target.value})}>
                    {AUTHORITY_OPTS.map(a=><option key={a.value} value={a.value}>{a.icon} {a.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" placeholder="contact@client.com" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" placeholder="+91 98765 43210" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">WhatsApp</label>
                  <input className="form-input" placeholder="+91 98765 43210" value={form.whatsapp} onChange={e=>setForm({...form,whatsapp:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Preferred Contact Method</label>
                  <select className="form-select" value={form.preferred} onChange={e=>setForm({...form,preferred:e.target.value})}>
                    {PREFERRED_OPTS.map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Typical Response Time</label>
                  <select className="form-select" value={form.response} onChange={e=>setForm({...form,response:e.target.value})}>
                    {RESPONSE_OPTS.map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input className="form-input" placeholder="Best time to reach, special instructions..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Contact'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// COMPETITORS TAB
// ══════════════════════════════════════════════════════════
const CONTENT_FREQ_OPTS = ['Daily','3–5x per week','1–2x per week','Bi-weekly','Monthly','Irregular'];
const COMP_THREAT_LEVELS = [
  { value:'high',   label:'🔴 High Threat',    color:'#e84545' },
  { value:'medium', label:'🟡 Medium Threat',   color:'#f7c34f' },
  { value:'low',    label:'🟢 Low Threat',      color:'#1db87a' },
  { value:'watch',  label:'👁️ Watch & Monitor', color:'#3b7ef0' },
];

function CompetitorsTab() {
  const [items,     setItems]     = useState([]);
  const [clients,   setClients]   = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [filterClient, setFilterClient] = useState('All');

  const emptyForm = {
    client:'', name:'', website:'', platforms:[], monthly_spend:'',
    content_freq:'3–5x per week', threat_level:'medium',
    strengths:'', weaknesses:'', opportunities:'', handle:'',
    followers:'', last_ad_seen:'', content_score:'',
    last_reviewed: new Date().toISOString().split('T')[0], notes:''
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [c, cl, p] = await Promise.all([api.get('/competitors'), api.get('/clients'), api.get('/platforms')]);
    setItems(c.data?.data || c.data || []);
    setClients(cl.data?.data || cl.data || []);
    setPlatforms(p.data?.data || p.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    let plats = c.platforms;
    if (typeof plats === 'string') { try { plats = JSON.parse(plats); } catch(e) { plats=[]; } }
    setForm({
      client: c.client||'', name: c.name||'', website: c.website||c.handle||'',
      platforms: Array.isArray(plats)?plats:[],
      monthly_spend: c.monthly_spend||c.spend||'',
      content_freq: c.content_freq||c.freq||'3–5x per week',
      threat_level: c.threat_level||'medium',
      strengths: c.strengths||'', weaknesses: c.weaknesses||'',
      opportunities: c.opportunities||'', handle: c.handle||'',
      followers: c.followers||'', last_ad_seen: c.last_ad_seen||'',
      content_score: c.content_score!=null?String(c.content_score):'',
      last_reviewed: c.last_reviewed||c.reviewed||new Date().toISOString().split('T')[0],
      notes: c.notes||''
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.client || !form.name.trim()) { alert('Client and competitor name required'); return; }
    setSaving(true);
    try {
      const payload = {
        client: form.client, name: form.name,
        platforms: JSON.stringify(form.platforms),
        monthly_spend: parseFloat(form.monthly_spend)||0,
        content_freq: form.content_freq, threat_level: form.threat_level,
        strengths: form.strengths, weaknesses: form.weaknesses,
        opportunities: form.opportunities,
        handle: form.website||form.handle,
        followers: form.followers, last_ad_seen: form.last_ad_seen,
        content_score: form.content_score!==''?parseInt(form.content_score):null,
        last_reviewed: form.last_reviewed, notes: form.notes
      };
      if (editing) await api.put('/competitors/'+editing.id, payload);
      else         await api.post('/competitors', payload);
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };

  const del = async (c) => { if(!window.confirm('Delete competitor "'+c.name+'"?')) return; await api.delete('/competitors/'+c.id); load(); };
  const togglePlatform = (name) => setForm(f => ({...f, platforms: f.platforms.includes(name) ? f.platforms.filter(p=>p!==name) : [...f.platforms, name]}));

  const getPlatforms = (c) => {
    let p = c.platforms;
    if (!p) return [];
    if (typeof p === 'string') { try { p = JSON.parse(p); } catch(e) { return []; } }
    return Array.isArray(p) ? p : [];
  };

  const threatInfo = (val) => COMP_THREAT_LEVELS.find(t=>t.value===val) || COMP_THREAT_LEVELS[1];
  const lines = (str) => (str||'').split('\n').map(s=>s.trim()).filter(Boolean);
  const allClients = ['All', ...new Set(items.map(c=>c.client).filter(Boolean))];
  const filtered = filterClient==='All' ? items : items.filter(c=>c.client===filterClient);
  const grouped  = filtered.reduce((acc, c) => { const k=c.client||'Unknown'; if(!acc[k]) acc[k]=[]; acc[k].push(c); return acc; }, {});

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🔍 Competitor & Market Reference</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Track competitor brands per client — platforms, content frequency, strengths, weaknesses and opportunities.</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Competitor</button>
      </div>

      {/* Client filter */}
      {allClients.length > 1 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'14px'}}>
          {allClients.map(name=>(
            <button key={name} onClick={()=>setFilterClient(name)} style={{
              padding:'4px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',
              border:`2px solid ${filterClient===name?'var(--accent)':'var(--border)'}`,
              background:filterClient===name?'var(--accent)':'var(--bg2)',
              color:filterClient===name?'#fff':'var(--text2)'
            }}>{name} {name!=='All'&&`(${items.filter(c=>c.client===name).length})`}</button>
          ))}
        </div>
      )}

      {/* Grouped by client */}
      {Object.entries(grouped).map(([clientName, rows]) => (
        <div key={clientName} className="mb-16">
          <div className="flex" style={{gap:'8px',alignItems:'center',marginBottom:'10px'}}>
            <span className="fw-700">👔 {clientName}</span>
            <span className="badge badge-gray" style={{fontSize:'0.65rem'}}>{rows.length} competitor{rows.length!==1?'s':''}</span>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:'12px'}}>
            {rows.map(c => {
              const plats   = getPlatforms(c);
              const threat  = threatInfo(c.threat_level||'medium');
              return (
                <div key={c.id} className="card" style={{borderLeft:`4px solid ${threat.color}`,padding:'14px 16px',position:'relative'}}>
                  <div className="platform-card-actions">
                    <button className="btn btn-secondary" style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={()=>openEdit(c)}>✏️</button>
                    <button className="btn btn-red"       style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={()=>del(c)}>✕</button>
                  </div>
                  {/* Header */}
                  <div className="flex-between mb-8">
                    <div>
                      <div className="fw-700">{c.name}</div>
                      {(c.handle||c.website) && <div className="text-xs text-muted">{c.handle||c.website}</div>}
                    </div>
                    <span className="badge" style={{background:`${threat.color}18`,color:threat.color,border:`1px solid ${threat.color}40`,fontSize:'0.68rem'}}>{threat.label}</span>
                  </div>
                  {/* Platform badges */}
                  {plats.length>0 && (
                    <div style={{display:'flex',flexWrap:'wrap',gap:'3px',marginBottom:'8px'}}>
                      {plats.map(p=><span key={p} className="badge badge-gray" style={{fontSize:'0.65rem'}}>{p}</span>)}
                    </div>
                  )}
                  {/* Stats row */}
                  <div className="flex" style={{gap:'12px',marginBottom:'8px',flexWrap:'wrap'}}>
                    {(c.monthly_spend||c.spend) && <span className="text-xs"><span className="text-muted">Spend:</span> <b>{fmtRupees(c.monthly_spend||c.spend)}/mo</b></span>}
                    {(c.content_freq||c.freq) && <span className="text-xs"><span className="text-muted">Posts:</span> <b>{c.content_freq||c.freq}</b></span>}
                    {c.followers && <span className="text-xs"><span className="text-muted">Followers:</span> <b>{c.followers}</b></span>}
                    {c.content_score!=null && c.content_score!=='' && <span className="text-xs"><span className="text-muted">Score:</span> <b>{c.content_score}/10</b> {parseInt(c.content_score)>=8?'🔥':parseInt(c.content_score)>=5?'⚡':'💤'}</span>}
                  </div>
                  {/* Strengths / Weaknesses */}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:c.opportunities?'8px':'0'}}>
                    {c.strengths && (
                      <div style={{background:'rgba(232,69,69,0.04)',border:'1px solid rgba(232,69,69,0.15)',borderRadius:'6px',padding:'6px 8px'}}>
                        <div className="text-xs fw-700" style={{color:'#e84545',marginBottom:'3px'}}>💪 Strengths</div>
                        {lines(c.strengths).slice(0,3).map((l,i)=><div key={i} className="text-xs text-muted">• {l}</div>)}
                      </div>
                    )}
                    {c.weaknesses && (
                      <div style={{background:'rgba(29,184,122,0.04)',border:'1px solid rgba(29,184,122,0.15)',borderRadius:'6px',padding:'6px 8px'}}>
                        <div className="text-xs fw-700" style={{color:'#1db87a',marginBottom:'3px'}}>🎯 Weaknesses</div>
                        {lines(c.weaknesses).slice(0,3).map((l,i)=><div key={i} className="text-xs text-muted">• {l}</div>)}
                      </div>
                    )}
                  </div>
                  {c.opportunities && (
                    <div style={{background:'rgba(59,126,240,0.04)',border:'1px solid rgba(59,126,240,0.15)',borderRadius:'6px',padding:'6px 8px',marginBottom:'4px'}}>
                      <div className="text-xs fw-700" style={{color:'#3b7ef0',marginBottom:'3px'}}>✨ Opportunities</div>
                      {lines(c.opportunities).slice(0,2).map((l,i)=><div key={i} className="text-xs text-muted">• {l}</div>)}
                    </div>
                  )}
                  {c.last_reviewed && <div className="text-xs text-muted" style={{marginTop:'6px'}}>📅 Reviewed: {c.last_reviewed}</div>}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {items.length===0 && (
        <div className="card" style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">
          No competitors yet. Click <b>+ Add Competitor</b> to track a competitor for a client.
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'660px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Competitor':'+ Add Competitor'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Client</label>
                  <select className="form-select" value={form.client} onChange={e=>setForm({...form,client:e.target.value})}>
                    <option value="">— Select Client —</option>
                    {clients.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Competitor Brand Name</label>
                  <input className="form-input" placeholder="Competitor brand name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Website / Social Handle</label>
                  <input className="form-input" placeholder="@handle or website.com" value={form.website} onChange={e=>setForm({...form,website:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Threat Level</label>
                  <select className="form-select" value={form.threat_level} onChange={e=>setForm({...form,threat_level:e.target.value})}>
                    {COMP_THREAT_LEVELS.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Active Platforms</label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'10px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
                  {platforms.filter(p=>p.is_active!==false).map(p=>(
                    <label key={p.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.75rem',cursor:'pointer',padding:'3px 8px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'6px',whiteSpace:'nowrap'}}>
                      <input type="checkbox" checked={form.platforms.includes(p.name)} onChange={()=>togglePlatform(p.name)} style={{accentColor:'var(--accent)'}} />
                      {p.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Est. Monthly Ad Spend (₹)</label>
                  <input className="form-input" type="number" placeholder="0" value={form.monthly_spend} onChange={e=>setForm({...form,monthly_spend:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Content Frequency</label>
                  <select className="form-select" value={form.content_freq} onChange={e=>setForm({...form,content_freq:e.target.value})}>
                    {CONTENT_FREQ_OPTS.map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" style={{color:'#e84545'}}>💪 Strengths <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(one per line)</span></label>
                  <textarea className="form-textarea" style={{minHeight:'80px',borderColor:'rgba(232,69,69,0.3)'}}
                    placeholder={'High engagement rate\nStrong video content\nLarge follower base'}
                    value={form.strengths} onChange={e=>setForm({...form,strengths:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{color:'#1db87a'}}>🎯 Weaknesses <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(one per line)</span></label>
                  <textarea className="form-textarea" style={{minHeight:'80px',borderColor:'rgba(29,184,122,0.3)'}}
                    placeholder={'Poor response to comments\nInconsistent posting\nGeneric copy'}
                    value={form.weaknesses} onChange={e=>setForm({...form,weaknesses:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" style={{color:'#3b7ef0'}}>✨ Opportunities <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(gaps we can exploit)</span></label>
                <textarea className="form-textarea" style={{minHeight:'60px',borderColor:'rgba(59,126,240,0.3)'}}
                  placeholder={'They are weak on short-form video — we should focus on Reels\nNo WhatsApp engagement — opportunity for us'}
                  value={form.opportunities} onChange={e=>setForm({...form,opportunities:e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Followers / Subscribers</label>
                  <input className="form-input" placeholder="e.g. 45K, 1.2M, 8500" value={form.followers} onChange={e=>setForm({...form,followers:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Content Score <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(0–10)</span></label>
                  <div className="flex" style={{gap:'8px',alignItems:'center'}}>
                    <input className="form-input" type="number" min="0" max="10" placeholder="e.g. 7" style={{maxWidth:'80px'}} value={form.content_score} onChange={e=>setForm({...form,content_score:e.target.value})} />
                    {form.content_score!=='' && <span className="text-xs text-muted">{parseInt(form.content_score)>=8?'🔥 Strong':parseInt(form.content_score)>=5?'⚡ Average':'💤 Weak'}</span>}
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Last Ad / Campaign Seen</label>
                <input className="form-input" placeholder="e.g. Meta carousel ad — Diwali offer 2026, running since Oct 10" value={form.last_ad_seen} onChange={e=>setForm({...form,last_ad_seen:e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Last Reviewed</label>
                  <input className="form-input" type="date" value={form.last_reviewed} onChange={e=>setForm({...form,last_reviewed:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input className="form-input" placeholder="Campaign observations, seasonal patterns..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Competitor'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// REPORT TEMPLATES TAB
// ══════════════════════════════════════════════════════════
const REPORT_SECTIONS_LIBRARY = [
  'Executive Summary','KPI Overview','Platform-wise Performance','Content Performance',
  'Audience Insights','Paid Campaign Summary','Spend vs Budget','Engagement Analysis',
  'Follower Growth','Reach & Impressions','Top Performing Posts','Competitor Comparison',
  'Recommendations','Next Month Plan','Client Feedback','Team Deliverables Summary',
  'Approval Status','Pending Actions','Video Performance','Story Performance',
  'Hashtag Analysis','Influencer Performance','Email Campaign Metrics','SEO Rankings',
  'Website Traffic','Lead Generation Summary','Conversion Analysis','ROI Summary',
];

const REPORT_FORMAT_OPTS  = ['PDF Presentation','Google Slides','PowerPoint','Excel / Sheets','Word Document','Dashboard Link','Email Summary','Notion Page'];
const REPORT_FREQ_OPTS    = ['Weekly','Bi-weekly','Monthly','Quarterly','Campaign-end','Ad-hoc'];

const DEFAULT_REPORT_TEMPLATES = [
  {
    name: 'Monthly Social Media Report',
    frequency: 'Monthly', service_type: 'Social Media', format: 'PDF Presentation',
    sections: JSON.stringify(['Executive Summary','KPI Overview','Platform-wise Performance','Content Performance','Engagement Analysis','Follower Growth','Top Performing Posts','Recommendations','Next Month Plan']),
    notes: 'Include client logo on cover page. Send by 5th of each month. Present in monthly review call.'
  },
  {
    name: 'Paid Campaign Performance Report',
    frequency: 'Monthly', service_type: 'Performance Marketing', format: 'Google Slides',
    sections: JSON.stringify(['Executive Summary','Paid Campaign Summary','Spend vs Budget','Audience Insights','Conversion Analysis','ROI Summary','Recommendations']),
    notes: 'Share editable Google Slides link. Include raw data sheet as attachment.'
  },
  {
    name: 'Weekly Quick Update',
    frequency: 'Weekly', service_type: '', format: 'Email Summary',
    sections: JSON.stringify(['KPI Overview','Content Performance','Top Performing Posts','Pending Actions']),
    notes: 'Short email format — no deck required. Send every Monday morning.'
  },
  {
    name: 'Quarterly Business Review',
    frequency: 'Quarterly', service_type: '', format: 'PowerPoint',
    sections: JSON.stringify(['Executive Summary','KPI Overview','Platform-wise Performance','Paid Campaign Summary','Spend vs Budget','Follower Growth','Competitor Comparison','ROI Summary','Recommendations','Next Month Plan']),
    notes: 'Comprehensive quarterly deck. Present in person or via video call with senior stakeholders.'
  },
  {
    name: 'SEO & Content Marketing Report',
    frequency: 'Monthly', service_type: 'SEO', format: 'PDF Presentation',
    sections: JSON.stringify(['Executive Summary','SEO Rankings','Website Traffic','Content Performance','Top Performing Posts','Reach & Impressions','Lead Generation Summary','Recommendations']),
    notes: 'Include keyword ranking table with changes from previous month. Attach Google Search Console screenshot.'
  },
  {
    name: 'Campaign End Report',
    frequency: 'Campaign-end', service_type: '', format: 'PDF Presentation',
    sections: JSON.stringify(['Executive Summary','KPI Overview','Platform-wise Performance','Content Performance','Paid Campaign Summary','Spend vs Budget','Audience Insights','Conversion Analysis','ROI Summary','Competitor Comparison','Recommendations']),
    notes: 'Full campaign retrospective. Include creative performance comparison and learnings for next campaign.'
  },
];

function ReportTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [services,  setServices]  = useState([]);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [seeding,   setSeeding]   = useState(false);

  const emptyForm = { name:'', frequency:'Monthly', service_type:'', format:'PDF Presentation', sections:[], auto_send_day:'', delivery_method:'Email', notes:'' };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [t, s] = await Promise.all([api.get('/report-templates'), api.get('/services')]);
    setTemplates(t.data?.data || t.data || []);
    setServices(s.data?.data || s.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    if (!window.confirm(`Add ${DEFAULT_REPORT_TEMPLATES.length} standard report templates? Continue?`)) return;
    setSeeding(true);
    try {
      let added=0, failed=0;
      for (const t of DEFAULT_REPORT_TEMPLATES) {
        try { await api.post('/report-templates', t); added++; } catch(e) { failed++; }
      }
      if (failed) alert(`${added} added, ${failed} failed.`);
      load();
    } finally { setSeeding(false); }
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (t) => {
    setEditing(t);
    let sects = t.sections;
    if (typeof sects === 'string') { try { sects = JSON.parse(sects); } catch(e) { sects=[]; } }
    setForm({
      name: t.name||'', frequency: t.frequency||'Monthly',
      service_type: t.service_type||t.serviceType||'',
      format: t.format||'PDF Presentation',
      sections: Array.isArray(sects)?sects:[],
      auto_send_day: t.auto_send_day||'',
      delivery_method: t.delivery_method||'Email',
      notes: t.notes||''
    });
    setModal(true);
  };
  const save = async () => {
    if (!form.name.trim()) return;
    if (!form.sections.length) { alert('Select at least one report section'); return; }
    setSaving(true);
    try {
      const payload = { name:form.name, frequency:form.frequency, service_type:form.service_type, format:form.format, sections:JSON.stringify(form.sections), auto_send_day:form.auto_send_day?parseInt(form.auto_send_day):null, delivery_method:form.delivery_method, notes:form.notes };
      if (editing) await api.put('/report-templates/'+editing.id, payload);
      else         await api.post('/report-templates', payload);
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };
  const del    = async (t) => { if(!window.confirm('Delete "'+t.name+'"?')) return; await api.delete('/report-templates/'+t.id); load(); };
  const toggle = async (t) => { await api.put('/report-templates/'+t.id, { name:t.name, frequency:t.frequency||'Monthly', format:t.format||'PDF Presentation', sections:t.sections||'[]', enabled:!t.enabled }); load(); };

  const toggleSection = (s) => setForm(f => ({...f, sections: f.sections.includes(s) ? f.sections.filter(x=>x!==s) : [...f.sections, s]}));

  const getSections = (t) => {
    let s = t.sections;
    if (typeof s === 'string') { try { s = JSON.parse(s); } catch(e) { s=[]; } }
    return Array.isArray(s) ? s : [];
  };

  const FREQ_COLORS = { Weekly:'#3b7ef0', Monthly:'#1db87a', Quarterly:'#9050e8', 'Campaign-end':'#e87830', 'Ad-hoc':'#888', 'Bi-weekly':'#18b8b8' };

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">📋 Report Templates</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Define standard report structures — sections, format and delivery frequency. Used when generating client reports.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>{seeding?'Adding...':'⚡ Add Standard Templates'}</button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Template</button>
        </div>
      </div>

      {/* Template Cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:'14px'}}>
        {templates.map(t => {
          const sects = getSections(t);
          const color = FREQ_COLORS[t.frequency]||'var(--accent)';
          return (
            <div key={t.id} className="card platform-card" style={{borderTop:`4px solid ${color}`,padding:'14px 16px',opacity:t.enabled===false?0.45:1}}>
              <div className="platform-card-actions">
                <button className="btn btn-secondary" style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={()=>openEdit(t)}>✏️</button>
                <button className="btn btn-red"       style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={()=>del(t)}>✕</button>
              </div>
              <div className="fw-700 mb-4">{t.name}</div>
              <div style={{display:'flex',gap:'5px',flexWrap:'wrap',marginBottom:'8px'}}>
                <span className="badge" style={{background:`${color}18`,color,border:`1px solid ${color}40`,fontSize:'0.68rem'}}>🔄 {t.frequency}</span>
                <span className="badge badge-gray" style={{fontSize:'0.68rem'}}>📄 {t.format}</span>
                {(t.service_type||t.serviceType) && <span className="badge badge-purple" style={{fontSize:'0.68rem'}}>📦 {t.service_type||t.serviceType}</span>}
                {t.delivery_method && <span className="badge badge-blue" style={{fontSize:'0.68rem',background:'rgba(59,126,240,0.08)',color:'var(--accent)'}}>📤 {t.delivery_method}</span>}
                {t.auto_send_day && <span className="badge badge-green" style={{fontSize:'0.68rem'}}>⏰ {t.auto_send_day}th</span>}
              </div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'3px',marginBottom:'8px'}}>
                {sects.map(s=><span key={s} className="badge badge-blue" style={{fontSize:'0.62rem',background:'rgba(59,126,240,0.07)',color:'var(--accent)'}}>{s}</span>)}
              </div>
              {t.notes && <div className="text-xs text-muted" style={{borderTop:'1px solid var(--border)',paddingTop:'6px',marginTop:'4px'}}>{t.notes}</div>}
              <div className="flex" style={{gap:'6px',alignItems:'center',marginTop:'8px',paddingTop:'6px',borderTop:'1px solid var(--border)'}}>
                <Toggle checked={t.enabled!==false} onChange={()=>toggle(t)} />
                <span className="text-xs text-muted">{t.enabled===false?'Disabled':'Active'}</span>
              </div>
            </div>
          );
        })}
        {templates.length===0 && (
          <div className="text-sm text-muted" style={{padding:'20px',gridColumn:'1/-1',textAlign:'center'}}>
            No report templates yet. Click <b>⚡ Add Standard Templates</b> to get started with {DEFAULT_REPORT_TEMPLATES.length} industry-standard templates.
          </div>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'660px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Report Template':'+ Add Report Template'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Template Name</label>
                  <input className="form-input" placeholder="e.g. Monthly Social Media Report" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Frequency</label>
                  <select className="form-select" value={form.frequency} onChange={e=>setForm({...form,frequency:e.target.value})}>
                    {REPORT_FREQ_OPTS.map(f=><option key={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Linked Service Type <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(optional)</span></label>
                  <select className="form-select" value={form.service_type} onChange={e=>setForm({...form,service_type:e.target.value})}>
                    <option value="">— All Services —</option>
                    {services.filter(s=>s.enabled!==false).map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Format / Delivery</label>
                  <select className="form-select" value={form.format} onChange={e=>setForm({...form,format:e.target.value})}>
                    {REPORT_FORMAT_OPTS.map(f=><option key={f}>{f}</option>)}
                  </select>
                </div>
              </div>

              {/* Sections checklist */}
              <div className="form-group">
                <label className="form-label">Report Sections <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(tick what's included — {form.sections.length} selected)</span></label>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px',padding:'12px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',maxHeight:'220px',overflowY:'auto'}}>
                  {REPORT_SECTIONS_LIBRARY.map(s=>(
                    <label key={s} style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'0.78rem',cursor:'pointer',padding:'3px 0'}}>
                      <input type="checkbox" checked={form.sections.includes(s)} onChange={()=>toggleSection(s)} style={{accentColor:'var(--accent)',flexShrink:0}} />
                      {s}
                    </label>
                  ))}
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'4px',marginTop:'6px'}}>
                  <button className="btn btn-secondary" style={{fontSize:'0.7rem',padding:'3px 10px'}} onClick={()=>setForm(f=>({...f,sections:[...REPORT_SECTIONS_LIBRARY]}))}>Select All</button>
                  <button className="btn btn-secondary" style={{fontSize:'0.7rem',padding:'3px 10px'}} onClick={()=>setForm(f=>({...f,sections:[]}))}>Clear All</button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Auto-Send Day <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(day of month, e.g. 5 = 5th)</span></label>
                  <input className="form-input" type="number" min="1" max="28" placeholder="e.g. 5" value={form.auto_send_day} onChange={e=>setForm({...form,auto_send_day:e.target.value})} />
                  {form.auto_send_day && <div className="text-xs text-muted" style={{marginTop:'4px'}}>📅 Will auto-send on the {form.auto_send_day}{['st','nd','rd'][form.auto_send_day-1]||'th'} of each {form.frequency==='Monthly'?'month':form.frequency==='Weekly'?'week':'period'}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Delivery Method</label>
                  <select className="form-select" value={form.delivery_method} onChange={e=>setForm({...form,delivery_method:e.target.value})}>
                    {['Email','WhatsApp','Client Portal','Google Drive Link','Presentation Call','Slack','All of the above'].map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Notes / Special Instructions</label>
                <textarea className="form-textarea" style={{minHeight:'70px'}}
                  placeholder="Branding notes, data sources, delivery deadline, sign-off required before sending..."
                  value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Template'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MESSAGE TEMPLATES TAB
// ══════════════════════════════════════════════════════════
const MSG_CATEGORIES = [
  { value:'onboarding',    icon:'🤝', label:'Onboarding',         color:'#3b7ef0' },
  { value:'approval',      icon:'✅', label:'Content Approval',   color:'#e87830' },
  { value:'report',        icon:'📊', label:'Report Delivery',    color:'#9050e8' },
  { value:'checkin',       icon:'💬', label:'Check-in / Update',  color:'#1db87a' },
  { value:'feedback',      icon:'⭐', label:'Feedback Request',   color:'#f7c34f' },
  { value:'escalation',    icon:'🚨', label:'Escalation',         color:'#e84545' },
  { value:'invoice',       icon:'💰', label:'Invoice / Payment',  color:'#18b8b8' },
  { value:'general',       icon:'📝', label:'General',            color:'#888'    },
];

const MSG_PLACEHOLDERS = [
  '[Client Name]','[Contact Name]','[Month]','[Date]','[Deadline]',
  '[Platform]','[Campaign Name]','[Link]','[Amount]','[Agency Name]',
];

const DEFAULT_MSG_TEMPLATES = [
  {
    icon:'🤝', category:'onboarding', name:'Welcome & Kickoff',
    body:`Dear [Contact Name],

Welcome to [Agency Name]! We are thrilled to have [Client Name] on board.

To get started, we'd love to schedule a brief kickoff call to understand your goals, brand voice and expectations. Please share your availability for this week.

In the meantime, here's what we'll need from you:
• Brand logo and guidelines
• Access to social media accounts
• Any existing content or campaign references

We'll share the content calendar and first month plan within 3 working days of the kickoff call.

Looking forward to a great partnership!

Best regards,
[Agency Name] Team`
  },
  {
    icon:'📋', category:'approval', name:'Content Preview — Request Approval',
    body:`Dear [Contact Name],

Please find attached the content package for [Month]. It includes:
• [X] static posts
• [X] reels/videos
• [X] stories
• Captions and hashtags for all posts

Kindly review and share your feedback by [Deadline]. This will ensure we stay on schedule for publishing.

You can leave comments directly on the shared file or reply to this message.

Please note that we can accommodate up to 2 rounds of revisions within the agreed scope.

Best regards,
[Agency Name] Team`
  },
  {
    icon:'🚀', category:'approval', name:'Final Approval Before Publishing',
    body:`Dear [Contact Name],

All revisions have been incorporated as per your feedback. The content is now ready for final sign-off.

Please confirm your approval by [Deadline] so we can proceed with scheduling.

If we do not hear from you by [Deadline], we will treat this as approval and proceed with publishing as planned.

Thank you for your prompt response!

Best regards,
[Agency Name] Team`
  },
  {
    icon:'💬', category:'checkin', name:'Mid-Month Performance Check-In',
    body:`Dear [Contact Name],

Here's a quick mid-month update on [Campaign Name / Platform] performance:

📈 Key highlights so far:
• Reach: [X]
• Engagement Rate: [X]%
• Top performing post: [Post Name/Link]
• Followers gained: [X]

We're on track with the content calendar. The remaining posts for this month will go live on schedule.

Please let us know if you have any feedback or would like to adjust the strategy.

Best regards,
[Agency Name] Team`
  },
  {
    icon:'📊', category:'report', name:'Monthly Report Delivery',
    body:`Dear [Contact Name],

Please find attached the performance report for [Month].

Key Highlights:
✅ Reach: [X] (vs last month: [X])
✅ Engagement Rate: [X]%
✅ New Followers: [X]
✅ Top Platform: [Platform]

The full report includes a detailed breakdown by platform, content type and post performance, along with our recommendations for [Next Month].

We'd love to walk you through the report on a quick call — please share your availability.

Best regards,
[Agency Name] Team`
  },
  {
    icon:'📅', category:'report', name:'Quarterly Business Review',
    body:`Dear [Contact Name],

The Q[X] performance review is ready. Please find the comprehensive report attached.

This quarter's summary:
• Total Reach: [X]
• Engagement: [X]%
• Content Published: [X] pieces
• Paid Ad Spend: ₹[Amount] | ROAS: [X]x

We've also included a competitive landscape analysis and strategic recommendations for Q[X+1].

Please confirm a time for our quarterly review call — we'd love to present this in person or via video call.

Best regards,
[Agency Name] Team`
  },
  {
    icon:'⭐', category:'feedback', name:'Monthly Satisfaction Survey',
    body:`Dear [Contact Name],

Thank you for partnering with us this month! Your feedback helps us serve you better.

Please take 2 minutes to complete our quick satisfaction survey:
[Link]

We truly value your input and use it to continuously improve our service, communication and deliverables.

If you'd like to share any specific feedback directly, please feel free to reply to this message — we're always listening.

Thank you!

[Agency Name] Team`
  },
  {
    icon:'🔄', category:'checkin', name:'Content Revision Follow-up',
    body:`Dear [Contact Name],

We wanted to follow up on the content revisions sent on [Date].

Could you please review and share your feedback at your earliest convenience? We'd love to incorporate your inputs and get the content approved in time for publishing.

If you have any questions or would like to discuss the revisions, please don't hesitate to reach out.

Best regards,
[Agency Name] Team`
  },
  {
    icon:'🚨', category:'escalation', name:'Urgent — Approval Deadline Missed',
    body:`Dear [Contact Name],

This is a gentle reminder that the content approval deadline of [Deadline] has passed.

To ensure we can publish on schedule and maintain your social media consistency, we need your approval by [New Deadline].

If we do not receive your approval by [New Deadline], we may need to reschedule the publishing dates, which could impact this month's performance targets.

Please let us know how you'd like to proceed.

Best regards,
[Agency Name] Team`
  },
  {
    icon:'💰', category:'invoice', name:'Monthly Invoice',
    body:`Dear [Contact Name],

Please find attached the invoice for [Month] services.

Invoice Details:
• Invoice No: [Invoice Number]
• Services: [Service Name]
• Period: [Month]
• Amount: ₹[Amount] + GST

Payment due by: [Payment Deadline]

Bank transfer details are mentioned on the invoice. Please confirm receipt of this email and share the payment confirmation once done.

For any queries, please contact us at [Agency Email].

Thank you for your continued trust!

[Agency Name] Team`
  },
  {
    icon:'🎉', category:'general', name:'Campaign Launch Announcement',
    body:`Dear [Contact Name],

We are excited to announce that the [Campaign Name] campaign is now live!

🚀 Campaign is running on: [Platform]
📅 Campaign period: [Date] to [Date]
🎯 Objective: [Goal]

We will keep you updated with performance metrics over the next few days. You can also track live performance on [Dashboard Link].

Thank you for your trust and collaboration!

[Agency Name] Team`
  },
  {
    icon:'📌', category:'onboarding', name:'Content Calendar Sharing',
    body:`Dear [Contact Name],

Please find the [Month] content calendar attached / linked below:
[Link]

The calendar includes:
• [X] posts planned across all platforms
• Post dates, times and platform details
• Caption previews and hashtag sets
• Key dates and campaign milestones

Please review and let us know if you'd like to make any changes to the theme, topics or posting schedule.

Kindly confirm by [Deadline] so we can begin production.

Best regards,
[Agency Name] Team`
  },
];

function MessageTemplatesTab() {
  const [templates,  setTemplates]  = useState([]);
  const [modal,      setModal]      = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [seeding,    setSeeding]    = useState(false);
  const [filterCat,  setFilterCat]  = useState('All');
  const [search,     setSearch]     = useState('');
  const [previewTpl, setPreviewTpl] = useState(null);
  const [copied,     setCopied]     = useState(false);

  const emptyForm = { icon:'📝', name:'', category:'general', body:'' };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const r = await api.get('/message-templates');
    setTemplates(r.data?.data || r.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    if (!window.confirm(`Add ${DEFAULT_MSG_TEMPLATES.length} standard message templates? Continue?`)) return;
    setSeeding(true);
    try {
      let added=0, failed=0;
      for (const t of DEFAULT_MSG_TEMPLATES) {
        try { await api.post('/message-templates', { name:t.name, category:t.category, body:t.body, icon:t.icon }); added++; }
        catch(e) { failed++; }
      }
      if (failed) alert(`${added} added, ${failed} failed.`);
      load();
    } finally { setSeeding(false); }
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ icon:t.icon||'📝', name:t.name||'', category:t.category||'general', body:t.body||t.content||'' });
    setModal(true);
  };
  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) { alert('Name and body are required'); return; }
    setSaving(true);
    try {
      const payload = { name:form.name, category:form.category, body:form.body, icon:form.icon };
      if (editing) await api.put('/message-templates/'+editing.id, payload);
      else         await api.post('/message-templates', payload);
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };
  const del    = async (t) => { if(!window.confirm('Delete "'+t.name+'"?')) return; await api.delete('/message-templates/'+t.id); load(); };
  const toggle = async (t) => { await api.put('/message-templates/'+t.id, { name:t.name, category:t.category||'general', body:t.body||t.content||'', enabled:!t.enabled }); load(); };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(()=>setCopied(false), 2000); });
  };

  const catInfo  = (val) => MSG_CATEGORIES.find(c=>c.value===val) || MSG_CATEGORIES[MSG_CATEGORIES.length-1];
  const allCats  = ['All', ...MSG_CATEGORIES.map(c=>c.value)];
  const filtered = templates.filter(t => {
    const matchCat    = filterCat==='All' || t.category===filterCat;
    const matchSearch = !search || t.name?.toLowerCase().includes(search.toLowerCase()) || (t.body||t.content||'').toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const insertPlaceholder = (ph) => {
    const ta = document.getElementById('msg-tpl-body');
    if (!ta) { setForm(f=>({...f,body:f.body+ph})); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    const newBody = form.body.substring(0,start) + ph + form.body.substring(end);
    setForm(f=>({...f,body:newBody}));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start+ph.length, start+ph.length); }, 0);
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">📝 Message Templates</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Client communication templates for emails and WhatsApp. Use placeholders like [Client Name], [Month], [Date].</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>{seeding?'Adding...':'⚡ Add Standard Templates'}</button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Template</button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex-between mb-12">
        <div style={{display:'flex',flexWrap:'wrap',gap:'6px'}}>
          {allCats.map(cat => {
            const info  = cat==='All' ? {icon:'📋',label:'All',color:'var(--accent)'} : catInfo(cat);
            const count = cat==='All' ? templates.length : templates.filter(t=>t.category===cat).length;
            const act   = filterCat===cat;
            return count===0 && cat!=='All' ? null : (
              <button key={cat} onClick={()=>setFilterCat(cat)} style={{
                padding:'4px 10px',borderRadius:'20px',fontSize:'0.72rem',fontWeight:600,cursor:'pointer',
                border:`2px solid ${act?(info.color||'var(--accent)'):'var(--border)'}`,
                background:act?(info.color||'var(--accent)'):'var(--bg2)',
                color:act?'#fff':'var(--text2)',
                display:'flex',alignItems:'center',gap:'4px'
              }}>{info.icon} {cat==='All'?'All':info.label} ({count})</button>
            );
          })}
        </div>
        <input className="form-input" style={{maxWidth:'220px'}} placeholder="Search templates..." value={search} onChange={e=>setSearch(e.target.value)} />
      </div>

      {/* Template Cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:'14px'}}>
        {filtered.map(t => {
          const cat   = catInfo(t.category||'general');
          const body  = t.body||t.content||'';
          return (
            <div key={t.id} className="card platform-card" style={{borderLeft:`4px solid ${cat.color}`,padding:'14px 16px',opacity:t.enabled===false?0.45:1}}>
              <div className="platform-card-actions">
                <button className="btn btn-secondary" style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={()=>openEdit(t)}>✏️</button>
                <button className="btn btn-red"       style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={()=>del(t)}>✕</button>
              </div>
              {/* Header */}
              <div className="flex" style={{gap:'8px',alignItems:'flex-start',marginBottom:'8px'}}>
                <span style={{fontSize:'1.4rem',lineHeight:1,flexShrink:0}}>{t.icon||'📝'}</span>
                <div>
                  <div className="fw-700">{t.name}</div>
                  <span className="badge" style={{background:`${cat.color}18`,color:cat.color,border:`1px solid ${cat.color}40`,fontSize:'0.65rem'}}>{cat.icon} {cat.label}</span>
                </div>
              </div>
              {/* Body preview */}
              <div className="text-xs text-muted" style={{lineHeight:'1.6',maxHeight:'80px',overflow:'hidden',whiteSpace:'pre-line',marginBottom:'8px'}}>{body.substring(0,180)}{body.length>180?'...':''}</div>
              {/* Actions */}
              <div className="flex" style={{gap:'6px',alignItems:'center'}}>
                <Toggle checked={t.enabled!==false} onChange={()=>toggle(t)} />
                <button className="btn btn-secondary" style={{flex:1,fontSize:'0.72rem',justifyContent:'center'}} onClick={()=>setPreviewTpl(t)}>👁️ Preview</button>
                <button className="btn btn-secondary" style={{flex:1,fontSize:'0.72rem',justifyContent:'center'}} onClick={()=>copyToClipboard(t.body||t.content||'')}>📋 Copy</button>
              </div>
            </div>
          );
        })}
        {filtered.length===0 && (
          <div className="text-sm text-muted" style={{padding:'20px',gridColumn:'1/-1',textAlign:'center'}}>
            {templates.length===0 ? <>No templates yet. Click <b>⚡ Add Standard Templates</b> to add {DEFAULT_MSG_TEMPLATES.length} ready-to-use templates.</> : 'No templates match your filter.'}
          </div>
        )}
      </div>

      {/* Copied toast */}
      {copied && (
        <div style={{position:'fixed',bottom:'24px',right:'24px',background:'#1db87a',color:'#fff',padding:'10px 18px',borderRadius:'8px',fontWeight:600,fontSize:'0.85rem',zIndex:9999,boxShadow:'0 4px 16px rgba(0,0,0,0.2)'}}>
          ✅ Template copied to clipboard!
        </div>
      )}

      {/* ── Preview Modal ── */}
      {previewTpl && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setPreviewTpl(null)}>
          <div className="modal" style={{maxWidth:'600px'}}>
            <div className="modal-header">
              <div className="modal-title" style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <span style={{fontSize:'1.2rem'}}>{previewTpl.icon||'📝'}</span>
                {previewTpl.name}
              </div>
              <div style={{display:'flex',gap:'6px'}}>
                <button className="btn btn-secondary" style={{padding:'4px 10px',fontSize:'0.75rem'}} onClick={()=>copyToClipboard(previewTpl.body||previewTpl.content||'')}>📋 Copy</button>
                <button className="btn btn-secondary" style={{padding:'4px 10px',fontSize:'0.75rem'}} onClick={()=>{setPreviewTpl(null);openEdit(previewTpl);}}>✏️ Edit</button>
                <div className="icon-btn" onClick={()=>setPreviewTpl(null)}>✕</div>
              </div>
            </div>
            <div className="modal-body">
              <div style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'16px',lineHeight:'1.8',whiteSpace:'pre-line',fontSize:'0.85rem'}}>
                {previewTpl.body||previewTpl.content||''}
              </div>
              <div className="text-xs text-muted" style={{marginTop:'8px'}}>
                💡 Placeholders: {MSG_PLACEHOLDERS.map(p=><span key={p} style={{marginRight:'6px',color:'var(--accent)'}}>{p}</span>)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'640px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Template':'+ Add Message Template'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group" style={{flex:'0 0 60px'}}>
                  <label className="form-label">Icon</label>
                  <input className="form-input" placeholder="📝" value={form.icon} onChange={e=>setForm({...form,icon:e.target.value})} style={{fontSize:'1.2rem',textAlign:'center'}} />
                </div>
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Template Name</label>
                  <input className="form-input" placeholder="e.g. Monthly Report Delivery" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
                </div>
                <div className="form-group" style={{flex:1}}>
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                    {MSG_CATEGORIES.map(c=><option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Placeholder quick-insert */}
              <div className="form-group">
                <label className="form-label">Quick Insert Placeholders</label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'5px',marginTop:'4px'}}>
                  {MSG_PLACEHOLDERS.map(ph=>(
                    <span key={ph} onClick={()=>insertPlaceholder(ph)} style={{
                      padding:'3px 9px',borderRadius:'20px',fontSize:'0.72rem',fontWeight:600,
                      background:'rgba(59,126,240,0.08)',color:'var(--accent)',
                      border:'1px solid rgba(59,126,240,0.25)',cursor:'pointer',userSelect:'none'
                    }}>{ph}</span>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Message Body</label>
                <textarea id="msg-tpl-body" className="form-textarea" style={{minHeight:'220px',lineHeight:'1.7',fontFamily:'inherit',fontSize:'0.85rem'}}
                  placeholder={`Dear [Contact Name],\n\n[Your message here...]\n\nBest regards,\n[Agency Name] Team`}
                  value={form.body} onChange={e=>setForm({...form,body:e.target.value})} />
                <div className="text-xs text-muted" style={{marginTop:'4px'}}>
                  {form.body.length} chars · {form.body.split('\n').length} lines
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Template'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// DASHBOARD WIDGETS TAB
// ══════════════════════════════════════════════════════════
const DASHBOARD_WIDGET_DEFS = [
  // ── KPI Strip ──────────────────────────────────────────
  {
    id:'kpi-at-risk', section:'KPI Cards', icon:'⚠️', name:'At Risk',
    desc:'Count of work orders whose deadline is close and a phase is still stuck or not started.',
    defaultOn:true, impact:'high'
  },
  {
    id:'kpi-awaiting-approval', section:'KPI Cards', icon:'⏳', name:'Awaiting Approval',
    desc:'Count of work orders pending internal or client approval this month.',
    defaultOn:true, impact:'high'
  },
  {
    id:'kpi-in-production', section:'KPI Cards', icon:'🎬', name:'In Production',
    desc:'Work orders actively being created, designed or edited right now.',
    defaultOn:true, impact:'high'
  },
  {
    id:'kpi-posted-monitoring', section:'KPI Cards', icon:'📡', name:'Posted & Monitoring',
    desc:'Work orders published and currently being monitored for performance.',
    defaultOn:true, impact:'medium'
  },
  {
    id:'kpi-closed-month', section:'KPI Cards', icon:'✅', name:'Closed This Month',
    desc:'Count of work orders fully completed and closed in the current month.',
    defaultOn:true, impact:'medium'
  },
  {
    id:'kpi-overdue', section:'KPI Cards', icon:'🚨', name:'Overdue Tasks',
    desc:'Tasks that have passed their SLA deadline and are flagged for escalation.',
    defaultOn:true, impact:'high'
  },
  // ── Workload & Team ────────────────────────────────────
  {
    id:'team-workload', section:'Team & Workload', icon:'👥', name:'Team Workload',
    desc:'Each team member\'s open task count, capacity percentage and availability status.',
    defaultOn:true, impact:'high'
  },
  {
    id:'my-tasks', section:'Team & Workload', icon:'📋', name:'My Tasks Today',
    desc:'Personalised task list for the logged-in user — due today and overdue.',
    defaultOn:true, impact:'high'
  },
  {
    id:'capacity-heatmap', section:'Team & Workload', icon:'🗓️', name:'Capacity Calendar',
    desc:'Weekly view of team capacity — who is available, busy or on leave.',
    defaultOn:false, impact:'medium'
  },
  // ── Work Orders ────────────────────────────────────────
  {
    id:'at-risk-list', section:'Work Orders', icon:'⚠️', name:'At Risk Work Orders',
    desc:'Detailed list of specific work orders at risk — which phase is stuck and by how many days.',
    defaultOn:true, impact:'high'
  },
  {
    id:'approvals-queue', section:'Work Orders', icon:'✅', name:'Approvals Queue',
    desc:'Every work order currently pending internal or client approval, with one-click jump.',
    defaultOn:true, impact:'high'
  },
  {
    id:'upcoming-deadlines', section:'Work Orders', icon:'📅', name:'Upcoming Deadlines',
    desc:'Work orders with deadlines in the next 7 days — sorted by urgency.',
    defaultOn:true, impact:'high'
  },
  {
    id:'recently-completed', section:'Work Orders', icon:'🏁', name:'Recently Completed',
    desc:'Work orders closed in the last 7 days — quick reference for what was delivered.',
    defaultOn:false, impact:'low'
  },
  // ── Client & Performance ───────────────────────────────
  {
    id:'client-snapshot', section:'Client & Performance', icon:'🏢', name:'Client Snapshot',
    desc:'Per-client active work orders, pending approvals and MRR contribution.',
    defaultOn:true, impact:'medium'
  },
  {
    id:'monitoring-watch', section:'Client & Performance', icon:'📡', name:'Monitoring Watch',
    desc:'Work orders flagged as Needs Attention or Action Required in the monitoring phase.',
    defaultOn:true, impact:'high'
  },
  {
    id:'client-health', section:'Client & Performance', icon:'💚', name:'Client Health Score',
    desc:'Traffic-light status per client — based on approval response time, overdue tasks and NPS.',
    defaultOn:false, impact:'medium'
  },
  // ── Finance & Cost ─────────────────────────────────────
  {
    id:'cost-snapshot', section:'Finance & Cost', icon:'💰', name:'Cost Snapshot',
    desc:'This month\'s spend by category vs planned budget — from Cost Tracking.',
    defaultOn:true, impact:'medium'
  },
  {
    id:'mrr-summary', section:'Finance & Cost', icon:'📈', name:'MRR Summary',
    desc:'Total monthly recurring revenue from active clients — with trend vs last month.',
    defaultOn:false, impact:'medium'
  },
  {
    id:'budget-burn', section:'Finance & Cost', icon:'🔥', name:'Budget Burn Rate',
    desc:'How fast the agency is spending vs budget — projected overspend alert.',
    defaultOn:false, impact:'medium'
  },
  // ── Activity & Insights ────────────────────────────────
  {
    id:'activity-feed', section:'Activity & Insights', icon:'⚡', name:'Live Activity Feed',
    desc:'Real-time stream of recent events — approvals, phase changes, comments, reallocations.',
    defaultOn:true, impact:'medium'
  },
  {
    id:'content-calendar-preview', section:'Activity & Insights', icon:'🗓️', name:'Content Calendar Preview',
    desc:'This week\'s scheduled posts across all clients — quick view without switching pages.',
    defaultOn:false, impact:'medium'
  },
  {
    id:'platform-performance', section:'Activity & Insights', icon:'📊', name:'Platform Performance',
    desc:'Aggregate engagement metrics by platform across all monitored work orders.',
    defaultOn:false, impact:'medium'
  },
];

const DW_SECTION_COLORS = {
  'KPI Cards':             '#3b7ef0',
  'Team & Workload':       '#9050e8',
  'Work Orders':           '#e87830',
  'Client & Performance':  '#1db87a',
  'Finance & Cost':        '#f7c34f',
  'Activity & Insights':   '#18b8b8',
};

const DW_IMPACT_COLORS = { high:'#e84545', medium:'#f7c34f', low:'#1db87a' };

function DashboardWidgetsTab() {
  // Store widget states in localStorage (per-user preference, no DB needed)
  const STORAGE_KEY = 'leadex_dashboard_widgets';

  const loadSettings = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch(e) { return {}; }
  };
  const [settings, setSettings] = useState(loadSettings);

  const isEnabled = (id) => {
    const s = settings[id];
    if (s === undefined) return DASHBOARD_WIDGET_DEFS.find(w=>w.id===id)?.defaultOn !== false;
    return s;
  };

  const toggle = (id) => {
    const newSettings = { ...settings, [id]: !isEnabled(id) };
    setSettings(newSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
  };

  const resetDefaults = () => {
    if (!window.confirm('Reset all Dashboard Widgets to default settings? Continue?')) return;
    setSettings({});
    localStorage.removeItem(STORAGE_KEY);
  };

  const enabledCount  = DASHBOARD_WIDGET_DEFS.filter(w=>isEnabled(w.id)).length;
  const disabledCount = DASHBOARD_WIDGET_DEFS.length - enabledCount;

  // Group by section
  const sections = [...new Set(DASHBOARD_WIDGET_DEFS.map(w=>w.section))];

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">📊 Dashboard Widgets</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>
            Control which widgets appear on the Dashboard for all users. Toggle off to hide, toggle on to show.
            Changes take effect immediately on next Dashboard load.
          </div>
        </div>
        <div className="flex" style={{gap:'8px',alignItems:'center'}}>
          <span className="text-xs text-muted">{enabledCount} shown · {disabledCount} hidden</span>
          <button className="btn btn-secondary" onClick={resetDefaults}>↺ Reset to Default</button>
        </div>
      </div>

      {/* Summary strip */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:'10px',marginBottom:'20px'}}>
        {sections.map(sec => {
          const secWidgets  = DASHBOARD_WIDGET_DEFS.filter(w=>w.section===sec);
          const secEnabled  = secWidgets.filter(w=>isEnabled(w.id)).length;
          const color       = DW_SECTION_COLORS[sec]||'#888';
          return (
            <div key={sec} className="card" style={{padding:'10px 14px',borderTop:`3px solid ${color}`,textAlign:'center'}}>
              <div className="text-xs fw-700" style={{color,fontSize:'0.65rem',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'4px'}}>{sec}</div>
              <div style={{fontSize:'1.4rem',fontWeight:800,fontFamily:"'Fraunces',serif"}}>{secEnabled}<span style={{fontSize:'0.8rem',color:'var(--text3)',fontWeight:400}}>/{secWidgets.length}</span></div>
            </div>
          );
        })}
      </div>

      {/* Widget grid by section */}
      {sections.map(sec => {
        const secWidgets = DASHBOARD_WIDGET_DEFS.filter(w=>w.section===sec);
        const secColor   = DW_SECTION_COLORS[sec]||'#888';
        return (
          <div key={sec} className="mb-20">
            <div className="flex" style={{alignItems:'center',gap:'8px',marginBottom:'10px'}}>
              <div style={{width:'12px',height:'12px',borderRadius:'50%',background:secColor,flexShrink:0}} />
              <span className="fw-700 text-sm" style={{color:secColor}}>{sec}</span>
              <span className="badge badge-gray" style={{fontSize:'0.65rem'}}>{secWidgets.filter(w=>isEnabled(w.id)).length}/{secWidgets.length} active</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'10px'}}>
              {secWidgets.map(w => {
                const on = isEnabled(w.id);
                return (
                  <div key={w.id} className="card" style={{padding:'14px 16px',opacity:on?1:0.5,borderLeft:`3px solid ${on?secColor:'var(--border)'}`,transition:'all 0.2s'}}>
                    <div className="flex-between">
                      <div className="flex" style={{gap:'10px',alignItems:'flex-start'}}>
                        <span style={{fontSize:'1.4rem',lineHeight:1,flexShrink:0}}>{w.icon}</span>
                        <div>
                          <div className="flex" style={{gap:'6px',alignItems:'center',flexWrap:'wrap'}}>
                            <span className="fw-700 text-sm">{w.name}</span>
                            {!on && <span className="badge badge-gray" style={{fontSize:'0.6rem'}}>Hidden</span>}
                            {w.defaultOn && on && <span className="badge badge-green" style={{fontSize:'0.6rem'}}>Default</span>}
                          </div>
                          <div className="text-xs text-muted" style={{marginTop:'3px',lineHeight:1.5}}>{w.desc}</div>
                          <div style={{marginTop:'6px',display:'flex',gap:'4px',alignItems:'center'}}>
                            <span style={{fontSize:'0.6rem',fontWeight:700,padding:'2px 6px',borderRadius:'10px',background:`${DW_IMPACT_COLORS[w.impact]||'#888'}18`,color:DW_IMPACT_COLORS[w.impact]||'#888'}}>
                              {w.impact === 'high' ? '🔴 High Impact' : w.impact === 'medium' ? '🟡 Medium Impact' : '🟢 Low Impact'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={{flexShrink:0,marginLeft:'8px'}}>
                        <Toggle checked={on} onChange={()=>toggle(w.id)} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Note */}
      <div className="card" style={{padding:'12px 16px',background:'rgba(59,126,240,0.03)',border:'1px solid rgba(59,126,240,0.15)'}}>
        <div className="text-xs text-muted" style={{lineHeight:1.7}}>
          💡 <b>Note:</b> These settings are saved in your browser and apply to the Dashboard for all users on this device.
          Widgets marked <b>Default</b> are pre-enabled in new installs.
          <b> High Impact</b> widgets are strongly recommended to keep enabled for efficient agency operations.
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// 📑 CONTRACTS / RETAINER TERMS TAB
// ══════════════════════════════════════════════════════════
const CONTRACT_PAYMENT_TERMS = ['Advance (100% upfront)','50% Advance + 50% on delivery','Monthly (1st of month)','Monthly (end of month)','Net 15','Net 30','Net 45','Milestone-based','Quarterly'];
const CONTRACT_INVOICE_CYCLES = ['Monthly','Bi-monthly','Quarterly','Per Project','Per Milestone'];
const CONTRACT_STATUSES = [
  {value:'active',    label:'✅ Active',          color:'#1db87a'},
  {value:'draft',     label:'📝 Draft',           color:'#6b7280'},
  {value:'expired',   label:'🔴 Expired',         color:'#e84545'},
  {value:'paused',    label:'⏸️ Paused',          color:'#e87830'},
  {value:'renewed',   label:'🔄 Renewed',         color:'#3b7ef0'},
];

function ContractsTab() {
  const [contracts, setContracts] = useState([]);
  const [clients,   setClients]   = useState([]);
  const [services,  setServices]  = useState([]);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [filter,    setFilter]    = useState('All');

  const emptyForm = {
    client:'', service:'', title:'', status:'active',
    start_date:'', end_date:'', value:'', monthly_value:'',
    payment_terms:'Monthly (1st of month)', invoice_cycle:'Monthly',
    auto_renewal:true, renewal_notice_days:30,
    penalty_clause:'', special_terms:'', notes:''
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [c, cl, s] = await Promise.all([api.get('/contracts'), api.get('/clients?active_only=false'), api.get('/services')]);
    setContracts(c.data?.data || c.data || []);
    setClients(cl.data?.data || cl.data || []);
    setServices(s.data?.data || s.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      client: c.client||'', service: c.service||'', title: c.title||'',
      status: c.status||'active', start_date: c.start_date||'', end_date: c.end_date||'',
      value: c.value||'', monthly_value: c.monthly_value||'',
      payment_terms: c.payment_terms||'Monthly (1st of month)',
      invoice_cycle: c.invoice_cycle||'Monthly',
      auto_renewal: c.auto_renewal!==false,
      renewal_notice_days: c.renewal_notice_days||30,
      penalty_clause: c.penalty_clause||'', special_terms: c.special_terms||'', notes: c.notes||''
    });
    setModal(true);
  };
  const save = async () => {
    if (!form.client || !form.title) { alert('Client and title required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, value:parseFloat(form.value)||0, monthly_value:parseFloat(form.monthly_value)||0, renewal_notice_days:parseInt(form.renewal_notice_days)||30 };
      if (editing) await api.put('/contracts/'+editing.id, payload);
      else         await api.post('/contracts', payload);
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };
  const del = async (c) => { if(!window.confirm('Delete contract "'+c.title+'"?')) return; await api.delete('/contracts/'+c.id); load(); };

  const statusInfo = (s) => CONTRACT_STATUSES.find(x=>x.value===s) || CONTRACT_STATUSES[0];
  const filtered = filter==='All' ? contracts : contracts.filter(c=>c.status===filter);

  // KPI
  const active = contracts.filter(c=>c.status==='active');
  const totalMRR = active.reduce((s,c)=>s+(parseFloat(c.monthly_value)||0),0);
  const expiringSoon = active.filter(c=>{ if(!c.end_date) return false; const d=(new Date(c.end_date)-new Date())/(1000*60*60*24); return d>=0&&d<=30; });

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">📑 Contracts & Retainer Terms</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Client contracts, retainer agreements and payment terms. Used for billing, renewal alerts and legal reference.</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Contract</button>
      </div>

      {/* KPI strip */}
      {contracts.length>0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:'10px',marginBottom:'16px'}}>
          <div className="card" style={{padding:'12px 16px',borderTop:'3px solid var(--accent)'}}>
            <div className="text-xs fw-700 text-muted" style={{textTransform:'uppercase',fontSize:'0.65rem'}}>Active</div>
            <div style={{fontSize:'1.8rem',fontWeight:800,fontFamily:"'Fraunces',serif"}}>{active.length}</div>
          </div>
          <div className="card" style={{padding:'12px 16px',borderTop:'3px solid #1db87a'}}>
            <div className="text-xs fw-700 text-muted" style={{textTransform:'uppercase',fontSize:'0.65rem'}}>MRR</div>
            <div style={{fontSize:'1.1rem',fontWeight:800,color:'#1db87a'}}>{fmtRupees(totalMRR)}</div>
          </div>
          {expiringSoon.length>0 && (
            <div className="card" style={{padding:'12px 16px',borderTop:'3px solid #e84545'}}>
              <div className="text-xs fw-700" style={{color:'#e84545',textTransform:'uppercase',fontSize:'0.65rem'}}>Expiring Soon</div>
              <div style={{fontSize:'1.8rem',fontWeight:800,color:'#e84545',fontFamily:"'Fraunces',serif"}}>{expiringSoon.length}</div>
              <div className="text-xs text-muted">within 30 days</div>
            </div>
          )}
        </div>
      )}

      {/* Status filters */}
      <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'14px'}}>
        {['All',...CONTRACT_STATUSES.map(s=>s.value)].map(s=>{
          const info = s==='All'?{label:'All',color:'var(--accent)'}:statusInfo(s);
          const count = s==='All'?contracts.length:contracts.filter(c=>c.status===s).length;
          const act = filter===s;
          return <button key={s} onClick={()=>setFilter(s)} style={{padding:'4px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',border:`2px solid ${act?(info.color||'var(--accent)'):'var(--border)'}`,background:act?(info.color||'var(--accent)'):'var(--bg2)',color:act?'#fff':'var(--text2)'}}>{s==='All'?'All':info.label} ({count})</button>;
        })}
      </div>

      {/* Table */}
      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Contract / Retainer</th><th>Client</th><th>Status</th><th>Period</th><th>Monthly Value</th><th>Payment Terms</th><th>Auto-Renewal</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length===0 && <tr><td colSpan={8} style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">{contracts.length===0?'No contracts yet. Click + Add Contract.':'No contracts match filter.'}</td></tr>}
            {filtered.map(c=>{
              const si = statusInfo(c.status||'active');
              const daysLeft = c.end_date ? Math.ceil((new Date(c.end_date)-new Date())/(1000*60*60*24)) : null;
              return (
                <tr key={c.id}>
                  <td>
                    <div className="fw-700">{c.title}</div>
                    {c.service && <div className="text-xs text-muted">{c.service}</div>}
                  </td>
                  <td className="fw-700 text-sm">{c.client}</td>
                  <td><span className="badge" style={{background:`${si.color}18`,color:si.color,border:`1px solid ${si.color}40`,fontSize:'0.68rem'}}>{si.label}</span></td>
                  <td>
                    <div className="text-xs">{c.start_date||'—'} → {c.end_date||'Open'}</div>
                    {daysLeft!==null && daysLeft<=30 && daysLeft>=0 && <div className="text-xs" style={{color:'#e84545'}}>⚠️ {daysLeft}d left</div>}
                  </td>
                  <td><span className="mono fw-700" style={{color:'#1db87a'}}>{fmtRupees(c.monthly_value)}</span><span className="text-xs text-muted">/mo</span></td>
                  <td className="text-xs text-muted">{c.payment_terms||'—'}</td>
                  <td>{c.auto_renewal!==false?<span className="badge badge-green" style={{fontSize:'0.65rem'}}>🔄 Yes</span>:<span className="badge badge-gray" style={{fontSize:'0.65rem'}}>No</span>}</td>
                  <td>
                    <div className="flex" style={{gap:'4px'}}>
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(c)}>✏️</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(c)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'640px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Contract':'+ Add Contract / Retainer'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Client</label>
                  <select className="form-select" value={form.client} onChange={e=>setForm({...form,client:e.target.value})}>
                    <option value="">— Select Client —</option>
                    {clients.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Contract / Retainer Title</label>
                  <input className="form-input" placeholder="e.g. Social Media Retainer 2026" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Linked Service</label>
                  <select className="form-select" value={form.service} onChange={e=>setForm({...form,service:e.target.value})}>
                    <option value="">— Select Service —</option>
                    {services.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                    {CONTRACT_STATUSES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input className="form-input" type="date" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(leave blank = open-ended)</span></label>
                  <input className="form-input" type="date" value={form.end_date} onChange={e=>setForm({...form,end_date:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Monthly Value (₹)</label>
                  <input className="form-input" type="number" placeholder="0" value={form.monthly_value} onChange={e=>setForm({...form,monthly_value:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Total Contract Value (₹)</label>
                  <input className="form-input" type="number" placeholder="0" value={form.value} onChange={e=>setForm({...form,value:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Payment Terms</label>
                  <select className="form-select" value={form.payment_terms} onChange={e=>setForm({...form,payment_terms:e.target.value})}>
                    {CONTRACT_PAYMENT_TERMS.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Invoice Cycle</label>
                  <select className="form-select" value={form.invoice_cycle} onChange={e=>setForm({...form,invoice_cycle:e.target.value})}>
                    {CONTRACT_INVOICE_CYCLES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Auto-Renewal</label>
                  <select className="form-select" value={form.auto_renewal?'yes':'no'} onChange={e=>setForm({...form,auto_renewal:e.target.value==='yes'})}>
                    <option value="yes">🔄 Yes — auto-renew</option>
                    <option value="no">❌ No — manual renewal</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Renewal Notice (days before expiry)</label>
                  <input className="form-input" type="number" placeholder="30" value={form.renewal_notice_days} onChange={e=>setForm({...form,renewal_notice_days:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Penalty Clause <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(early termination / late payment)</span></label>
                <input className="form-input" placeholder="e.g. 1 month fee penalty on early exit within 3 months" value={form.penalty_clause} onChange={e=>setForm({...form,penalty_clause:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Special Terms</label>
                <textarea className="form-textarea" style={{minHeight:'60px'}} placeholder="e.g. Scope includes up to 20 posts/month. Any additional work billed separately at ₹1500/hr." value={form.special_terms} onChange={e=>setForm({...form,special_terms:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" style={{minHeight:'50px'}} placeholder="Internal notes..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Contract'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// 🏷️ TAGS & LABELS TAB
// ══════════════════════════════════════════════════════════
const TAG_APPLIES_TO = ['Work Orders','Tasks','Clients','Content Calendar','All'];
const TAG_COLOR_OPTS = ['#3b7ef0','#e84545','#1db87a','#e87830','#9050e8','#f7c34f','#18b8b8','#e83898','#6b7280','#6c58f0','#25d366','#ff9900'];
const DEFAULT_TAGS = [
  {name:'urgent',        color:'#e84545', applies_to:'All',           desc:'Needs immediate attention'},
  {name:'festival',      color:'#f7c34f', applies_to:'Work Orders',   desc:'Festival or special day campaign'},
  {name:'paid-campaign', color:'#e87830', applies_to:'Work Orders',   desc:'Paid advertising campaign'},
  {name:'organic',       color:'#1db87a', applies_to:'Work Orders',   desc:'Organic / non-paid content'},
  {name:'hold',          color:'#6b7280', applies_to:'All',           desc:'On hold — pending client action'},
  {name:'vip-client',    color:'#9050e8', applies_to:'Clients',       desc:'High-value or priority client'},
  {name:'revision',      color:'#e84545', applies_to:'Tasks',         desc:'Requires revision or rework'},
  {name:'approved',      color:'#1db87a', applies_to:'Tasks',         desc:'Approved and ready to publish'},
  {name:'video',         color:'#6c58f0', applies_to:'Tasks',         desc:'Video content task'},
  {name:'repost',        color:'#18b8b8', applies_to:'Tasks',         desc:'Repurposed or reposted content'},
  {name:'seasonal',      color:'#e87830', applies_to:'Work Orders',   desc:'Seasonal campaign content'},
  {name:'performance',   color:'#3b7ef0', applies_to:'Work Orders',   desc:'Performance marketing focused'},
];

function TagsTab() {
  const [tags,    setTags]    = useState([]);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [filterApplies, setFilterApplies] = useState('All');

  const emptyForm = { name:'', color:'#3b7ef0', applies_to:'All', desc:'' };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const r = await api.get('/tags');
    setTags(r.data?.data || r.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    if (!window.confirm(`Add ${DEFAULT_TAGS.length} standard tags? Continue?`)) return;
    setSeeding(true);
    try {
      let added=0, failed=0;
      for (const t of DEFAULT_TAGS) {
        try { await api.post('/tags', t); added++; } catch(e) { failed++; }
      }
      if (failed) alert(`${added} added, ${failed} failed.`);
      load();
    } finally { setSeeding(false); }
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (t) => { setEditing(t); setForm({name:t.name||'',color:t.color||'#3b7ef0',applies_to:t.applies_to||'All',desc:t.desc||t.description||''}); setModal(true); };
  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name:form.name.toLowerCase().replace(/\s+/g,'-'), color:form.color, applies_to:form.applies_to, desc:form.desc };
      if (editing) await api.put('/tags/'+editing.id, payload);
      else         await api.post('/tags', payload);
      setModal(false); load();
    } finally { setSaving(false); }
  };
  const del = async (t) => { if(!window.confirm('Delete tag "#'+t.name+'"?')) return; await api.delete('/tags/'+t.id); load(); };

  const filtered = filterApplies==='All' ? tags : tags.filter(t=>t.applies_to===filterApplies||t.applies_to==='All');

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🏷️ Tags & Labels</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Global tags for work orders, tasks and clients. Enables quick filtering and cross-page search across the system.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          {tags.length===0 && <button className="btn btn-secondary" onClick={seedDefaults} disabled={seeding}>{seeding?'Adding...':'⚡ Add Standard Tags'}</button>}
          <button className="btn btn-primary" onClick={openAdd}>+ Add Tag</button>
        </div>
      </div>

      {/* Filter */}
      <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'14px'}}>
        {['All',...TAG_APPLIES_TO].map(a=>{
          const count = a==='All'?tags.length:tags.filter(t=>t.applies_to===a||t.applies_to==='All').length;
          const act = filterApplies===a;
          return <button key={a} onClick={()=>setFilterApplies(a)} style={{padding:'4px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',border:`2px solid ${act?'var(--accent)':'var(--border)'}`,background:act?'var(--accent)':'var(--bg2)',color:act?'#fff':'var(--text2)'}}>{a} ({count})</button>;
        })}
      </div>

      {/* Tags grid */}
      <div style={{display:'flex',flexWrap:'wrap',gap:'10px',marginBottom:'16px'}}>
        {filtered.map(t=>(
          <div key={t.id} className="card" style={{padding:'10px 14px',display:'inline-flex',alignItems:'center',gap:'10px',borderLeft:`4px solid ${t.color||'#888'}`}}>
            <span style={{padding:'4px 12px',borderRadius:'20px',fontSize:'0.82rem',fontWeight:700,background:`${t.color||'#888'}18`,color:t.color||'#888',border:`1px solid ${t.color||'#888'}40`}}>#{t.name}</span>
            <div>
              <div className="text-xs fw-700">{t.applies_to}</div>
              {(t.desc||t.description) && <div className="text-xs text-muted">{t.desc||t.description}</div>}
            </div>
            <div className="flex" style={{gap:'4px',marginLeft:'4px'}}>
              <button className="btn btn-secondary" style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={()=>openEdit(t)}>✏️</button>
              <button className="btn btn-red"       style={{padding:'2px 7px',fontSize:'0.65rem'}} onClick={()=>del(t)}>✕</button>
            </div>
          </div>
        ))}
        {filtered.length===0 && <div className="text-sm text-muted" style={{padding:'20px'}}>No tags yet. Click <b>⚡ Add Standard Tags</b> or <b>+ Add Tag</b>.</div>}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'440px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Tag':'+ Add Tag'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tag Name <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(no spaces — use-hyphens)</span></label>
                  <input className="form-input" placeholder="e.g. urgent, paid-campaign" value={form.name} onChange={e=>setForm({...form,name:e.target.value.toLowerCase().replace(/\s+/g,'-')})} />
                  {form.name && <div style={{marginTop:'6px'}}><span style={{padding:'3px 10px',borderRadius:'20px',fontSize:'0.8rem',fontWeight:700,background:`${form.color}18`,color:form.color,border:`1px solid ${form.color}40`}}>#{form.name}</span></div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Applies To</label>
                  <select className="form-select" value={form.applies_to} onChange={e=>setForm({...form,applies_to:e.target.value})}>
                    {TAG_APPLIES_TO.map(a=><option key={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'8px',marginTop:'4px'}}>
                  {TAG_COLOR_OPTS.map(c=>(
                    <div key={c} onClick={()=>setForm({...form,color:c})} style={{width:'28px',height:'28px',borderRadius:'50%',background:c,cursor:'pointer',border:form.color===c?'3px solid var(--text)':'2px solid transparent'}} />
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="form-input" placeholder="What this tag means..." value={form.desc} onChange={e=>setForm({...form,desc:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// 🔔 NOTIFICATION RULES TAB
// ══════════════════════════════════════════════════════════
const NOTIF_EVENTS = [
  {value:'task_assigned',        label:'📋 Task Assigned',          desc:'When a task is assigned to a team member'},
  {value:'task_due_soon',        label:'⏰ Task Due Soon',           desc:'When a task deadline is within N days'},
  {value:'task_overdue',         label:'🚨 Task Overdue',           desc:'When a task passes its SLA deadline'},
  {value:'phase_changed',        label:'🔄 Phase Changed',          desc:'When a work order moves to a new phase'},
  {value:'approval_due',         label:'⏳ Approval Due',           desc:'When client approval is pending for N days'},
  {value:'approval_received',    label:'✅ Approval Received',      desc:'When client approves content'},
  {value:'approval_rejected',    label:'❌ Approval Rejected',      desc:'When client rejects content with feedback'},
  {value:'sla_breach',           label:'🔴 SLA Breach',             desc:'When SLA turnaround time is exceeded'},
  {value:'work_order_created',   label:'🆕 Work Order Created',     desc:'When a new work order is created'},
  {value:'work_order_completed', label:'🏁 Work Order Completed',   desc:'When a work order is fully completed'},
  {value:'client_feedback',      label:'💬 Client Feedback',        desc:'When a client adds a comment or note'},
  {value:'contract_expiry',      label:'📑 Contract Expiring',      desc:'When a contract is expiring within N days'},
  {value:'budget_exceeded',      label:'💰 Budget Exceeded',        desc:'When monthly spend exceeds the budget'},
];
const NOTIF_CHANNELS = ['In-App','Email','WhatsApp','Slack'];

function NotificationRulesTab() {
  const [rules,   setRules]   = useState([]);
  const [team,    setTeam]    = useState([]);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);

  const emptyForm = { event:'task_assigned', notify_roles:[], notify_members:[], channels:['In-App'], advance_days:1, enabled:true, notes:'' };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const [r, t] = await Promise.all([api.get('/notification-rules'), api.get('/team-members')]);
    setRules(r.data?.data || r.data || []);
    setTeam(t.data?.data || t.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    let roles = r.notify_roles; if (typeof roles==='string') { try{roles=JSON.parse(roles);}catch(e){roles=[];} }
    let members = r.notify_members; if (typeof members==='string') { try{members=JSON.parse(members);}catch(e){members=[];} }
    let channels = r.channels; if (typeof channels==='string') { try{channels=JSON.parse(channels);}catch(e){channels=['In-App'];} }
    setForm({ event:r.event||'task_assigned', notify_roles:roles||[], notify_members:members||[], channels:channels||['In-App'], advance_days:r.advance_days||1, enabled:r.enabled!==false, notes:r.notes||'' });
    setModal(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { event:form.event, notify_roles:JSON.stringify(form.notify_roles), notify_members:JSON.stringify(form.notify_members), channels:JSON.stringify(form.channels), advance_days:parseInt(form.advance_days)||1, enabled:form.enabled, notes:form.notes };
      if (editing) await api.put('/notification-rules/'+editing.id, payload);
      else         await api.post('/notification-rules', payload);
      setModal(false); load();
    } catch(e) { alert('Save failed: '+(e.response?.data?.message||e.message)); }
    finally { setSaving(false); }
  };
  const del    = async (r) => { if(!window.confirm('Delete this rule?')) return; await api.delete('/notification-rules/'+r.id); load(); };
  const toggle = async (r) => { await api.put('/notification-rules/'+r.id, { event:r.event, enabled:!r.enabled }); load(); };

  const toggleArr = (key, val) => setForm(f=>({ ...f, [key]:f[key].includes(val)?f[key].filter(x=>x!==val):[...f[key],val] }));
  const eventInfo = (val) => NOTIF_EVENTS.find(e=>e.value===val) || NOTIF_EVENTS[0];

  const parseArr = (v) => { if (Array.isArray(v)) return v; if (typeof v==='string') { try{return JSON.parse(v);}catch(e){return [];} } return []; };

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🔔 Notification Rules</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Define who gets notified for what — phase changes, approvals, SLA breaches, deadlines. Keeps the team informed automatically.</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Rule</button>
      </div>

      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Event</th><th>Notify</th><th>Channels</th><th>Advance Notice</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {rules.length===0 && <tr><td colSpan={6} style={{padding:'24px',textAlign:'center'}} className="text-muted text-sm">No rules yet. Click <b>+ Add Rule</b> to set up your first notification rule.</td></tr>}
            {rules.map(r=>{
              const ei = eventInfo(r.event);
              const roles = parseArr(r.notify_roles);
              const members = parseArr(r.notify_members);
              const channels = parseArr(r.channels);
              return (
                <tr key={r.id} style={{opacity:r.enabled===false?0.45:1}}>
                  <td>
                    <div className="fw-700 text-sm">{ei.label}</div>
                    <div className="text-xs text-muted">{ei.desc}</div>
                  </td>
                  <td>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'3px'}}>
                      {roles.map(role=><span key={role} className="badge badge-blue" style={{fontSize:'0.65rem'}}>{role}</span>)}
                      {members.map(m=><span key={m} className="badge badge-gray" style={{fontSize:'0.65rem'}}>{m}</span>)}
                      {!roles.length&&!members.length&&<span className="text-xs text-muted">—</span>}
                    </div>
                  </td>
                  <td>
                    <div style={{display:'flex',gap:'3px',flexWrap:'wrap'}}>
                      {channels.map(c=><span key={c} className="badge badge-purple" style={{fontSize:'0.65rem'}}>{c}</span>)}
                    </div>
                  </td>
                  <td className="text-xs text-muted">{['task_due_soon','approval_due','contract_expiry'].includes(r.event)?`${r.advance_days}d before`:'—'}</td>
                  <td><Toggle checked={r.enabled!==false} onChange={()=>toggle(r)} /></td>
                  <td>
                    <div className="flex" style={{gap:'4px'}}>
                      <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>openEdit(r)}>✏️</button>
                      <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(r)}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay open" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:'560px'}}>
            <div className="modal-header">
              <div className="modal-title">{editing?'✏️ Edit Rule':'+ Add Notification Rule'}</div>
              <div className="icon-btn" onClick={()=>setModal(false)}>✕</div>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Trigger Event</label>
                <select className="form-select" value={form.event} onChange={e=>setForm({...form,event:e.target.value})}>
                  {NOTIF_EVENTS.map(e=><option key={e.value} value={e.value}>{e.label} — {e.desc}</option>)}
                </select>
              </div>
              {['task_due_soon','approval_due','contract_expiry'].includes(form.event) && (
                <div className="form-group">
                  <label className="form-label">Send notification N days before</label>
                  <input className="form-input" type="number" min="1" max="90" value={form.advance_days} onChange={e=>setForm({...form,advance_days:e.target.value})} style={{maxWidth:'100px'}} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Notify Roles <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(everyone with this role)</span></label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'8px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
                  {['Account Manager','Creative Director','Copywriter','Designer','Video Editor','SEO Specialist','All Team'].map(role=>(
                    <label key={role} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.75rem',cursor:'pointer',padding:'3px 8px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'6px'}}>
                      <input type="checkbox" checked={form.notify_roles.includes(role)} onChange={()=>toggleArr('notify_roles',role)} style={{accentColor:'var(--accent)'}} />
                      {role}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notify Specific Members</label>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'8px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
                  {team.filter(m=>!m.deleted_at).map(m=>(
                    <label key={m.id} style={{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'0.75rem',cursor:'pointer',padding:'3px 8px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'6px'}}>
                      <input type="checkbox" checked={form.notify_members.includes(m.name)} onChange={()=>toggleArr('notify_members',m.name)} style={{accentColor:'var(--accent)'}} />
                      {m.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notification Channels</label>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                  {NOTIF_CHANNELS.map(ch=>(
                    <label key={ch} style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'6px 12px',borderRadius:'8px',cursor:'pointer',border:`2px solid ${form.channels.includes(ch)?'var(--accent)':'var(--border)'}`,background:form.channels.includes(ch)?'rgba(59,126,240,0.06)':'var(--bg3)',fontSize:'0.8rem',fontWeight:600}}>
                      <input type="checkbox" checked={form.channels.includes(ch)} onChange={()=>toggleArr('channels',ch)} style={{display:'none'}} />
                      {ch}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-input" placeholder="Optional context..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'💾 '+(editing?'Update':'Save')+' Rule'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// 💳 PAYMENT TERMS TAB  (single config — stored in localStorage)
// ══════════════════════════════════════════════════════════
function PaymentTermsTab() {
  const STORAGE_KEY = 'leadex_payment_terms';
  const loadConfig = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)||'{}'); } catch(e) { return {}; } };

  const [config, setConfig] = useState(loadConfig);
  const [saved,  setSaved]  = useState(false);

  const update = (key, val) => setConfig(c=>({...c, [key]:val}));

  const saveConfig = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    setSaved(true); setTimeout(()=>setSaved(false), 2000);
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">💳 Payment & Billing Configuration</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>GST details, invoice settings, bank account and payment gateway. Used when generating invoices and client billing.</div>
        </div>
        <button className="btn btn-primary" onClick={saveConfig}>{saved?'✅ Saved!':'💾 Save Settings'}</button>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
        {/* GST & Tax */}
        <div className="card" style={{padding:'16px 18px'}}>
          <div className="fw-700 text-sm mb-12">🧾 GST & Tax Settings</div>
          <div className="form-group">
            <label className="form-label">GSTIN</label>
            <input className="form-input" placeholder="22AAAAA0000A1Z5" value={config.gstin||''} onChange={e=>update('gstin',e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">GST Rate (%)</label>
            <select className="form-select" value={config.gst_rate||'18'} onChange={e=>update('gst_rate',e.target.value)}>
              {['0','5','12','18','28'].map(r=><option key={r} value={r}>{r}% GST</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Business Legal Name</label>
            <input className="form-input" placeholder="As per GST registration" value={config.legal_name||''} onChange={e=>update('legal_name',e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Business Address</label>
            <textarea className="form-textarea" style={{minHeight:'60px'}} placeholder="Registered address for invoices" value={config.business_address||''} onChange={e=>update('business_address',e.target.value)} />
          </div>
        </div>

        {/* Invoice Settings */}
        <div className="card" style={{padding:'16px 18px'}}>
          <div className="fw-700 text-sm mb-12">📄 Invoice Settings</div>
          <div className="form-group">
            <label className="form-label">Invoice Prefix</label>
            <input className="form-input" placeholder="e.g. INV, OHT, LEX" value={config.invoice_prefix||''} onChange={e=>update('invoice_prefix',e.target.value)} />
            {config.invoice_prefix && <div className="text-xs text-muted" style={{marginTop:'4px'}}>Preview: <b>{config.invoice_prefix}-2026-001</b></div>}
          </div>
          <div className="form-group">
            <label className="form-label">Invoice Starting Number</label>
            <input className="form-input" type="number" placeholder="1" value={config.invoice_start||''} onChange={e=>update('invoice_start',e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Default Credit Period</label>
            <select className="form-select" value={config.credit_period||'15'} onChange={e=>update('credit_period',e.target.value)}>
              {['0 (Immediate)','7','15','30','45','60'].map(d=><option key={d} value={d}>{d==='0 (Immediate)'?d:d+' days'}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Invoice Footer Note</label>
            <textarea className="form-textarea" style={{minHeight:'60px'}} placeholder="e.g. Please pay within 15 days. Late payments attract 2% monthly interest." value={config.invoice_footer||''} onChange={e=>update('invoice_footer',e.target.value)} />
          </div>
        </div>

        {/* Bank Details */}
        <div className="card" style={{padding:'16px 18px'}}>
          <div className="fw-700 text-sm mb-12">🏦 Bank Account Details</div>
          <div className="form-group">
            <label className="form-label">Account Holder Name</label>
            <input className="form-input" placeholder="Name as per bank records" value={config.bank_name||''} onChange={e=>update('bank_name',e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Account Number</label>
            <input className="form-input" placeholder="Bank account number" value={config.bank_account||''} onChange={e=>update('bank_account',e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">IFSC Code</label>
              <input className="form-input" placeholder="e.g. SBIN0001234" value={config.ifsc||''} onChange={e=>update('ifsc',e.target.value.toUpperCase())} />
            </div>
            <div className="form-group">
              <label className="form-label">Bank Name</label>
              <input className="form-input" placeholder="e.g. SBI, HDFC" value={config.bank||''} onChange={e=>update('bank',e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">UPI ID <span className="text-xs text-muted" style={{fontWeight:400,textTransform:'none'}}>(for QR payments)</span></label>
            <input className="form-input" placeholder="e.g. agency@okaxis" value={config.upi||''} onChange={e=>update('upi',e.target.value)} />
          </div>
        </div>

        {/* Payment Gateway */}
        <div className="card" style={{padding:'16px 18px'}}>
          <div className="fw-700 text-sm mb-12">💳 Payment Gateway & Late Fees</div>
          <div className="form-group">
            <label className="form-label">Payment Gateway</label>
            <select className="form-select" value={config.gateway||''} onChange={e=>update('gateway',e.target.value)}>
              <option value="">— None / Manual —</option>
              {['Razorpay','PayU','PayTM for Business','Instamojo','Cashfree','Stripe','PayPal'].map(g=><option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Payment Link / Gateway URL</label>
            <input className="form-input" placeholder="https://rzp.io/your-link" value={config.payment_link||''} onChange={e=>update('payment_link',e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Late Fee (%/month)</label>
              <input className="form-input" type="number" min="0" max="5" step="0.5" placeholder="e.g. 2" value={config.late_fee||''} onChange={e=>update('late_fee',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Grace Period (days)</label>
              <input className="form-input" type="number" placeholder="e.g. 5" value={config.grace_days||''} onChange={e=>update('grace_days',e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Preferred Payment Method</label>
            <select className="form-select" value={config.preferred_payment||'Bank Transfer'} onChange={e=>update('preferred_payment',e.target.value)}>
              {['Bank Transfer','UPI','Cheque','Payment Gateway','Any'].map(m=><option key={m}>{m}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// 🌐 INTEGRATIONS / API KEYS TAB
// ══════════════════════════════════════════════════════════
const INTEGRATION_DEFS = [
  { id:'meta_bm',       icon:'📘', name:'Meta Business Manager', category:'Social Media',    fields:[{key:'bm_id',label:'Business Manager ID',ph:'123456789'},{key:'ad_account',label:'Ad Account ID',ph:'act_123456789'},{key:'pixel_id',label:'Pixel ID',ph:'123456789'}] },
  { id:'google_ads',    icon:'🎯', name:'Google Ads',            category:'Ads',             fields:[{key:'customer_id',label:'Customer ID',ph:'123-456-7890'},{key:'developer_token',label:'Developer Token',ph:'xxxx',secret:true}] },
  { id:'google_analytics',icon:'📊',name:'Google Analytics 4',   category:'Analytics',       fields:[{key:'ga4_id',label:'Measurement ID',ph:'G-XXXXXXXXXX'},{key:'property_id',label:'Property ID',ph:'123456789'}] },
  { id:'buffer',        icon:'📅', name:'Buffer',                category:'Scheduling',      fields:[{key:'access_token',label:'Access Token',ph:'xxxx',secret:true}] },
  { id:'hootsuite',     icon:'🦉', name:'Hootsuite',             category:'Scheduling',      fields:[{key:'api_key',label:'API Key',ph:'xxxx',secret:true}] },
  { id:'whatsapp_api',  icon:'💬', name:'WhatsApp Business API', category:'Messaging',       fields:[{key:'phone_id',label:'Phone Number ID',ph:'123456789'},{key:'waba_token',label:'Access Token',ph:'EAAx...',secret:true}] },
  { id:'zoho_books',    icon:'📒', name:'Zoho Books',            category:'Finance',         fields:[{key:'org_id',label:'Organisation ID',ph:'123456789'},{key:'client_id',label:'Client ID',ph:'xxxx'},{key:'refresh_token',label:'Refresh Token',ph:'xxxx',secret:true}] },
  { id:'razorpay',      icon:'💳', name:'Razorpay',              category:'Finance',         fields:[{key:'key_id',label:'Key ID',ph:'rzp_live_xxxx'},{key:'key_secret',label:'Key Secret',ph:'xxxx',secret:true}] },
  { id:'slack',         icon:'💼', name:'Slack',                 category:'Communication',   fields:[{key:'webhook_url',label:'Webhook URL',ph:'https://hooks.slack.com/...',secret:true}] },
  { id:'semrush',       icon:'🔍', name:'SEMrush',               category:'SEO',             fields:[{key:'api_key',label:'API Key',ph:'xxxx',secret:true}] },
  { id:'canva',         icon:'🎨', name:'Canva',                 category:'Design',          fields:[{key:'client_id',label:'Client ID',ph:'xxxx'},{key:'client_secret',label:'Client Secret',ph:'xxxx',secret:true}] },
  { id:'mailchimp',     icon:'📧', name:'Mailchimp',             category:'Email',           fields:[{key:'api_key',label:'API Key',ph:'xxxx-usX',secret:true},{key:'audience_id',label:'Audience ID',ph:'xxxx'}] },
];
const INTEG_CATEGORIES = ['All',...new Set(INTEGRATION_DEFS.map(i=>i.category))];

function IntegrationsTab() {
  const STORAGE_KEY = 'leadex_integrations';
  const loadConfig = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)||'{}'); } catch(e) { return {}; } };
  const [config,    setConfig]    = useState(loadConfig);
  const [connected, setConnected] = useState({});
  const [saved,     setSaved]     = useState('');
  const [reveal,    setReveal]    = useState({});
  const [filterCat, setFilterCat] = useState('All');

  const loadConnected = () => {
    const c = {};
    INTEGRATION_DEFS.forEach(i => { c[i.id] = i.fields.some(f => (config[i.id]||{})[f.key]?.trim()); });
    setConnected(c);
  };
  useEffect(() => { loadConnected(); }, [config]);

  const update = (integId, key, val) => setConfig(c=>({...c, [integId]:{...(c[integId]||{}), [key]:val}}));
  const saveInteg = (integId) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    setSaved(integId); setTimeout(()=>setSaved(''), 2000);
  };

  const filtered = filterCat==='All' ? INTEGRATION_DEFS : INTEGRATION_DEFS.filter(i=>i.category===filterCat);

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">🌐 Integrations & API Keys</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Connect your tools — Meta, Google, WhatsApp, scheduling, finance and more. Stored locally in this browser.</div>
        </div>
        <div className="badge badge-gray" style={{fontSize:'0.72rem',padding:'6px 12px'}}>🔒 Stored in browser — not sent to server</div>
      </div>

      {/* Category filter */}
      <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'16px'}}>
        {INTEG_CATEGORIES.map(cat=>{
          const count = cat==='All'?INTEGRATION_DEFS.length:INTEGRATION_DEFS.filter(i=>i.category===cat).length;
          const connCount = cat==='All'?Object.values(connected).filter(Boolean).length:INTEGRATION_DEFS.filter(i=>i.category===cat&&connected[i.id]).length;
          const act = filterCat===cat;
          return <button key={cat} onClick={()=>setFilterCat(cat)} style={{padding:'4px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',border:`2px solid ${act?'var(--accent)':'var(--border)'}`,background:act?'var(--accent)':'var(--bg2)',color:act?'#fff':'var(--text2)'}}>{cat} ({connCount}/{count})</button>;
        })}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:'14px'}}>
        {filtered.map(integ=>{
          const isConn = connected[integ.id];
          const cfg = config[integ.id] || {};
          return (
            <div key={integ.id} className="card" style={{padding:'14px 16px',borderLeft:`4px solid ${isConn?'#1db87a':'var(--border)'}`,opacity:1}}>
              <div className="flex-between mb-10">
                <div className="flex" style={{gap:'8px',alignItems:'center'}}>
                  <span style={{fontSize:'1.3rem'}}>{integ.icon}</span>
                  <div>
                    <div className="fw-700 text-sm">{integ.name}</div>
                    <span className="badge badge-gray" style={{fontSize:'0.62rem'}}>{integ.category}</span>
                  </div>
                </div>
                <span className={`badge ${isConn?'badge-green':'badge-gray'}`} style={{fontSize:'0.65rem'}}>{isConn?'✅ Connected':'⚪ Not set'}</span>
              </div>
              {integ.fields.map(f=>(
                <div key={f.key} className="form-group" style={{marginBottom:'8px'}}>
                  <label className="form-label" style={{fontSize:'0.72rem'}}>{f.label}</label>
                  <div className="flex" style={{gap:'6px'}}>
                    <input className="form-input" style={{fontSize:'0.78rem'}} type={f.secret&&!reveal[`${integ.id}_${f.key}`]?'password':'text'} placeholder={f.ph} value={cfg[f.key]||''} onChange={e=>update(integ.id,f.key,e.target.value)} />
                    {f.secret && <button onClick={()=>setReveal(r=>({...r,[`${integ.id}_${f.key}`]:!r[`${integ.id}_${f.key}`]}))} style={{padding:'0 8px',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',background:'var(--bg3)',cursor:'pointer',fontSize:'0.75rem',flexShrink:0}}>{reveal[`${integ.id}_${f.key}`]?'🙈':'👁️'}</button>}
                  </div>
                </div>
              ))}
              <button className="btn btn-secondary" style={{width:'100%',justifyContent:'center',fontSize:'0.75rem',marginTop:'4px'}} onClick={()=>saveInteg(integ.id)}>
                {saved===integ.id?'✅ Saved!':'💾 Save'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// 📁 FOLDER STRUCTURE TAB
// ══════════════════════════════════════════════════════════
const DEFAULT_FOLDER_TEMPLATES = [
  {
    name:'Standard Client Folder',
    desc:'Main folder structure for each client',
    structure:`📁 [ClientName]/
  📁 Brand Assets/
    📁 Logos/
    📁 Brand Guidelines/
    📁 Photos & Videos/
  📁 Content Calendar/
    📁 [YYYY-MM]/
      📁 Drafts/
      📁 Approved/
      📁 Published/
  📁 Creatives/
    📁 Static Posts/
    📁 Reels & Videos/
    📁 Stories/
    📁 Ad Creatives/
  📁 Reports/
    📁 Monthly Reports/
    📁 Campaign Reports/
  📁 Contracts & Invoices/`
  },
  {
    name:'Campaign Folder',
    desc:'For each individual campaign or work order',
    structure:`📁 [ClientName]_[CampaignName]_[YYYY-MM]/
  📁 Brief/
    📄 Campaign_Brief.pdf
    📄 Content_Calendar.xlsx
  📁 Scripts & Copy/
    📄 Captions_Draft_v1.docx
    📄 Captions_Final.docx
  📁 Creatives/
    📁 Design_WIP/
    📁 Design_Final/
    📁 Video_Raw/
    📁 Video_Edited/
  📁 Client_Approval/
    📁 Sent_For_Review/
    📁 Approved/
    📁 Revision_Notes/
  📁 Published/`
  },
  {
    name:'File Naming Convention',
    desc:'Standard file naming format for all assets',
    structure:`Static Post:
[ClientName]_[Platform]_[YYYY-MM-DD]_[Type]_v[N]
Example: ABCCorp_Instagram_2026-10-01_FeedPost_v2.psd

Reel / Video:
[ClientName]_[Platform]_[YYYY-MM-DD]_Reel_v[N]
Example: ABCCorp_Reels_2026-10-05_ProductLaunch_v1.mp4

Report:
[ClientName]_Report_[YYYY-MM]_v[N]
Example: ABCCorp_MonthlyReport_2026-10_v1.pdf

Ad Creative:
[ClientName]_[Platform]_[CampaignName]_[Format]_v[N]
Example: ABCCorp_MetaAds_Diwali_1080x1080_v3.jpg`
  }
];

function FolderStructureTab() {
  const STORAGE_KEY = 'leadex_folder_structure';
  const loadStructures = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || JSON.stringify(DEFAULT_FOLDER_TEMPLATES)); } catch(e) { return DEFAULT_FOLDER_TEMPLATES; } };
  const [structures, setStructures] = useState(loadStructures);
  const [editing,    setEditing]    = useState(null);
  const [saved,      setSaved]      = useState(false);
  const [copied,     setCopied]     = useState(null);

  const save = (idx, updated) => {
    const newList = structures.map((s,i)=>i===idx?updated:s);
    setStructures(newList);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newList));
    setEditing(null);
    setSaved(true); setTimeout(()=>setSaved(false), 2000);
  };

  const add = () => {
    const newItem = {name:'New Template', desc:'', structure:''};
    const newList = [...structures, newItem];
    setStructures(newList);
    setEditing(newList.length-1);
  };

  const del = (idx) => {
    if (!window.confirm('Delete this template?')) return;
    const newList = structures.filter((_,i)=>i!==idx);
    setStructures(newList);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newList));
  };

  const copy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopied(idx); setTimeout(()=>setCopied(null), 2000);
  };

  const reset = () => {
    if (!window.confirm('Reset to default folder templates?')) return;
    setStructures(DEFAULT_FOLDER_TEMPLATES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_FOLDER_TEMPLATES));
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <div className="card-title">📁 File & Asset Folder Structure</div>
          <div className="text-xs text-muted" style={{marginTop:'4px'}}>Standard folder naming conventions and file structure templates. Ensures consistent organization across all clients and campaigns.</div>
        </div>
        <div className="flex" style={{gap:'8px'}}>
          <button className="btn btn-secondary" onClick={reset}>↺ Reset Default</button>
          <button className="btn btn-primary"   onClick={add}>+ Add Template</button>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(380px,1fr))',gap:'16px'}}>
        {structures.map((s, idx) => (
          <div key={idx} className="card" style={{padding:'16px'}}>
            {editing===idx ? (
              <>
                <div className="form-group">
                  <label className="form-label">Template Name</label>
                  <input className="form-input" value={s.name} onChange={e=>{const u={...s,name:e.target.value};setStructures(st=>st.map((x,i)=>i===idx?u:x));}} />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input className="form-input" value={s.desc} onChange={e=>{const u={...s,desc:e.target.value};setStructures(st=>st.map((x,i)=>i===idx?u:x));}} />
                </div>
                <div className="form-group">
                  <label className="form-label">Folder Structure</label>
                  <textarea className="form-textarea" style={{minHeight:'200px',fontFamily:'monospace',fontSize:'0.8rem'}} value={s.structure} onChange={e=>{const u={...s,structure:e.target.value};setStructures(st=>st.map((x,i)=>i===idx?u:x));}} />
                </div>
                <div className="flex" style={{gap:'8px'}}>
                  <button className="btn btn-primary" onClick={()=>save(idx,s)}>💾 Save</button>
                  <button className="btn btn-secondary" onClick={()=>setEditing(null)}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="flex-between mb-8">
                  <div>
                    <div className="fw-700">{s.name}</div>
                    <div className="text-xs text-muted">{s.desc}</div>
                  </div>
                  <div className="flex" style={{gap:'4px'}}>
                    <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>copy(s.structure, idx)}>{copied===idx?'✅':'📋'}</button>
                    <button className="btn btn-secondary" style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>setEditing(idx)}>✏️</button>
                    <button className="btn btn-red"       style={{padding:'3px 8px',fontSize:'0.7rem'}} onClick={()=>del(idx)}>✕</button>
                  </div>
                </div>
                <pre style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'12px',fontSize:'0.75rem',lineHeight:'1.6',overflowX:'auto',whiteSpace:'pre-wrap',wordBreak:'break-word',margin:0,maxHeight:'220px',overflowY:'auto',color:'var(--text)'}}>
                  {s.structure}
                </pre>
              </>
            )}
          </div>
        ))}
      </div>

      {saved && (
        <div style={{position:'fixed',bottom:'24px',right:'24px',background:'#1db87a',color:'#fff',padding:'10px 18px',borderRadius:'8px',fontWeight:600,fontSize:'0.85rem',zIndex:9999}}>
          ✅ Saved!
        </div>
      )}
    </div>
  );
}

/**
 * Adds the standard Master Data for a new account (run once, on the owner's first login).
 * Uses the same standard lists and the same saves as the "Add Standard ..." buttons.
 * Items that already exist (matched by name) are skipped, so it is safe to re-run.
 * Campaign Types are already added by the backend when the account is created.
 */
export async function seedStandardMasterData() {
  const list = async (url) => { const r = await api.get(url); return r.data?.data || r.data || []; };
  const seedList = async (url, items, nameOf, existingNameOf, payloadOf) => {
    const existing = new Set((await list(url)).map(x => String(existingNameOf(x) || '').toLowerCase()));
    for (const item of items) {
      if (existing.has(String(nameOf(item)).toLowerCase())) continue;
      try { await api.post(url, payloadOf(item)); } catch (e) { /* skip item, continue */ }
    }
  };
  const slaName = r => r.task_type ? `${r.task_type} — ${r.priority}` : `All Tasks — ${r.priority}`;

  await seedList('/departments', STANDARD_DEPT_SETS.onehundred.depts, d => d.name, x => x.name,
    d => ({ name:d.name, description:d.description, color:d.color, enabled:true }));
  await seedList('/roles', STANDARD_ROLES, r => r.name, x => x.name,
    r => ({ name:r.name, department:r.department, dept:r.department, responsibilities:r.responsibilities, skills:r.skills, permissions:r.permissions, color:r.color, enabled:true }));
  await seedList('/platforms', STD_PLATFORMS, p => p.name, x => x.name, p => {
    const defaults = PLATFORM_DEFAULT_METRICS[p.name] || ['Reach','Likes','Comments','Shares'];
    const tpl = { metrics: defaults.map((label,i) => ({id:'m'+i,label})), referenceImage:'', notes:'' };
    return { name:p.name, category:p.category, icon:p.icon, color:p.color, audience:p.audience, notes:'', report_template:JSON.stringify(tpl), is_active:true };
  });
  await seedList('/services', DEFAULT_SERVICES, s => s.name, x => x.name,
    s => ({ name:s.name, category:s.category, description:s.desc, monthly_price:s.monthly_price, setup_fee:s.setup_fee, deliverables:JSON.stringify(s.deliverables), enabled:true }));
  await seedList('/sla-rules', DEFAULT_SLA_RULES, slaName, x => x.name, r => ({
    ...r,
    name:             slaName(r),
    phase:            r.priority,
    trigger:          'task_created',
    working_days:     String(r.turnaround_days),
    action_on_breach: 'escalate',
  }));
  await seedList('/tags', DEFAULT_TAGS, t => t.name, x => x.name, t => t);
  await seedList('/message-templates', DEFAULT_MSG_TEMPLATES, t => t.name, x => x.name,
    t => ({ name:t.name, category:t.category, body:t.body, icon:t.icon }));
  await seedList('/rate-cards', DEFAULT_RATE_CARDS, r => r.name, x => x.service || x.name,
    r => ({ service:r.name, category:r.category, unit:r.unit, rate:r.rate, min_rate:r.min_rate, max_rate:r.max_rate, notes:r.notes||'' }));
  await seedList('/report-templates', DEFAULT_REPORT_TEMPLATES, t => t.name, x => x.name, t => t);
  await seedList('/task-types', DEFAULT_TASK_TYPES, t => t.name, x => x.name,
    t => ({ name:t.name, platforms:JSON.stringify(t.platforms), format:t.format, size:t.size||'', duration:t.duration, priority:t.priority, description:t.desc, enabled:true }));
  await seedList('/content-types', DEFAULT_OUTPUT_CONTENT_TYPES, t => t.name, x => x.name,
    t => ({ name:t.name, description:t.desc, icon:t.icon, color:t.color, enabled:true }));
  // Output formats can share a name across platforms, so match on platform + name
  await seedList('/output-formats', OUTPUT_FORMAT_SEED_FULL, f => `${f.platform}|${f.name}`, x => `${x.platform}|${x.name}`, f => ({
    name: f.name, platform: f.platform, type: f.outputType,
    width: f.width, height: f.height, ratio: f.ratio,
    dimensions: f.width && f.height ? `${f.width} × ${f.height}` : '',
    aspect_ratio: f.ratio,
    duration: f.duration, file_format: f.fileFormat,
    file_size: f.fileSize, max_size: f.fileSize,
    safe_zone: f.safeZone,
    notes: f.notes, enabled: true
  }));
}

export default function MasterData() {
  const { can } = useAuth();
  const [activeTab, setActiveTab] = useState('depts');

  // Filter tabs: restricted tabs only if user has permission.
  // Tabs with no permissionKey stay open, unless this user has been given any sub-tab
  // access from the 'Master Data — Sub Tabs' list — then only the granted ones show.
  const subTabKey = t => `tab:masterdata:${t.id}`;
  const subTabsConfigured = TABS.some(t => !t.permissionKey && can(subTabKey(t))) && !can('*');
  const visibleTabs = TABS.filter(t => {
    if (can('*')) return true;
    if (t.permissionKey) return can(t.permissionKey);
    return !subTabsConfigured || can(subTabKey(t));
  });

  // If the current tab is not permitted, switch to the first permitted one
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some(t => t.id === activeTab)) setActiveTab(visibleTabs[0].id);
  }, [visibleTabs, activeTab]);

  // Tab bar horizontal scroll
  const tabsRef = useRef(null);
  const wrapperRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    if (wrapperRef.current) {
      wrapperRef.current.classList.toggle('has-scroll-right', el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    }
  }, []);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => { el.removeEventListener('scroll', checkScroll); window.removeEventListener('resize', checkScroll); };
  }, [checkScroll, visibleTabs]);

  const scrollTabs = (dir) => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  const renderTab = () => {
    switch(activeTab) {
      case 'depts':            return <DepartmentsTab />;
      case 'roles':            return <RolesTab />;
      case 'team':             return <TeamTab />;
      case 'platforms':        return <PlatformsTab />;
      case 'clients':          return <ClientsTab />;
      case 'vendors':          return <VendorsTab />;
      case 'holidays':         return <HolidaysTab />;
      case 'sla':              return <SLATab />;

      case 'tasktypes':
        return <TaskTypesTab />;

      case 'contenttypes':
        return <OutputContentTypesTab />;

      case 'outputformats':
        return <OutputFormatsTab />;

      case 'campaigntypes':
        return <CampaignTypesTab onManagePhases={(ctId) => { setActiveTab('phases'); }} />;

      case 'services':
        return <ServicesTab />;

      case 'internalapprovers':
        return <InternalApproversTab />;

      case 'brandguidelines':
        return <BrandGuidelinesTab />;

      case 'approvalcontacts':
        return <ApprovalContactsTab />;

      case 'competitors':
        return <CompetitorsTab />;

      case 'reporttemplates':
        return <ReportTemplatesTab />;

      case 'templates':
        return <MessageTemplatesTab />;

      case 'rates':
        return <RateCardTab />;

      case 'phases':
        return <PhasesTab />;

      case 'dashboardwidgets':
        return <DashboardWidgetsTab />;
      case 'contracts':
        return <ContractsTab />;
      case 'tags':
        return <TagsTab />;
      case 'notifications':
        return <NotificationRulesTab />;
      case 'paymentterms':
        return <PaymentTermsTab />;
      case 'integrations':
        return <IntegrationsTab />;
      case 'folderstructure':
        return <FolderStructureTab />;

      default:
        return <div className="card" style={{padding:'40px',textAlign:'center'}} className="text-muted">Tab coming soon…</div>;
    }
  };

  const exportAll = async () => {
    try {
      const r = await api.get('/master-data/export');
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `Leadex-MasterData-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch(e) { alert('Export failed: ' + e.message); }
  };

  const importAll = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    let payload;
    try { payload = JSON.parse(text); } catch { alert('Invalid JSON file'); return; }
    if (!payload.__leadexMasterDataExport) { alert('Not a valid Leadex Master Data export file'); return; }
    const cats = Object.keys(payload.data || {});
    if (!window.confirm(`Import Master Data?\n\nThis will OVERWRITE ${cats.length} categories.\n\nContinue?`)) return;
    try {
      const r = await api.post('/master-data/import', payload);
      alert('✅ ' + r.data.message + '\nPage will reload.');
      window.location.reload();
    } catch(e) { alert('Import failed: ' + (e.response?.data?.message || e.message)); }
    e.target.value = '';
  };

  return (
    <div style={{padding:'8px 24px 24px', overflowX:'hidden', maxWidth:'100%', boxSizing:'border-box'}}>
      {/* Header with Export/Import */}
      <div style={{display:'flex', justifyContent:'flex-end', gap:'8px', marginBottom:'10px'}}>
        <button className="btn btn-secondary" onClick={exportAll}>⬇️ Export All</button>
        <label className="btn btn-secondary" style={{cursor:'pointer'}}>
          ⬆️ Import
          <input type="file" accept=".json" style={{display:'none'}} onChange={importAll} />
        </label>
      </div>

      {/* Tab bar */}
      <div className="tabs-wrapper" ref={wrapperRef}>
        {canScrollLeft && (
          <button className="tab-scroll-btn tab-scroll-left" onClick={() => scrollTabs(-1)} aria-label="Scroll tabs left">‹</button>
        )}
        <div className="tabs" ref={tabsRef} style={{display:'flex',gap:'2px',overflowX:'auto',paddingBottom:'2px'}}>
          {visibleTabs.map(t => (
            <div key={t.id}
              className={`tab ${activeTab===t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}
              style={{whiteSpace:'nowrap',cursor:'pointer'}}>
              {t.icon} {t.label}
            </div>
          ))}
        </div>
        {canScrollRight && (
          <button className="tab-scroll-btn tab-scroll-right" onClick={() => scrollTabs(1)} aria-label="Scroll tabs right">›</button>
        )}
      </div>

      <div style={{marginTop:'20px'}}>
        {renderTab()}
      </div>
    </div>
  );
}